import { useState, useEffect, useCallback, useRef } from "react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { LayoutDashboard, DollarSign, Users, Activity, TrendingUp, Zap, Shield, Settings, Bell, Mail, FlaskConical, Flag, BarChart3, Search, Globe, Crosshair, Bug, FileText, LogOut, Check, X, Plus, Trash2, Crown, RefreshCw, CheckCircle, AlertTriangle, Star, ArrowUpRight, ArrowDownRight, Menu, Download, Calendar, Link, MessageSquare, Cpu } from "lucide-react";
import AutoPilotIntelligenceDashboard from "./pages/AutoPilotIntelligenceDashboard";

/* ═══════════════════════════════════════════════
   STYLE TOKENS
   ═══════════════════════════════════════════════ */
const DARK = { bg: "#060608", card: "#0c0c10", border: "#252530", accent: "#f97316", accentDim: "#1a0f00", text: "#f0f0f0", dim: "#9ca3af", muted: "#6b7280", green: "#22c55e", red: "#ef4444", blue: "#3b82f6", purple: "#a855f7", cyan: "#06b6d4", yellow: "#eab308" };
const LIGHT = { bg: "#f5f5f7", card: "#ffffff", border: "#e0e0e5", accent: "#ea580c", accentDim: "#fff7ed", text: "#1a1a1a", dim: "#71717a", muted: "#a1a1aa", green: "#16a34a", red: "#dc2626", blue: "#2563eb", purple: "#9333ea", cyan: "#0891b2", yellow: "#ca8a04" };
let _theme = "dark";
const C = new Proxy({}, { get(_, p) { return (_theme === "light" ? LIGHT : DARK)[p]; }, ownKeys() { return Object.keys(DARK); }, getOwnPropertyDescriptor(_, p) { return { value: (_theme === "light" ? LIGHT : DARK)[p], enumerable: true, configurable: true }; } });
const APPS_DEFAULT = ["radiant", "mise", "drift", "lidarmapper", "velkravoice", "clipforge", "deallens", "cleanerkit", "radiopulse", "linguist", "leaflens", "prosuite", "flavorbook", "cardtap", "braintrainer", "familybridge", "gpsareameasure", "mathlens", "notecraft", "docuflow", "prophotoeditor", "bookkeeper", "quicktransfer", "dailyplanner", "nightwell", "dreamwell", "velkrabrowse", "talktext", "calmhaven", "trainforge", "fitvault", "cashpilot", "inkboard", "pawtrack", "skyloom", "tuneforge", "growlog", "packlist", "nestiq", "labnote", "snapmeter", "routeforge", "vaultkeep", "habitloop", "soundbyte", "pixelpop", "timecraft", "birdlog", "stepquest", "aquatrack"];
let APPS = APPS_DEFAULT; // M3: Overridden dynamically after login

/* App metadata — keep in sync with Web/Catalogue/data.js. Used by AppSelector + AppIconLabel. */
const APP_META = {
  "radiant": { icon: "☀", color: "#F59E0B", name: "Radiant", tag: "Journal & Mood Tracker" },
  "mise": { icon: "🥗", color: "#10B981", name: "Mise", tag: "Meal Planner & Nutrition" },
  "drift": { icon: "🌙", color: "#818CF8", name: "Drift", tag: "Sleep Coach" },
  "nightwell": { icon: "🎵", color: "#A78BFA", name: "NightWell", tag: "White Noise & Sounds" },
  "braintrainer": { icon: "🧠", color: "#F472B6", name: "BrainTrainer", tag: "Cognitive Training" },
  "mathlens": { icon: "📐", color: "#60A5FA", name: "MathLens", tag: "Camera Math Solver" },
  "linguist": { icon: "🌍", color: "#2DD4BF", name: "Linguist", tag: "Language Learning" },
  "notecraft": { icon: "📝", color: "#FB923C", name: "Notecraft", tag: "Rich Note-Taking" },
  "dailyplanner": { icon: "📅", color: "#38BDF8", name: "DailyPlanner", tag: "Planner & Habits" },
  "docuflow": { icon: "📄", color: "#F87171", name: "DocuFlow", tag: "PDF Reader & Editor" },
  "prosuite": { icon: "⚡", color: "#C084FC", name: "ProSuite", tag: "Productivity Suite" },
  "velkravoice": { icon: "🎙", color: "#E879F9", name: "VelkraVoice", tag: "Voice Recorder" },
  "clipforge": { icon: "🎬", color: "#FB7185", name: "ClipForge", tag: "Video Editor" },
  "prophotoeditor": { icon: "📸", color: "#D946EF", name: "ProPhotoEditor", tag: "Photo Studio" },
  "cardtap": { icon: "💳", color: "#6366F1", name: "CardTap", tag: "Digital Business Card" },
  "bookkeeper": { icon: "📊", color: "#34D399", name: "BookKeeper", tag: "Small Biz Accounting" },
  "deallens": { icon: "🏷", color: "#FBBF24", name: "DealLens", tag: "Deal Finder" },
  "leaflens": { icon: "🌿", color: "#4ADE80", name: "LeafLens", tag: "Plant Identifier" },
  "flavorbook": { icon: "👨‍🍳", color: "#F97316", name: "FlavorBook", tag: "Step-by-Step Cooking" },
  "familybridge": { icon: "👨‍👩‍👧", color: "#22D3EE", name: "FamilyBridge", tag: "Co-Parenting" },
  "lidarmapper": { icon: "📏", color: "#7C3AED", name: "LiDARMapper", tag: "3D Room Scanner" },
  "cleanerkit": { icon: "🧹", color: "#94A3B8", name: "CleanerKit", tag: "Storage Optimizer" },
  "gpsareameasure": { icon: "🗺", color: "#22C55E", name: "GPSAreaMeasure", tag: "Land Measurement" },
  "quicktransfer": { icon: "📤", color: "#0EA5E9", name: "QuickTransfer", tag: "File Transfer" },
  "radiopulse": { icon: "📻", color: "#EF4444", name: "RadioPulse", tag: "Radio & Podcasts" },
  "dreamwell": { icon: "😴", color: "#7C3AED", name: "DreamWell", tag: "Sleep & Wellness" },
  "velkrabrowse": { icon: "🧅", color: "#1E293B", name: "VelkraBrowse", tag: "Private Browser" },
  "talktext": { icon: "🦦", color: "#059669", name: "TalkText", tag: "Voice Transcription" },
  "snapmeter": { icon: "⚡", color: "#F59E0B", name: "SnapMeter", tag: "Meter Scanner & Bills" },
  "routeforge": { icon: "🗺️", color: "#3B82F6", name: "RouteForge", tag: "Road Trip Planner" },
  "vaultkeep": { icon: "🔐", color: "#6366F1", name: "VaultKeep", tag: "Password Vault" },
  "habitloop": { icon: "✅", color: "#10B981", name: "HabitLoop", tag: "Habit Tracker" },
  "soundbyte": { icon: "🎵", color: "#8B5CF6", name: "SoundByte", tag: "Sound Board & Effects" },
  "pixelpop": { icon: "🎨", color: "#EC4899", name: "PixelPop", tag: "Pixel Art Studio" },
  "timecraft": { icon: "⏱️", color: "#0EA5E9", name: "TimeCraft", tag: "Time Tracking" },
  "birdlog": { icon: "🐦", color: "#22C55E", name: "BirdLog", tag: "Birding Journal" },
  "stepquest": { icon: "🏃", color: "#F97316", name: "StepQuest", tag: "Gamified Walking" },
  "aquatrack": { icon: "💧", color: "#06B6D4", name: "AquaTrack", tag: "Hydration Tracker" },
  "calmhaven": { icon: "🧘", color: "#8B5CF6", name: "CalmHaven", tag: "Meditation & Mindfulness" },
  "cashpilot": { icon: "💰", color: "#14B8A6", name: "CashPilot", tag: "Personal Finance" },
  "fitvault": { icon: "💪", color: "#DC2626", name: "FitVault", tag: "Workout & Strength" },
  "growlog": { icon: "🌱", color: "#84CC16", name: "GrowLog", tag: "Garden & Plant Care" },
  "inkboard": { icon: "✏️", color: "#1E40AF", name: "InkBoard", tag: "Sketchbook & Drawing" },
  "labnote": { icon: "🔬", color: "#0891B2", name: "LabNote", tag: "Lab Notebook" },
  "nestiq": { icon: "🏠", color: "#EAB308", name: "NestIQ", tag: "Smart Home Hub" },
  "packlist": { icon: "🧳", color: "#3B82F6", name: "PackList", tag: "Packing & Travel Lists" },
  "pawtrack": { icon: "🐾", color: "#92400E", name: "PawTrack", tag: "Pet Care & Health" },
  "skyloom": { icon: "☁️", color: "#0EA5E9", name: "SkyLoom", tag: "Weather & Astronomy" },
  "trainforge": { icon: "🏃", color: "#F43F5E", name: "TrainForge", tag: "Run & Cycle Training" },
  "tuneforge": { icon: "🎵", color: "#A855F7", name: "TuneForge", tag: "Music Production" },
};
// Helper: how an app's display label appears in <option> text (emoji + name)
const appLabel = (slug) => {
  const m = APP_META[slug];
  return m ? `${m.icon} ${m.name}` : slug;
};
const CHART_COLORS = [C.accent, C.blue, C.green, C.purple, C.cyan, C.yellow, C.red];
const inp = () => ({ width: "100%", padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: "none", boxSizing: "border-box" });
const lbl = () => ({ display: "block", fontSize: 11, color: C.dim, fontWeight: 500, marginBottom: 4, marginTop: 8 });
const btnP = () => ({ display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", background: C.accent, color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer" });
const PAGES_WITHOUT_APP = new Set(["approvals","ai","autopilot","gdpr","audit","accounts","alerts","reports","search","anomalies","webhookBuilder","aiChat","webhooks","monitoring","automation","buildQueue","phasedRollout","pushOptimization","revenueEstimation","distTesters","distGroups","distBuilds","distDevices","distFeedback","distBugReports","distCrashes","distReliability","distAudit","distStorage","distABTest","distCompare","distFixQueue","distAutoFixSettings"]);

/* ═══════════════════════════════════════════════
   API HELPER
   ═══════════════════════════════════════════════ */
// Fix D1: sessionStorage token — clear on tab close to limit XSS window.
// NOTE: For production, migrate to httpOnly cookie set by backend (see MODULARIZATION.md).
// Remember Me: localStorage persists across browser sessions, sessionStorage clears on tab close
function getToken() { return localStorage.getItem("stellarToken") || sessionStorage.getItem("stellarToken") || ""; }
// Backend URL resolution priority:
//   1. localStorage/sessionStorage (user override) — survives across sessions
//   2. VITE_API_BASE env var (set on Vercel: e.g. https://stellar-api-service-production.up.railway.app)
//   3. Hardcoded fallback (api.velkra.app — once DNS is wired up to Railway)
function getDefaultBackendUrl() {
  const fromEnv = (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE) || "";
  if (fromEnv) return fromEnv;
  return "https://api.velkra.app";
}
function getUrl() {
  return localStorage.getItem("stellarUrl") || sessionStorage.getItem("stellarUrl") || getDefaultBackendUrl();
}
function setToken(t, remember) {
  // Clear both first
  localStorage.removeItem("stellarToken"); sessionStorage.removeItem("stellarToken");
  if (!t) return;
  if (remember || localStorage.getItem("stellarRemember") === "1") { localStorage.setItem("stellarToken", t); localStorage.setItem("stellarRemember", "1"); }
  else { sessionStorage.setItem("stellarToken", t); }
}
function setUrl(u) { localStorage.setItem("stellarUrl", u); sessionStorage.setItem("stellarUrl", u); }
function getRememberedEmail() { return localStorage.getItem("stellarEmail") || ""; }
function setRememberedEmail(email, remember) { if (remember) localStorage.setItem("stellarEmail", email); else localStorage.removeItem("stellarEmail"); }

// Fix D3/D4: Validate IDs before interpolation into URL paths (matches iOS sanitizeId)
function sanitizeId(id) {
  if (!id || typeof id !== "string") return null;
  const cleaned = id.trim();
  if (!cleaned || !/^[a-zA-Z0-9_-]+$/.test(cleaned)) return null;
  return cleaned;
}

// Fix D2: Validate backend URL is HTTPS (or localhost for dev)
function isSecureUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
  } catch(_) { return false; }
}

// Auth state — simple approach: 401 = show error, user can retry or sign out
async function api(path, opts = {}) {
  const base = getUrl();
  if (!base) throw new Error("No backend URL configured");
  try { new URL(base); } catch(_) { throw new Error("Invalid backend URL"); }
  if (!isSecureUrl(base)) throw new Error("HTTPS is required. Update your backend URL.");
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...opts.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  let r;
  try {
    r = await fetch(`${base}${path}`, { ...opts, headers, body: opts.body ? JSON.stringify(opts.body) : undefined });
  } catch (networkErr) {
    throw new Error(`Cannot reach server: ${networkErr.message}`);
  }
  const text = await r.text();
  let data = {};
  if (text) { try { data = JSON.parse(text); } catch(_) { data = { raw: text }; } }
  if (r.status === 401) {
    // Don't aggressively clear session — let the auth/me handler decide
    throw new Error(data.error || "Unauthorized");
  }
  if (!r.ok) { const msg = data.error || `Error ${r.status}`; throw new Error(msg.length > 200 ? msg.substring(0, 200) + "…" : msg); }
  return data;
}

/* ═══════════════════════════════════════════════
   HOOKS
   ═══════════════════════════════════════════════ */
// Fix D13: `deps` controls when `load` is recreated. The fetch function itself is read from
// fnRef.current (avoiding stale closures). Callers: pass stable deps (primitives, not objects)
// to avoid infinite re-fetching loops.
function useFetch(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  const dataRef = useRef(null);
  fnRef.current = fn;
  const load = useCallback(async () => {
    setError(null);
    let delayedLoadingTimer = null;
    if (dataRef.current === null) {
      // First load — show spinner immediately (no stale data to display)
      setLoading(true);
    } else {
      // Refetch with stale data already shown — only escalate to spinner
      // if the request takes >250ms. Most cached/fast responses finish first.
      delayedLoadingTimer = setTimeout(() => setLoading(true), 250);
    }
    try {
      const d = await fnRef.current();
      dataRef.current = d;
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      if (delayedLoadingTimer) clearTimeout(delayedLoadingTimer);
      setLoading(false);
    }
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, error, loading, reload: load };
}

function usePolling(fn, interval, deps = []) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const tick = () => { if (!document.hidden) ref.current(); };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [interval, ...deps]);
}

// U1: Warn before navigating away with unsaved changes
function useUnsavedWarning(isDirty) {
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);
}

/* ═══════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════ */
const Card = ({ children, style, onClick }) => <div onClick={onClick} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, ...style }}>{children}</div>;
const Stat = ({ label, value, sub, trend, color }) => (
  <Card><div style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 700, color: color || C.text, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>{sub}</div>}
    {trend !== undefined && <div style={{ fontSize: 12, color: trend >= 0 ? C.green : C.red, marginTop: 4, display: "flex", alignItems: "center", gap: 2 }}>{trend >= 0 ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}{Math.abs(trend)}%</div>}
  </Card>
);
const SectionTitle = ({ children }) => <h2 style={{ color: C.text, fontSize: 18, fontWeight: 600, margin: "24px 0 12px" }}>{children}</h2>;
const Badge = ({ children, color }) => <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 6, background: (color || C.accent) + "18", color: color || C.accent }}>{children}</span>;
const Btn = ({ children, onClick, primary, disabled, small, destructive, style: s }) => <button onClick={onClick} disabled={disabled} style={{ display: "flex", alignItems: "center", gap: 6, padding: small ? "4px 10px" : "8px 16px", background: destructive ? C.red : primary ? C.accent : C.card, color: destructive || primary ? "#fff" : C.dim, border: `1px solid ${primary || destructive ? "transparent" : C.border}`, borderRadius: 8, fontSize: small ? 12 : 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, ...s }}>{children}</button>;
const AppSelector = ({ app, setApp }) => (
  <select value={app} onChange={e => setApp(e.target.value)} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: "pointer", background: C.card, color: C.text, outline: "none", minWidth: 180 }}>
    {APPS.map(a => <option key={a} value={a}>{appLabel(a)}</option>)}
  </select>
);
// Inline icon + name for buttons / chips. `hideLabel` for icon-only.
const AppIconLabel = ({ slug, hideLabel = false, size = 18 }) => {
  const meta = APP_META[slug];
  if (!meta) return <span style={{ textTransform: "capitalize" }}>{slug}</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span aria-hidden="true" style={{ fontSize: size, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0 }}>{meta.icon}</span>
      {!hideLabel && <span>{meta.name}</span>}
    </span>
  );
};
const Grid = ({ cols, children, gap }) => <div className="resp-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${cols || 4}, 1fr)`, gap: gap || 12 }}>{children}</div>;
const Loading = () => <div style={{ color: C.dim, padding: 40, textAlign: "center" }}><RefreshCw size={20} style={{ animation: "spin 1s linear infinite" }}/><style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style><div style={{ marginTop: 8 }}>Loading...</div></div>;
const Empty = ({ msg }) => <div style={{ color: C.muted, padding: 40, textAlign: "center" }}>{msg || "No data"}</div>;

const ErrorState = ({ error, onRetry }) => (
  <Card style={{ textAlign: "center", padding: 40 }}>
    <AlertTriangle size={24} color={C.red} style={{ marginBottom: 8 }}/>
    <div style={{ color: C.red, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Failed to load</div>
    <div style={{ color: C.dim, fontSize: 13, marginBottom: 16 }}>{error}</div>
    {onRetry && <Btn primary onClick={onRetry}><RefreshCw size={14}/> Retry</Btn>}
  </Card>
);

function extractText(node, depth = 0) {
  if (depth > 20) return ""; // Fix D14: Prevent stack overflow on deeply nested React trees
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (typeof node === "boolean") return node ? "Yes" : "No";
  if (Array.isArray(node)) return node.map(n => extractText(n, depth + 1)).join(" ");
  if (typeof node === "object" && node.props) {
    return extractText(node.props.children, depth + 1);
  }
  return String(node);
}

function exportCSV(cols, rows, filename) {
  const header = cols.join(",");
  const body = rows.map(r => r.map(c => {
    let s = extractText(c);
    // Fix D-2.3: Prevent CSV formula injection — neutralize cells that start with formula characters
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return `"${s.replace(/"/g, '""')}"`;
  }).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const Table = ({ cols, rows, exportName }) => (
  <div>
    {exportName && rows.length > 0 && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
      <button onClick={() => exportCSV(cols, rows, `${exportName}.csv`)} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", color: C.dim, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}><Download size={11}/> CSV</button>
    </div>}
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead><tr>{cols.map((c, i) => <th key={i} style={{ textAlign: "left", padding: "8px 12px", borderBottom: `1px solid ${C.border}`, color: C.dim, fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((row, i) => <tr key={i} style={{ borderBottom: `1px solid ${C.border}08` }}>{row.map((cell, j) => <td key={j} style={{ padding: "10px 12px", color: C.text }}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  </div>
);

function ConfirmDialog({ open, title, message, confirmLabel, destructive, onConfirm, onCancel }) {
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)" }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: 380, maxWidth: "90vw" }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.dim, marginBottom: 20, lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={onCancel}>Cancel</Btn>
          <Btn primary={!destructive} destructive={destructive} onClick={onConfirm}>{confirmLabel || "Confirm"}</Btn>
        </div>
      </div>
    </div>
  );
}

// A2: Error boundary — catches page-level errors without crashing the whole app
import React from "react";
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("ErrorBoundary caught:", error, info); }
  render() {
    if (this.state.hasError) return (
      <Card style={{ textAlign: "center", padding: 40 }}>
        <AlertTriangle size={24} color={C.red} style={{ marginBottom: 8 }}/>
        <div style={{ color: C.red, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Something went wrong</div>
        <div style={{ color: C.dim, fontSize: 13, marginBottom: 16 }}>{this.state.error?.message || "Unknown error"}</div>
        <Btn primary onClick={() => this.setState({ hasError: false, error: null })}><RefreshCw size={14}/> Try Again</Btn>
      </Card>
    );
    return this.props.children;
  }
}

/* ═══════════════════════════════════════════════
   COMMAND PALETTE (Cmd+K)
   ═══════════════════════════════════════════════ */
function CommandPalette({ open, onClose, onNavigate, onAppChange, currentApp }) {
  const [q, setQ] = useState("");
  const inputRef = React.useRef(null);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  useEffect(() => { setQ(""); }, [open]);

  const PAGES = [
    { id: "dashboard", label: "Dashboard", icon: "📊", section: "Pages" },
    { id: "realtime", label: "Real-time Events", icon: "⚡", section: "Pages" },
    { id: "revenue", label: "Revenue", icon: "💰", section: "Pages" },
    { id: "retention", label: "Retention", icon: "📈", section: "Pages" },
    { id: "churn", label: "Churn Analysis", icon: "🔻", section: "Pages" },
    { id: "sessions", label: "Sessions", icon: "👤", section: "Pages" },
    { id: "funnels", label: "Funnels", icon: "🔽", section: "Pages" },
    { id: "experiments", label: "Experiments", icon: "🧪", section: "Pages" },
    { id: "flags", label: "Feature Flags", icon: "🚩", section: "Pages" },
    { id: "push", label: "Push Campaigns", icon: "📱", section: "Pages" },
    { id: "emails", label: "Email Campaigns", icon: "✉️", section: "Pages" },
    { id: "reviews", label: "App Reviews", icon: "⭐", section: "Pages" },
    { id: "crashes", label: "Crash Reports", icon: "💥", section: "Pages" },
    { id: "ads", label: "Ad Performance", icon: "📢", section: "Pages" },
    { id: "competitors", label: "Competitors", icon: "🔍", section: "Pages" },
    { id: "aso", label: "ASO Rankings", icon: "🏆", section: "Pages" },
    { id: "ai", label: "AI Monitor", icon: "🤖", section: "Pages" },
    { id: "mlIntelligence", label: "ML Intelligence", icon: "🧠", section: "Pages" },
    { id: "empireHealth", label: "Empire Health", icon: "💚", section: "Pages" },
    { id: "appProfile", label: "App Profiles", icon: "📱", section: "Pages" },
    { id: "gamification", label: "Gamification", icon: "🏆", section: "Pages" },
    { id: "empireOverview", label: "Empire Overview", icon: "👑", section: "Pages" },
    { id: "approvals", label: "Approvals", icon: "✅", section: "Pages" },
    { id: "social", label: "Social Hub", icon: "📣", section: "Pages" },
    { id: "pricing", label: "Pricing", icon: "💲", section: "Pages" },
    { id: "launch", label: "Launch Checklist", icon: "🚀", section: "Pages" },
    { id: "accounts", label: "Accounts", icon: "👥", section: "Admin" },
    { id: "audit", label: "Audit Log", icon: "📋", section: "Admin" },
    { id: "sql", label: "SQL Explorer", icon: "🗄️", section: "Admin" },
    { id: "alerts", label: "Smart Alerts", icon: "🔔", section: "Admin" },
    { id: "reports", label: "Scheduled Reports", icon: "📅", section: "Admin" },
    { id: "search", label: "Global Search", icon: "🔍", section: "Admin" },
    { id: "anomalies", label: "Anomaly Detection", icon: "📊", section: "Intelligence" },
    { id: "webhookBuilder", label: "Webhook Builder", icon: "🔗", section: "Admin" },
    { id: "aiChat", label: "AI Chat", icon: "🤖", section: "Intelligence" },
    { id: "gdpr", label: "GDPR / Privacy", icon: "🛡️", section: "Admin" },
    { id: "support", label: "Support Tickets", icon: "🎫", section: "Admin" },
    { id: "testHealth", label: "Test Health", icon: "🧪", section: "Admin" },
    { id: "playbooks", label: "Playbooks", icon: "📖", section: "Admin" },
    { id: "webhooks", label: "Webhooks", icon: "🔗", section: "Admin" },
    { id: "monitoring", label: "Monitoring", icon: "📡", section: "Admin" },
    { id: "automation", label: "Automation", icon: "⚙️", section: "Admin" },
    { id: "competitorKeywords", label: "Competitor Keywords", icon: "🔑", section: "Intelligence" },
    { id: "subscriptions", label: "Subscriptions", icon: "💳", section: "Pages" },
    { id: "onboarding", label: "Onboarding Funnels", icon: "🎯", section: "Pages" },
    { id: "phasedRollout", label: "Phased Rollout", icon: "🚢", section: "Pages" },
    { id: "pushOptimization", label: "Push Optimization", icon: "📲", section: "Intelligence" },
    { id: "revenueEstimation", label: "Revenue Forecast", icon: "📈", section: "Intelligence" },
    { id: "paywallOptimization", label: "Paywall A/B", icon: "🔒", section: "Pages" },
    { id: "buildQueue", label: "Build Queue", icon: "🔨", section: "Admin" },
  ];
  const APP_ITEMS = APPS.map(a => {
    const meta = APP_META[a];
    return {
      id: `app:${a}`,
      label: meta?.name || a,
      icon: meta?.icon || "📱",
      section: "Switch App",
      action: () => onAppChange(a),
    };
  });
  const all = [...PAGES, ...APP_ITEMS];
  const filtered = q.trim() ? all.filter(i => i.label.toLowerCase().includes(q.toLowerCase())) : all.slice(0, 12);
  const [sel, setSel] = useState(0);
  useEffect(() => { setSel(0); }, [q]);

  const execute = (item) => {
    if (item.action) item.action();
    else onNavigate(item.id);
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel(s => Math.min(s + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
    else if (e.key === "Enter" && filtered[sel]) { execute(filtered[sel]); }
    else if (e.key === "Escape") { onClose(); }
  };

  if (!open) return null;
  return (<div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", paddingTop: 120, backdropFilter: "blur(4px)" }}>
    <div onClick={e => e.stopPropagation()} style={{ width: 520, maxHeight: 440, background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
        <Search size={16} style={{ color: C.dim, flexShrink: 0 }}/>
        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={handleKey} placeholder="Search pages, apps, actions..." style={{ ...inp(), border: "none", background: "transparent", fontSize: 15, padding: 0, flex: 1, outline: "none" }}/>
        <kbd style={{ fontSize: 10, color: C.dim, background: C.bg, padding: "2px 6px", borderRadius: 4, border: `1px solid ${C.border}` }}>ESC</kbd>
      </div>
      <div style={{ maxHeight: 360, overflowY: "auto", padding: "4px 0" }}>
        {filtered.map((item, i) => (
          <div key={item.id} onClick={() => execute(item)} onMouseEnter={() => setSel(i)}
            style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: i === sel ? C.accentDim : "transparent", color: i === sel ? C.accent : C.text, fontSize: 14, transition: "background 0.1s" }}>
            <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{item.icon}</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            <span style={{ fontSize: 11, color: C.dim }}>{item.section}</span>
          </div>
        ))}
        {filtered.length === 0 && <div style={{ padding: 24, textAlign: "center", color: C.dim, fontSize: 13 }}>No results for "{q}"</div>}
      </div>
    </div>
  </div>);
}

/* ═══════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════ */
export default function App() {
  const [token, setTokenState] = useState(getToken());
  const [me, setMe] = useState(null);
  const [page, setPage] = useState("dashboard");
  // Navigate with View Transitions API where available — Chrome/Edge 111+, Safari 18+.
  // Falls back to plain setPage on older browsers (still no flicker due to useFetch
  // stale-while-revalidate, but no crossfade animation).
  const navigate = useCallback((newPage) => {
    if (newPage === page) return;
    if (typeof document !== "undefined" && document.startViewTransition) {
      document.startViewTransition(() => setPage(newPage));
    } else {
      setPage(newPage);
    }
  }, [page]);
  const [cmdOpen, setCmdOpen] = useState(false);
  useEffect(() => { const handler = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setCmdOpen(o => !o); } }; window.addEventListener("keydown", handler); return () => window.removeEventListener("keydown", handler); }, []);
  const [app, setApp] = useState("radiant");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [theme, setTheme] = useState(() => { const t = localStorage.getItem("stellarTheme") || "dark"; _theme = t; return t; });
  const toggleTheme = () => { const t = theme === "dark" ? "light" : "dark"; _theme = t; setTheme(t); localStorage.setItem("stellarTheme", t); };
  // Sync theme to document so scrollbars, overscroll bounce, and form controls match.
  // Without this, you toggle to light mode but the page <body> stays dark behind any
  // overscroll, which looks broken.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const colors = theme === "light"
      ? { bg: "#f5f5f7", text: "#1a1a1a" }
      : { bg: "#060608", text: "#f0f0f0" };
    document.body.style.backgroundColor = colors.bg;
    document.body.style.color = colors.text;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpError, setCpError] = useState("");
  const [cpSuccess, setCpSuccess] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);
  const doChangePw = async () => { if (cpNew.length < 8 || cpNew !== cpConfirm) return; setCpLoading(true); setCpError(""); try { await api("/v1/auth/change-password", { method: "POST", body: { currentPassword: cpCurrent, newPassword: cpNew } }); setCpSuccess(true); setTimeout(() => { setShowChangePw(false); setCpCurrent(""); setCpNew(""); setCpConfirm(""); setCpSuccess(false); }, 1500); } catch (e) { setCpError(e.message); } setCpLoading(false); };
  const [pendingCount, setPendingCount] = useState(0);
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (label) => setOpenSections(p => {
    if (Object.keys(p).length === 0) {
      const o = {}; SECTIONS.forEach(s => { o[s.label] = true; }); o[label] = false; return o;
    }
    return { ...p, [label]: !p[label] };
  });

  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Step 1: Verify token with /auth/me BEFORE doing anything else
  useEffect(() => {
    if (!token) { setAuthChecked(false); return; }
    setAuthError(null); setAuthChecked(false);
    api("/v1/auth/me").then(d => {
      setMe(d);
      setAuthChecked(true);
    }).catch(err => {
      console.warn("Auth check failed:", err.message);
      // If 401/unauthorized, token is invalid — go back to login
      if (err.message.includes("nauthorized") || err.message.includes("expired") || err.message.includes("Session") || err.message.includes("Invalid")) {
        localStorage.removeItem("stellarToken"); localStorage.removeItem("stellarRemember");
        sessionStorage.removeItem("stellarToken");
        setTokenState("");
        setMe(null);
      } else {
        // Network error — show retry screen
        setAuthError(err.message);
      }
    });
  }, [token]);

  // Step 2: Only fetch app list + start polling AFTER auth is confirmed
  useEffect(() => {
    if (!authChecked || !token) return;
    api("/v1/ai/snapshot").then(snap => {
      if (snap?.apps) {
        const keys = Object.keys(snap.apps);
        // Merge backend's known apps with our default 50 — backend may have analytics
        // data for fewer apps but the user's portfolio still has all 50. Without merging,
        // the AppSelector dropdown collapses to whatever subset has analytics data.
        const merged = Array.from(new Set([...APPS_DEFAULT, ...keys]));
        if (merged.length > 0) { APPS = merged; if (!merged.includes(app)) setApp(merged[0]); }
      }
    }).catch(() => {});
    const fetchPending = () => api("/v1/command/pending").then(d => setPendingCount(d?.pending?.length || 0)).catch(() => {});
    fetchPending();
    const id = setInterval(fetchPending, 30000);
    return () => clearInterval(id);
  }, [authChecked]);

  const doLogin = (t, remember) => { setToken(t, remember); setTokenState(t); };
  const doLogout = () => {
    const base = getUrl(); const t = getToken();
    if (base && t) {
      fetch(`${base}/v1/auth/logout`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` } }).catch(() => {});
    }
    localStorage.removeItem("stellarToken"); localStorage.removeItem("stellarRemember");
    sessionStorage.removeItem("stellarToken");
    setTokenState(""); setMe(null); setAuthError(null);
  };

  if (!token) return <LoginPage onLogin={doLogin}/>;
  if (!me) return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", background: C.bg, color: C.dim, fontFamily: "'DM Sans',sans-serif", gap: 16 }}>
      {authError ? (<>
        <AlertTriangle size={32} color={C.red}/>
        <div style={{ color: C.text, fontWeight: 600, fontSize: 16 }}>Connection Failed</div>
        <div style={{ color: C.dim, fontSize: 13, maxWidth: 360, textAlign: "center", lineHeight: 1.5 }}>{authError}</div>
        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <Btn primary onClick={() => { setAuthError(null); const t = getToken(); setTokenState(t || ""); }} style={{ fontSize: 13 }}>Retry</Btn>
          <Btn onClick={doLogout} style={{ fontSize: 13 }}>Sign Out</Btn>
        </div>
      </>) : (<>
        <RefreshCw size={24} style={{ animation: "spin 1s linear infinite" }}/>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        Connecting to server...
      </>)}
    </div>
  );

  const perms = me.permissions?.length ? me.permissions : (me.account?.isOwner ? ['*'] : []);
  const can = p => perms.includes('*') || perms.includes(p);
  const showApp = !PAGES_WITHOUT_APP.has(page);

  const SECTIONS = [
    { label: "Overview", items: [
      { id: "dashboard", icon: <LayoutDashboard size={16}/>, label: "Dashboard", perm: "dashboard:read" },
      { id: "realtime", icon: <Activity size={16}/>, label: "Real-Time", perm: "analytics:read" },
    ]},
    { label: "Analytics", items: [
      { id: "revenue", icon: <DollarSign size={16}/>, label: "Revenue", perm: "analytics:read" },
      { id: "retention", icon: <TrendingUp size={16}/>, label: "Retention", perm: "analytics:read" },
      { id: "sessions", icon: <Activity size={16}/>, label: "Sessions", perm: "analytics:read" },
      { id: "funnels", icon: <BarChart3 size={16}/>, label: "Funnels", perm: "analytics:read" },
      { id: "stickiness", icon: <TrendingUp size={16}/>, label: "Stickiness", perm: "analytics:read" },
      { id: "breakdown", icon: <BarChart3 size={16}/>, label: "Breakdowns", perm: "analytics:read" },
      { id: "correlations", icon: <TrendingUp size={16}/>, label: "Correlations", perm: "analytics:read" },
    ]},
    { label: "Users", items: [
      { id: "profiles", icon: <Users size={16}/>, label: "Profiles", perm: "analytics:read" },
      { id: "churn", icon: <AlertTriangle size={16}/>, label: "Churn & Health", perm: "analytics:read" },
      { id: "segments", icon: <Users size={16}/>, label: "Segments", perm: "analytics:read" },
      { id: "tags", icon: <Users size={16}/>, label: "Tags", perm: "analytics:read" },
      { id: "groups", icon: <Users size={16}/>, label: "Groups", perm: "analytics:read" },
      { id: "conversion", icon: <TrendingUp size={16}/>, label: "Conversion Scores", perm: "analytics:read" },
    ]},
    { label: "Engagement", items: [
      { id: "push", icon: <Bell size={16}/>, label: "Push", perm: "campaigns:manage" },
      { id: "emails", icon: <Mail size={16}/>, label: "Emails", perm: "campaigns:manage" },
      { id: "iam", icon: <Bell size={16}/>, label: "In-App Messages", perm: "campaigns:manage" },
      { id: "drip", icon: <Bell size={16}/>, label: "Drip Campaigns", perm: "campaigns:manage" },
    ]},
    { label: "Growth", items: [
      { id: "ads", icon: <Globe size={16}/>, label: "Ads & ROAS", perm: "ads:manage" },
      { id: "aso", icon: <Search size={16}/>, label: "ASO", perm: "analytics:read" },
      { id: "competitors", icon: <Crosshair size={16}/>, label: "Competitors", perm: "analytics:read" },
      { id: "social", icon: <Globe size={16}/>, label: "Social", perm: "analytics:read" },
      { id: "reviews", icon: <Star size={16}/>, label: "Reviews", perm: "analytics:read" },
      { id: "referrals", icon: <Users size={16}/>, label: "Referrals", perm: "campaigns:manage" },
      { id: "deeplinks", icon: <Globe size={16}/>, label: "Deep Links", perm: "campaigns:manage" },
    ]},
    { label: "Experiments", items: [
      { id: "experiments", icon: <FlaskConical size={16}/>, label: "Experiments", perm: "experiments:manage" },
      { id: "flags", icon: <Flag size={16}/>, label: "Feature Flags", perm: "experiments:manage" },
      { id: "paywalls", icon: <Shield size={16}/>, label: "Paywalls", perm: "experiments:manage" },
    ]},
    { label: "Intelligence", items: [
      { id: "ai", icon: <Zap size={16}/>, label: "AI Monitor", perm: "analytics:read" },
      { id: "autopilot", icon: <Cpu size={16}/>, label: "AutoPilot", perm: "analytics:read" },
      { id: "forecast", icon: <TrendingUp size={16}/>, label: "Forecast", perm: "billing:read" },
      { id: "journeys", icon: <Globe size={16}/>, label: "User Journeys", perm: "analytics:read" },
      { id: "adoption", icon: <Activity size={16}/>, label: "Adoption", perm: "analytics:read" },
      { id: "taxonomy", icon: <FileText size={16}/>, label: "Event Taxonomy", perm: "analytics:read" },
      { id: "notebooks", icon: <FileText size={16}/>, label: "Notebooks", perm: "analytics:read" },
    ]},
    { label: "Operations", items: [
      { id: "approvals", icon: <CheckCircle size={16}/>, label: "Approvals", perm: "approvals:read", badge: pendingCount },
      { id: "releases", icon: <FileText size={16}/>, label: "Releases", perm: "admin:full" },
      { id: "launch", icon: <CheckCircle size={16}/>, label: "Launch Prep", perm: "config:manage" },
      { id: "crashes", icon: <Bug size={16}/>, label: "Crashes", perm: "analytics:read" },
      { id: "pricing", icon: <DollarSign size={16}/>, label: "Pricing", perm: "config:manage" },
      { id: "subscriptions", icon: <TrendingUp size={16}/>, label: "Subscriptions", perm: "analytics:read" },
    ]},
    { label: "Distribution", items: [
      { id: "distTesters", icon: <Users size={16}/>, label: "Testers", perm: "admin:full" },
      { id: "distGroups", icon: <Users size={16}/>, label: "Groups", perm: "admin:full" },
      { id: "distBuilds", icon: <Download size={16}/>, label: "Builds", perm: "admin:full" },
      { id: "distDevices", icon: <Activity size={16}/>, label: "Devices", perm: "admin:full" },
      { id: "distFeedback", icon: <MessageSquare size={16}/>, label: "Feedback", perm: "admin:full" },
      { id: "distBugReports", icon: <Bug size={16}/>, label: "Bug Reports", perm: "admin:full" },
      { id: "distCrashes", icon: <Bug size={16}/>, label: "Crashes", perm: "admin:full" },
      { id: "distReliability", icon: <Star size={16}/>, label: "Reliability", perm: "admin:full" },
      { id: "distAudit", icon: <FileText size={16}/>, label: "Audit Log", perm: "admin:full" },
      { id: "distStorage", icon: <Shield size={16}/>, label: "Storage & Config", perm: "admin:full" },
      { id: "distABTest", icon: <FlaskConical size={16}/>, label: "A/B Tests", perm: "admin:full" },
      { id: "distCompare", icon: <BarChart3 size={16}/>, label: "Compare Builds", perm: "admin:full" },
      { id: "distFixQueue", icon: <Cpu size={16}/>, label: "Auto-Fix Queue", perm: "admin:full" },
      { id: "distAutoFixSettings", icon: <Settings size={16}/>, label: "Auto-Fix Settings", perm: "admin:full" },
    ]},
    { label: "Settings", defaultClosed: true, items: [
      { id: "accounts", icon: <Users size={16}/>, label: "Accounts", perm: "accounts:manage" },
      { id: "config", icon: <Settings size={16}/>, label: "Config", perm: "config:manage" },
      { id: "gdpr", icon: <Shield size={16}/>, label: "GDPR", perm: "admin:full" },
      { id: "consent", icon: <Shield size={16}/>, label: "Privacy Consent", perm: "analytics:read" },
      { id: "audit", icon: <FileText size={16}/>, label: "Audit Log", perm: "admin:full" },
      { id: "alerts", icon: <Bell size={16}/>, label: "Smart Alerts", perm: "config:manage" },
      { id: "reports", icon: <Calendar size={16}/>, label: "Reports", perm: "config:manage" },
      { id: "search", icon: <Search size={16}/>, label: "Search", perm: "analytics:read" },
      { id: "anomalies", icon: <AlertTriangle size={16}/>, label: "Anomalies", perm: "analytics:read" },
      { id: "webhookBuilder", icon: <Link size={16}/>, label: "Webhooks", perm: "config:manage" },
      { id: "aiChat", icon: <MessageSquare size={16}/>, label: "AI Chat", perm: "analytics:read" },
      { id: "sql", icon: <FileText size={16}/>, label: "SQL Explorer", perm: "admin:full" },
      { id: "webhooks", icon: <Globe size={16}/>, label: "Webhooks", perm: "admin:full" },
      { id: "schemas", icon: <Shield size={16}/>, label: "Schema Gov.", perm: "admin:full" },
      { id: "retention-policy", icon: <Shield size={16}/>, label: "Data Retention", perm: "admin:full" },
      { id: "annotations", icon: <FileText size={16}/>, label: "Annotations", perm: "analytics:read" },
      { id: "debugger", icon: <Bug size={16}/>, label: "Event Debugger", perm: "analytics:read" },
      { id: "crashes", icon: <AlertTriangle size={16}/>, label: "Crash Intelligence", perm: "analytics:read" },
    ]},
  ];
  // Filter by permissions, remove empty sections
  const NAV_SECTIONS = SECTIONS.map(s => ({ ...s, items: s.items.filter(n => can(n.perm)) })).filter(s => s.items.length > 0);
  // Flat list for page title lookup
  const NAV = NAV_SECTIONS.flatMap(s => s.items);
  // Sections default to open if openSections hasn't been populated yet
  const isSectionOpen = (label) => {
    if (Object.keys(openSections).length === 0) {
      const sec = SECTIONS.find(s => s.label === label);
      return sec?.defaultClosed ? false : true;
    }
    return !!openSections[label];
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'DM Sans',-apple-system,sans-serif" }}>
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} onNavigate={navigate} onAppChange={setApp} currentApp={app}/>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet"/>
      <style>{`
        *:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }
        button:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }
        input:focus-visible, select:focus-visible { outline: 2px solid ${C.accent} !important; outline-offset: 1px; }
        ::selection { background: ${C.accent}33; }
        @keyframes pageEnter {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .page-content { will-change: opacity, transform; }
        @media (prefers-reduced-motion: reduce) {
          .page-content { animation: none !important; }
        }
        @media (max-width: 768px) {
          .sidebar { transform: translateX(-100%); }
          .sidebar.open { transform: translateX(0); }
          .main-content { margin-left: 0 !important; padding: 16px !important; }
          .mobile-menu { display: flex !important; }
          .sidebar-overlay { display: block !important; }
          .resp-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .resp-grid { grid-template-columns: 1fr !important; }
          .stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
          table { display: block; overflow-x: auto; white-space: nowrap; }
          .chart-container { min-height: 200px !important; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .resp-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .sidebar { width: 200px !important; }
          .main-content { margin-left: 200px !important; }
        }
      `}</style>

      {/* Mobile overlay */}
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="sidebar-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 90 }}/>}

      {/* Sidebar */}
      <nav className={`sidebar ${sidebarOpen ? "open" : ""}`} style={{ width: 220, background: C.card, borderRight: `1px solid ${C.border}`, padding: "16px 8px", display: "flex", flexDirection: "column", position: "fixed", top: 0, bottom: 0, zIndex: 100, transition: "transform 0.2s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 12px", marginBottom: 20 }}>
          <span style={{ fontSize: 20 }}>⚡</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Stellar</span>
        </div>
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: `${C.border} transparent` }}>
          {NAV_SECTIONS.map(sec => (
            <div key={sec.label} style={{ marginBottom: 4 }}>
              <button onClick={() => toggleSection(sec.label)} style={{ display: "flex", alignItems: "center", width: "100%", padding: "6px 12px", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", background: "transparent", color: C.dim, marginBottom: 2 }}>
                <span style={{ transform: isSectionOpen(sec.label) ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", marginRight: 6, fontSize: 8 }}>▶</span>
                {sec.label}
                {sec.items.some(n => n.badge > 0) && <span style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: 3, background: C.red }}/>}
              </button>
              {isSectionOpen(sec.label) && sec.items.map(n => (
                <button key={n.id} onClick={() => { navigate(n.id); setSidebarOpen(false); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 12px 6px 24px", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontWeight: page === n.id ? 600 : 400, background: page === n.id ? C.accentDim : "transparent", color: page === n.id ? C.accent : C.dim, marginBottom: 1, transition: "all 0.1s" }}>
                  {n.icon}<span style={{ flex: 1, textAlign: "left" }}>{n.label}</span>
                  {n.badge > 0 && <span style={{ background: C.red, color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 10, padding: "1px 5px", minWidth: 16, textAlign: "center" }}>{n.badge}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 8 }}>
          <div style={{ fontSize: 12, color: C.dim, padding: "0 12px", marginBottom: 4 }}>{me.account?.email}</div>
          <button onClick={toggleTheme} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "8px 12px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, background: "transparent", color: C.dim }}>
            {theme === "dark" ? "☀️" : "🌙"} {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          <button onClick={() => setShowChangePw(true)} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "8px 12px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, background: "transparent", color: C.dim }}>
            <Settings size={14}/> Change Password
          </button>
          <button onClick={doLogout} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "8px 12px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, background: "transparent", color: C.dim }}>
            <LogOut size={14}/> Sign Out
          </button>
        </div>
      </nav>

      <main className="main-content" style={{ flex: 1, marginLeft: 220, padding: "20px 28px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setSidebarOpen(true)} style={{ display: "none", background: "none", border: "none", color: C.dim, cursor: "pointer", padding: 4 }} className="mobile-menu"><Menu size={22}/></button>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>{NAV.find(n => n.id === page)?.label || page}</h1>
          </div>
          {showApp && <AppSelector app={app} setApp={setApp}/>}
        </div>
        <ErrorBoundary>
        <div className="page-content" key={page + app} style={{ animation: "pageEnter 0.18s ease-out" }}>
        {page === "dashboard" && <DashboardPage app={app}/>}
        {page === "realtime" && <RealtimePage app={app} key={`rt-${theme}`}/>}
        {page === "stickiness" && <StickinessPage app={app}/>}
        {page === "journeys" && <JourneysPage app={app}/>}
        {page === "taxonomy" && <TaxonomyPage app={app}/>}
        {page === "profiles" && <UserProfilesPage app={app}/>}
        {page === "conversion" && <ConversionScoresPage app={app}/>}
        {page === "iam" && <IAMPage app={app}/>}
        {page === "consent" && <ConsentPage app={app}/>}
        {page === "sql" && <SQLExplorerPage/>}
        {page === "breakdown" && <BreakdownPage app={app}/>}
        {page === "correlations" && <CorrelationsPage app={app}/>}
        {page === "adoption" && <AdoptionPage app={app}/>}
        {page === "debugger" && <DebuggerPage app={app}/>}
        {page === "crashes" && <CrashIntelligencePage app={app}/>}
        {page === "drip" && <DripPage app={app}/>}
        {page === "webhooks" && <WebhooksPage/>}
        {page === "tags" && <TagsPage/>}
        {page === "groups" && <GroupsPage/>}
        {page === "annotations" && <AnnotationsPage/>}
        {page === "notebooks" && <NotebooksPage/>}
        {page === "schemas" && <SchemasPage app={app}/>}
        {page === "retention-policy" && <RetentionPolicyPage/>}
        {page === "revenue" && <RevenuePage app={app}/>}
        {page === "retention" && <RetentionPage app={app}/>}
        {page === "funnels" && <FunnelsPage app={app}/>}
        {page === "sessions" && <SessionsPage app={app}/>}
        {page === "churn" && <ChurnPage app={app}/>}
        {page === "reviews" && <ReviewsPage app={app}/>}
        {page === "ads" && <AdsPage app={app}/>}
        {page === "experiments" && <ExperimentsPage app={app}/>}
        {page === "flags" && <FlagsPage app={app}/>}
        {page === "push" && <PushPage app={app}/>}
        {page === "emails" && <EmailsPage app={app}/>}
        {page === "crashes" && <CrashesPage app={app}/>}
        {page === "aso" && <ASOPage app={app}/>}
        {page === "competitors" && <CompetitorsPage app={app}/>}
        {page === "social" && <SocialPage app={app}/>}
        {page === "approvals" && <ApprovalsPage/>}
        {page === "ai" && <AIMonitorPage/>}
        {page === "autopilot" && <AutoPilotIntelligenceDashboard token={getToken()} baseUrl={getUrl()}/>}
        {page === "mlIntelligence" && <MLIntelligencePage/>}
        {page === "empireHealth" && <EmpireHealthPage/>}
        {page === "appProfile" && <AppProfilePage app={app}/>}
        {page === "gamification" && <GamificationPage/>}
        {page === "empireOverview" && <EmpireOverviewPage/>}
        {page === "releases" && <ReleasesPage app={app}/>}
        {page === "launch" && <LaunchPage app={app}/>}
        {page === "pricing" && <PricingPage app={app}/>}
        {page === "subscriptions" && <SubscriptionsPage app={app}/>}
        {page === "segments" && <SegmentsPage app={app}/>}
        {page === "paywalls" && <PaywallsPage app={app}/>}
        {page === "deeplinks" && <DeepLinksPage app={app}/>}
        {page === "referrals" && <ReferralsPage app={app}/>}
        {page === "gdpr" && <GDPRPage/>}
        {page === "support" && <SupportPage/>}
        {page === "testHealth" && <TestHealthPage/>}
        {page === "playbooks" && <PlaybooksPage/>}
        {page === "webhooks" && <WebhooksPage/>}
        {page === "monitoring" && <MonitoringPage/>}
        {page === "automation" && <AutomationPage/>}
        {page === "competitorKeywords" && <CompetitorKeywordsPage app={app}/>}
        {page === "subscriptions" && <SubscriptionsPage app={app}/>}
        {page === "onboarding" && <OnboardingPage app={app}/>}
        {page === "phasedRollout" && <PhasedRolloutPage/>}
        {page === "pushOptimization" && <PushOptimizationPage/>}
        {page === "revenueEstimation" && <RevenueEstimationPage/>}
        {page === "paywallOptimization" && <PaywallOptimizationPage app={app}/>}
        {page === "buildQueue" && <BuildQueuePage/>}
        {page === "forecast" && <ForecastPage app={app}/>}
        {page === "audit" && <AuditLogPage/>}
        {page === "accounts" && <AccountsPage/>}
        {page === "config" && <ConfigPage app={app}/>}
        {page === "alerts" && <AlertsPage/>}
        {page === "reports" && <ReportsPage/>}
        {page === "search" && <SearchPage/>}
        {page === "anomalies" && <AnomaliesPage app={app}/>}
        {page === "webhookBuilder" && <WebhookBuilderPage/>}
        {page === "distTesters" && <DistTestersPage/>}
        {page === "distGroups" && <DistGroupsPage/>}
        {page === "distBuilds" && <DistBuildsPage/>}
        {page === "distDevices" && <DistDevicesPage/>}
        {page === "distFeedback" && <DistFeedbackPage/>}
        {page === "distBugReports" && <DistBugReportsPage/>}
        {page === "distCrashes" && <DistCrashesPage/>}
        {page === "distReliability" && <DistReliabilityPage/>}
        {page === "distAudit" && <DistAuditPage/>}
        {page === "distStorage" && <DistStoragePage/>}
        {page === "distABTest" && <DistABTestPage/>}
        {page === "distCompare" && <DistComparePage/>}
        {page === "distFixQueue" && <DistFixQueuePage/>}
        {page === "distAutoFixSettings" && <DistAutoFixSettingsPage/>}
        {page === "aiChat" && <AIChatPage app={app}/>}
        </div>
        </ErrorBoundary>
        {showChangePw && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }} onClick={() => setShowChangePw(false)}><div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, padding: 24, width: 380, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 16 }}>Change Password</div>
          {cpSuccess ? <div style={{ color: C.green, fontWeight: 600, textAlign: "center", padding: 20 }}>✅ Password changed!</div> : <>
            <div style={{ marginBottom: 12 }}><label style={lbl()}>Current Password</label><input type="password" value={cpCurrent} onChange={e => setCpCurrent(e.target.value)} style={inp()} autoComplete="current-password"/></div>
            <div style={{ marginBottom: 12 }}><label style={lbl()}>New Password (min 8 chars)</label><input type="password" value={cpNew} onChange={e => setCpNew(e.target.value)} style={inp()} autoComplete="new-password"/></div>
            <div style={{ marginBottom: 12 }}><label style={lbl()}>Confirm New Password</label><input type="password" value={cpConfirm} onChange={e => setCpConfirm(e.target.value)} style={inp()} autoComplete="new-password"/></div>
            {cpNew && cpConfirm && cpNew !== cpConfirm && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>Passwords do not match</div>}
            {cpError && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{cpError}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}><Btn onClick={() => { setShowChangePw(false); setCpCurrent(""); setCpNew(""); setCpConfirm(""); setCpError(""); }}>Cancel</Btn><Btn primary onClick={doChangePw} disabled={!cpCurrent || cpNew.length < 8 || cpNew !== cpConfirm || cpLoading}>{cpLoading ? "..." : "Change Password"}</Btn></div>
          </>}
        </div></div>}
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   LOGIN PAGE
   ═══════════════════════════════════════════════ */
function LoginPage({ onLogin }) {
  const [url, setUrlState] = useState(getUrl());
  const [showUrlField, setShowUrlField] = useState(false);
  const [email, setEmail] = useState(getRememberedEmail()); const [pw, setPw] = useState("");
  const [isSetup, setIsSetup] = useState(false); const [name, setName] = useState("");
  const [remember, setRemember] = useState(localStorage.getItem("stellarRemember") === "1");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false); const [testResult, setTestResult] = useState("");

  const testConnection = async () => {
    setTesting(true); setTestResult(""); setErr("");
    const u = url.trim();
    if (!u) { setTestResult("Enter a URL first."); setTesting(false); return; }
    try { new URL(u); } catch(_) { setTestResult("Invalid URL format."); setTesting(false); return; }
    if (!isSecureUrl(u)) { setTestResult("HTTPS required (or localhost for dev)."); setTesting(false); return; }
    try {
      const r = await fetch(`${u}/health`, { method: "GET" });
      if (r.ok) {
        setTestResult("✓ Backend reachable. Sign in below.");
        setUrl(u);
        setUrlState(u);
      } else {
        setTestResult(`✗ Backend reached but returned ${r.status}. Check the URL is right.`);
      }
    } catch (_) {
      setTestResult("✗ Cannot reach this URL. Verify it's deployed and CORS allows this origin.");
    }
    setTesting(false);
  };

  const submit = async e => {
    e.preventDefault(); setLoading(true); setErr("");
    const backendUrl = (url || "").trim() || getDefaultBackendUrl();
    try { new URL(backendUrl); } catch(_) { setErr("Invalid backend URL"); setLoading(false); return; }
    if (!isSecureUrl(backendUrl)) { setErr("HTTPS is required."); setLoading(false); return; }
    setUrl(backendUrl);
    try {
      const ep = isSetup ? "/v1/auth/setup" : "/v1/auth/login";
      const body = isSetup ? { email, password: pw, displayName: name } : { email, password: pw };
      let r;
      try {
        r = await fetch(`${backendUrl}${ep}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      } catch (networkErr) {
        // Auto-expand URL field so user can fix it
        setShowUrlField(true);
        throw new Error("Cannot reach the backend. The URL may be wrong, the server may be down, or CORS may be blocking this origin. Try testing the connection below.");
      }
      const d = await r.json(); if (!r.ok) throw new Error(d.error || `Server returned ${r.status}`);
      setRememberedEmail(email, remember);
      // Credential Management API — trigger Chrome's "Save password?" prompt
      if (window.PasswordCredential && window.isSecureContext) {
        try {
          const cred = new window.PasswordCredential({ id: email, password: pw, name: name || email });
          await navigator.credentials.store(cred);
          console.log("[Stellar] Credential store completed");
        } catch(e) { console.log("[Stellar] Credential store:", e.message); }
      } else {
        console.log("[Stellar] PasswordCredential unavailable. SecureContext:", window.isSecureContext);
      }
      onLogin(d.token, remember);
    } catch (e) { setErr(e.message); setPw(""); } setLoading(false);
  };
  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <style>{`*:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }`}</style>
      <form onSubmit={submit} action="/login" method="post" autoComplete="on" style={{ width: 380, padding: 40, background: C.card, borderRadius: 20, border: `1px solid ${C.border}`, maxWidth: "90vw" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 44, marginBottom: 6 }}>⚡</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>Stellar Command</div>
          <div style={{ fontSize: 13, color: C.dim }}>{isSetup ? "Create owner account" : "Sign in to your empire"}</div>
        </div>
        {isSetup && <><label style={lbl()}>Display Name</label><input name="name" value={name} onChange={e => setName(e.target.value)} style={inp()} autoComplete="name"/></>}
        <label style={lbl()}>Email</label><input id="stellar-email" type="email" name="username" value={email} onChange={e => setEmail(e.target.value)} style={inp()} autoComplete="username"/>
        <label style={lbl()}>Password</label><input id="stellar-password" type="password" name="password" value={pw} onChange={e => setPw(e.target.value)} style={inp()} autoComplete={isSetup ? "new-password" : "current-password"}/>
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 0", cursor: "pointer", fontSize: 13, color: C.dim }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ accentColor: C.accent, width: 16, height: 16 }}/>
          Remember me
        </label>
        {err && <div style={{ color: C.red, fontSize: 13, margin: "10px 0 0", padding: "10px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 8, border: "1px solid rgba(239,68,68,0.2)", lineHeight: 1.5 }}>{err}</div>}
        <button type="submit" disabled={loading || !email || !pw} style={{ ...btnP(), width: "100%", marginTop: 12, justifyContent: "center", opacity: loading ? 0.6 : 1 }}>{loading ? "Signing in..." : isSetup ? "Create Owner Account" : "Sign In"}</button>
        <div style={{ textAlign: "center", marginTop: 12 }}><button type="button" onClick={() => setIsSetup(!isSetup)} style={{ background: "none", border: "none", color: C.accent, fontSize: 12, cursor: "pointer" }}>{isSetup ? "Already have an account? Sign In" : "First time? Set Up Owner Account"}</button></div>

        {/* Backend URL — collapsed by default, expand to edit/test */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <button
            type="button"
            onClick={() => setShowUrlField(!showUrlField)}
            style={{ background: "none", border: "none", color: C.dim, fontSize: 11, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}
          >
            <span>{showUrlField ? "▾" : "▸"}</span>
            <span>Backend: {url.replace(/^https?:\/\//, "").substring(0, 32)}{url.length > 32 ? "…" : ""}</span>
          </button>
          {showUrlField && (
            <div style={{ marginTop: 10 }}>
              <label style={{ ...lbl(), fontSize: 11 }}>Server URL</label>
              <input
                value={url}
                onChange={e => setUrlState(e.target.value)}
                style={{ ...inp(), fontSize: 12, fontFamily: "ui-monospace, monospace" }}
                placeholder="https://api.velkra.app"
                autoComplete="off"
                spellCheck="false"
              />
              <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={testConnection}
                  disabled={testing}
                  style={{ background: "transparent", color: C.accent, border: `1px solid ${C.accent}`, padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: testing ? "default" : "pointer", opacity: testing ? 0.6 : 1, fontFamily: "inherit" }}
                >
                  {testing ? "Testing..." : "Test connection"}
                </button>
                {testResult && (
                  <span style={{ fontSize: 11, color: testResult.startsWith("✓") ? C.green : C.red, lineHeight: 1.4 }}>
                    {testResult}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 8, lineHeight: 1.5 }}>
                Default comes from <code style={{ fontFamily: "ui-monospace, monospace" }}>VITE_API_BASE</code> env var, or <code style={{ fontFamily: "ui-monospace, monospace" }}>api.velkra.app</code> as fallback. CORS_ORIGINS on Railway must include this dashboard's domain.
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE COMPONENTS — all use useFetch with error/retry
   ═══════════════════════════════════════════════ */
function DashboardPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/ai/snapshot"), []);
  usePolling(reload, 60000);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const a = d.apps?.[app] || {};
  return (<>
    <Grid cols={4}><Stat label="DAU" value={a.dau?.toLocaleString() || 0} sub={`WAU: ${a.wau?.toLocaleString() || 0}`}/><Stat label="MRR" value={`$${a.revenue?.mrr?.toLocaleString() || 0}`} sub={`ARR: $${a.revenue?.arr?.toLocaleString() || 0}`} color={C.green}/><Stat label="D1 Retention" value={`${a.retention?.d1 || 0}%`} sub={`D7: ${a.retention?.d7 || 0}%`}/><Stat label="Churn Risk" value={a.churnRisk?.critical || 0} sub={`High: ${a.churnRisk?.high || 0}, Med: ${a.churnRisk?.medium || 0}`} color={C.red}/></Grid>
    <Grid cols={4} gap={12}><Stat label="Total Users" value={a.totalUsers?.toLocaleString() || 0} sub={`+${a.newUsersThisWeek || 0} this week`}/><Stat label="Sessions (7d)" value={a.sessions?.totalLast7d?.toLocaleString() || 0} sub={`Avg ${a.sessions?.avgDurationSec || 0}s`}/><Stat label="Onboarding" value={`${a.onboarding?.completionRate || 0}%`} sub={`${a.onboarding?.completed || 0}/${a.onboarding?.started || 0}`}/><Stat label="Trial Conv." value={`${a.revenue?.trialConversionRate || 0}%`}/></Grid>
    {a.topEvents?.length > 0 && <><SectionTitle>Top Events (7d)</SectionTitle><Card><ResponsiveContainer width="100%" height={250}><BarChart data={a.topEvents.slice(0, 8)}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="event" tick={{ fill: C.dim, fontSize: 11 }} angle={-20} textAnchor="end" height={60}/><YAxis tick={{ fill: C.dim, fontSize: 11 }}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Bar dataKey="count" fill={C.accent} radius={[4,4,0,0]}/></BarChart></ResponsiveContainer></Card></>}
    {d.pushPerformance?.[app] && <><SectionTitle>Push Performance (7d)</SectionTitle><Grid cols={3}><Stat label="Sent" value={d.pushPerformance[app].sent||0}/><Stat label="Delivered" value={d.pushPerformance[app].delivered||0} color={C.green}/><Stat label="Open Rate" value={`${d.pushPerformance[app].openRate||0}%`}/></Grid></>}
    {d.financials?.current && <><SectionTitle>Financials</SectionTitle><Grid cols={4}><Stat label="Gross Revenue" value={`$${d.financials.current.gross_revenue||0}`} color={C.green}/><Stat label="Ad Spend" value={`$${d.financials.current.ad_spend||0}`} color={C.red}/><Stat label="Apple Commission" value={`$${d.financials.current.apple_commission||0}`}/><Stat label="Profit" value={`$${d.financials.current.profit||0}`} color={(d.financials.current.profit||0) >= 0 ? C.green : C.red}/></Grid></>}
  </>);
}

// Country centroids (lat/lng) for world map markers
const COUNTRY_GEO = {US:[-98,39],GB:[-2,54],CA:[-106,56],AU:[134,-25],DE:[10,51],FR:[2,47],JP:[138,36],BR:[-51,-14],IN:[78,21],KR:[128,36],MX:[-102,23],IT:[12,42],ES:[-4,40],CN:[104,35],RU:[90,60],NL:[5,52],SE:[16,62],PL:[20,52],TR:[35,39],ZA:[25,-30],SG:[104,1],TW:[121,24],MY:[102,4],PH:[122,12],TH:[101,15],ID:[120,-5],AR:[-64,-34],CL:[-71,-35],CO:[-74,4],PK:[69,30],NG:[8,10],EG:[30,27],SA:[45,24],AE:[54,24],IL:[35,31],CH:[8,47],AT:[14,47],BE:[4,51],PT:[-8,40],NO:[10,62],DK:[10,56],FI:[26,64],CZ:[15,50],NZ:[174,-41],IE:[-8,53]};
// Equirectangular coordinates for country markers (used by Leaflet map)

function RealtimePage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/realtime/${app}`), [app]);
  const { data: all } = useFetch(() => api("/v1/realtime"), []);
  usePolling(reload, 5000);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  // Initialize Leaflet map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    if (!window.L) {
      // Leaflet CDN failed to load — show fallback
      if (mapRef.current) mapRef.current.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#666;font-size:13px">Map unavailable — Leaflet CDN could not be loaded</div>';
      return;
    }
    const map = window.L.map(mapRef.current, {
      center: [25, 0], zoom: 2, minZoom: 1, maxZoom: 6,
      zoomControl: false, attributionControl: false,
      scrollWheelZoom: false, dragging: true
    });
    const tileUrl = _theme === "light"
      ? "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
      : "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png";
    window.L.tileLayer(tileUrl, { subdomains: "abcd", maxZoom: 6 }).addTo(map);
    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  // Update markers when data changes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !d?.byCountry) return;
    // Clear old markers
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    const countries = d.byCountry || [];
    const maxUsers = Math.max(...countries.map(c => c.users), 1);
    for (const c of countries) {
      const coords = COUNTRY_GEO[c.country];
      if (!coords) continue;
      const r = Math.max(5, Math.min(25, (c.users / maxUsers) * 25));
      const marker = window.L.circleMarker([coords[1], coords[0]], {
        radius: r, fillColor: "#f97316", color: "#f97316", weight: 1, opacity: 0.8, fillOpacity: 0.4
      }).addTo(map);
      marker.bindTooltip(`<b>${c.country}</b><br>${c.users.toLocaleString()} users`, { className: "leaflet-tooltip" });
      markersRef.current.push(marker);
    }
  }, [d]);

  if (loading && !d) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;
  if (!d) return <Empty/>;
  const countries = d.byCountry || [];
  return (<>
    <Grid cols={4}>
      <Stat label="Active Now" value={d.activeUsers||0} color={C.green} sub="Last 5 minutes"/>
      <Stat label="Sessions" value={d.activeSessions||0}/>
      <Stat label="Countries" value={countries.length}/>
      <Stat label="Events/min" value={d.perMinute?.length > 0 ? d.perMinute[d.perMinute.length-1]?.events || 0 : 0}/>
    </Grid>

    <SectionTitle>Live World Map</SectionTitle>
    <Card>
      <div ref={mapRef} style={{ width: "100%", height: 320, borderRadius: 12, overflow: "hidden" }}/>
    </Card>

    {d.perMinute?.length > 0 && <><SectionTitle>Events Per Minute (30m)</SectionTitle>
    <Card><ResponsiveContainer width="100%" height={150}><BarChart data={d.perMinute}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="minute" tick={false}/><YAxis tick={{ fill: C.dim, fontSize: 10 }}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }} labelFormatter={v => new Date(v).toLocaleTimeString()}/><Bar dataKey="events" fill={C.accent} radius={[2,2,0,0]} name="Events"/><Bar dataKey="users" fill={C.green} radius={[2,2,0,0]} name="Users"/></BarChart></ResponsiveContainer></Card></>}

    <Grid cols={2}>
      <div>
        <SectionTitle>Top Countries</SectionTitle>
        <Card>{countries.length > 0 ? <Table cols={["Country","Users","Events"]} rows={countries.slice(0,10).map(c => [c.country, c.users, c.events])}/> : <Empty msg="No active users"/>}</Card>
      </div>
      <div>
        <SectionTitle>Top Devices</SectionTitle>
        <Card>{d.byDevice?.length > 0 ? <Table cols={["Device","Users"]} rows={d.byDevice.slice(0,8).map(d => [d.device, d.users])}/> : <Empty msg="No device data"/>}</Card>
      </div>
    </Grid>

    <SectionTitle>Live Event Feed</SectionTitle>
    <Card>{d.feed?.length > 0 ? <Table cols={["Event","User","Device","Country","Time"]} rows={d.feed.map(f => [f.event, f.userId, f.device, f.country, new Date(f.time).toLocaleTimeString()])}/> : <Empty msg="No recent events"/>}</Card>

    {all?.byApp?.length > 0 && <><SectionTitle>All Apps — Active Now</SectionTitle><Grid cols={3}>{all.byApp.map(a => <Stat key={a.app} label={appLabel(a.app)} value={a.users} color={C.accent}/>)}</Grid></>}
  </>);
}

function RevenuePage({ app }) {
  const [days, setDays] = useState(30);
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/revenue/${app}?days=${days}`), [app, days]);
  const { data: ltvData } = useFetch(() => api(`/v1/ltv/${app}`), [app]);
  const { data: finData } = useFetch(() => api("/v1/finance/history?type=weekly"), []);
  const fin = finData?.reports || [];
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  return (<>
    <div style={{ display:"flex",justifyContent:"flex-end",marginBottom:12 }}><select value={days} onChange={e=>setDays(+e.target.value)} style={{...inp(),width:100}}>{[7,14,30,60,90].map(d=><option key={d} value={d}>{d}d</option>)}</select></div>
    <Grid cols={4}><Stat label="MRR" value={`$${d.mrr||0}`} color={C.green}/><Stat label="ARR" value={`$${d.arr||0}`}/><Stat label="ARPU" value={`$${ltvData?.arpu||0}`}/><Stat label="Trial Conv." value={`${d.trialConversionRate||0}%`}/></Grid>
    {d.subscribers?.length > 0 && <><SectionTitle>Subscribers by Tier</SectionTitle><Card><ResponsiveContainer width="100%" height={200}><PieChart><Pie data={d.subscribers} dataKey="count" nameKey="tier" cx="50%" cy="50%" outerRadius={70} label={({ tier, count }) => `${tier}: ${count}`}>{d.subscribers.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}</Pie><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/></PieChart></ResponsiveContainer></Card></>}
    {fin.length > 0 && <><SectionTitle>Weekly P&L</SectionTitle><Card><ResponsiveContainer width="100%" height={280}><LineChart data={fin.slice(0, 12).reverse()}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="period_start" tick={{ fill: C.dim, fontSize: 10 }}/><YAxis tick={{ fill: C.dim, fontSize: 11 }}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Line type="monotone" dataKey="gross_revenue" stroke={C.green} strokeWidth={2} dot={false} name="Revenue"/><Line type="monotone" dataKey="ad_spend" stroke={C.red} strokeWidth={2} dot={false} name="Ad Spend"/><Line type="monotone" dataKey="profit" stroke={C.accent} strokeWidth={2} dot={false} name="Profit"/><Legend wrapperStyle={{ color: C.dim }}/></LineChart></ResponsiveContainer></Card></>}
  </>);
}

function StickinessPage({ app }) {
  const [days, setDays] = useState(30);
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/stickiness/${app}?days=${days}`), [app, days]);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const trend = d.trend || [];
  const latest = trend[trend.length - 1] || {};
  const stickiness = latest.stickiness || 0;
  const ringClr = stickiness >= 30 ? C.green : stickiness >= 15 ? C.yellow : C.red;
  const prev = trend.length >= 2 ? (trend[trend.length - 2]?.stickiness || 0) : 0;
  const delta = stickiness - prev;
  return (<>
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}><select value={days} onChange={e => setDays(+e.target.value)} style={{...inp(), width: 100}}>{[7,14,30,60,90].map(d => <option key={d} value={d}>{d}d</option>)}</select></div>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 40, padding: "16px 0" }}>
        <div style={{ textAlign: "center" }}>
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke={C.border} strokeWidth="10"/>
            <circle cx="60" cy="60" r="50" fill="none" stroke={ringClr} strokeWidth="10" strokeDasharray={`${stickiness * 3.14} 314`} strokeDashoffset="0" transform="rotate(-90 60 60)" strokeLinecap="round"/>
            <text x="60" y="55" textAnchor="middle" fontSize="24" fontWeight="700" fill={ringClr}>{stickiness}%</text>
            <text x="60" y="72" textAnchor="middle" fontSize="10" fill={C.dim}>DAU/MAU</text>
          </svg>
          {delta !== 0 && <div style={{ fontSize: 12, color: delta > 0 ? C.green : C.red, marginTop: 4 }}>{delta > 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}% vs yesterday</div>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div><div style={{ fontSize: 11, color: C.dim }}>DAU (today)</div><div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{(latest.dau || 0).toLocaleString()}</div></div>
          <div><div style={{ fontSize: 11, color: C.dim }}>MAU (approx)</div><div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{(latest.mau || 0).toLocaleString()}</div></div>
        </div>
      </div>
    </Card>
    {trend.length > 0 && <><SectionTitle>Stickiness Trend ({d.days}d)</SectionTitle><Card><ResponsiveContainer width="100%" height={250}><LineChart data={trend}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="date" tick={{ fill: C.dim, fontSize: 10 }}/><YAxis tick={{ fill: C.dim, fontSize: 11 }} domain={[0, 100]}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Line type="monotone" dataKey="stickiness" stroke={C.accent} strokeWidth={2} dot={false} name="DAU/MAU %"/></LineChart></ResponsiveContainer></Card></>}
  </>);
}

function JourneysPage({ app }) {
  const [days, setDays] = useState(7); const [depth, setDepth] = useState(4);
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/journeys/${app}?days=${days}&depth=${depth}`), [app, days, depth]);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const links = d.links || [];
  const maxVal = Math.max(...links.map(l => l.value || 0), 1);
  // Group by step
  const steps = {};
  for (const l of links) { const s = l.step || 0; if (!steps[s]) steps[s] = []; steps[s].push(l); }
  return (<>
    <Card style={{ marginBottom: 16 }}><div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
      <div><label style={lbl()}>Days</label><select value={days} onChange={e => setDays(+e.target.value)} style={inp()}>{[3,7,14,30].map(d => <option key={d} value={d}>{d}d</option>)}</select></div>
      <div><label style={lbl()}>Depth</label><select value={depth} onChange={e => setDepth(+e.target.value)} style={inp()}>{[2,3,4,5,6].map(d => <option key={d} value={d}>{d} steps</option>)}</select></div>
      <div style={{ fontSize: 12, color: C.dim }}>{links.length} transitions · {d.nodes?.length || 0} unique events</div>
    </div></Card>
    {Object.keys(steps).length > 0 ? Object.entries(steps).sort(([a],[b]) => +a - +b).map(([step, flows]) => (
      <div key={step}>
        <SectionTitle>Step {step} → {+step + 1}</SectionTitle>
        <Card style={{ marginBottom: 12 }}>
          {flows.sort((a, b) => (b.value || 0) - (a.value || 0)).slice(0, 10).map((l, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < Math.min(flows.length, 10) - 1 ? `1px solid ${C.border}08` : "none" }}>
              <Badge>{l.sourceEvent}</Badge>
              <span style={{ color: C.dim, fontSize: 12 }}>→</span>
              <Badge color={C.green}>{l.targetEvent}</Badge>
              <div style={{ flex: 1, height: 8, background: C.bg, borderRadius: 4, overflow: "hidden", marginLeft: 8 }}>
                <div style={{ width: `${(l.value / maxVal) * 100}%`, height: "100%", background: C.accent, borderRadius: 4, opacity: 0.7 }}/>
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.text, minWidth: 60, textAlign: "right" }}>{l.value} users</span>
            </div>
          ))}
        </Card>
      </div>
    )) : <Empty msg="Not enough journey data — users need at least 2 events per session"/>}
  </>);
}

function TaxonomyPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/taxonomy/${app}`), [app]);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const events = d.events || [];
  const maxCount = Math.max(...events.map(e => e.totalCount || 0), 1);
  const topEvents = events.slice(0, 10);
  return (<>
    <Grid cols={4}>
      <Stat label="Event Types" value={d.totalEventTypes || 0}/>
      <Stat label="Expected" value={(d.expectedEvents || []).length} color={C.green}/>
      <Stat label="Missing" value={(d.missingEvents || []).length} color={(d.missingEvents || []).length > 0 ? C.red : C.green}/>
      <Stat label="Custom" value={(d.unexpected || []).length} color={C.blue}/>
    </Grid>
    {d.missingEvents?.length > 0 && <Card style={{ borderLeft: `3px solid ${C.red}`, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.red, marginBottom: 8 }}>MISSING EXPECTED EVENTS</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{d.missingEvents.map(e => <Badge key={e} color={C.red}>{e}</Badge>)}</div>
    </Card>}
    {topEvents.length > 0 && <Card><ResponsiveContainer width="100%" height={250}><BarChart data={topEvents} layout="vertical" margin={{ left: 120 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
      <XAxis type="number" tick={{ fill: C.dim, fontSize: 11 }}/>
      <YAxis type="category" dataKey="event" tick={{ fill: C.dim, fontSize: 10 }} width={120}/>
      <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/>
      <Bar dataKey="totalCount" fill={C.accent} radius={[0, 4, 4, 0]} name="Total Count"/>
    </BarChart></ResponsiveContainer></Card>}
    <SectionTitle>All Events ({events.length})</SectionTitle>
    <Card>{events.map((e, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < events.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: e.isExpected ? C.green : C.blue, flexShrink: 0 }}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{e.event}</div>
          {e.propertyKeys?.length > 0 && <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{e.propertyKeys.join(", ")}</div>}
        </div>
        <div style={{ width: 100, height: 6, background: C.bg, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${(e.totalCount / maxCount) * 100}%`, height: "100%", background: C.accent, borderRadius: 3 }}/>
        </div>
        <span style={{ fontSize: 12, color: C.text, fontWeight: 600, minWidth: 55, textAlign: "right" }}>{(e.totalCount || 0).toLocaleString()}</span>
        <span style={{ fontSize: 11, color: C.dim, minWidth: 55, textAlign: "right" }}>{e.uniqueUsers} users</span>
        <Badge color={e.isExpected ? C.green : C.blue} style={{ fontSize: 10 }}>{e.isExpected ? "Expected" : "Custom"}</Badge>
      </div>
    ))}</Card>
  </>);
}

function UserProfilesPage({ app }) {
  const [uid, setUid] = useState(""); const [profile, setProfile] = useState(null); const [err, setErr] = useState(null); const [loading, setLoading] = useState(false);
  const load = async () => { if (!uid) return; setLoading(true); setErr(null); try { setProfile(await api(`/v1/users/${uid}/timeline`)); } catch (e) { setErr(e.message); } setLoading(false); };
  const u = profile?.user;
  const tierColor = u?.subscription_tier === "premium" ? C.green : u?.subscription_tier === "trial" ? C.yellow : C.dim;
  return (<>
    <Card><div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}><div style={{ flex: 1 }}><label style={lbl()}>User ID</label><input value={uid} onChange={e => setUid(e.target.value)} style={inp()} placeholder="Enter user_id" onKeyDown={e => e.key === 'Enter' && load()}/></div><Btn primary onClick={load} disabled={!uid || loading}>{loading ? "..." : "Load Profile"}</Btn></div></Card>
    {err && <Card style={{ marginTop: 12 }}><div style={{ color: C.red }}>{err}</div></Card>}
    {u && <>
      {/* Visual profile header */}
      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 56, height: 56, borderRadius: 28, background: tierColor + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: tierColor }}>
            {(u.user_id || "?")[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{u.user_id?.substring(0, 20)}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Badge color={tierColor}>{u.subscription_tier || "free"}</Badge>
              {u.device_model && <Badge>{u.device_model}</Badge>}
              {u.locale && <Badge>{u.locale}</Badge>}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.green }}>${u.total_revenue || 0}</div>
            <div style={{ fontSize: 11, color: C.dim }}>lifetime revenue</div>
          </div>
        </div>
      </Card>
      <Grid cols={4}>
        <Stat label="Sessions" value={u.total_sessions || 0} color={C.blue}/>
        <Stat label="First Seen" value={u.first_seen ? new Date(u.first_seen).toLocaleDateString() : "-"}/>
        <Stat label="Last Active" value={u.last_seen ? new Date(u.last_seen).toLocaleDateString() : "-"}/>
        <Stat label="Days Active" value={u.first_seen ? Math.round((Date.now() - new Date(u.first_seen)) / 86400000) : 0}/>
      </Grid>
      {profile.subscriptions?.length > 0 && <><SectionTitle>Subscriptions</SectionTitle><Card>{profile.subscriptions.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < profile.subscriptions.length - 1 ? `1px solid ${C.border}08` : "none" }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: s.is_active ? C.green : C.dim }}/>
          <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{s.product_id}</span>
          <Badge color={s.is_active ? C.green : C.dim}>{s.is_active ? "Active" : "Expired"}</Badge>
          {s.is_trial && <Badge color={C.yellow}>Trial</Badge>}
          <span style={{ marginLeft: "auto", fontSize: 13, color: C.text }}>${s.price || 0}</span>
        </div>
      ))}</Card></>}
      {profile.events?.length > 0 && <><SectionTitle>Event Timeline ({profile.events.length})</SectionTitle><Card>{profile.events.slice(0, 30).map((e, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: i < 29 ? `1px solid ${C.border}08` : "none", fontSize: 12 }}>
          <span style={{ width: 4, height: 4, borderRadius: 2, background: C.accent, flexShrink: 0 }}/>
          <span style={{ fontWeight: 500, color: C.text, minWidth: 140 }}>{e.event}</span>
          <span style={{ color: C.dim }}>{e.app_id}</span>
          <span style={{ color: C.dim }}>{e.device_model}</span>
          <span style={{ marginLeft: "auto", color: C.dim, fontSize: 11 }}>{new Date(e.timestamp).toLocaleString()}</span>
        </div>
      ))}</Card></>}
    </>}
  </>);
}

function ConversionScoresPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/conversion-scores/${app}`), [app]);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const total = d.total || 0; const high = d.highPropensity || 0; const med = d.mediumPropensity || 0; const low = Math.max(0, total - high - med);
  const distData = [{ name: "High (60+)", value: high, fill: C.green }, { name: "Medium (30-59)", value: med, fill: C.yellow }, { name: "Low (<30)", value: low, fill: C.dim }].filter(x => x.value > 0);
  return (<>
    <Grid cols={4}>
      <Stat label="High Propensity" value={high} color={C.green} sub={total ? `${Math.round(high/total*100)}%` : ""}/>
      <Stat label="Medium" value={med} color={C.yellow} sub={total ? `${Math.round(med/total*100)}%` : ""}/>
      <Stat label="Low" value={low} color={C.dim}/>
      <Stat label="Total Free Users" value={total}/>
    </Grid>
    {distData.length > 0 && <Card><ResponsiveContainer width="100%" height={200}><PieChart><Pie data={distData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40} label={({ name, value }) => `${name}: ${value}`}>{distData.map((d,i) => <Cell key={i} fill={d.fill}/>)}</Pie><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Legend wrapperStyle={{ color: C.dim }}/></PieChart></ResponsiveContainer></Card>}
    <SectionTitle>Top Conversion Prospects</SectionTitle>
    {(d.users||[]).length > 0 ? <Card>{(d.users||[]).slice(0, 25).map((u, i) => {
      const score = u.score || 0;
      const clr = score >= 60 ? C.green : score >= 30 ? C.yellow : C.dim;
      return (<div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < 24 ? `1px solid ${C.border}08` : "none" }}>
        <div style={{ width: 36, height: 36, borderRadius: 18, background: clr + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: clr }}>{score}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{u.userId?.substring(0, 16)}...</div>
          <div style={{ fontSize: 11, color: C.dim }}>{u.sessions} sessions · {u.coreActions} actions · {u.paywallViews} paywall views</div>
        </div>
        <div style={{ width: 120 }}>
          <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${score}%`, height: "100%", background: clr, borderRadius: 3 }}/></div>
        </div>
        <div style={{ fontSize: 11, color: C.dim, width: 50, textAlign: "right" }}>{u.activeDays7d}d active</div>
      </div>);
    })}</Card> : <Empty msg="Not enough free users to score"/>}
  </>);
}

function IAMPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/iam/campaigns/${app}`), [app]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState(""); const [msgType, setMsgType] = useState("banner");
  const [trigger, setTrigger] = useState("session_started"); const [freq, setFreq] = useState("once");
  const [title, setTitle] = useState(""); const [body, setBody] = useState("");
  const [ctaText, setCtaText] = useState(""); const [ctaAction, setCtaAction] = useState("");
  const [err, setErr] = useState("");

  const create = async () => {
    setErr("");
    try {
      await api("/v1/iam/campaigns", { method: "POST", body: { appId: app, name, messageType: msgType, triggerEvent: trigger, frequency: freq, title, body, ctaText, ctaAction } });
      setName(""); setTitle(""); setBody(""); setCtaText(""); setCtaAction(""); setShowCreate(false); reload();
    } catch (e) { setErr(e.message); }
  };

  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => setShowCreate(!showCreate)}><Plus size={14}/> Create Message</Btn>
    </div>

    {showCreate && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>New In-App Message</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Rate us prompt"/></div>
        <div><label style={lbl()}>Type</label><select value={msgType} onChange={e => setMsgType(e.target.value)} style={inp()}>{["banner","modal","fullscreen","tooltip","slideup"].map(t => <option key={t} value={t}>{t}</option>)}</select></div>
        <div><label style={lbl()}>Frequency</label><select value={freq} onChange={e => setFreq(e.target.value)} style={inp()}>{["once","once_per_session","always","every_3_days","every_7_days"].map(f => <option key={f} value={f}>{f.replace(/_/g," ")}</option>)}</select></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Trigger Event</label><input value={trigger} onChange={e => setTrigger(e.target.value)} style={inp()} placeholder="session_started"/></div>
        <div><label style={lbl()}>Title *</label><input value={title} onChange={e => setTitle(e.target.value)} style={inp()} placeholder="Enjoying the app?"/></div>
      </div>
      <div><label style={lbl()}>Body</label><input value={body} onChange={e => setBody(e.target.value)} style={inp()} placeholder="Tap to rate us on the App Store"/></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>CTA Text</label><input value={ctaText} onChange={e => setCtaText(e.target.value)} style={inp()} placeholder="Rate Now"/></div>
        <div><label style={lbl()}>CTA Action</label><input value={ctaAction} onChange={e => setCtaAction(e.target.value)} style={inp()} placeholder="open_paywall / deep_link / dismiss"/></div>
      </div>
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!name || !title}><Check size={14}/> Create</Btn><Btn onClick={() => setShowCreate(false)}>Cancel</Btn></div>
    </Card>}

    <SectionTitle>In-App Message Campaigns</SectionTitle>
    {d.campaigns?.length > 0 ? d.campaigns.map(c => (<Card key={c.id} style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <div><span style={{ fontWeight: 600 }}>{c.name}</span> <Badge>{c.message_type}</Badge> <Badge color={c.is_active ? C.green : C.dim}>{c.is_active ? "Active" : "Off"}</Badge></div>
        <span style={{ fontSize: 11, color: C.dim }}>{c.frequency} · {c.trigger_event || "immediate"}</span>
      </div>
      <div style={{ fontSize: 12, color: C.dim }}>{c.title}: {c.body?.substring(0, 100)}</div>
    </Card>)) : <Empty msg="No in-app messages — create one above"/>}
  </>);
}

function ConsentPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/consent/stats/${app}`), [app]);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const total = d.total || 1;
  const rates = [
    { name: "Analytics", count: d.analyticsOptIn || 0, rate: Math.round((d.analyticsOptIn||0)/total*100), color: C.green },
    { name: "Marketing", count: d.marketingOptIn || 0, rate: Math.round((d.marketingOptIn||0)/total*100), color: C.blue },
    { name: "Personalization", count: d.personalizationOptIn || 0, rate: Math.round((d.personalizationOptIn||0)/total*100), color: C.purple }
  ];
  return (<>
    <Grid cols={4}>
      <Stat label="Total Users" value={(d.total||0).toLocaleString()}/>
      {rates.map(r => <Stat key={r.name} label={`${r.name} Opt-In`} value={`${r.rate}%`} sub={`${r.count.toLocaleString()} users`} color={r.color}/>)}
    </Grid>
    <Card>
      <div style={{ display: "flex", justifyContent: "center", gap: 40, padding: "16px 0" }}>
        {rates.map(r => (
          <div key={r.name} style={{ textAlign: "center" }}>
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke={C.border} strokeWidth="6"/>
              <circle cx="40" cy="40" r="34" fill="none" stroke={r.color} strokeWidth="6" strokeDasharray={`${r.rate * 2.136} 213.6`} strokeDashoffset="0" transform="rotate(-90 40 40)" strokeLinecap="round"/>
              <text x="40" y="44" textAnchor="middle" fontSize="16" fontWeight="700" fill={r.color}>{r.rate}%</text>
            </svg>
            <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>{r.name}</div>
          </div>
        ))}
      </div>
    </Card>
  </>);
}

function SQLExplorerPage() {
  const [query, setQuery] = useState("SELECT event, COUNT(*) as count FROM events WHERE app_id='radiant' GROUP BY event ORDER BY count DESC LIMIT 20");
  const [result, setResult] = useState(null); const [err, setErr] = useState(null); const [running, setRunning] = useState(false);
  const run = async () => { setRunning(true); setErr(null); try { setResult(await api("/v1/sql", { method: "POST", body: { query } })); } catch (e) { setErr(e.message); } setRunning(false); };
  return (<><SectionTitle>SQL Explorer (read-only)</SectionTitle><Card>
    <textarea value={query} onChange={e => setQuery(e.target.value)} style={{ ...inp(), height: 80, fontFamily: "JetBrains Mono", fontSize: 12, resize: "vertical" }}/>
    <div style={{ display: "flex", gap: 8, marginTop: 8 }}><Btn primary onClick={run} disabled={running}>{running ? "Running..." : "Execute"}</Btn><span style={{ fontSize: 11, color: C.dim, alignSelf: "center" }}>SELECT only · LIMIT 500 auto-applied (don't add your own LIMIT)</span></div>
  </Card>
    {err && <Card style={{ marginTop: 12 }}><div style={{ color: C.red, fontSize: 13 }}>{err}</div></Card>}
    {result && <><SectionTitle>Results ({result.rowCount} rows)</SectionTitle><Card><Table exportName="sql-results" cols={result.columns||[]} rows={(result.rows||[]).map(r => (result.columns||[]).map(c => typeof r[c] === 'object' ? JSON.stringify(r[c]) : String(r[c] ?? '')))}/></Card></>}</>);
}

function BreakdownPage({ app }) {
  const [groupBy, setGroupBy] = useState("locale"); const [event, setEvent] = useState(""); const [days, setDays] = useState(7);
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/breakdown/${app}?groupBy=${groupBy}&days=${days}${event ? `&event=${event}` : ''}`), [app, groupBy, days, event]);
  const items = d?.data || [];
  const total = items.reduce((s, r) => s + (r.value || 0), 0);
  return (<><Card><div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
    <div><label style={lbl()}>Group By</label><select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={inp()}>{["locale","device_model","os_version","app_version","country","tier"].map(o => <option key={o} value={o}>{o}</option>)}</select></div>
    <div><label style={lbl()}>Event (optional)</label><input value={event} onChange={e => setEvent(e.target.value)} style={{...inp(), width: 180}} placeholder="e.g. paywall_viewed"/></div>
    <div><label style={lbl()}>Days</label><select value={days} onChange={e => setDays(e.target.value)} style={inp()}>{[7,14,30,60,90].map(d => <option key={d} value={d}>{d}d</option>)}</select></div>
    {total > 0 && <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{total.toLocaleString()} total across {items.length} segments</span>}
  </div></Card>
    {loading ? <Loading/> : error ? <ErrorState error={error} onRetry={reload}/> : items.length > 0 ? <>
    <Card><ResponsiveContainer width="100%" height={280}><BarChart data={items.slice(0,15)}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="dimension" tick={{ fill: C.dim, fontSize: 10 }} angle={-20} textAnchor="end" height={60}/><YAxis tick={{ fill: C.dim, fontSize: 11 }}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Bar dataKey="value" fill={C.accent} radius={[4,4,0,0]}/></BarChart></ResponsiveContainer></Card>
    <Card>{items.map((r, i) => {
      const pct = total > 0 ? Math.round(r.value / total * 100) : 0;
      return (<div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < items.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: C.text, minWidth: 140 }}>{r.dimension || "unknown"}</span>
        <div style={{ flex: 1, height: 8, background: C.bg, borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: C.accent, borderRadius: 4, opacity: 0.7 }}/>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text, minWidth: 60, textAlign: "right" }}>{r.value.toLocaleString()}</span>
        <span style={{ fontSize: 11, color: C.dim, minWidth: 40, textAlign: "right" }}>{pct}%</span>
      </div>);
    })}</Card></> : <Empty msg="No data for this breakdown"/>}</>);
}

function CorrelationsPage({ app }) {
  const [target, setTarget] = useState("subscription_started");
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/correlations/${app}?target=${target}`), [app, target]);
  return (<><Card><div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}><div style={{ flex: 1 }}><label style={lbl()}>Target Event</label><input value={target} onChange={e => setTarget(e.target.value)} style={inp()} placeholder="e.g. subscription_started"/></div>
    <Btn primary onClick={reload}><Search size={14}/> Analyze</Btn></div></Card>
    {loading ? <Loading/> : error ? <ErrorState error={error} onRetry={reload}/> : <>{d?.correlations?.length > 0 ? <>
    <SectionTitle>Actions Most Correlated with "{target}"</SectionTitle>
    {d.correlations.map((c, i) => {
      const maxRate = Math.max(...d.correlations.map(x => x.conversionRate || 0), 1);
      return (<Card key={i} style={{ marginBottom: 6, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{c.event}</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{c.converterUsers} of {c.totalUsers} users · avg {c.avgCount} times</div>
          </div>
          <div style={{ width: 200, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, height: 10, background: C.bg, borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${Math.min((c.conversionRate || 0) / maxRate * 100, 100)}%`, height: "100%", background: c.conversionRate >= 50 ? C.green : c.conversionRate >= 25 ? C.accent : C.yellow, borderRadius: 5 }}/>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: c.conversionRate >= 50 ? C.green : C.accent, minWidth: 48, textAlign: "right" }}>{c.conversionRate}%</span>
          </div>
        </div>
      </Card>);
    })}
    </> : <Empty msg="Not enough data — try a different target event"/>}</>}</>);
}

function AdoptionPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/adoption/${app}`), [app]);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const features = d.features || [];
  const avgRate = features.length > 0 ? Math.round(features.reduce((s, f) => s + (f.adoptionRate || 0), 0) / features.length) : 0;
  return (<>
    <Grid cols={3}>
      <Stat label="Active Users (30d)" value={(d.totalActiveUsers || 0).toLocaleString()} color={C.blue}/>
      <Stat label="Features Tracked" value={features.length}/>
      <Stat label="Avg Adoption Rate" value={`${avgRate}%`} color={avgRate >= 50 ? C.green : C.yellow}/>
    </Grid>
    <SectionTitle>Feature Adoption</SectionTitle>
    {features.length > 0 ? <Card>{features.map((f, i) => {
      const rate = f.adoptionRate || 0;
      const clr = rate >= 60 ? C.green : rate >= 30 ? C.accent : rate >= 10 ? C.yellow : C.dim;
      return (<div key={i} style={{ padding: "10px 0", borderBottom: i < features.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{f.event}</span>
            {f.version && <Badge style={{ marginLeft: 8 }}>{f.version}</Badge>}
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: clr }}>{rate}%</span>
        </div>
        <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: "hidden", marginBottom: 4 }}>
          <div style={{ width: `${Math.min(rate, 100)}%`, height: "100%", background: clr, borderRadius: 4, transition: "width 0.5s" }}/>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.dim }}>
          <span>{(f.adopters || 0).toLocaleString()} users adopted</span>
          <span>{(f.totalUses || 0).toLocaleString()} total uses</span>
        </div>
      </div>);
    })}</Card> : <Empty msg="No feature_used events tracked — instrument features with event tracking"/>}
  </>);
}

function DebuggerPage({ app }) {
  const [event, setEvent] = useState(""); const [userId, setUserId] = useState(""); const [device, setDevice] = useState("");
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/debug/events/${app}?minutes=5${event?`&event=${event}`:''}${userId?`&userId=${userId}`:''}${device?`&device=${device}`:''}`), [app, event, userId, device]);
  usePolling(reload, 3000);
  const events = d?.events || [];
  const eventCounts = {};
  for (const e of events) { eventCounts[e.event] = (eventCounts[e.event] || 0) + 1; }
  const topEvents = Object.entries(eventCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const eventColors = { session_started: C.green, subscription_started: C.green, paywall_viewed: C.accent, core_action_completed: C.blue, app_backgrounded: C.dim, screen_viewed: C.cyan };
  return (<>
    <Card><div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
      <div><label style={lbl()}>Event</label><input value={event} onChange={e => setEvent(e.target.value)} style={{...inp(), width: 160}} placeholder="Filter..."/></div>
      <div><label style={lbl()}>User ID</label><input value={userId} onChange={e => setUserId(e.target.value)} style={{...inp(), width: 160}} placeholder="Filter..."/></div>
      <div><label style={lbl()}>Device</label><input value={device} onChange={e => setDevice(e.target.value)} style={{...inp(), width: 140}} placeholder="Filter..."/></div>
      <div style={{ alignSelf: "flex-end" }}><Badge color={C.green}>● LIVE</Badge></div>
      <div style={{ alignSelf: "flex-end", fontSize: 12, color: C.dim, marginLeft: "auto" }}>{events.length} events in last 5 min</div>
    </div></Card>
    {topEvents.length > 0 && <div style={{ display: "flex", gap: 6, marginTop: 8, marginBottom: 8, flexWrap: "wrap" }}>
      {topEvents.map(([name, count]) => (
        <span key={name} onClick={() => setEvent(name)} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12, background: (eventColors[name] || C.accent) + "18", color: eventColors[name] || C.accent, cursor: "pointer" }}>{name} ({count})</span>
      ))}
    </div>}
    {events.length > 0 ? <Card>{events.map((e, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: i < events.length - 1 ? `1px solid ${C.border}08` : "none", fontSize: 12 }}>
        <span style={{ width: 4, height: 4, borderRadius: 2, background: eventColors[e.event] || C.accent, flexShrink: 0 }}/>
        <span style={{ fontWeight: 600, color: eventColors[e.event] || C.text, minWidth: 160 }}>{e.event}</span>
        <span style={{ color: C.dim, minWidth: 100 }}>{e.user_id?.substring(0,10)}...</span>
        <span style={{ color: C.dim }}>{e.device_model}</span>
        <span style={{ color: C.dim }}>{e.app_version}</span>
        <span style={{ marginLeft: "auto", color: C.dim, fontSize: 11 }}>{new Date(e.timestamp).toLocaleTimeString()}</span>
      </div>
    ))}</Card> : <Empty msg="No events in last 5 minutes — make sure the SDK is sending data"/>}
  </>);
}

function DripPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/drip/campaigns/${app}`), [app]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState(""); const [trigger, setTrigger] = useState("onboarding_completed");
  const [steps, setSteps] = useState([
    { channel: "push", title: "", body: "", delayHours: 24 },
    { channel: "push", title: "", body: "", delayHours: 72 }
  ]);
  const [err, setErr] = useState("");

  const create = async () => {
    setErr("");
    try {
      await api("/v1/drip/campaigns", { method: "POST", body: { appId: app, name, triggerEvent: trigger, steps } });
      setName(""); setSteps([{ channel: "push", title: "", body: "", delayHours: 24 }, { channel: "push", title: "", body: "", delayHours: 72 }]); setShowCreate(false); reload();
    } catch (e) { setErr(e.message); }
  };

  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => setShowCreate(!showCreate)}><Plus size={14}/> Create Drip Campaign</Btn>
    </div>

    {showCreate && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>New Drip Campaign</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Campaign Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Onboarding sequence"/></div>
        <div><label style={lbl()}>Trigger Event</label><input value={trigger} onChange={e => setTrigger(e.target.value)} style={inp()} placeholder="onboarding_completed"/></div>
      </div>
      <div style={{ marginTop: 12 }}><label style={lbl()}>Steps</label>
        {steps.map((s, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "100px 1fr 1fr 80px 32px", gap: 8, marginBottom: 6, alignItems: "end" }}>
          <div><label style={lbl()}>Channel</label><select value={s.channel} onChange={e => { const n = [...steps]; n[i].channel = e.target.value; setSteps(n); }} style={inp()}><option value="push">Push</option><option value="email">Email</option><option value="iam">In-App</option></select></div>
          <div><label style={lbl()}>Title</label><input value={s.title} onChange={e => { const n = [...steps]; n[i].title = e.target.value; setSteps(n); }} style={inp()} placeholder="Step title"/></div>
          <div><label style={lbl()}>Body</label><input value={s.body} onChange={e => { const n = [...steps]; n[i].body = e.target.value; setSteps(n); }} style={inp()} placeholder="Message body"/></div>
          <div><label style={lbl()}>Delay (h)</label><input type="number" value={s.delayHours} onChange={e => { const n = [...steps]; n[i].delayHours = +e.target.value; setSteps(n); }} style={inp()} min={0}/></div>
          {steps.length > 1 && <button onClick={() => setSteps(steps.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", alignSelf: "end", paddingBottom: 10 }}><Trash2 size={14}/></button>}
        </div>)}
        <Btn small onClick={() => setSteps([...steps, { channel: "push", title: "", body: "", delayHours: (steps.length + 1) * 24 }])}><Plus size={12}/> Add Step</Btn>
      </div>
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!name || !steps[0].title}><Check size={14}/> Create</Btn><Btn onClick={() => setShowCreate(false)}>Cancel</Btn></div>
    </Card>}

    <SectionTitle>Drip Campaigns</SectionTitle>
    {d.campaigns?.length > 0 ? d.campaigns.map(c => (<Card key={c.id} style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div><span style={{ fontWeight: 600 }}>{c.name}</span> <Badge color={c.is_active ? C.green : C.dim}>{c.is_active ? "Active" : "Off"}</Badge></div>
        <span style={{ fontSize: 11, color: C.dim }}>Trigger: {c.trigger_event}</span>
      </div>
      <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>{(c.steps || []).length} steps · {(c.steps || []).map((s, i) => `${i + 1}. ${s.channel || "push"} @ ${s.delayHours || 0}h`).join(", ")}</div>
    </Card>)) : <Empty msg="No drip campaigns — create one above"/>}
  </>);
}

function WebhooksPage() {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/webhooks/outbound"), []);
  const [show,setShow]=useState(false); const [url,setUrlVal]=useState(""); const [hookApp,setHookApp]=useState(""); const [events,setEvents]=useState(""); const [err,setErr]=useState("");
  const create = async () => { setErr(""); try { await api("/v1/webhooks/outbound", { method: "POST", body: { appId: hookApp || null, url, events: events ? events.split(",").map(e=>e.trim()) : [] } }); setUrlVal(""); setEvents(""); setShow(false); reload(); } catch(e) { setErr(e.message); } };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const hooks = d.webhooks || [];
  return (<>
    <div style={{ display:"flex",gap:8,marginBottom:16 }}>
      <Btn primary onClick={()=>setShow(!show)}><Plus size={14}/> Create Webhook</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{hooks.filter(w => w.is_active).length} active · {hooks.length} total</span>
    </div>
    {show && <Card style={{ marginBottom:16 }}>
      <div style={{ fontWeight:600,marginBottom:12,color:C.text }}>New Outbound Webhook</div>
      <div style={{ display:"grid",gridTemplateColumns:"2fr 1fr",gap:12 }}>
        <div><label style={lbl()}>URL *</label><input value={url} onChange={e=>setUrlVal(e.target.value)} style={inp()} placeholder="https://hooks.slack.com/..."/></div>
        <div><label style={lbl()}>App (empty = all)</label><select value={hookApp} onChange={e=>setHookApp(e.target.value)} style={inp()}><option value="">All apps</option>{APPS.map(a=><option key={a} value={a}>{appLabel(a)}</option>)}</select></div>
      </div>
      <div><label style={lbl()}>Events (comma-separated, empty = all)</label><input value={events} onChange={e=>setEvents(e.target.value)} style={inp()} placeholder="subscription_started, churn_risk, push_sent"/></div>
      {err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}
      <div style={{ display:"flex",gap:8,marginTop:12 }}><Btn primary onClick={create} disabled={!url}><Check size={14}/> Create</Btn><Btn onClick={()=>setShow(false)}>Cancel</Btn></div>
    </Card>}
    <SectionTitle>Outbound Webhooks</SectionTitle>
    {hooks.length > 0 ? <Card>{hooks.map((w, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < hooks.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <span style={{ width: 10, height: 10, borderRadius: 5, background: w.is_active ? C.green : C.dim, flexShrink: 0 }}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.url}</div>
          <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
            {w.app_id && <Badge>{w.app_id}</Badge>}
            {(w.events || []).slice(0, 3).map(e => <Badge key={e} color={C.dim}>{e}</Badge>)}
            {(w.events || []).length > 3 && <Badge color={C.dim}>+{w.events.length - 3}</Badge>}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{w.fire_count || 0}</div>
          <div style={{ fontSize: 10, color: C.dim }}>fired</div>
        </div>
        {w.last_fired && <div style={{ fontSize: 10, color: C.dim, flexShrink: 0, textAlign: "right", minWidth: 80 }}>{new Date(w.last_fired).toLocaleDateString()}</div>}
      </div>
    ))}</Card> : <Empty msg="No webhooks — create one to receive event notifications"/>}
  </>);
}

function TagsPage() {
  const [uid, setUid] = useState(""); const [tags, setTags] = useState(null); const [newTag, setNewTag] = useState(""); const [err, setErr] = useState("");
  const load = async () => { if (!uid) return; setErr(""); try { setTags(await api(`/v1/tags/${uid}`)); } catch(e) { setErr(e.message); } };
  const addTag = async () => { if (!newTag || !uid) return; try { await api("/v1/tags", { method: "POST", body: { userId: uid, tags: [newTag] } }); setNewTag(""); load(); } catch(e) { setErr(e.message); } };
  const removeTag = async (tag) => { try { await api("/v1/tags", { method: "DELETE", body: { userId: uid, tags: [tag] } }); load(); } catch(e) { setErr(e.message); } };
  return (<><Card><div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}><div style={{ flex: 1 }}><label style={lbl()}>User ID</label><input value={uid} onChange={e => setUid(e.target.value)} style={inp()} onKeyDown={e => e.key === 'Enter' && load()}/></div><Btn primary onClick={load}>Load Tags</Btn></div></Card>
    {err && <Card style={{ marginTop: 12 }}><div style={{ color: C.red, fontSize: 12 }}>{err}</div></Card>}
    {tags && <Card style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
        {(tags.tags||[]).map(t => <span key={t} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 6, background: C.accent + "18", color: C.accent }}>
          {t} <button onClick={() => removeTag(t)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
        </span>)}
        {(tags.tags||[]).length === 0 && <div style={{ color: C.dim, fontSize: 12 }}>No tags</div>}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={newTag} onChange={e => setNewTag(e.target.value)} style={{ ...inp(), flex: 1 }} placeholder="Add a tag..." onKeyDown={e => e.key === 'Enter' && addTag()}/>
        <Btn small primary onClick={addTag} disabled={!newTag}><Plus size={12}/> Add</Btn>
      </div>
    </Card>}</>);
}

function GroupsPage() {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/groups"), []);
  const [show,setShow]=useState(false); const [gid,setGid]=useState(""); const [name,setName]=useState(""); const [err,setErr]=useState("");
  const create = async () => { setErr(""); try { await api("/v1/groups", { method: "POST", body: { groupId: gid, name } }); setGid(""); setName(""); setShow(false); reload(); } catch(e) { setErr(e.message); } };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const groups = d.groups || [];
  const totalMembers = groups.reduce((s, g) => s + (g.member_count || 0), 0);
  const maxMembers = Math.max(...groups.map(g => g.member_count || 0), 1);
  return (<>
    <div style={{ display:"flex",gap:8,marginBottom:16 }}>
      <Btn primary onClick={()=>setShow(!show)}><Plus size={14}/> Create Group</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{groups.length} groups · {totalMembers} total members</span>
    </div>
    {show && <Card style={{ marginBottom:16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>Create Group</div>
      <div style={{ display:"flex",gap:8,alignItems:"flex-end" }}>
        <div style={{ flex:1 }}><label style={lbl()}>Group ID *</label><input value={gid} onChange={e=>setGid(e.target.value)} style={inp()} placeholder="company_acme"/></div>
        <div style={{ flex:1 }}><label style={lbl()}>Display Name *</label><input value={name} onChange={e=>setName(e.target.value)} style={inp()} placeholder="Acme Corp"/></div>
        <Btn primary onClick={create} disabled={!gid||!name}><Check size={14}/> Create</Btn>
      </div>{err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}
    </Card>}
    <SectionTitle>User Groups</SectionTitle>
    {groups.length > 0 ? <Card>{groups.map((g, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < groups.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: C.blue + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: C.blue }}>
          {(g.name || g.group_id || "?")[0].toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{g.name || g.group_id}</div>
          <div style={{ fontSize: 11, color: C.dim }}>{g.group_id}</div>
        </div>
        <div style={{ width: 80, height: 6, background: C.bg, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${(g.member_count / maxMembers) * 100}%`, height: "100%", background: C.blue, borderRadius: 3 }}/>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text, minWidth: 60, textAlign: "right" }}>{g.member_count || 0}</span>
        <span style={{ fontSize: 10, color: C.dim }}>members</span>
      </div>
    ))}</Card> : <Empty msg="No groups — create one to organize users by company or team"/>}
  </>);
}

/* ═══════════════════════════════════════════════
   DISTRIBUTION HUB — Testers / Groups / Builds
   Manages testers and ad-hoc IPA distribution to dist.velkra.app
   ═══════════════════════════════════════════════ */

function DistTestersPage() {
  const { data: testersData, error, loading, reload } = useFetch(() => api("/v1/dist/admin/testers"), []);
  const { data: groupsData } = useFetch(() => api("/v1/dist/admin/groups"), []);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all / active / inactive / external / internal
  const [busy, setBusy] = useState(""); // tester id currently being acted on
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [addExternal, setAddExternal] = useState(true);
  const [addGroups, setAddGroups] = useState([]);
  const [addErr, setAddErr] = useState("");

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const testers = testersData?.testers || [];
  const groups = groupsData?.groups || [];

  const filtered = testers.filter(t => {
    if (search) {
      const s = search.toLowerCase();
      if (!(t.email || "").toLowerCase().includes(s) && !(t.display_name || "").toLowerCase().includes(s)) return false;
    }
    if (filter === "active" && !t.is_active) return false;
    if (filter === "inactive" && t.is_active) return false;
    if (filter === "external" && !t.is_external) return false;
    if (filter === "internal" && t.is_external) return false;
    return true;
  });

  const stats = {
    total: testers.length,
    active: testers.filter(t => t.is_active).length,
    external: testers.filter(t => t.is_external).length,
    devices: testers.reduce((s, t) => s + parseInt(t.device_count || 0), 0),
  };

  const addTester = async () => {
    setAddErr("");
    if (!addEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addEmail)) { setAddErr("Valid email required"); return; }
    try {
      await api("/v1/dist/admin/testers", {
        method: "POST",
        body: { email: addEmail, displayName: addName || undefined, isExternal: addExternal, groupIds: addGroups },
      });
      setAddEmail(""); setAddName(""); setAddExternal(true); setAddGroups([]);
      setShowAdd(false);
      reload();
    } catch (e) { setAddErr(e.message); }
  };

  const revokeSessions = async (t) => {
    if (!window.confirm(`Revoke all sessions for ${t.email}?\n\nThey'll be signed out of every device. Push subscriptions are also wiped.`)) return;
    setBusy(t.id);
    try { await api(`/v1/dist/admin/testers/${t.id}/sessions/revoke`, { method: "POST" }); reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  const deactivate = async (t) => {
    if (!window.confirm(`Deactivate ${t.email}?\n\nThey'll lose access to dist.velkra.app and all sessions are revoked. The account record is kept (not deleted) so you can reactivate later.`)) return;
    setBusy(t.id);
    try { await api(`/v1/dist/admin/testers/${t.id}`, { method: "DELETE" }); reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  const reactivate = async (t) => {
    setBusy(t.id);
    try {
      // Reactivation: re-POST the same email — backend's ON CONFLICT branch sets is_active=true
      await api("/v1/dist/admin/testers", {
        method: "POST",
        body: { email: t.email, displayName: t.display_name, isExternal: t.is_external, groupIds: [] },
      });
      reload();
    } catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  return (<>
    <Grid cols={4}>
      <Stat label="Total Testers" value={stats.total}/>
      <Stat label="Active" value={stats.active} color={C.green}/>
      <Stat label="External" value={stats.external} color={C.blue}/>
      <Stat label="Devices Registered" value={stats.devices}/>
    </Grid>

    <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
      <Btn primary onClick={() => setShowAdd(!showAdd)}><Plus size={14}/> Add Tester</Btn>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by email or name..."
        style={{ ...inp(), flex: 1, minWidth: 200, maxWidth: 320 }}
      />
      <select value={filter} onChange={e => setFilter(e.target.value)} style={{ ...inp(), width: 140 }}>
        <option value="all">All testers</option>
        <option value="active">Active only</option>
        <option value="inactive">Inactive</option>
        <option value="external">External</option>
        <option value="internal">Internal</option>
      </select>
      <span style={{ fontSize: 12, color: C.dim }}>{filtered.length} of {testers.length}</span>
    </div>

    {showAdd && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>Add Tester</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl()}>Email *</label>
          <input
            type="email"
            value={addEmail}
            onChange={e => setAddEmail(e.target.value)}
            style={inp()}
            placeholder="tester@example.com"
            autoComplete="off"
          />
        </div>
        <div>
          <label style={lbl()}>Display Name</label>
          <input
            value={addName}
            onChange={e => setAddName(e.target.value)}
            style={inp()}
            placeholder="(optional — defaults to email handle)"
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: C.dim, padding: "10px 0" }}>
          <input type="checkbox" checked={addExternal} onChange={e => setAddExternal(e.target.checked)} style={{ accentColor: C.accent }}/>
          External tester
          <span style={{ fontSize: 11, color: C.muted, marginLeft: 4 }}>(outside your org — counts against ad-hoc cap)</span>
        </label>
      </div>
      {groups.length > 0 && <div style={{ marginBottom: 12 }}>
        <label style={lbl()}>Add to groups (optional)</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {groups.map(g => {
            const selected = addGroups.includes(g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => setAddGroups(prev => selected ? prev.filter(id => id !== g.id) : [...prev, g.id])}
                style={{
                  fontSize: 12, padding: "5px 10px", borderRadius: 6,
                  background: selected ? C.accent : C.bg,
                  color: selected ? "#fff" : C.dim,
                  border: `1px solid ${selected ? C.accent : C.border}`,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                {g.name} <span style={{ opacity: 0.7, marginLeft: 4 }}>{g.app_slug}</span>
              </button>
            );
          })}
        </div>
      </div>}
      {addErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{addErr}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn primary onClick={addTester} disabled={!addEmail}><Check size={14}/> Add Tester</Btn>
        <Btn onClick={() => { setShowAdd(false); setAddErr(""); }}>Cancel</Btn>
        <span style={{ fontSize: 11, color: C.muted, alignSelf: "center", marginLeft: 8 }}>
          They'll sign in at dist.velkra.app with Sign in with Apple using this email
        </span>
      </div>
    </Card>}

    {filtered.length === 0 ? (
      <Empty msg={testers.length === 0 ? "No testers yet — add your first one above" : "No testers match the current filter"}/>
    ) : (
      <Card style={{ padding: 0 }}>
        {filtered.map((t, i) => (
          <div
            key={t.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 20px",
              borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : "none",
              opacity: t.is_active ? 1 : 0.55,
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 18,
              background: t.is_active ? (t.is_external ? C.blue + "22" : C.green + "22") : C.border,
              color: t.is_active ? (t.is_external ? C.blue : C.green) : C.muted,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 14, flexShrink: 0,
            }}>
              {((t.display_name || t.email || "?")[0] || "?").toUpperCase()}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.display_name || t.email}
                </span>
                {t.is_external ? <Badge color={C.blue}>External</Badge> : <Badge color={C.green}>Internal</Badge>}
                {!t.is_active && <Badge color={C.muted}>Inactive</Badge>}
              </div>
              <div style={{ fontSize: 12, color: C.dim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.email}
              </div>
            </div>

            <div style={{ textAlign: "right", flexShrink: 0, fontSize: 11, color: C.dim, minWidth: 90 }}>
              <div><strong style={{ color: C.text, fontWeight: 600 }}>{t.device_count || 0}</strong> devices</div>
              <div><strong style={{ color: C.text, fontWeight: 600 }}>{t.group_count || 0}</strong> groups</div>
            </div>

            <div style={{ flexShrink: 0, fontSize: 11, color: C.dim, minWidth: 110, textAlign: "right" }}>
              <div>{t.last_login_at ? `Last: ${new Date(t.last_login_at).toLocaleDateString()}` : "Never logged in"}</div>
              <div style={{ color: C.muted, marginTop: 2 }}>Added {new Date(t.created_at).toLocaleDateString()}</div>
            </div>

            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              {t.is_active ? (<>
                <Btn small onClick={() => revokeSessions(t)} disabled={busy === t.id} style={{ padding: "5px 10px" }}>
                  <RefreshCw size={12}/> Revoke
                </Btn>
                <Btn small destructive onClick={() => deactivate(t)} disabled={busy === t.id} style={{ padding: "5px 10px" }}>
                  <Trash2 size={12}/> Deactivate
                </Btn>
              </>) : (
                <Btn small primary onClick={() => reactivate(t)} disabled={busy === t.id} style={{ padding: "5px 10px" }}>
                  <Check size={12}/> Reactivate
                </Btn>
              )}
            </div>
          </div>
        ))}
      </Card>
    )}
  </>);
}

function DistGroupsPage() {
  const { data: groupsData, error, loading, reload } = useFetch(() => api("/v1/dist/admin/groups"), []);
  const { data: testersData } = useFetch(() => api("/v1/dist/admin/testers"), []);
  const [showAdd, setShowAdd] = useState(false);
  const [addApp, setAddApp] = useState("");
  const [addName, setAddName] = useState("");
  const [addDesc, setAddDesc] = useState("");
  const [addErr, setAddErr] = useState("");
  const [expanded, setExpanded] = useState({}); // group id → bool
  const [memberAdd, setMemberAdd] = useState({}); // group id → array of tester ids to add
  const [busy, setBusy] = useState("");

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const groups = groupsData?.groups || [];
  const testers = (testersData?.testers || []).filter(t => t.is_active);

  const create = async () => {
    setAddErr("");
    if (!addApp || !addName) { setAddErr("App and name required"); return; }
    try {
      await api("/v1/dist/admin/groups", {
        method: "POST",
        body: { appSlug: addApp, name: addName, description: addDesc || undefined },
      });
      setAddApp(""); setAddName(""); setAddDesc("");
      setShowAdd(false);
      reload();
    } catch (e) { setAddErr(e.message); }
  };

  const addMembers = async (groupId) => {
    const ids = memberAdd[groupId] || [];
    if (ids.length === 0) return;
    setBusy(groupId);
    try {
      await api(`/v1/dist/admin/groups/${groupId}/members`, { method: "POST", body: { testerIds: ids } });
      setMemberAdd(prev => ({ ...prev, [groupId]: [] }));
      reload();
    } catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  const totalMembers = groups.reduce((s, g) => s + parseInt(g.member_count || 0), 0);
  const avgMembers = groups.length ? Math.round(totalMembers / groups.length) : 0;

  return (<>
    <Grid cols={3}>
      <Stat label="Total Groups" value={groups.length}/>
      <Stat label="Total Members" value={totalMembers}/>
      <Stat label="Avg per Group" value={avgMembers}/>
    </Grid>

    <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 16 }}>
      <Btn primary onClick={() => setShowAdd(!showAdd)}><Plus size={14}/> Create Group</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>
        Groups bundle testers per-app for build targeting
      </span>
    </div>

    {showAdd && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>New Group</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl()}>App *</label>
          <select value={addApp} onChange={e => setAddApp(e.target.value)} style={inp()}>
            <option value="">— select —</option>
            {APPS.map(a => <option key={a} value={a}>{appLabel(a)}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl()}>Name *</label>
          <input value={addName} onChange={e => setAddName(e.target.value)} style={inp()} placeholder="e.g. Internal QA"/>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl()}>Description</label>
        <input value={addDesc} onChange={e => setAddDesc(e.target.value)} style={inp()} placeholder="(optional)"/>
      </div>
      {addErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{addErr}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn primary onClick={create} disabled={!addApp || !addName}><Check size={14}/> Create</Btn>
        <Btn onClick={() => { setShowAdd(false); setAddErr(""); }}>Cancel</Btn>
      </div>
    </Card>}

    {groups.length === 0 ? (
      <Empty msg="No groups yet — create one to start bundling testers per-app for build targeting"/>
    ) : (
      groups.map(g => {
        const isOpen = !!expanded[g.id];
        const selectedToAdd = memberAdd[g.id] || [];
        return (
          <Card key={g.id} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} onClick={() => setExpanded(prev => ({ ...prev, [g.id]: !prev[g.id] }))}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: C.purple + "22", color: C.purple,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700, flexShrink: 0,
              }}>
                {(g.name || "?")[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{g.name}</span>
                  <Badge color={C.dim}>{g.app_slug}</Badge>
                </div>
                {g.description && <div style={{ fontSize: 12, color: C.dim, marginTop: 3 }}>{g.description}</div>}
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, fontSize: 12, color: C.dim }}>
                <div><strong style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{g.member_count || 0}</strong> members</div>
                <div style={{ marginTop: 2 }}>{isOpen ? "▾" : "▸"} {isOpen ? "Collapse" : "Manage"}</div>
              </div>
            </div>

            {isOpen && <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>Add members</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12, maxHeight: 220, overflowY: "auto", padding: 4 }}>
                {testers.length === 0 ? (
                  <span style={{ fontSize: 12, color: C.muted }}>No active testers — add some on the Testers page first</span>
                ) : testers.map(t => {
                  const selected = selectedToAdd.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setMemberAdd(prev => ({
                        ...prev,
                        [g.id]: selected ? selectedToAdd.filter(id => id !== t.id) : [...selectedToAdd, t.id],
                      }))}
                      style={{
                        fontSize: 12, padding: "5px 10px", borderRadius: 6,
                        background: selected ? C.accent : C.bg,
                        color: selected ? "#fff" : C.dim,
                        border: `1px solid ${selected ? C.accent : C.border}`,
                        cursor: "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", gap: 4,
                      }}
                    >
                      {t.display_name || t.email}
                      {t.is_external && <span style={{ opacity: 0.7, fontSize: 10 }}>·ext</span>}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Btn primary small onClick={() => addMembers(g.id)} disabled={busy === g.id || selectedToAdd.length === 0}>
                  <Plus size={12}/> Add {selectedToAdd.length || ""}
                </Btn>
                {selectedToAdd.length > 0 && (
                  <Btn small onClick={() => setMemberAdd(prev => ({ ...prev, [g.id]: [] }))}>Clear</Btn>
                )}
                <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>
                  Click testers above to add. Existing members aren't shown — duplicates are silently skipped.
                </span>
              </div>
            </div>}
          </Card>
        );
      })
    )}
  </>);
}

function DistBuildsPage() {
  const [filterApp, setFilterApp] = useState("");
  const [filterState, setFilterState] = useState("");
  const queryStr = (filterApp || filterState) ? `?${[filterApp && `app=${filterApp}`, filterState && `state=${filterState}`].filter(Boolean).join("&")}&limit=200` : "?limit=200";
  const { data, error, loading, reload } = useFetch(() => api(`/v1/dist/admin/builds${queryStr}`), [filterApp, filterState]);
  const { data: groupsData } = useFetch(() => api("/v1/dist/admin/groups"), []);
  const [editingTargets, setEditingTargets] = useState(null); // build id being edited
  const [targetSelection, setTargetSelection] = useState([]);
  const [busy, setBusy] = useState("");

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const builds = data?.builds || [];
  const groups = groupsData?.groups || [];

  const stateBadge = (s) => {
    const map = {
      ready: { color: C.green, label: "Ready" },
      pending: { color: C.yellow, label: "Pending" },
      uploading: { color: C.yellow, label: "Uploading" },
      processing: { color: C.yellow, label: "Processing" },
      expired: { color: C.muted, label: "Expired" },
      failed: { color: C.red, label: "Failed" },
    };
    const m = map[s] || { color: C.dim, label: s || "—" };
    return <Badge color={m.color}>{m.label}</Badge>;
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    const mb = n / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const deleteBuild = async (b) => {
    if (!window.confirm(`Delete build ${b.app_slug} ${b.version} (${b.build_number})?\n\nThis removes the IPA and revokes all targeting. Testers will lose access. Cannot be undone.`)) return;
    setBusy(b.id);
    try { await api(`/v1/dist/admin/builds/${b.id}`, { method: "DELETE" }); reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  const startEditTargets = (b) => {
    setEditingTargets(b.id);
    setTargetSelection([]); // we don't get current targets from the list endpoint, so user starts fresh — saving will overwrite
  };

  const saveTargets = async (b) => {
    setBusy(b.id);
    try {
      await api(`/v1/dist/admin/builds/${b.id}/targets`, { method: "POST", body: { groupIds: targetSelection } });
      setEditingTargets(null);
      setTargetSelection([]);
      reload();
    } catch (e) { alert(e.message); }
    finally { setBusy(""); }
  };

  const stats = {
    total: builds.length,
    ready: builds.filter(b => b.state === "ready").length,
    expired: builds.filter(b => b.state === "expired").length,
    sizeBytes: builds.reduce((s, b) => s + parseInt(b.ipa_size_bytes || 0), 0),
  };

  return (<>
    <Grid cols={4}>
      <Stat label="Total Builds" value={stats.total}/>
      <Stat label="Ready" value={stats.ready} color={C.green}/>
      <Stat label="Expired" value={stats.expired} color={C.muted}/>
      <Stat label="Storage Used" value={fmtBytes(stats.sizeBytes)}/>
    </Grid>

    <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 16, alignItems: "center" }}>
      <select value={filterApp} onChange={e => setFilterApp(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All apps</option>
        {APPS.map(a => <option key={a} value={a}>{appLabel(a)}</option>)}
      </select>
      <select value={filterState} onChange={e => setFilterState(e.target.value)} style={{ ...inp(), width: 140 }}>
        <option value="">All states</option>
        <option value="ready">Ready</option>
        <option value="pending">Pending</option>
        <option value="processing">Processing</option>
        <option value="expired">Expired</option>
        <option value="failed">Failed</option>
      </select>
      <Btn small onClick={reload}><RefreshCw size={12}/> Refresh</Btn>
      <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>
        Builds upload via Mac-side StellarFixerWeb — this is a read-only view + targeting
      </span>
    </div>

    {builds.length === 0 ? (
      <Empty msg="No builds yet — uploads happen via the Mac CLI or StellarFixerWeb"/>
    ) : (
      <Card style={{ padding: 0 }}>
        {builds.map((b, i) => {
          const editing = editingTargets === b.id;
          return (
            <div key={b.id} style={{ borderBottom: i < builds.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px" }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: C.cyan + "22", color: C.cyan,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Download size={16}/>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                      {b.app_name || b.app_slug}
                    </span>
                    <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: C.dim }}>
                      v{b.version} ({b.build_number})
                    </span>
                    {stateBadge(b.state)}
                    {b.promoted_to_tf && <Badge color={C.purple}>TestFlight</Badge>}
                  </div>
                  <div style={{ fontSize: 11, color: C.dim }}>
                    {b.app_slug} · uploaded {b.uploaded_at ? new Date(b.uploaded_at).toLocaleString() : "—"}
                    {b.expires_at && ` · expires ${new Date(b.expires_at).toLocaleDateString()}`}
                  </div>
                </div>

                <div style={{ flexShrink: 0, fontSize: 12, color: C.dim, textAlign: "right", minWidth: 80 }}>
                  {fmtBytes(b.ipa_size_bytes)}
                </div>

                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <Btn small onClick={() => editing ? setEditingTargets(null) : startEditTargets(b)} style={{ padding: "5px 10px" }}>
                    {editing ? <X size={12}/> : <Users size={12}/>} {editing ? "Cancel" : "Targets"}
                  </Btn>
                  <Btn small destructive onClick={() => deleteBuild(b)} disabled={busy === b.id} style={{ padding: "5px 10px" }}>
                    <Trash2 size={12}/>
                  </Btn>
                </div>
              </div>

              {editing && <div style={{ padding: "0 20px 16px 68px" }}>
                <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>
                  Select groups to target. Saving overwrites the existing targets — only the selected groups will have access.
                </div>
                {groups.filter(g => g.app_slug === b.app_slug).length === 0 ? (
                  <span style={{ fontSize: 12, color: C.muted }}>No groups exist for {b.app_slug} — create one on the Groups page first</span>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                    {groups.filter(g => g.app_slug === b.app_slug).map(g => {
                      const sel = targetSelection.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => setTargetSelection(prev => sel ? prev.filter(id => id !== g.id) : [...prev, g.id])}
                          style={{
                            fontSize: 12, padding: "5px 10px", borderRadius: 6,
                            background: sel ? C.accent : C.bg,
                            color: sel ? "#fff" : C.dim,
                            border: `1px solid ${sel ? C.accent : C.border}`,
                            cursor: "pointer", fontFamily: "inherit",
                          }}
                        >
                          {g.name} <span style={{ opacity: 0.7, marginLeft: 4 }}>{g.member_count} members</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn primary small onClick={() => saveTargets(b)} disabled={busy === b.id}>
                    <Check size={12}/> Save Targets
                  </Btn>
                  <Btn small onClick={() => { setEditingTargets(null); setTargetSelection([]); }}>Cancel</Btn>
                </div>
              </div>}
            </div>
          );
        })}
      </Card>
    )}
  </>);
}

function DistDevicesPage() {
  const [diagnostic, setDiagnostic] = useState(false);
  const { data, error, loading, reload } = useFetch(
    () => api(diagnostic ? "/v1/dist/admin/devices/diagnostic" : "/v1/dist/admin/devices"),
    [diagnostic]
  );
  const [filter, setFilter] = useState("all"); // all / synced / pending / excluded
  const [busy, setBusy] = useState(null); // device id being acted on
  const [actionMsg, setActionMsg] = useState(null);

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const devices = data?.devices || [];
  const capacity = data?.capacity || [];
  const capPerClass = data?.capPerClass || 100;
  const pendingSync = data?.pendingMacSync || 0;
  // Diagnostic-mode-only fields
  const eligibleCount = data?.eligibleCount;
  const isDiagnostic = diagnostic && eligibleCount !== undefined;

  const filtered = devices.filter(d => {
    if (filter === "synced") return d.asc_synced_at;
    if (filter === "pending") return !d.asc_synced_at && (isDiagnostic ? d.eligibleForSync : true);
    if (filter === "excluded") return isDiagnostic && !d.eligibleForSync && !d.asc_synced_at;
    return true;
  });

  const totalActive = devices.length;
  const totalSynced = devices.filter(d => d.asc_synced_at).length;
  const totalExcluded = isDiagnostic
    ? devices.filter(d => !d.eligibleForSync && !d.asc_synced_at).length
    : 0;
  const allClasses = ["iphone", "ipad", "ipod", "mac", "appletv", "watch"];
  const fmtClass = (c) => ({ iphone: "iPhone", ipad: "iPad", ipod: "iPod", mac: "Mac", appletv: "Apple TV", watch: "Apple Watch" }[c] || c);

  const requeue = async (id) => {
    setBusy(id); setActionMsg(null);
    try {
      await api(`/v1/dist/admin/devices/${id}/requeue`, { method: "POST", body: JSON.stringify({}) });
      setActionMsg({ ok: true, text: "Device re-queued. Will sync on next agent run." });
      reload();
    } catch (e) {
      setActionMsg({ ok: false, text: "Re-queue failed: " + (e.message || e) });
    } finally { setBusy(null); }
  };
  const removeDevice = async (id, udid) => {
    if (!confirm(`Delete device ${udid?.slice(0, 12)}…? This cannot be undone.`)) return;
    setBusy(id); setActionMsg(null);
    try {
      await api(`/v1/dist/admin/devices/${id}`, { method: "DELETE" });
      setActionMsg({ ok: true, text: "Device deleted." });
      reload();
    } catch (e) {
      setActionMsg({ ok: false, text: "Delete failed: " + (e.message || e) });
    } finally { setBusy(null); }
  };

  return (<>
    <Grid cols={isDiagnostic ? 4 : 4}>
      <Stat label="Total Devices" value={totalActive}/>
      <Stat label="ASC Synced" value={totalSynced} color={C.green}/>
      {isDiagnostic ? (
        <>
          <Stat label="Eligible for Sync" value={eligibleCount} color={eligibleCount > 0 ? C.yellow : C.dim}/>
          <Stat label="Excluded" value={totalExcluded} color={totalExcluded > 0 ? C.red : C.dim} sub={totalExcluded > 0 ? "see reasons below" : "none"}/>
        </>
      ) : (
        <>
          <Stat label="Pending Sync" value={pendingSync} color={pendingSync > 0 ? C.yellow : C.dim}/>
          <Stat label="Per-Class Cap" value={`${capPerClass}/yr`} sub="Apple limit"/>
        </>
      )}
    </Grid>

    {!isDiagnostic && pendingSync > 0 && <Card style={{ marginTop: 16, borderLeft: `3px solid ${C.yellow}`, background: C.yellow + "0a" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <AlertTriangle size={20} color={C.yellow} style={{ flexShrink: 0, marginTop: 2 }}/>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>
            {pendingSync} device{pendingSync === 1 ? "" : "s"} awaiting Mac-side ASC sync
          </div>
          <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5 }}>
            Open <strong>StellarFixerWeb → Distribution Hub</strong> on your Mac and click <code style={{ fontFamily: "ui-monospace, monospace", padding: "1px 6px", background: C.bg, borderRadius: 4 }}>Sync ASC Devices Now</code>, or wait — it auto-syncs every 60s when StellarFixerWeb is running. The backend doesn't hold Apple credentials; ASC operations happen locally on your Mac.
          </div>
        </div>
      </div>
    </Card>}

    {/* v160.4.60: diagnostic-mode banner — shown when the user toggled it on
        AND there are excluded devices. Tells them what to look for. */}
    {isDiagnostic && totalExcluded > 0 && <Card style={{ marginTop: 16, borderLeft: `3px solid ${C.red}`, background: C.red + "0a" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <AlertTriangle size={20} color={C.red} style={{ flexShrink: 0, marginTop: 2 }}/>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>
            {totalExcluded} device{totalExcluded === 1 ? " is" : "s are"} not eligible for sync
          </div>
          <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5 }}>
            These devices exist in the database but won't appear in the StellarFixerWeb sync queue. Each row below shows the reason. Use <strong>Re-queue</strong> to clear stale sync state, or <strong>Delete</strong> for orphans.
          </div>
        </div>
      </div>
    </Card>}

    {actionMsg && <Card style={{ marginTop: 12, borderLeft: `3px solid ${actionMsg.ok ? C.green : C.red}`, background: (actionMsg.ok ? C.green : C.red) + "0a" }}>
      <div style={{ fontSize: 13, color: C.text }}>{actionMsg.text}</div>
    </Card>}

    {!isDiagnostic && <>
      <SectionTitle>Capacity by Device Class</SectionTitle>
      <Grid cols={3} gap={12}>
        {allClasses.map(cls => {
          const used = parseInt((capacity.find(c => c.device_class === cls) || {}).used || 0);
          const pct = Math.min(100, (used / capPerClass) * 100);
          const tone = pct > 80 ? C.red : pct > 60 ? C.yellow : C.green;
          return (
            <Card key={cls} style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{fmtClass(cls)}</span>
                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 13, color: tone, fontWeight: 600 }}>
                  {used}/{capPerClass}
                </span>
              </div>
              <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: tone, borderRadius: 3, transition: "width 0.3s ease" }}/>
              </div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>
                {pct === 0 ? "No devices registered" : `${pct.toFixed(0)}% of yearly cap`}
              </div>
            </Card>
          );
        })}
      </Grid>
    </>}

    <SectionTitle>{isDiagnostic ? "Device Diagnostic" : "Registered Devices"}</SectionTitle>
    <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
      <select value={filter} onChange={e => setFilter(e.target.value)} style={{ ...inp(), width: 200 }}>
        <option value="all">All ({devices.length})</option>
        <option value="synced">Synced to ASC ({totalSynced})</option>
        <option value="pending">Pending sync ({isDiagnostic ? eligibleCount : pendingSync})</option>
        {isDiagnostic && <option value="excluded">Excluded ({totalExcluded})</option>}
      </select>
      <Btn small onClick={reload}><RefreshCw size={12}/> Refresh</Btn>
      {/* v160.4.60: Diagnostic toggle — fetches the richer endpoint with
          per-device reasonsExcluded so admins can see why devices aren't syncing. */}
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.dim, cursor: "pointer" }}>
        <input type="checkbox" checked={diagnostic} onChange={e => setDiagnostic(e.target.checked)}/>
        Diagnostic mode
      </label>
      <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>
        {filtered.length} of {devices.length} shown
      </span>
    </div>

    {filtered.length === 0 ? (
      <Empty msg={devices.length === 0 ? "No devices registered yet — testers register devices when they install their first build" : "No devices match the current filter"}/>
    ) : (
      <Card style={{ padding: 0 }}>
        {filtered.map((d, i) => {
          const synced = !!d.asc_synced_at;
          const excluded = isDiagnostic && !d.eligibleForSync && !synced;
          const orphan = isDiagnostic && !d.tester_join_id;
          return (
            <div key={d.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 20px", borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: (synced ? C.green : excluded ? C.red : C.yellow) + "22",
                color: synced ? C.green : excluded ? C.red : C.yellow,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, flexShrink: 0,
              }}>
                {d.device_class === "iphone" ? "📱" : d.device_class === "ipad" ? "📱" : d.device_class === "mac" ? "💻" : d.device_class === "watch" ? "⌚" : d.device_class === "appletv" ? "📺" : "📱"}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                    {d.device_name || `${fmtClass(d.device_class)} (unnamed)`}
                  </span>
                  <Badge color={C.dim}>{d.device_class}</Badge>
                  {synced ? <Badge color={C.green}>Synced</Badge>
                    : excluded ? <Badge color={C.red}>Excluded</Badge>
                    : <Badge color={C.yellow}>Pending</Badge>}
                  {isDiagnostic && d.is_active === false && <Badge color={C.red}>Inactive</Badge>}
                  {orphan && <Badge color={C.red}>Orphan</Badge>}
                </div>
                <div style={{ fontSize: 11, color: C.dim, fontFamily: "ui-monospace, monospace", marginBottom: 4 }}>
                  {(d.tester_email || (orphan ? "(tester missing)" : "—"))} · {(d.udid || "").substring(0, 16)}{(d.udid || "").length > 16 ? "…" : ""}
                </div>
                {/* v160.4.60: per-device exclusion reasons (diagnostic mode only) */}
                {isDiagnostic && d.reasonsExcluded?.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {d.reasonsExcluded.map((r, idx) => (
                      <span key={idx} style={{
                        fontSize: 10, padding: "2px 8px", borderRadius: 4,
                        background: C.red + "1a", color: C.red,
                        fontFamily: "ui-monospace, monospace",
                      }}>{r}</span>
                    ))}
                  </div>
                )}
                {/* Sync error from a previous failed attempt */}
                {d.asc_sync_error && (
                  <div style={{ fontSize: 10, color: C.red, fontFamily: "ui-monospace, monospace", marginTop: 4 }}>
                    Last error: {d.asc_sync_error}
                  </div>
                )}
              </div>

              <div style={{ flexShrink: 0, fontSize: 11, color: C.dim, textAlign: "right", minWidth: 110 }}>
                <div>iOS {d.ios_version || "—"}</div>
                <div style={{ marginTop: 2 }}>{d.registered_at ? new Date(d.registered_at).toLocaleDateString() : "—"}</div>
                {/* v160.4.60: admin actions in diagnostic mode */}
                {isDiagnostic && (
                  <div style={{ display: "flex", gap: 4, marginTop: 6, justifyContent: "flex-end" }}>
                    {!synced && (
                      <button
                        onClick={() => requeue(d.id)}
                        disabled={busy === d.id}
                        style={{ ...btnSmall(), opacity: busy === d.id ? 0.5 : 1 }}
                        title="Clear asc_synced_at + asc_sync_error so device re-enters the pending queue"
                      >Re-queue</button>
                    )}
                    {synced && (
                      <button
                        onClick={() => requeue(d.id)}
                        disabled={busy === d.id}
                        style={{ ...btnSmall(), opacity: busy === d.id ? 0.5 : 1 }}
                        title="Force resync — clears asc_synced_at"
                      >Resync</button>
                    )}
                    <button
                      onClick={() => removeDevice(d.id, d.udid)}
                      disabled={busy === d.id}
                      style={{ ...btnSmall(), color: C.red, opacity: busy === d.id ? 0.5 : 1 }}
                      title="Permanently delete this device row"
                    >Delete</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </Card>
    )}
  </>);
}

// v160.4.60: small inline action button used in diagnostic mode.
function btnSmall() {
  return {
    fontSize: 11,
    padding: "2px 8px",
    background: "transparent",
    border: `1px solid ${C.border}`,
    color: C.dim,
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: "inherit",
  };
}

/* ─── Distribution: Feedback ─────────────────────────────────────────── */
function DistFeedbackPage() {
  const [filterApp, setFilterApp] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterSev, setFilterSev] = useState("");
  const queryStr = [
    filterApp && `app=${filterApp}`,
    filterState && `state=${filterState}`,
    filterSev && `severity=${filterSev}`,
  ].filter(Boolean).join("&");
  const { data, error, loading, reload } = useFetch(
    () => api(`/v1/dist/admin/feedback${queryStr ? "?" + queryStr : ""}`),
    [filterApp, filterState, filterSev]
  );
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const items = data?.feedback || [];
  const apps = [...new Set(items.map(i => i.app_slug))].sort();
  const sevColor = { critical: C.red, high: C.accent, normal: C.blue, low: C.dim, feature_request: C.purple };
  const stateColor = { open: C.yellow, investigating: C.blue, resolved: C.green, wontfix: C.dim, dupe: C.muted };

  const setState = async (id, state) => {
    setBusy(true);
    try {
      await api(`/v1/dist/admin/feedback/${id}`, { method: "PATCH", body: { state } });
      reload();
      if (active?.id === id) setActive({ ...active, state });
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const stats = {
    total: items.length,
    open: items.filter(i => i.state === "open").length,
    critical: items.filter(i => i.severity === "critical").length,
    investigating: items.filter(i => i.state === "investigating").length,
  };

  return (<>
    <Grid cols={4}>
      <Stat label="Total" value={stats.total}/>
      <Stat label="Open" value={stats.open} color={C.yellow}/>
      <Stat label="Investigating" value={stats.investigating} color={C.blue}/>
      <Stat label="Critical" value={stats.critical} color={C.red}/>
    </Grid>

    <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
      <select value={filterApp} onChange={e => setFilterApp(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All apps</option>
        {apps.map(a => <option key={a} value={a}>{appLabel(a)}</option>)}
      </select>
      <select value={filterState} onChange={e => setFilterState(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All states</option>
        <option value="open">Open</option>
        <option value="investigating">Investigating</option>
        <option value="resolved">Resolved</option>
        <option value="wontfix">Won't fix</option>
        <option value="dupe">Duplicate</option>
      </select>
      <select value={filterSev} onChange={e => setFilterSev(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All severities</option>
        <option value="critical">🚨 Critical</option>
        <option value="high">⚠️ High</option>
        <option value="normal">📝 Normal</option>
        <option value="low">Low</option>
        <option value="feature_request">💡 Feature</option>
      </select>
      <Btn small onClick={reload}><RefreshCw size={12}/> Refresh</Btn>
      <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>{items.length} items</span>
    </div>

    {items.length === 0 ? (
      <Empty msg="No feedback matches filters"/>
    ) : (
      <div style={{ display: "grid", gridTemplateColumns: active ? "1fr 1fr" : "1fr", gap: 16 }}>
        <Card style={{ padding: 0, maxHeight: "70vh", overflowY: "auto" }}>
          {items.map((f, i) => (
            <div
              key={f.id}
              onClick={() => setActive(f)}
              style={{
                padding: "12px 16px",
                borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : "none",
                cursor: "pointer",
                background: active?.id === f.id ? C.accentDim : "transparent",
                borderLeft: active?.id === f.id ? `3px solid ${C.accent}` : "3px solid transparent",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Badge color={sevColor[f.severity] || C.dim}>{f.severity}</Badge>
                <Badge color={stateColor[f.state] || C.dim}>{f.state}</Badge>
                <span style={{ fontSize: 11, color: C.dim }}>{f.app_slug}</span>
                <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>{new Date(f.created_at).toLocaleDateString()}</span>
              </div>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.4, marginBottom: 4 }}>
                {f.message.slice(0, 140)}{f.message.length > 140 ? "…" : ""}
              </div>
              <div style={{ fontSize: 11, color: C.dim }}>{f.tester_name || f.tester_email}</div>
            </div>
          ))}
        </Card>

        {active && (
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Badge color={sevColor[active.severity] || C.dim}>{active.severity}</Badge>
              <Badge color={stateColor[active.state] || C.dim}>{active.state}</Badge>
              <button onClick={() => setActive(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>
              {active.app_slug} {active.build_version && `· v${active.build_version}`}
            </div>
            <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
              from {active.tester_name || active.tester_email} · {new Date(active.created_at).toLocaleString()}
            </div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, padding: 12, background: C.bg, borderRadius: 8, marginBottom: 12, whiteSpace: "pre-wrap" }}>
              {active.message}
            </div>
            {active.payload && Object.keys(active.payload).length > 0 && (
              <details style={{ marginBottom: 12 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: C.dim, marginBottom: 8 }}>Device + state payload</summary>
                <pre style={{ fontSize: 11, color: C.dim, padding: 10, background: C.bg, borderRadius: 6, overflowX: "auto", maxHeight: 200 }}>{JSON.stringify(active.payload, null, 2)}</pre>
              </details>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Btn small onClick={() => setState(active.id, "investigating")} disabled={busy || active.state === "investigating"}>👀 Investigating</Btn>
              <Btn small primary onClick={() => setState(active.id, "resolved")} disabled={busy}>✓ Resolved</Btn>
              <Btn small onClick={() => setState(active.id, "wontfix")} disabled={busy}>🚫 Won't fix</Btn>
              <Btn small onClick={() => setState(active.id, "dupe")} disabled={busy}>≡ Dupe</Btn>
            </div>
          </Card>
        )}
      </div>
    )}
  </>);
}

/* ─── Distribution: Bug Reports (v161.22.27) ─────────────────────────────
 * Admin view of the structured bug-report intake that landed in v22.24.
 * Reports group multiple bug entries filed in one testing session. From
 * here the dev can:
 *   • See the entire reply thread (Claude's auto-replies + admin replies)
 *   • Post manual replies/questions/summaries
 *   • Override report state (archive resolved threads, re-open mistakes)
 */
function DistBugReportsPage() {
  const [filterApp, setFilterApp] = useState("");
  const [filterState, setFilterState] = useState("");
  const queryStr = [
    filterApp && `app=${filterApp}`,
    filterState && `state=${filterState}`,
  ].filter(Boolean).join("&");
  const { data, error, loading, reload } = useFetch(
    () => api(`/v1/dist/admin/bug-reports${queryStr ? "?" + queryStr : ""}`),
    [filterApp, filterState]
  );
  const [activeId, setActiveId] = useState(null);

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const reports = data?.reports || [];
  const apps = [...new Set(reports.map(r => r.app_slug))].sort();
  const stateColor = {
    draft: C.dim, submitted: C.purple, fixing: C.yellow,
    reopened: C.yellow,
    fixed: C.green, archived: C.muted,
    likely_duplicate: C.yellow,
    needs_human_review: C.red
  };
  const stats = {
    total: reports.length,
    // v161.22.32 (audit fix): "in progress" now spans every non-terminal
    // state so reopened/needs_review/likely_duplicate reports don't fall
    // off the dashboard's primary attention indicator.
    open: reports.filter(r => ["submitted", "fixing", "reopened",
                                "needs_human_review", "likely_duplicate"].includes(r.state)).length,
    fixed: reports.filter(r => r.state === "fixed").length,
    archived: reports.filter(r => r.state === "archived").length,
  };

  return (<>
    <Grid cols={4}>
      <Stat label="Total" value={stats.total}/>
      <Stat label="In progress" value={stats.open} color={C.yellow}/>
      <Stat label="Fixed" value={stats.fixed} color={C.green}/>
      <Stat label="Archived" value={stats.archived} color={C.muted}/>
    </Grid>

    <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
      <select value={filterApp} onChange={e => setFilterApp(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All apps</option>
        {apps.map(a => <option key={a} value={a}>{appLabel(a)}</option>)}
      </select>
      <select value={filterState} onChange={e => setFilterState(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All states</option>
        <option value="draft">Draft</option>
        <option value="submitted">Submitted</option>
        <option value="fixing">Fixing</option>
        <option value="reopened">Reopened (retry)</option>
        <option value="fixed">Fixed</option>
        <option value="needs_human_review">Needs review</option>
        <option value="likely_duplicate">Likely duplicate</option>
        <option value="archived">Archived</option>
      </select>
      <Btn small onClick={reload}><RefreshCw size={12}/> Refresh</Btn>
      {/* v161.22.28: webhook config toggle, visible when a single app is selected */}
      {filterApp && <WebhookConfigPill appSlug={filterApp}/>}
      {/* v161.22.30: fixed-bugs archive viewer, visible when a single app is selected */}
      {filterApp && <FixedBugsArchiveButton appSlug={filterApp}/>}
      <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>{reports.length} reports</span>
    </div>

    {reports.length === 0 ? (
      <Empty msg="No bug reports match filters"/>
    ) : (
      <div style={{ display: "grid", gridTemplateColumns: activeId ? "1fr 1fr" : "1fr", gap: 16 }}>
        <Card style={{ padding: 0, maxHeight: "70vh", overflowY: "auto" }}>
          {reports.map((r) => (
            <div
              key={r.id}
              onClick={() => setActiveId(r.id)}
              style={{
                padding: 12, borderBottom: `1px solid ${C.border}`,
                cursor: "pointer",
                background: activeId === r.id ? C.bgElev : "transparent"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>
                  {r.title || "Untitled session report"}
                </span>
                <Badge style={{ background: stateColor[r.state] }}>{r.state}</Badge>
              </div>
              <div style={{ fontSize: 12, color: C.dim }}>
                {appLabel(r.app_slug)} · {r.tester_name || r.tester_id?.slice(0, 8)}
                {r.device_model && ` · ${r.device_model}`}
                {` · ${r.entry_count} ${r.entry_count === 1 ? "bug" : "bugs"}`}
                {r.reply_count > 0 && ` · 💬 ${r.reply_count}`}
              </div>
            </div>
          ))}
        </Card>

        {activeId && <BugReportDetail reportId={activeId} onClose={() => setActiveId(null)} onChange={reload}/>}
      </div>
    )}
  </>);
}

// v161.22.30: archive viewer button + modal. Renders the "what we've
// already fixed for this app" document — admins use this to triage
// likely-duplicate reports and to spot-check the matcher's accuracy.
function FixedBugsArchiveButton({ appSlug }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          padding: "4px 10px", background: `${C.purple}22`, color: C.purple,
          border: `1px solid ${C.purple}`, borderRadius: 8,
          fontSize: 11, cursor: "pointer", fontWeight: 600
        }}
        title="View the recently-fixed-bugs archive for this app"
      >
        📚 Fixed bugs archive
      </button>
      {open && <FixedBugsArchiveModal appSlug={appSlug} onClose={() => setOpen(false)}/>}
    </>
  );
}

function FixedBugsArchiveModal({ appSlug, onClose }) {
  const [sinceDays, setSinceDays] = useState(30);
  const { data, loading, error, reload } = useFetch(
    () => api(`/v1/dist/admin/apps/${appSlug}/fixed-bugs?sinceDays=${sinceDays}`),
    [appSlug, sinceDays]
  );

  const fixedBugs = data?.fixedBugs || [];
  const verified = fixedBugs.filter(b => b.verified).length;
  const regressed = fixedBugs.filter(b => b.regressed).length;
  const unverified = fixedBugs.length - verified - regressed;

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.card, borderRadius: 16, padding: 20,
          width: "90%", maxWidth: 800, maxHeight: "85vh", overflowY: "auto",
          border: `1px solid ${C.border}`
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              📚 Fixed bugs archive — {appLabel(appSlug)}
            </div>
            <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>
              Bugs auto-fix has shipped fixes for. New incoming reports are
              matched against this list — high-confidence matches get
              short-circuited into "likely duplicate" state without queuing
              another fix run.
            </div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: C.dim }}>Window:</span>
          {[7, 30, 90, 180].map(d => (
            <Btn
              key={d}
              small
              onClick={() => setSinceDays(d)}
              style={sinceDays === d ? { background: C.accent, color: "#fff" } : {}}
            >
              {d}d
            </Btn>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 11, color: C.muted }}>
            {fixedBugs.length} bug(s) — ✓ {verified} verified · ⚠️ {regressed} regressed · ? {unverified} unverified
          </span>
        </div>

        {loading && <Loading/>}
        {error && <ErrorState error={error} onRetry={reload}/>}
        {!loading && !error && fixedBugs.length === 0 && (
          <Empty msg="No bugs have been auto-fixed for this app in this window yet."/>
        )}
        {!loading && fixedBugs.length > 0 && (
          <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {fixedBugs.map(b => {
              const stateColor = b.regressed ? C.red : b.verified ? C.green : C.dim;
              const stateIcon = b.regressed ? "⚠️" : b.verified ? "✓" : "?";
              return (
                <li
                  key={b.id}
                  style={{
                    padding: 12, marginBottom: 8, borderRadius: 8,
                    background: C.bgElev,
                    borderLeft: `3px solid ${stateColor}`
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                        <span style={{ color: stateColor, marginRight: 6 }}>{stateIcon}</span>
                        {b.bug_title}
                      </div>
                      <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
                        Fixed in {b.fixed_build_label || "build unknown"}
                        {" · "}{new Date(b.fixed_at).toLocaleString()}
                        {b.diff_files_changed > 0 && (
                          <> · {b.diff_files_changed} file(s), +{b.diff_lines_added}/-{b.diff_lines_deleted}</>
                        )}
                      </div>
                      {b.changed_file_paths && b.changed_file_paths.length > 0 && (
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 4, fontFamily: "monospace" }}>
                          {b.changed_file_paths.slice(0, 5).join(", ")}
                          {b.changed_file_paths.length > 5 && ` … +${b.changed_file_paths.length - 5}`}
                        </div>
                      )}
                    </div>
                  </div>
                  {b.bug_summary && (
                    <div style={{
                      fontSize: 12, color: C.dim, marginTop: 6,
                      maxHeight: 60, overflow: "hidden",
                      whiteSpace: "pre-wrap"
                    }}>
                      {b.bug_summary.slice(0, 300)}
                      {b.bug_summary.length > 300 && "…"}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

function WebhookConfigPill({ appSlug }) {
  const { data, reload } = useFetch(
    () => api(`/v1/dist/admin/apps/${appSlug}/bug-report-webhook-config`),
    [appSlug]
  );
  const [busy, setBusy] = useState(false);
  if (!data) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      await api(`/v1/dist/admin/apps/${appSlug}/bug-report-webhook-config`, {
        method: "PATCH",
        body: { enabled: !data.enabled }
      });
      reload();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const color = data.enabled ? C.green : C.muted;
  const label = data.enabled ? "🔔 Webhooks on" : "🔕 Webhooks off";

  return (
    <button
      onClick={toggle}
      disabled={busy}
      style={{
        padding: "4px 10px", background: `${color}22`, color,
        border: `1px solid ${color}`, borderRadius: 8,
        fontSize: 11, cursor: "pointer", fontWeight: 600
      }}
      title={data.enabled
        ? "Slack/Discord notifications enabled for bug-report state changes on this app"
        : "Bug-report webhooks silenced for this app"
      }
    >
      {label}
    </button>
  );
}

function BugReportDetail({ reportId, onClose, onChange }) {
  const { data, error, loading, reload } = useFetch(
    () => api(`/v1/dist/admin/bug-reports/${reportId}`),
    [reportId]
  );
  const [replyKind, setReplyKind] = useState("status");
  const [replyBody, setReplyBody] = useState("");
  const [replySendPush, setReplySendPush] = useState(true);
  // v161.22.28: per-entry replies. null = report-level (default).
  const [replyEntryId, setReplyEntryId] = useState(null);
  const [sending, setSending] = useState(false);
  const [stateBusy, setStateBusy] = useState(false);

  if (loading) return <Card><Loading/></Card>;
  if (error) return <Card><ErrorState error={error} onRetry={reload}/></Card>;

  const { report, entries = [], replies = [], fixState } = data || {};
  if (!report) return null;

  // v161.22.28: group replies by entry for inline rendering under each.
  const reportLevelReplies = replies.filter(r => !r.entry_id);
  const repliesByEntry = new Map();
  for (const r of replies) {
    if (!r.entry_id) continue;
    if (!repliesByEntry.has(r.entry_id)) repliesByEntry.set(r.entry_id, []);
    repliesByEntry.get(r.entry_id).push(r);
  }

  const sendReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await api(`/v1/dist/admin/bug-reports/${reportId}/reply`, {
        method: "POST",
        body: {
          kind: replyKind,
          body: replyBody.trim(),
          sendPush: replySendPush,
          entryId: replyEntryId
        }
      });
      setReplyBody("");
      reload();
      onChange && onChange();
    } catch (e) { alert(e.message); }
    finally { setSending(false); }
  };

  const changeState = async (newState) => {
    if (!confirm(`Change report state to "${newState}"?`)) return;
    setStateBusy(true);
    try {
      await api(`/v1/dist/admin/bug-reports/${reportId}`, {
        method: "PATCH",
        body: { state: newState }
      });
      reload();
      onChange && onChange();
    } catch (e) { alert(e.message); }
    finally { setStateBusy(false); }
  };

  // v161.22.32.9: admin can force-delete a bug report regardless of state.
  // Backend cancels any linked fix_request and fails in-flight attempts
  // before deleting the row so AgentMac releases its claim. CASCADE on
  // dist_bug_replies handles reply cleanup automatically.
  const deleteReport = async () => {
    if (!confirm(
      `Permanently delete this bug report?\n\n` +
      `This will:\n` +
      `  • Cancel any linked auto-fix request\n` +
      `  • Mark in-flight attempts as failed\n` +
      `  • Delete all replies and feedback entries\n` +
      `  • Cannot be undone.\n\n` +
      `Report: ${report.title || '(untitled)'}\n` +
      `State: ${report.state}`
    )) return;
    setStateBusy(true);
    try {
      await api(`/v1/dist/admin/bug-reports/${reportId}`, { method: "DELETE" });
      onChange && onChange();
      onClose && onClose();
    } catch (e) { alert(e.message); setStateBusy(false); }
  };

  const kindColor = {
    status: C.dim, summary: C.green, question: C.yellow, error: C.red
  };
  const authorIcon = { claude: "🤖", admin: "👤", system: "⚙️" };

  return (
    <Card style={{ padding: 16, maxHeight: "70vh", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
            {report.title || "Untitled session report"}
          </div>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>
            {appLabel(report.app_slug)}
            {report.app_version && ` · v${report.app_version}`}
            {report.device_model && ` · ${report.device_model}`}
            {report.ios_version && ` · iOS ${report.ios_version}`}
            <br/>
            From <strong>{report.tester_name || report.tester_email || report.tester_id?.slice(0, 8)}</strong>
            {" · "}{new Date(report.created_at).toLocaleString()}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Btn
            small
            onClick={deleteReport}
            disabled={stateBusy}
            style={{ background: C.red, color: "#fff" }}
            title="Force-delete this report and cancel any linked auto-fix"
          >
            🗑 Delete
          </Btn>
          <Btn small onClick={onClose}>Close</Btn>
        </div>
      </div>

      {/* State management */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: C.dim, alignSelf: "center" }}>State:</span>
        {["submitted", "fixing", "reopened", "fixed", "needs_human_review", "archived"].map(s => (
          <Btn
            key={s}
            small
            onClick={() => changeState(s)}
            disabled={stateBusy || report.state === s}
            style={report.state === s ? { background: C.accent, color: "#fff" } : {}}
          >
            {s}
          </Btn>
        ))}
      </div>

      {/* Fix state quick view */}
      {fixState && (
        <div style={{ padding: 8, background: C.bgElev, borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
          <strong>Auto-fix:</strong> {fixState.state}
          {fixState.requested_at && ` · requested ${new Date(fixState.requested_at).toLocaleString()}`}
        </div>
      )}

      {/* Bug entries */}
      <div style={{ fontSize: 12, color: C.dim, fontWeight: 600, marginBottom: 6 }}>
        Bug entries ({entries.length})
      </div>
      {entries.map((e, idx) => (
        <Card key={e.id} style={{ padding: 10, marginBottom: 6, background: C.bgElev }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>#{idx + 1}: {e.title}</div>
          {e.steps?.length > 0 && (
            <ol style={{ fontSize: 12, color: C.dim, paddingLeft: 20, margin: "4px 0" }}>
              {e.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
          {e.observed && <div style={{ fontSize: 12, color: C.dim }}><strong>Obs:</strong> {e.observed}</div>}
          {e.expected && <div style={{ fontSize: 12, color: C.dim }}><strong>Exp:</strong> {e.expected}</div>}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            {e.fixRequestState && `state: ${e.fixRequestState} · `}
            {e.severity}
            {e.hasScreenshot && " · 📷"}
            {e.hasRecording && " · 🎥"}
            {e.hasPdf && (
              <>
                {" · "}
                <a
                  href={`/api/v1/dist/admin/bug-reports/${reportId}/entries/${e.id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: C.accent }}
                >
                  📄 PDF ({e.pdfPageCount || '?'} pages)
                </a>
              </>
            )}
          </div>
          {/* v161.22.28: per-entry replies rendered inline */}
          {repliesByEntry.has(e.id) && (
            <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
              {repliesByEntry.get(e.id).map(r => (
                <div
                  key={r.id}
                  style={{
                    padding: 6, marginBottom: 4, borderRadius: 6,
                    background: C.bg,
                    borderLeft: `2px solid ${kindColor[r.kind] || C.dim}`,
                    fontSize: 12
                  }}
                >
                  <div style={{ color: C.dim, fontSize: 10 }}>
                    {authorIcon[r.author_kind] || ""} {r.author_kind} · {r.kind}
                    {' · '}{new Date(r.created_at).toLocaleString()}
                  </div>
                  <div style={{ color: C.text, whiteSpace: "pre-wrap" }}>{r.body}</div>
                </div>
              ))}
            </div>
          )}
          <Btn
            small
            onClick={() => setReplyEntryId(e.id)}
            style={{ marginTop: 6, fontSize: 11 }}
          >
            Reply to this bug
          </Btn>
        </Card>
      ))}

      {/* Reply thread (report-level only) */}
      <div style={{ fontSize: 12, color: C.dim, fontWeight: 600, marginTop: 12, marginBottom: 6 }}>
        Report-level thread ({reportLevelReplies.length})
      </div>
      {reportLevelReplies.map(r => (
        <div
          key={r.id}
          style={{
            padding: 10,
            marginBottom: 6,
            borderRadius: 8,
            background: C.bgElev,
            borderLeft: `3px solid ${kindColor[r.kind] || C.dim}`
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.dim }}>
            <span>{authorIcon[r.author_kind] || ""} {r.author_kind} · {r.kind}</span>
            <span>{new Date(r.created_at).toLocaleString()}</span>
          </div>
          <div style={{ fontSize: 13, color: C.text, marginTop: 4, whiteSpace: "pre-wrap" }}>{r.body}</div>
        </div>
      ))}

      {/* Reply form */}
      <div style={{ marginTop: 16, padding: 12, background: C.bgElev, borderRadius: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.text }}>
          Post reply
          {replyEntryId && (
            <span style={{ color: C.accent, marginLeft: 8 }}>
              → on bug entry
              <button
                onClick={() => setReplyEntryId(null)}
                style={{
                  marginLeft: 6, background: "transparent", border: "none",
                  color: C.dim, cursor: "pointer", fontSize: 11
                }}
              >
                (clear, reply at report level)
              </button>
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <select value={replyKind} onChange={e => setReplyKind(e.target.value)} style={{ ...inp(), width: 130 }}>
            <option value="status">Status</option>
            <option value="summary">Summary</option>
            <option value="question">Question</option>
            <option value="error">Error / needs review</option>
          </select>
          <select
            value={replyEntryId || ""}
            onChange={e => setReplyEntryId(e.target.value || null)}
            style={{ ...inp(), flex: 1 }}
          >
            <option value="">Report-level reply</option>
            {entries.map((e, i) => (
              <option key={e.id} value={e.id}>Reply on bug #{i + 1}: {e.title.slice(0, 50)}</option>
            ))}
          </select>
          <label style={{ fontSize: 12, color: C.dim, display: "flex", alignItems: "center", gap: 4 }}>
            <input type="checkbox" checked={replySendPush} onChange={e => setReplySendPush(e.target.checked)}/>
            Push notify
          </label>
        </div>
        <textarea
          value={replyBody}
          onChange={e => setReplyBody(e.target.value)}
          rows={3}
          placeholder="Your reply to the tester…"
          style={{ ...inp(), resize: "vertical", minHeight: 60 }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <Btn primary onClick={sendReply} disabled={sending || !replyBody.trim()}>
            {sending ? "Sending…" : "Post reply"}
          </Btn>
        </div>
      </div>
    </Card>
  );
}

/* ─── Distribution: Crashes ──────────────────────────────────────────── */
function DistCrashesPage() {
  const [filterApp, setFilterApp] = useState("");
  const queryStr = filterApp ? `?app=${filterApp}` : "";
  const { data, error, loading, reload } = useFetch(() => api(`/v1/dist/admin/crashes${queryStr}`), [filterApp]);
  const [active, setActive] = useState(null);

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const crashes = data?.crashes || [];

  // Cluster by exception_type + first stack frame
  const clusters = {};
  for (const c of crashes) {
    const firstFrame = (c.stack_raw || "").split("\n")[0]?.trim() || "unknown";
    const key = `${c.exception_type || "unknown"}|${firstFrame}`;
    if (!clusters[key]) clusters[key] = { key, count: 0, latest: c, sample: c };
    clusters[key].count++;
    if (new Date(c.occurred_at) > new Date(clusters[key].latest.occurred_at)) clusters[key].latest = c;
  }
  const sorted = Object.values(clusters).sort((a, b) => b.count - a.count);
  const apps = [...new Set(crashes.map(c => c.app_slug))].sort();

  return (<>
    <Grid cols={3}>
      <Stat label="Total Crashes" value={crashes.length} color={crashes.length > 0 ? C.red : C.green}/>
      <Stat label="Unique Clusters" value={sorted.length}/>
      <Stat label="Affected Apps" value={apps.length}/>
    </Grid>

    <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 16, alignItems: "center" }}>
      <select value={filterApp} onChange={e => setFilterApp(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All apps</option>
        {apps.map(a => <option key={a} value={a}>{appLabel(a)}</option>)}
      </select>
      <Btn small onClick={reload}><RefreshCw size={12}/> Refresh</Btn>
      <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>Clusters by exception type + first frame</span>
    </div>

    {sorted.length === 0 ? (
      <Empty msg="No crashes reported — that's good news"/>
    ) : (
      <div style={{ display: "grid", gridTemplateColumns: active ? "1fr 1fr" : "1fr", gap: 16 }}>
        <Card style={{ padding: 0, maxHeight: "70vh", overflowY: "auto" }}>
          {sorted.map((cluster, i) => (
            <div
              key={cluster.key}
              onClick={() => setActive(cluster)}
              style={{
                padding: "12px 16px",
                borderBottom: i < sorted.length - 1 ? `1px solid ${C.border}` : "none",
                cursor: "pointer",
                background: active?.key === cluster.key ? C.accentDim : "transparent",
                borderLeft: active?.key === cluster.key ? `3px solid ${C.red}` : "3px solid transparent",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.red }}>×{cluster.count}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: "ui-monospace, monospace" }}>
                  {cluster.sample.exception_type || "Unknown"}
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.dim, fontFamily: "ui-monospace, monospace", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {(cluster.sample.stack_raw || "").split("\n")[0]?.slice(0, 100) || "(no stack)"}
              </div>
              <div style={{ fontSize: 11, color: C.dim }}>
                {cluster.sample.app_slug}
                {cluster.sample.build_version && ` · v${cluster.sample.build_version}`}
                {" · latest "}
                {new Date(cluster.latest.occurred_at).toLocaleString()}
              </div>
            </div>
          ))}
        </Card>

        {active && (
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: C.red }}>×{active.count}</span>
              <span style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{active.sample.exception_type || "Unknown"}</span>
              <button onClick={() => setActive(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
            {active.sample.exception_message && (
              <div style={{ fontSize: 13, color: C.text, marginBottom: 12, padding: 10, background: C.red + "11", borderRadius: 6 }}>
                {active.sample.exception_message}
              </div>
            )}
            <div style={{ fontSize: 11, color: C.dim, marginBottom: 12 }}>
              {active.sample.app_slug}
              {active.sample.tester_email && ` · ${active.sample.tester_email}`}
              {active.sample.product_type && ` · ${active.sample.product_type}`}
              {active.sample.ios_version && ` · iOS ${active.sample.ios_version}`}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Stack trace {active.sample.stack_symbolicated && <Badge color={C.green}>symbolicated</Badge>}
            </div>
            <pre style={{ fontSize: 11, color: C.dim, padding: 12, background: C.bg, borderRadius: 6, overflowX: "auto", maxHeight: 320, fontFamily: "ui-monospace, monospace", whiteSpace: "pre", lineHeight: 1.5 }}>
              {active.sample.stack_symbolicated || active.sample.stack_raw || "(no stack captured)"}
            </pre>
            {active.sample.payload && Object.keys(active.sample.payload).length > 0 && (
              <details style={{ marginTop: 12 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: C.dim, marginBottom: 8 }}>Device payload</summary>
                <pre style={{ fontSize: 11, color: C.dim, padding: 10, background: C.bg, borderRadius: 6, overflowX: "auto", maxHeight: 200 }}>{JSON.stringify(active.sample.payload, null, 2)}</pre>
              </details>
            )}
          </Card>
        )}
      </div>
    )}
  </>);
}

/* ─── Distribution: Reliability ──────────────────────────────────────── */
function DistReliabilityPage() {
  const { data, error, loading, reload } = useFetch(() => api("/v1/dist/admin/testers/reliability"), []);
  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const testers = data?.testers || [];
  const score = (n) => {
    if (n >= 70) return { color: C.green, label: "Champion" };
    if (n >= 40) return { color: C.yellow, label: "Active" };
    if (n >= 10) return { color: C.dim, label: "Casual" };
    return { color: C.muted, label: "Ghost" };
  };

  const stats = {
    champions: testers.filter(t => t.score >= 70).length,
    active: testers.filter(t => t.score >= 40 && t.score < 70).length,
    casual: testers.filter(t => t.score >= 10 && t.score < 40).length,
    ghosts: testers.filter(t => t.score < 10).length,
  };

  return (<>
    <Grid cols={4}>
      <Stat label="Champions" value={stats.champions} color={C.green} sub="70+"/>
      <Stat label="Active" value={stats.active} color={C.yellow} sub="40-69"/>
      <Stat label="Casual" value={stats.casual} color={C.dim} sub="10-39"/>
      <Stat label="Ghosts" value={stats.ghosts} color={C.muted} sub="<10"/>
    </Grid>

    <div style={{ fontSize: 12, color: C.dim, margin: "16px 0", lineHeight: 1.5 }}>
      Score combines feedback volume, install activity, signal quality (high-severity reports), and recency.
      <Btn small onClick={reload} style={{ marginLeft: 12 }}><RefreshCw size={12}/> Refresh</Btn>
    </div>

    {testers.length === 0 ? (
      <Empty msg="No testers yet"/>
    ) : (
      <Card style={{ padding: 0 }}>
        <Table
          cols={["Score", "Tester", "Devices", "Installs", "Feedback", "High-sev", "Last Login"]}
          rows={testers.map(t => {
            const s = score(t.score);
            return [
              <span key="s" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 12, background: s.color + "22", color: s.color, fontWeight: 700, fontSize: 12, minWidth: 32, textAlign: "center" }}>
                  {t.score}
                </span>
                <span style={{ fontSize: 11, color: C.dim }}>{s.label}</span>
              </span>,
              <div key="t">
                <div style={{ fontSize: 13, color: C.text }}>{t.display_name || "—"}</div>
                <div style={{ fontSize: 11, color: C.dim }}>{t.email}{t.is_external ? " · external" : ""}</div>
              </div>,
              t.device_count || 0,
              t.install_count || 0,
              t.feedback_count || 0,
              <span key="hs" style={{ color: t.high_severity_count > 0 ? C.red : C.dim, fontWeight: t.high_severity_count > 0 ? 600 : 400 }}>{t.high_severity_count || 0}</span>,
              t.last_login_at ? new Date(t.last_login_at).toLocaleDateString() : <span style={{ color: C.muted }}>never</span>,
            ];
          })}
        />
      </Card>
    )}
  </>);
}

/* ─── Distribution: Audit Log ────────────────────────────────────────── */
function DistAuditPage() {
  const [filterAction, setFilterAction] = useState("");
  const [filterType, setFilterType] = useState("");
  const queryStr = [filterAction && `action=${filterAction}`, filterType && `resourceType=${filterType}`, "limit=500"].filter(Boolean).join("&");
  const { data, error, loading, reload } = useFetch(() => api(`/v1/dist/admin/audit?${queryStr}`), [filterAction, filterType]);

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const entries = data?.entries || [];
  const actionEmoji = (action) => {
    if (action.startsWith("build.")) return "📦";
    if (action.startsWith("tester.")) return "👤";
    if (action.startsWith("webhook.")) return "🪝";
    if (action.startsWith("feedback.")) return "💬";
    if (action.startsWith("comment.")) return "💭";
    if (action.startsWith("testplan.")) return "🧪";
    if (action.includes("rollback")) return "🔥";
    return "•";
  };

  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
      <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ ...inp(), width: 200 }}>
        <option value="">All actions</option>
        <option value="build.upload">Build upload</option>
        <option value="build.delete">Build delete</option>
        <option value="build.dsym_upload">dSYM upload</option>
        <option value="build.promote_tf">Promote to TF</option>
        <option value="build.auto_rollback">Auto-rollback</option>
        <option value="tester.deactivate">Tester deactivate</option>
        <option value="tester.sessions.revoke">Sessions revoke</option>
        <option value="webhook.configure">Webhook config</option>
        <option value="comment.delete">Comment delete</option>
        <option value="feedback.triage">AI triage</option>
        <option value="testplan.generate">Test plan gen</option>
      </select>
      <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inp(), width: 160 }}>
        <option value="">All resources</option>
        <option value="build">Build</option>
        <option value="tester">Tester</option>
        <option value="feedback">Feedback</option>
        <option value="app_settings">App settings</option>
        <option value="comment">Comment</option>
      </select>
      <Btn small onClick={reload}><RefreshCw size={12}/> Refresh</Btn>
      <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>{entries.length} entries (max 500)</span>
    </div>

    {entries.length === 0 ? (
      <Empty msg="No audit entries match filters"/>
    ) : (
      <Card style={{ padding: 0 }}>
        <Table
          cols={["When", "Who", "Action", "Resource", "Diff"]}
          rows={entries.map(e => [
            <span key="w" style={{ fontSize: 11, color: C.dim }} title={e.occurred_at}>{new Date(e.occurred_at).toLocaleString()}</span>,
            e.account_email || <em style={{ color: C.muted }}>system</em>,
            <span key="a"><span style={{ marginRight: 6 }}>{actionEmoji(e.action)}</span><code style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: C.text }}>{e.action}</code></span>,
            <div key="r">
              <div style={{ fontSize: 12, color: C.text }}>{e.resource_type}</div>
              <div style={{ fontSize: 10, color: C.dim, fontFamily: "ui-monospace, monospace" }}>{e.resource_id?.slice(0, 12)}</div>
            </div>,
            <span key="d" style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", color: C.dim }}>
              {e.diff && Object.keys(e.diff).length > 0 ? JSON.stringify(e.diff).slice(0, 80) : "—"}
            </span>,
          ])}
        />
      </Card>
    )}
  </>);
}

/* ─── Distribution: Storage & Config ─────────────────────────────────── */
function DistStoragePage() {
  const { data: usage, error: e1, loading: l1, reload: r1 } = useFetch(() => api("/v1/dist/admin/storage"), []);
  const { data: warnings, reload: r2 } = useFetch(() => api("/v1/dist/admin/expiry-warnings"), []);
  const [busy, setBusy] = useState(false);
  const [rollbackApp, setRollbackApp] = useState("");
  const [rollbackEnabled, setRollbackEnabled] = useState(false);
  const [rollbackThreshold, setRollbackThreshold] = useState(0.10);
  const [webhookApp, setWebhookApp] = useState("");
  const [slackUrl, setSlackUrl] = useState("");
  const [discordUrl, setDiscordUrl] = useState("");

  if (l1) return <Loading/>;
  if (e1) return <ErrorState error={e1} onRetry={r1}/>;

  const fmtBytes = (n) => {
    if (!n) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1073741824) return `${(n / 1048576).toFixed(1)} MB`;
    return `${(n / 1073741824).toFixed(2)} GB`;
  };

  const sweep = async () => {
    if (!window.confirm("Delete all expired build IPAs from disk?\n\nBuild records keep their metadata but the .ipa files will be permanently deleted. This frees Railway volume space but the actual builds can no longer be installed.")) return;
    setBusy(true);
    try {
      const r = await api("/v1/dist/admin/storage/sweep", { method: "POST" });
      alert(`Swept ${r.deleted} expired builds.`);
      r1(); r2();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const saveRollback = async () => {
    if (!rollbackApp) { alert("App slug required"); return; }
    try {
      await api(`/v1/dist/admin/rollback-config/${rollbackApp}`, {
        method: "PUT",
        body: { enabled: rollbackEnabled, crashRateThreshold: parseFloat(rollbackThreshold) },
      });
      alert(`Auto-rollback ${rollbackEnabled ? "enabled" : "disabled"} for ${rollbackApp}`);
    } catch (e) { alert(e.message); }
  };

  const saveWebhook = async () => {
    if (!webhookApp) { alert("App slug required"); return; }
    try {
      await api(`/v1/dist/admin/webhooks/${webhookApp}`, {
        method: "PUT",
        body: { slackUrl, discordUrl },
      });
      alert("Webhooks saved");
    } catch (e) { alert(e.message); }
  };

  return (<>
    <SectionTitle>Disk Usage (Railway volume)</SectionTitle>
    <Grid cols={4}>
      <Stat label="IPAs" value={fmtBytes(usage?.ipas)}/>
      <Stat label="Icons" value={fmtBytes(usage?.icons)}/>
      <Stat label="Feedback" value={fmtBytes(usage?.feedback)}/>
      <Stat label="Total" value={fmtBytes(usage?.total)} color={C.accent}/>
    </Grid>
    <div style={{ marginTop: 12 }}>
      <Btn destructive onClick={sweep} disabled={busy}><Trash2 size={14}/> {busy ? "Sweeping…" : "Sweep Expired IPAs"}</Btn>
    </div>

    <SectionTitle>Expiring Soon (next 7 days)</SectionTitle>
    {(warnings?.builds || []).length === 0 ? (
      <Empty msg="No builds expiring in the next 7 days"/>
    ) : (
      <Card style={{ padding: 0 }}>
        {(warnings?.builds || []).map((w, i) => (
          <div key={w.id} style={{ padding: "10px 16px", borderBottom: i < warnings.builds.length - 1 ? `1px solid ${C.border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{w.app_name}</span>
            <span style={{ fontSize: 12, color: C.dim, fontFamily: "ui-monospace, monospace" }}>v{w.version} ({w.build_number})</span>
            <span style={{ fontSize: 11, color: C.yellow, marginLeft: "auto" }}>expires {new Date(w.expires_at).toLocaleString()}</span>
          </div>
        ))}
      </Card>
    )}

    <SectionTitle>Auto-Rollback Config</SectionTitle>
    <Card>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
        Automatically archive a build if its crash rate exceeds the threshold within the rolling window.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 1fr auto", gap: 12, alignItems: "end" }}>
        <div>
          <label style={lbl()}>App slug</label>
          <input value={rollbackApp} onChange={e => setRollbackApp(e.target.value)} style={inp()} placeholder="aquatrack"/>
        </div>
        <div>
          <label style={lbl()}>Threshold</label>
          <input type="number" step="0.01" min="0" max="1" value={rollbackThreshold} onChange={e => setRollbackThreshold(e.target.value)} style={inp()}/>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: C.dim, padding: "10px 0" }}>
          <input type="checkbox" checked={rollbackEnabled} onChange={e => setRollbackEnabled(e.target.checked)} style={{ accentColor: C.accent }}/>
          Enabled
        </label>
        <Btn primary onClick={saveRollback} disabled={!rollbackApp}><Check size={14}/> Save</Btn>
      </div>
    </Card>

    <SectionTitle>Webhooks (Slack / Discord)</SectionTitle>
    <Card>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
        Notify a channel when a new build is published for an app. Use <code style={{ fontFamily: "ui-monospace, monospace", padding: "1px 4px", background: C.bg, borderRadius: 3 }}>*</code> as app slug for all apps.
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl()}>App slug</label>
        <input value={webhookApp} onChange={e => setWebhookApp(e.target.value)} style={inp()} placeholder="aquatrack or *"/>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl()}>Slack incoming webhook URL</label>
        <input type="url" value={slackUrl} onChange={e => setSlackUrl(e.target.value)} style={inp()} placeholder="https://hooks.slack.com/…"/>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl()}>Discord webhook URL</label>
        <input type="url" value={discordUrl} onChange={e => setDiscordUrl(e.target.value)} style={inp()} placeholder="https://discord.com/api/webhooks/…"/>
      </div>
      <Btn primary onClick={saveWebhook} disabled={!webhookApp}><Check size={14}/> Save Webhooks</Btn>
    </Card>
  </>);
}

/* ─── Distribution: A/B Tests ────────────────────────────────────────── */
function DistABTestPage() {
  const { data: groupsData } = useFetch(() => api("/v1/dist/admin/groups"), []);
  const { data: buildsData } = useFetch(() => api("/v1/dist/admin/builds?limit=200"), []);
  const [abId, setAbId] = useState("");
  const [appSlug, setAppSlug] = useState("");
  const [groupId, setGroupId] = useState("");
  const [buildA, setBuildA] = useState("");
  const [buildB, setBuildB] = useState("");
  const [createErr, setCreateErr] = useState("");
  const [loadAbId, setLoadAbId] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const groups = groupsData?.groups || [];
  const builds = buildsData?.builds || [];
  const apps = [...new Set(builds.map(b => b.app_slug))].sort();
  const appBuilds = builds.filter(b => !appSlug || b.app_slug === appSlug);

  const create = async () => {
    setCreateErr("");
    if (!abId || !appSlug || !groupId || !buildA || !buildB) {
      setCreateErr("All fields required");
      return;
    }
    setBusy(true);
    try {
      const r = await api("/v1/dist/admin/ab-test", {
        method: "POST",
        body: { abId, appSlug, buildA, buildB, groupId },
      });
      alert(`A/B "${abId}" created. Assigned ${r.assignedA} to A, ${r.assignedB} to B.`);
      setLoadAbId(abId);
    } catch (e) { setCreateErr(e.message); }
    finally { setBusy(false); }
  };

  const loadResults = async () => {
    if (!loadAbId) return;
    setBusy(true);
    try {
      const r = await api(`/v1/dist/admin/ab-test/${loadAbId}`);
      setResults(r.results || []);
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (<>
    <SectionTitle>Create A/B Test</SectionTitle>
    <Card>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
        Deterministically assigns each tester in a group to either build A or build B.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl()}>A/B label (unique) *</label>
          <input value={abId} onChange={e => setAbId(e.target.value)} style={inp()} placeholder="onboarding-redesign-q2"/>
        </div>
        <div>
          <label style={lbl()}>App *</label>
          <select value={appSlug} onChange={e => { setAppSlug(e.target.value); setBuildA(""); setBuildB(""); }} style={inp()}>
            <option value="">— select —</option>
            {apps.map(a => <option key={a} value={a}>{appLabel(a)}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl()}>Group *</label>
        <select value={groupId} onChange={e => setGroupId(e.target.value)} style={inp()}>
          <option value="">— select —</option>
          {groups.filter(g => !appSlug || g.app_slug === appSlug || g.app_slug === "*").map(g => (
            <option key={g.id} value={g.id}>{g.app_slug} / {g.name} ({g.member_count} members)</option>
          ))}
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl()}>Build A *</label>
          <select value={buildA} onChange={e => setBuildA(e.target.value)} style={inp()}>
            <option value="">— select —</option>
            {appBuilds.map(b => <option key={b.id} value={b.id}>v{b.version} ({b.build_number}) · {new Date(b.uploaded_at).toLocaleDateString()}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl()}>Build B *</label>
          <select value={buildB} onChange={e => setBuildB(e.target.value)} style={inp()}>
            <option value="">— select —</option>
            {appBuilds.map(b => <option key={b.id} value={b.id}>v{b.version} ({b.build_number}) · {new Date(b.uploaded_at).toLocaleDateString()}</option>)}
          </select>
        </div>
      </div>
      {createErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{createErr}</div>}
      <Btn primary onClick={create} disabled={busy || !abId || !appSlug || !groupId || !buildA || !buildB}>
        <FlaskConical size={14}/> Create A/B Test
      </Btn>
    </Card>

    <SectionTitle>View Results</SectionTitle>
    <Card>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl()}>A/B label</label>
          <input value={loadAbId} onChange={e => setLoadAbId(e.target.value)} style={inp()} placeholder="onboarding-redesign-q2"/>
        </div>
        <Btn primary onClick={loadResults} disabled={busy || !loadAbId}>Load</Btn>
      </div>
      {results && results.length > 0 && (
        <Table
          cols={["Variant", "Build", "Testers", "Installs", "Crashes", "Feedback"]}
          rows={results.map(r => [
            <strong key="v" style={{ color: C.text, fontSize: 14 }}>{r.variant}</strong>,
            <span key="b" style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>v{r.version} ({r.build_number})</span>,
            r.testers,
            r.installs,
            <span key="c" style={{ color: r.crashes > 0 ? C.red : C.dim, fontWeight: r.crashes > 0 ? 600 : 400 }}>{r.crashes}</span>,
            r.feedback_count,
          ])}
        />
      )}
      {results && results.length === 0 && <Empty msg="No data for that A/B label"/>}
    </Card>
  </>);
}

/* ─── Distribution: Compare Builds ───────────────────────────────────── */
function DistComparePage() {
  const { data: buildsData } = useFetch(() => api("/v1/dist/admin/builds?limit=100"), []);
  const [idA, setIdA] = useState("");
  const [idB, setIdB] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const builds = buildsData?.builds || [];

  const compare = async () => {
    if (!idA || !idB) return;
    setBusy(true); setErr("");
    try {
      const r = await api(`/v1/dist/admin/builds/${idA}/compare/${idB}`);
      setResult(r);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const Row = ({ label, a, b, fmt = String, betterIs = "higher" }) => {
    const av = a ?? 0, bv = b ?? 0;
    const winner = av === bv ? null : betterIs === "higher" ? (bv > av ? "B" : "A") : betterIs === "lower" ? (bv < av ? "B" : "A") : null;
    const cell = (val, isWin) => (
      <span style={{ color: isWin ? C.green : C.text, fontWeight: isWin ? 600 : 400, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
        {fmt(val)}
      </span>
    );
    return [label, cell(av, winner === "A"), cell(bv, winner === "B")];
  };

  return (<>
    <SectionTitle>Pick Two Builds</SectionTitle>
    <Card>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto", gap: 12, alignItems: "end" }}>
        <div>
          <label style={lbl()}>Build A</label>
          <select value={idA} onChange={e => setIdA(e.target.value)} style={inp()}>
            <option value="">— select —</option>
            {builds.map(b => <option key={b.id} value={b.id}>{b.app_slug} v{b.version} ({b.build_number}) · {new Date(b.uploaded_at).toLocaleDateString()}</option>)}
          </select>
        </div>
        <span style={{ fontSize: 13, color: C.dim, paddingBottom: 10 }}>vs</span>
        <div>
          <label style={lbl()}>Build B</label>
          <select value={idB} onChange={e => setIdB(e.target.value)} style={inp()}>
            <option value="">— select —</option>
            {builds.map(b => <option key={b.id} value={b.id}>{b.app_slug} v{b.version} ({b.build_number}) · {new Date(b.uploaded_at).toLocaleDateString()}</option>)}
          </select>
        </div>
        <Btn primary onClick={compare} disabled={!idA || !idB || busy}>
          {busy ? "Comparing…" : "Compare"}
        </Btn>
      </div>
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{err}</div>}
    </Card>

    {result && (<>
      <SectionTitle>Results {result.diff?.verdict && <Badge color={C.accent}>verdict: {result.diff.verdict}</Badge>}</SectionTitle>
      <Card style={{ padding: 0 }}>
        <Table
          cols={["Metric", `A · v${result.a.build.version}`, `B · v${result.b.build.version}`]}
          rows={[
            Row({ label: "Installs", a: result.a.installs, b: result.b.installs }),
            Row({ label: "Launched", a: result.a.launched, b: result.b.launched }),
            Row({ label: "Crashes", a: result.a.crashes, b: result.b.crashes, betterIs: "lower" }),
            Row({ label: "Crash rate", a: result.a.crashRate, b: result.b.crashRate, betterIs: "lower", fmt: n => (n * 100).toFixed(1) + "%" }),
            Row({ label: "Critical feedback", a: result.a.feedbackBySeverity?.critical || 0, b: result.b.feedbackBySeverity?.critical || 0, betterIs: "lower" }),
            Row({ label: "High feedback", a: result.a.feedbackBySeverity?.high || 0, b: result.b.feedbackBySeverity?.high || 0, betterIs: "lower" }),
            Row({ label: "Normal feedback", a: result.a.feedbackBySeverity?.normal || 0, b: result.b.feedbackBySeverity?.normal || 0 }),
            Row({ label: "Channel", a: result.a.build.channel, b: result.b.build.channel, betterIs: "tied" }),
            Row({ label: "Built by", a: result.a.build.built_by || "—", b: result.b.build.built_by || "—", betterIs: "tied" }),
            Row({ label: "Git", a: (result.a.build.git_branch || "?") + "@" + (result.a.build.git_sha?.slice(0, 7) || "?"), b: (result.b.build.git_branch || "?") + "@" + (result.b.build.git_sha?.slice(0, 7) || "?"), betterIs: "tied" }),
          ]}
        />
      </Card>
    </>)}
  </>);
}

/* ─── Distribution: Auto-Fix Queue ───────────────────────────────────── */
function DistFixQueuePage() {
  const { data, error, loading, reload } = useFetch(() => api("/v1/dist/admin/fix-queue"), []);
  const [selected, setSelected] = useState(null); // attempt detail modal
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Poll every 15s
  useEffect(() => {
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [reload]);

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const queued = data?.queued || [];
  const awaiting = data?.awaitingApproval || [];
  const recent = data?.recent || [];

  const stateColor = (s) => {
    if (s === "distributed") return C.green;
    if (s === "awaiting_approval") return C.yellow;
    if (["failed", "gates_failed", "rejected", "regressed"].includes(s)) return C.red;
    return C.purple;
  };

  const timeAgo = (iso) => {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const viewAttempt = async (id) => {
    try {
      const r = await api(`/v1/dist/admin/fix-attempts/${id}`);
      setSelected(r);
    } catch (e) { alert(e.message); }
  };

  const approve = async (id) => {
    if (!window.confirm("Approve this auto-fix?\n\nIt will ship to the original tester(s) immediately.")) return;
    setBusy(true);
    try {
      await api(`/v1/dist/admin/fix-attempts/${id}/approve`, { method: "POST" });
      setSelected(null);
      reload();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const submitReject = async (id) => {
    if (!rejectReason.trim()) { alert("Please provide a reason."); return; }
    setBusy(true);
    try {
      await api(`/v1/dist/admin/fix-attempts/${id}/reject`, { method: "POST", body: { reason: rejectReason } });
      setSelected(null);
      setRejectingId(null);
      setRejectReason("");
      reload();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (<>
    <Grid cols={3}>
      <Stat label="Awaiting Approval" value={awaiting.length} color={awaiting.length > 0 ? C.yellow : C.dim}/>
      <Stat label="Queued" value={queued.length}/>
      <Stat label="Recent (7d)" value={recent.length}/>
    </Grid>

    <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, color: C.dim }}>
      Polls every 15s. <Btn small onClick={reload} style={{ marginLeft: 8 }}><RefreshCw size={12}/> Refresh</Btn>
    </div>

    {awaiting.length > 0 && (<>
      <SectionTitle>⏳ Awaiting Your Approval ({awaiting.length})</SectionTitle>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
        These attempts passed all safety gates and need your sign-off before shipping to testers.
      </div>
      {awaiting.map(a => (
        <Card key={a.id} style={{ marginBottom: 8, borderLeft: `3px solid ${C.yellow}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                {a.app_slug} — based on v{a.parent_version} ({a.parent_build_number})
              </div>
              <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>
                Requested by {a.tester_email} · attempt #{a.attempt_number} ·
                Δ +{a.diff_lines_added}/-{a.diff_lines_deleted} lines across {a.diff_files_changed} files
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn small onClick={() => viewAttempt(a.id)}>Review diff</Btn>
              <Btn small primary onClick={() => approve(a.id)} disabled={busy}><Check size={12}/> Approve</Btn>
              <Btn small destructive onClick={() => { setRejectingId(a.id); setRejectReason(""); }}><X size={12}/> Reject</Btn>
            </div>
          </div>
        </Card>
      ))}
    </>)}

    <SectionTitle>📥 Queued ({queued.length})</SectionTitle>
    <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
      Awaiting Mac-side processing. StellarFixerWeb auto-claims every 30s.
    </div>
    {queued.length === 0 ? (
      <Empty msg="Queue empty"/>
    ) : (
      <Card style={{ padding: 0 }}>
        {queued.map((r, i) => (
          <div key={r.id} style={{ padding: "12px 16px", borderBottom: i < queued.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{r.app_name || r.app_slug} v{r.version}</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
              By {r.tester_email} · {r.feedback_count} feedback items · requested {timeAgo(r.requested_at)}
              {r.state === "claimed" && <span style={{ marginLeft: 8, color: C.purple }}>· claimed by {r.claimed_by_mac}</span>}
            </div>
          </div>
        ))}
      </Card>
    )}

    <SectionTitle>📜 Recent Attempts (7 days)</SectionTitle>
    {recent.length === 0 ? (
      <Empty msg="No recent attempts"/>
    ) : (
      <Card style={{ padding: 0 }}>
        <Table
          cols={["When", "App", "State", "Δ lines", "Build", "Outcome", "Note"]}
          rows={recent.map(a => [
            <span key="w" style={{ fontSize: 11, color: C.dim }}>{new Date(a.started_at).toLocaleString()}</span>,
            <code key="a" style={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>{a.app_slug}</code>,
            <Badge key="s" color={stateColor(a.state)}>{a.state}</Badge>,
            <span key="d" style={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>+{a.diff_lines_added || 0}/-{a.diff_lines_deleted || 0}</span>,
            a.output_build_id ? <span key="b" style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: C.dim }} title={a.output_build_id}>{a.output_build_id.slice(0, 8)}…</span> : <span key="b" style={{ color: C.muted }}>—</span>,
            a.state === "regressed" ? <span key="o" style={{ color: C.red, fontWeight: 600 }} title="Tester re-reported a similar bug within 48h">🚨 regressed</span>
              : a.state === "distributed" && a.completed_at && (Date.now() - new Date(a.completed_at).getTime() > 48 * 3600 * 1000) ? <span key="o" style={{ color: C.green }} title="No regression signal within 48h retest window">✓ implicit</span>
              : a.state === "distributed" ? <span key="o" style={{ color: C.dim }} title="Awaiting 48h retest window">⏳ pending</span>
              : <span key="o" style={{ color: C.muted }}>—</span>,
            <span key="n" style={{ fontSize: 11, color: C.dim }}>{a.gate_failure_reason || a.rejection_reason || ""}</span>,
          ])}
        />
      </Card>
    )}

    {/* Diff modal */}
    {selected && (
      <div onClick={() => setSelected(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, padding: 24, maxWidth: 900, width: "100%", maxHeight: "90vh", overflowY: "auto", border: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Auto-fix attempt: {selected.attempt.app_slug}</span>
            <button onClick={() => setSelected(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 22, lineHeight: 1 }}>×</button>
          </div>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 16 }}>
            State: <code style={{ fontFamily: "ui-monospace, monospace" }}>{selected.attempt.state}</code> ·
            Branch: <code style={{ fontFamily: "ui-monospace, monospace" }}>{selected.attempt.branch_name}</code> ·
            Files: {selected.attempt.diff_files_changed} ·
            Δ: +{selected.attempt.diff_lines_added}/-{selected.attempt.diff_lines_deleted}
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>Feedback being addressed ({selected.feedback?.length || 0})</div>
          <div style={{ marginBottom: 16 }}>
            {(selected.feedback || []).map(f => (
              <div key={f.id} style={{ padding: "8px 10px", background: C.bg, borderRadius: 6, marginBottom: 6, fontSize: 12, color: C.text }}>
                <Badge color={f.severity === "critical" ? C.red : f.severity === "high" ? C.accent : C.dim}>{f.severity}</Badge>
                <span style={{ marginLeft: 8 }}>{f.message.slice(0, 200)}</span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>Diff</div>
          <pre style={{ maxHeight: 360, overflow: "auto", background: C.bg, padding: 12, fontSize: 11, borderRadius: 6, color: C.text, fontFamily: "ui-monospace, monospace" }}>
            {selected.attempt.diff_text || "(no diff captured)"}
          </pre>

          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 16, marginBottom: 6 }}>Gate results</div>
          <pre style={{ background: C.bg, padding: 12, fontSize: 11, borderRadius: 6, color: C.dim, fontFamily: "ui-monospace, monospace", maxHeight: 200, overflow: "auto" }}>
            {JSON.stringify(selected.attempt.gates_result, null, 2)}
          </pre>

          <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
            <Btn onClick={() => setSelected(null)}>Close</Btn>
            {selected.attempt.state === "awaiting_approval" && (<>
              <Btn destructive onClick={() => { setRejectingId(selected.attempt.id); setRejectReason(""); }}>Reject</Btn>
              <Btn primary onClick={() => approve(selected.attempt.id)} disabled={busy}><Check size={14}/> Approve & Ship</Btn>
            </>)}
          </div>
        </div>
      </div>
    )}

    {/* Reject modal */}
    {rejectingId && (
      <div onClick={() => setRejectingId(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, padding: 24, maxWidth: 520, width: "100%", border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>Reject auto-fix?</div>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
            Provide a reason. The branch will be deleted and the feedback will surface for manual review.
          </div>
          <textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="e.g., Claude misunderstood the bug — fix would break onboarding"
            rows={4}
            style={{ width: "100%", padding: 10, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
            <Btn onClick={() => setRejectingId(null)}>Cancel</Btn>
            <Btn destructive onClick={() => submitReject(rejectingId)} disabled={busy}>Reject</Btn>
          </div>
        </div>
      </div>
    )}
  </>);
}

/* ─── Distribution: Auto-Fix Settings ────────────────────────────────── */
function DistAutoFixSettingsPage() {
  const { data: buildsData } = useFetch(() => api("/v1/dist/admin/builds?limit=200"), []);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [config, setConfig] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const builds = buildsData?.builds || [];
  const appSlugs = [...new Set(builds.map(b => b.app_slug))].sort();

  useEffect(() => {
    if (!selectedSlug && appSlugs.length > 0) setSelectedSlug(appSlugs[0]);
  }, [appSlugs.join(","), selectedSlug]);

  useEffect(() => {
    if (!selectedSlug) return;
    api(`/v1/dist/admin/auto-fix/config/${selectedSlug}`)
      .then(r => setConfig(r.config))
      .catch(e => setErr(e.message));
  }, [selectedSlug]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const r = await api(`/v1/dist/admin/auto-fix/config/${selectedSlug}`, {
        method: "PUT",
        body: {
          mode: config.auto_fix_mode,
          maxPerDay: config.auto_fix_max_per_day,
          maxPerThread: config.auto_fix_max_per_thread,
          maxLinesDeleted: config.auto_fix_max_lines_deleted,
          dailySpendCapCents: config.auto_fix_daily_spend_cap_cents,
          killSwitch: config.auto_fix_kill_switch,
        },
      });
      setConfig(r.config);
      alert("Saved");
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const update = (field, value) => setConfig(c => ({ ...c, [field]: value }));

  return (<>
    <div style={{ fontSize: 12, color: C.dim, marginBottom: 16 }}>
      Control closed-loop auto-fix behavior per app. Disabled by default.
    </div>

    <SectionTitle>App</SectionTitle>
    <Card>
      <select value={selectedSlug} onChange={e => setSelectedSlug(e.target.value)} style={inp()}>
        <option value="">— select an app —</option>
        {appSlugs.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </Card>

    {err && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{err}</div>}

    {config && (<>
      <SectionTitle>Mode</SectionTitle>
      <Card>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {["disabled", "manual", "automatic"].map(m => {
            const isSelected = config.auto_fix_mode === m;
            const tone = m === "automatic" ? C.red : m === "manual" ? C.yellow : C.dim;
            return (
              <button
                key={m}
                onClick={() => update("auto_fix_mode", m)}
                style={{
                  padding: "10px 18px", borderRadius: 8, fontSize: 13,
                  background: isSelected ? tone : C.bg,
                  color: isSelected ? "#fff" : C.dim,
                  border: `1px solid ${isSelected ? tone : C.border}`,
                  fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  textTransform: "capitalize",
                }}
              >
                {isSelected ? "✓ " : ""}{m}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5 }}>
          {config.auto_fix_mode === "disabled" && 'Tester "Request fix" button is hidden. Reports go to the manual admin queue only.'}
          {config.auto_fix_mode === "manual" && "🛡️ Tester triggers a fix → Mac runs Claude → diff posted in Auto-Fix Queue for your approval → ships only after you click Approve."}
          {config.auto_fix_mode === "automatic" && "⚠️ Tester triggers a fix → Mac runs Claude → if all gates pass, ships immediately. NO admin review. Use only for low-risk apps with strong test coverage."}
        </div>
      </Card>

      <SectionTitle>Limits</SectionTitle>
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
          <div>
            <label style={lbl()}>Max attempts per day</label>
            <input type="number" min={1} max={20} value={config.auto_fix_max_per_day || 5}
                   onChange={e => update("auto_fix_max_per_day", parseInt(e.target.value))}
                   style={inp()}/>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Hard cap to prevent runaway loops.</div>
          </div>
          <div>
            <label style={lbl()}>Max retries per feedback thread</label>
            <input type="number" min={1} max={5} value={config.auto_fix_max_per_thread || 3}
                   onChange={e => update("auto_fix_max_per_thread", parseInt(e.target.value))}
                   style={inp()}/>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>After N failed attempts, surface for manual review.</div>
          </div>
          <div>
            <label style={lbl()}>Max lines deleted per fix</label>
            <input type="number" min={10} max={2000} value={config.auto_fix_max_lines_deleted || 500}
                   onChange={e => update("auto_fix_max_lines_deleted", parseInt(e.target.value))}
                   style={inp()}/>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Diff exceeding this is auto-rejected — guards against Claude removing features.</div>
          </div>
          <div>
            <label style={lbl()}>Daily spend cap ($)</label>
            <input type="number" min={1} max={100} value={(config.auto_fix_daily_spend_cap_cents || 1000) / 100}
                   onChange={e => update("auto_fix_daily_spend_cap_cents", Math.round(parseFloat(e.target.value) * 100))}
                   style={inp()}/>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Stops accepting requests once daily Claude API spend hits this. (When using API path; in-app Claude is free.)</div>
          </div>
        </div>
      </Card>

      <SectionTitle>Kill Switch</SectionTitle>
      <Card style={{ borderLeft: `3px solid ${C.red}`, background: C.red + "08" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.text }}>
          <input type="checkbox" checked={!!config.auto_fix_kill_switch}
                 onChange={e => update("auto_fix_kill_switch", e.target.checked)}
                 style={{ accentColor: C.red, width: 16, height: 16 }}/>
          🚨 Block ALL auto-fix activity for this app
        </label>
        <div style={{ fontSize: 12, color: C.dim, marginTop: 8 }}>
          Use if something is going wrong. Pending requests will reject with a clear message; in-flight attempts will complete but no new ones will start.
        </div>
      </Card>

      <div style={{ marginTop: 16 }}>
        <Btn primary onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Settings"}
        </Btn>
      </div>
    </>)}
  </>);
}

function AnnotationsPage() {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/annotations"), []);
  const [show, setShow] = useState(false);
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState(""); const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const create = async () => { try { await api("/v1/annotations", { method: "POST", body: { date, title, description: desc } }); setTitle(""); setDesc(""); setShow(false); reload(); } catch(e) { alert("Failed: " + e.message); } };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const annotations = d.annotations || [];
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => setShow(!show)}><Plus size={14}/> Add Annotation</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{annotations.length} annotations</span>
    </div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>New Annotation</div>
      <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp()}/></div>
        <div><label style={lbl()}>Title *</label><input value={title} onChange={e => setTitle(e.target.value)} style={inp()} placeholder="e.g. v2.1 release"/></div>
        <div><label style={lbl()}>Description</label><input value={desc} onChange={e => setDesc(e.target.value)} style={inp()} placeholder="What happened?"/></div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!title}><Check size={14}/> Create</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    <SectionTitle>Timeline</SectionTitle>
    {annotations.length > 0 ? <Card>{annotations.map((a, i) => (
      <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: i < annotations.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20 }}>
          <div style={{ width: 10, height: 10, borderRadius: 5, background: C.purple, flexShrink: 0 }}/>
          {i < annotations.length - 1 && <div style={{ width: 2, flex: 1, background: C.purple + "33", marginTop: 4 }}/>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{a.title}</span>
            <span style={{ fontSize: 11, color: C.dim }}>{a.date ? new Date(a.date).toLocaleDateString() : ""}</span>
          </div>
          {a.description && <div style={{ fontSize: 12, color: C.dim, marginTop: 3, lineHeight: 1.5 }}>{a.description}</div>}
          {a.category && <Badge style={{ marginTop: 4 }}>{a.category}</Badge>}
        </div>
      </div>
    ))}</Card> : <Empty msg="No annotations — mark releases, incidents, or milestones"/>}
  </>);
}

function NotebooksPage() {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/notebooks"), []);
  const [open, setOpen] = useState(null); const [title, setTitle] = useState(""); const [cells, setCells] = useState([]); const [saving, setSaving] = useState(false);
  useUnsavedWarning(!!open);
  const openNotebook = (n) => { setOpen(n); setTitle(n.title); setCells(n.cells || [{ type: "markdown", content: "" }]); };
  const save = async () => { setSaving(true); try { await api(`/v1/notebooks/${open.id}`, { method: "PUT", body: { title, cells } }); reload(); } catch(e) { alert("Save failed: " + e.message); } setSaving(false); };
  const addCell = () => setCells([...cells, { type: "markdown", content: "" }]);
  const updateCell = (i, content) => { const n = [...cells]; n[i].content = content; setCells(n); };
  const removeCell = (i) => setCells(cells.filter((_, j) => j !== i));
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;

  if (open) return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
      <Btn onClick={() => setOpen(null)}>← Back</Btn>
      <input value={title} onChange={e => setTitle(e.target.value)} style={{ ...inp(), flex: 1, fontSize: 16, fontWeight: 600 }}/>
      <Btn primary onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Btn>
    </div>
    {cells.map((cell, i) => <Card key={i} style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <select value={cell.type} onChange={e => { const n = [...cells]; n[i].type = e.target.value; setCells(n); }} style={{ ...inp(), width: 120, padding: "4px 8px" }}>
          <option value="markdown">Markdown</option><option value="sql">SQL</option><option value="note">Note</option>
        </select>
        <button onClick={() => removeCell(i)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer" }}><Trash2 size={14}/></button>
      </div>
      <textarea value={cell.content} onChange={e => updateCell(i, e.target.value)} style={{ ...inp(), height: 80, fontFamily: cell.type === "sql" ? "JetBrains Mono" : "inherit", fontSize: 12, resize: "vertical" }} placeholder={cell.type === "sql" ? "SELECT ..." : "Write your analysis..."}/>
    </Card>)}
    <Btn small onClick={addCell}><Plus size={12}/> Add Cell</Btn>
  </>);

  return (<><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}><SectionTitle>Notebooks</SectionTitle>
    <Btn primary onClick={async () => { try { await api("/v1/notebooks", { method: "POST", body: { title: "New Analysis" } }); reload(); } catch(e) { alert("Create failed: " + e.message); } }}><Plus size={14}/> New</Btn></div>
    {d.notebooks?.length > 0 ? d.notebooks.map(n => (<Card key={n.id} style={{ marginBottom: 8, cursor: "pointer" }} onClick={() => openNotebook(n)}>
      <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontWeight: 600 }}>{n.title}</span><span style={{ fontSize: 11, color: C.dim }}>{new Date(n.updated_at).toLocaleString()}</span></div>
      <div style={{ fontSize: 12, color: C.dim }}>{(n.cells||[]).length} cells · Click to open</div>
    </Card>)) : <Empty msg="No notebooks — create one above"/>}</>);
}

function SchemasPage({ app }) {
  const { data: schemas, error: e1, loading: l1, reload: r1 } = useFetch(() => api(`/v1/schema/${app}`), [app]);
  const { data: violations } = useFetch(() => api(`/v1/schema/violations/${app}`), [app]);
  const [show,setShow]=useState(false); const [event,setEvent]=useState(""); const [required,setRequired]=useState(""); const [optional,setOptional]=useState(""); const [enforce,setEnforce]=useState(false); const [err,setErr]=useState("");
  if (l1) return <Loading/>; if (e1) return <ErrorState error={e1} onRetry={r1}/>;
  const create = async () => { setErr(""); try { await api("/v1/schema", { method: "POST", body: { appId: app, eventName: event, requiredProperties: required.split(",").map(s=>s.trim()).filter(Boolean), optionalProperties: optional.split(",").map(s=>s.trim()).filter(Boolean), enforce } }); setEvent(""); setRequired(""); setOptional(""); setShow(false); r1(); } catch(e) { setErr(e.message); } };
  return (<><div style={{ display:"flex",gap:8,marginBottom:16 }}><Btn primary onClick={()=>setShow(!show)}><Plus size={14}/> Define Schema</Btn></div>
    {show && <Card style={{ marginBottom:16 }}><div style={{ fontWeight:600,marginBottom:12,color:C.text }}>Define Event Schema</div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
        <div><label style={lbl()}>Event Name *</label><input value={event} onChange={e=>setEvent(e.target.value)} style={inp()} placeholder="subscription_started"/></div>
        <div><label style={lbl()}>Enforce?</label><div style={{ marginTop:6 }}><button onClick={()=>setEnforce(!enforce)} style={{ padding:"6px 14px",border:`1px solid ${enforce?C.green:C.border}`,borderRadius:8,fontSize:12,cursor:"pointer",background:enforce?C.green+"18":"transparent",color:enforce?C.green:C.dim }}>{enforce?"● Enforcing":"○ Not enforcing"}</button></div></div>
      </div>
      <div><label style={lbl()}>Required Properties (comma-separated)</label><input value={required} onChange={e=>setRequired(e.target.value)} style={inp()} placeholder="tier, product_id, price"/></div>
      <div><label style={lbl()}>Optional Properties (comma-separated)</label><input value={optional} onChange={e=>setOptional(e.target.value)} style={inp()} placeholder="trial_end, currency"/></div>
      {err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}
      <div style={{ display:"flex",gap:8,marginTop:12 }}><Btn primary onClick={create} disabled={!event}><Check size={14}/> Save Schema</Btn><Btn onClick={()=>setShow(false)}>Cancel</Btn></div>
    </Card>}
    <SectionTitle>Event Schemas</SectionTitle>
    {schemas?.schemas?.length > 0 ? <Card><Table cols={["Event","Required Props","Optional Props","Enforce"]} rows={schemas.schemas.map(s => [s.event_name, (s.required_properties||[]).join(", ")||"-", (s.optional_properties||[]).join(", ")||"-", s.enforce?"Yes":"No"])}/></Card> : <Empty msg="No schemas defined — add one above"/>}
    <SectionTitle>Schema Violations</SectionTitle>
    {violations?.violations?.length > 0 ? <Card><Table cols={["Event","Violation","Detected"]} rows={violations.violations.slice(0,20).map(v => [v.event_name, v.violation, new Date(v.detected_at).toLocaleString()])}/></Card> : <Empty msg="No violations"/>}</>);
}

function RetentionPolicyPage() {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/retention-policy"), []);
  const [editing, setEditing] = useState(false); const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
  const startEdit = () => { setForm({ eventsDays: d?.eventsDays||365, pushLogDays: d?.pushLogDays||90, crashDays: d?.crashDays||180, auditDays: d?.auditDays||730, enabled: d?.enabled||false }); setEditing(true); };
  const save = async () => { setSaving(true); setErr(""); try { await api("/v1/retention-policy", { method: "PUT", body: form }); setEditing(false); reload(); } catch(e) { setErr(e.message); } setSaving(false); };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  return (<><SectionTitle>Data Retention Policy</SectionTitle><Card>
    {editing ? <>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
        <div><label style={lbl()}>Events (days)</label><input type="number" value={form.eventsDays} onChange={e=>setForm({...form,eventsDays:+e.target.value})} style={inp()} min={30}/></div>
        <div><label style={lbl()}>Push Logs (days)</label><input type="number" value={form.pushLogDays} onChange={e=>setForm({...form,pushLogDays:+e.target.value})} style={inp()} min={7}/></div>
        <div><label style={lbl()}>Crash Reports (days)</label><input type="number" value={form.crashDays} onChange={e=>setForm({...form,crashDays:+e.target.value})} style={inp()} min={30}/></div>
        <div><label style={lbl()}>Audit Logs (days)</label><input type="number" value={form.auditDays} onChange={e=>setForm({...form,auditDays:+e.target.value})} style={inp()} min={90}/></div>
      </div>
      <div style={{ marginTop:12 }}><button onClick={()=>setForm({...form,enabled:!form.enabled})} style={{ padding:"6px 14px",border:`1px solid ${form.enabled?C.green:C.border}`,borderRadius:8,fontSize:12,cursor:"pointer",background:form.enabled?C.green+"18":"transparent",color:form.enabled?C.green:C.dim }}>{form.enabled?"● Auto-purge enabled":"○ Auto-purge disabled"}</button></div>
      {err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}
      <div style={{ display:"flex",gap:8,marginTop:12 }}><Btn primary onClick={save} disabled={saving}>{saving?"Saving...":"Save"}</Btn><Btn onClick={()=>setEditing(false)}>Cancel</Btn></div>
    </> : <>
      <Grid cols={2}><Stat label="Events" value={`${d.eventsDays||365} days`}/><Stat label="Push Logs" value={`${d.pushLogDays||90} days`}/><Stat label="Crash Reports" value={`${d.crashDays||180} days`}/><Stat label="Audit Logs" value={`${d.auditDays||730} days`}/></Grid>
      <div style={{ marginTop: 12, fontSize: 12, color: d.enabled ? C.green : C.dim }}>{d.enabled ? "● Auto-purge enabled" : "○ Auto-purge disabled"}</div>
      <div style={{ marginTop:12 }}><Btn small onClick={startEdit}>Edit Policy</Btn></div>
    </>}
  </Card></>);
}

function RetentionPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/retention/${app}`), [app]); if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; const latest = d.cohorts?.[0]?.retention || {}; const curve = [1,3,7,14,30,60,90].filter(day => latest[`d${day}`] != null).map(day => ({ day: `D${day}`, rate: latest[`d${day}`] })); return (<><Grid cols={4}><Stat label="D1" value={`${latest.d1??'-'}%`}/><Stat label="D7" value={`${latest.d7??'-'}%`}/><Stat label="D14" value={`${latest.d14??'-'}%`}/><Stat label="D30" value={`${latest.d30??'-'}%`}/></Grid>{curve.length > 0 && <><SectionTitle>Retention Curve</SectionTitle><Card><ResponsiveContainer width="100%" height={300}><LineChart data={curve}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="day" tick={{ fill: C.dim, fontSize: 11 }}/><YAxis tick={{ fill: C.dim, fontSize: 11 }} domain={[0, 100]}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Line type="monotone" dataKey="rate" stroke={C.accent} strokeWidth={2} dot name="Retention %"/></LineChart></ResponsiveContainer></Card></>}{d.cohorts?.length > 1 && <><SectionTitle>Weekly Cohorts</SectionTitle><Card><Table cols={["Week","Size","D1","D7","D14","D30"]} rows={d.cohorts.map(c => [c.week, c.cohortSize, c.retention?.d1 != null ? `${c.retention.d1}%` : '-', c.retention?.d7 != null ? `${c.retention.d7}%` : '-', c.retention?.d14 != null ? `${c.retention.d14}%` : '-', c.retention?.d30 != null ? `${c.retention.d30}%` : '-'])}/></Card></>}</>); }

function FunnelsPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/funnels/${app}`), [app]); if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; const steps = d.steps || []; return (<>{d.overallConversion != null && <Grid cols={1}><Stat label="Overall Conversion" value={`${d.overallConversion||0}%`} color={C.green}/></Grid>}<Card><div style={{ fontSize: 13, color: C.dim, marginBottom: 12 }}>Steps: {steps.map(s => s.step || s).join(" → ")}</div>{steps.map((step, i) => (<div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}><div style={{ width: 160, fontSize: 13, color: C.text }}>{step.step}</div><div style={{ flex: 1, height: 28, background: C.bg, borderRadius: 6, overflow: "hidden" }}><div style={{ width: `${step.conversionRate||0}%`, height: "100%", background: CHART_COLORS[i % CHART_COLORS.length], borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 8, fontSize: 12, fontWeight: 600, color: "#fff" }}>{step.users?.toLocaleString()}</div></div><div style={{ width: 60, textAlign: "right", fontSize: 13, fontWeight: 600, color: C.text }}>{step.conversionRate}%</div></div>))}</Card></>); }

function SessionsPage({ app }) { const [days, setDays] = useState(7); const { data: d, error, loading, reload } = useFetch(() => api(`/v1/sessions/${app}?days=${days}`), [app, days]); if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; const daily = d.daily || []; const totalSessions = daily.reduce((s, r) => s + (r.sessions||0), 0); const avgDur = daily.length > 0 ? Math.round(daily.reduce((s, r) => s + (r.avgDuration||0), 0) / daily.length) : 0; const medDur = daily.length > 0 ? Math.round(daily.reduce((s, r) => s + (r.medianDuration||0), 0) / daily.length) : 0; const avgDep = daily.length > 0 ? Math.round(daily.reduce((s, r) => s + (r.avgDepth||0), 0) / daily.length * 10) / 10 : 0; return (<><div style={{ display:"flex",justifyContent:"flex-end",marginBottom:12 }}><select value={days} onChange={e=>setDays(+e.target.value)} style={{...inp(),width:100}}>{[7,14,30,60,90].map(d=><option key={d} value={d}>{d}d</option>)}</select></div><Grid cols={4}><Stat label={`Total (${days}d)`} value={totalSessions.toLocaleString()}/><Stat label="Avg Duration" value={`${avgDur}s`}/><Stat label="Median Duration" value={`${medDur}s`}/><Stat label="Avg Depth" value={`${avgDep} events`}/></Grid>{daily.length > 0 && <><SectionTitle>Daily Sessions</SectionTitle><Card><ResponsiveContainer width="100%" height={200}><BarChart data={[...daily].reverse()}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="date" tick={{ fill: C.dim, fontSize: 10 }}/><YAxis tick={{ fill: C.dim, fontSize: 11 }}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Bar dataKey="sessions" fill={C.blue} radius={[4,4,0,0]}/></BarChart></ResponsiveContainer></Card></>}</>); }

function ChurnPage({ app }) { const { data: d, error: e1, loading: l1, reload: r1 } = useFetch(() => api(`/v1/churn/${app}`), [app]); const { data: h } = useFetch(() => api(`/v1/health-scores/${app}`), [app]); if (l1) return <Loading/>; if (e1) return <ErrorState error={e1} onRetry={r1}/>; const sm = d?.summary || {}; const riskDist = ["critical","high","medium","low"].filter(l => sm[l] > 0).map(l => ({ level: l, count: sm[l] })); return (<><Grid cols={4}><Stat label="Critical" value={sm.critical||0} color={C.red}/><Stat label="High Risk" value={sm.high||0} color={C.accent}/><Stat label="Medium Risk" value={sm.medium||0} color={C.yellow}/><Stat label="At-Risk Revenue" value={`$${Math.round(sm.atRiskRevenue||0)}`} color={C.red}/></Grid>{h?.distribution && <Grid cols={4}>{h.distribution.map(r => <Stat key={r.risk_level} label={r.risk_level} value={r.count||0} sub={`Avg score: ${Math.round(r.avg_score)}`} color={r.risk_level==='critical'?C.red:r.risk_level==='high_risk'?C.accent:r.risk_level==='at_risk'?C.yellow:C.green}/>)}</Grid>}{riskDist.length > 0 && <><SectionTitle>Risk Distribution</SectionTitle><Card><ResponsiveContainer width="100%" height={200}><PieChart><Pie data={riskDist} dataKey="count" nameKey="level" cx="50%" cy="50%" outerRadius={70} label>{riskDist.map((_,i)=><Cell key={i} fill={[C.red,C.accent,C.yellow,C.green][i]}/>)}</Pie><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Legend wrapperStyle={{ color: C.dim }}/></PieChart></ResponsiveContainer></Card></>}{d?.users?.length > 0 && <><SectionTitle>At-Risk Users</SectionTitle><Card><Table exportName="at-risk-users" cols={["User","Risk","Inactive (days)","Sessions","LTV","Tier"]} rows={d.users.slice(0,20).map(u=>[u.userId?.substring(0,12)+"...",u.riskLevel,u.daysInactive,u.sessions,`$${u.ltv||0}`,u.tier])}/></Card></>}</>); }

function ReviewsPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/reviews/${app}`), [app]); const [syncing,setSyncing]=useState(false); const [analyzing,setAnalyzing]=useState(false);
  const sync = async () => { setSyncing(true); try { await api(`/v1/reviews/sync/${app}`, { method: "POST" }); reload(); } catch(e) { alert("Sync failed: " + e.message); } setSyncing(false); };
  const analyze = async () => { setAnalyzing(true); try { await api(`/v1/reviews/${app}/analyze`, { method: "POST" }); reload(); } catch(e) { alert("Analysis failed: " + e.message); } setAnalyzing(false); };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; const s = d.summary || {}; return (<>
    <div style={{ display:"flex",gap:8,marginBottom:16 }}><Btn primary onClick={sync} disabled={syncing}><RefreshCw size={14}/> {syncing?"Syncing...":"Sync Reviews"}</Btn><Btn onClick={analyze} disabled={analyzing}><Zap size={14}/> {analyzing?"Analyzing...":"AI Analyze"}</Btn></div>
    <Grid cols={4}><Stat label="Avg Rating" value={s.avgRating||"-"} color={s.avgRating>=4?C.green:s.avgRating>=3?C.yellow:C.red}/><Stat label="Total Reviews" value={s.totalReviews||0}/><Stat label="Flagged" value={s.flagged||0} color={C.red}/><Stat label="Negative" value={s.negative||0} color={C.red}/></Grid>{s.distribution && <><SectionTitle>Rating Distribution</SectionTitle><Card><ResponsiveContainer width="100%" height={180}><BarChart data={[5,4,3,2,1].map(r => ({ rating: `${r}★`, count: s.distribution[r]||0 }))}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="rating" tick={{ fill: C.dim, fontSize: 12 }}/><YAxis tick={{ fill: C.dim, fontSize: 11 }}/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Bar dataKey="count" fill={C.yellow} radius={[4,4,0,0]}/></BarChart></ResponsiveContainer></Card></>}{d.reviews?.length>0 && <><SectionTitle>Recent Reviews</SectionTitle>{d.reviews.slice(0,15).map((r,i)=>(<Card key={i} style={{ marginBottom: 8 }}><div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}><div><span style={{ color:C.yellow }}>{"★".repeat(r.rating)}{"☆".repeat(5-r.rating)}</span> <span style={{ fontWeight:600,marginLeft:8 }}>{r.title}</span></div><span style={{ fontSize:11,color:C.dim }}>{r.author} · {r.country}</span></div><div style={{ fontSize:13,color:C.dim,lineHeight:1.5 }}>{r.body?.substring(0,300)}</div>{r.ai_summary && <div style={{ fontSize:12,color:C.accent,marginTop:6 }}>AI: {r.ai_summary}</div>}{r.ai_tags?.length>0 && <div style={{ display:"flex",gap:4,marginTop:6,flexWrap:"wrap" }}>{r.ai_tags.map(t=><Badge key={t}>{t}</Badge>)}</div>}</Card>))}</>}</>); }

function AdsPage({ app }) { const { data: perf, error: e1, loading: l1, reload: r1 } = useFetch(() => api(`/v1/ads/performance/${app}`), [app]); const { data: roas } = useFetch(() => api(`/v1/ads/roas/${app}`), [app]); const [syncing,setSyncing]=useState(false);
  const sync = async () => { setSyncing(true); try { await api(`/v1/asa/sync/${app}`, { method: "POST" }); r1(); } catch(e) { alert("ASA sync failed: " + e.message); } setSyncing(false); };
  if (l1) return <Loading/>; if (e1) return <ErrorState error={e1} onRetry={r1}/>; const networks = perf?.byNetwork || perf?.networks || []; return (<>
    <div style={{ display:"flex",gap:8,marginBottom:16 }}><Btn primary onClick={sync} disabled={syncing}><RefreshCw size={14}/> {syncing?"Syncing...":"Sync Ad Data"}</Btn></div>{perf?.summary && <Grid cols={3}><Stat label="Total Spend" value={`$${perf.summary.totalSpend||0}`} color={C.red}/><Stat label="Total Installs" value={perf.summary.totalInstalls||0} color={C.green}/><Stat label="Blended CPA" value={`$${perf.summary.blendedCPA||0}`}/></Grid>}{networks.length>0 && <><SectionTitle>Network Performance</SectionTitle><Card><Table exportName="ad-performance" cols={["Network","Spend","Installs","CPA","CTR","Conv. Rate"]} rows={networks.map(n=>[n.network,`$${n.spend}`,n.installs,`$${n.cpa}`,`${n.ctr||0}%`,`${n.cr||0}%`])}/></Card></>}{roas?.campaigns?.length>0 && <><SectionTitle>Campaign ROAS</SectionTitle><Card><Table exportName="campaign-roas" cols={["Network","Campaign","Users","Subs","Sub Rate","Revenue","Spend","ROAS"]} rows={roas.campaigns.slice(0,20).map(c=>[c.network,c.campaign,c.users,c.subscribers,`${c.subscriptionRate}%`,`$${c.revenue}`,`$${c.spend}`,c.roas!=null?`${c.roas}x`:'-'])}/></Card></>}</>); }

function ExperimentsPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/experiments/${app}`), [app]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState(""); const [metric, setMetric] = useState("subscription_started"); const [variants, setVariants] = useState("control,variant_a");
  const [err, setErr] = useState("");
  const create = async () => { setErr(""); try { await api("/v1/experiments", { method: "POST", body: { appId: app, name, primaryMetric: metric, variants: variants.split(",").map(v => v.trim()) } }); setName(""); setShowCreate(false); reload(); } catch(e) { setErr(e.message); } };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const exps = d.experiments || [];
  const active = exps.filter(e => e.status === "active").length;
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => setShowCreate(!showCreate)}><Plus size={14}/> New Experiment</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{active} active · {exps.length} total</span>
    </div>
    {showCreate && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>Create Experiment</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Paywall A/B test"/></div>
        <div><label style={lbl()}>Primary Metric</label><input value={metric} onChange={e => setMetric(e.target.value)} style={inp()}/></div>
        <div><label style={lbl()}>Variants (comma-separated)</label><input value={variants} onChange={e => setVariants(e.target.value)} style={inp()}/></div>
      </div>
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!name}><Check size={14}/> Create</Btn><Btn onClick={() => setShowCreate(false)}>Cancel</Btn></div>
    </Card>}
    {exps.length > 0 ? exps.map(exp => {
      const maxConv = Math.max(...(exp.variants || []).map(v => v.conversionRate || 0), 1);
      return (<Card key={exp.name} style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 600, color: C.text, fontSize: 14 }}>{exp.name}</span>
            <Badge color={exp.status === "active" ? C.green : C.dim}>{exp.status}</Badge>
            {exp.significant && <Badge color={C.green}>✓ Significant</Badge>}
          </div>
          <span style={{ fontSize: 11, color: C.dim }}>Metric: {exp.primaryMetric}</span>
        </div>
        {exp.variants?.length > 0 && exp.variants.map((v, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: C.text, minWidth: 80 }}>{v.variant}</span>
            <div style={{ flex: 1, height: 10, background: C.bg, borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${(v.conversionRate / maxConv) * 100}%`, height: "100%", background: CHART_COLORS[i % CHART_COLORS.length], borderRadius: 5 }}/>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text, minWidth: 50, textAlign: "right" }}>{v.conversionRate}%</span>
            <span style={{ fontSize: 11, color: C.dim, minWidth: 80 }}>{v.converted}/{v.exposed}</span>
          </div>
        ))}
      </Card>);
    }) : <Empty msg="No experiments — create one to start A/B testing"/>}
  </>);
}

function FlagsPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/flags/${app}`), [app]); const [toggling, setToggling] = useState(null);
  const [showCreate, setShowCreate] = useState(false); const [newKey, setNewKey] = useState(""); const [newVariant, setNewVariant] = useState(""); const [err, setErr] = useState("");
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; const flags = d.flags ? d.flags : Object.values(d).filter(f => f && typeof f === 'object' && f.key);
  const toggle = async (key, current) => { setToggling(key); try { await api(`/v1/flags/${app}/${key}`, { method: "PUT", body: { enabled: !current } }); reload(); } catch(e) { alert("Toggle failed: " + e.message); } setToggling(null); };
  const create = async () => { setErr(""); try { await api("/v1/flags", { method: "POST", body: { appId: app, key: newKey, variant: newVariant || undefined } }); setNewKey(""); setNewVariant(""); setShowCreate(false); reload(); } catch(e) { setErr(e.message); } };
  const onCount = flags.filter(f => f.enabled).length;
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => setShowCreate(!showCreate)}><Plus size={14}/> New Flag</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{onCount} of {flags.length} enabled</span>
    </div>
    {showCreate && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>Create Feature Flag</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Flag Key *</label><input value={newKey} onChange={e => setNewKey(e.target.value)} style={inp()} placeholder="e.g. show_new_paywall"/></div>
        <div><label style={lbl()}>Variant (optional)</label><input value={newVariant} onChange={e => setNewVariant(e.target.value)} style={inp()} placeholder="e.g. control"/></div>
      </div>
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!newKey}><Check size={14}/> Create</Btn><Btn onClick={() => setShowCreate(false)}>Cancel</Btn></div>
    </Card>}
    {flags.length > 0 ? <Card>{flags.map((f, i) => (
      <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < flags.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <div onClick={() => toggle(f.key, f.enabled)} style={{ width: 44, height: 24, borderRadius: 12, background: f.enabled ? C.green : C.bg, cursor: "pointer", position: "relative", transition: "background 0.2s", opacity: toggling === f.key ? 0.5 : 1 }}>
          <div style={{ width: 18, height: 18, borderRadius: 9, background: "#fff", position: "absolute", top: 3, left: f.enabled ? 23 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }}/>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{f.key}</div>
          {f.variant && <div style={{ fontSize: 11, color: C.dim }}>Variant: {f.variant}</div>}
        </div>
        <Badge color={f.enabled ? C.green : C.dim}>{f.enabled ? "On" : "Off"}</Badge>
      </div>
    ))}</Card> : <Empty msg="No feature flags — create one to start testing"/>}
  </>);
}

function PushPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/push/campaigns/${app}`), [app]);
  const { data: stats } = useFetch(() => api(`/v1/push/stats/${app}`), [app]);
  const [showSend, setShowSend] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [segment, setSegment] = useState("all");
  const [title, setTitle] = useState(""); const [body, setBody] = useState("");
  const [campName, setCampName] = useState(""); const [campType, setCampType] = useState("one-shot");
  const [msgs, setMsgs] = useState([{ title: "", body: "", delayHours: 0 }]);
  const [sending, setSending] = useState(false); const [result, setResult] = useState(null); const [confirmSend, setConfirmSend] = useState(false);

  const sendPush = async () => {
    setSending(true); setResult(null);
    try {
      const r = await api(`/v1/push/send`, { method: "POST", body: { appId: app, segment, title, body } });
      setResult(r); setTitle(""); setBody("");
    } catch (e) { setResult({ error: e.message }); }
    setSending(false); setConfirmSend(false);
  };
  const trySend = () => { if (segment === "all") setConfirmSend(true); else sendPush(); };
  const createCampaign = async () => {
    try {
      await api("/v1/push/campaign", { method: "POST", body: { appId: app, name: campName, campaignType: campType, messages: msgs } });
      setCampName(""); setMsgs([{ title: "", body: "", delayHours: 0 }]); setShowCreate(false); reload();
    } catch (e) { setResult({ error: e.message }); }
  };

  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>;
  const o = stats?.overall || stats || {};
  return (<>
    <ConfirmDialog open={confirmSend} title="Send to All Users?" message={`This will send a push notification to ALL registered ${app} devices. Are you sure?`} confirmLabel="Send to All" onConfirm={sendPush} onCancel={() => setConfirmSend(false)}/>
    {stats && <Grid cols={4}><Stat label="Sent (7d)" value={o.sent||0}/><Stat label="Delivered" value={o.delivered||0} color={C.green}/><Stat label="Opened" value={o.opened||0}/><Stat label="Open Rate" value={`${o.openRate||0}%`}/></Grid>}
    {stats?.registeredTokens != null && <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Registered devices: {stats.registeredTokens}</div>}

    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => { setShowSend(!showSend); setShowCreate(false); }}><Bell size={14}/> Quick Send</Btn>
      <Btn onClick={() => { setShowCreate(!showCreate); setShowSend(false); }}><Plus size={14}/> Create Campaign</Btn>
    </div>

    {showSend && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>Send Push Notification</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Segment</label><select value={segment} onChange={e => setSegment(e.target.value)} style={inp()}>{["all","free","premium","churning"].map(s => <option key={s} value={s}>{s}</option>)}</select></div>
        <div><label style={lbl()}>Title *</label><input value={title} onChange={e => setTitle(e.target.value)} style={inp()} placeholder="Hey there!"/></div>
      </div>
      <div><label style={lbl()}>Body *</label><input value={body} onChange={e => setBody(e.target.value)} style={inp()} placeholder="We miss you — come check out what's new"/></div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
        <Btn primary onClick={trySend} disabled={!title || !body || sending}>{sending ? "Sending..." : "Send Now"}</Btn>
        {result && !result.error && <span style={{ fontSize: 12, color: C.green }}>Sent: {result.sent}, Delivered: {result.delivered}, Failed: {result.failed}{result.loggedOnly ? " (log-only — APNs not configured)" : ""}</span>}
        {result?.error && <span style={{ fontSize: 12, color: C.red }}>{result.error}</span>}
      </div>
    </Card>}

    {showCreate && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>Create Campaign</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Campaign Name *</label><input value={campName} onChange={e => setCampName(e.target.value)} style={inp()} placeholder="Onboarding drip"/></div>
        <div><label style={lbl()}>Type</label><select value={campType} onChange={e => setCampType(e.target.value)} style={inp()}><option value="one-shot">One-Shot</option><option value="drip">Drip</option></select></div>
      </div>
      <div style={{ marginTop: 12 }}><label style={lbl()}>Messages</label>
        {msgs.map((m, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 32px", gap: 8, marginBottom: 6 }}>
          <input value={m.title} onChange={e => { const n = [...msgs]; n[i].title = e.target.value; setMsgs(n); }} style={inp()} placeholder="Title"/>
          <input value={m.body} onChange={e => { const n = [...msgs]; n[i].body = e.target.value; setMsgs(n); }} style={inp()} placeholder="Body"/>
          <input type="number" value={m.delayHours} onChange={e => { const n = [...msgs]; n[i].delayHours = +e.target.value; setMsgs(n); }} style={inp()} placeholder="Delay h" min={0}/>
          {msgs.length > 1 && <button onClick={() => setMsgs(msgs.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: C.red, cursor: "pointer" }}><Trash2 size={14}/></button>}
        </div>)}
        <Btn small onClick={() => setMsgs([...msgs, { title: "", body: "", delayHours: (msgs.length) * 24 }])}><Plus size={12}/> Add Step</Btn>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={createCampaign} disabled={!campName || !msgs[0].title}><Check size={14}/> Create</Btn><Btn onClick={() => setShowCreate(false)}>Cancel</Btn></div>
    </Card>}

    <SectionTitle>Campaigns</SectionTitle>
    {d?.campaigns?.length > 0 ? d.campaigns.map(c => (<Card key={c.id} style={{ marginBottom: 8 }}><div style={{ display: "flex", justifyContent: "space-between" }}><div><span style={{ fontWeight: 600 }}>{c.name}</span> <Badge color={c.status === 'active' ? C.green : C.dim}>{c.status}</Badge> <Badge>{c.campaign_type}</Badge></div><span style={{ fontSize: 12, color: C.dim }}>Sent: {c.sent_count || 0}</span></div></Card>)) : <Empty msg="No push campaigns — create one above"/>}
  </>);
}

function EmailsPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/emails/campaigns?appId=${app}`), [app]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState(""); const [subject, setSubject] = useState(""); const [htmlBody, setHtmlBody] = useState("");
  const [sendingId, setSendingId] = useState(null); const [sendResult, setSendResult] = useState(null); const [err, setErr] = useState("");

  const createCampaign = async () => {
    setErr("");
    try {
      await api("/v1/emails/campaign", { method: "POST", body: { name, subject, htmlBody } });
      setName(""); setSubject(""); setHtmlBody(""); setShowCreate(false); reload();
    } catch (e) { setErr(e.message); }
  };
  const sendCampaign = async (id) => {
    setSendingId(id); setSendResult(null);
    try {
      const r = await api(`/v1/emails/send/${id}`, { method: "POST" });
      setSendResult({ id, ...r });
    } catch (e) { setSendResult({ id, error: e.message }); }
    setSendingId(null);
  };

  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => setShowCreate(!showCreate)}><Plus size={14}/> Create Campaign</Btn>
    </div>

    {showCreate && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>New Email Campaign</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Campaign Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Welcome series"/></div>
        <div><label style={lbl()}>Subject Line *</label><input value={subject} onChange={e => setSubject(e.target.value)} style={inp()} placeholder="You're going to love this"/></div>
      </div>
      <div><label style={lbl()}>HTML Body</label><textarea value={htmlBody} onChange={e => setHtmlBody(e.target.value)} style={{ ...inp(), height: 100, fontFamily: "JetBrains Mono", fontSize: 12, resize: "vertical" }} placeholder="<h1>Hello!</h1><p>Welcome to...</p>"/></div>
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={createCampaign} disabled={!name || !subject}><Check size={14}/> Create</Btn><Btn onClick={() => setShowCreate(false)}>Cancel</Btn></div>
    </Card>}

    {sendResult && <Card style={{ marginBottom: 12, borderColor: sendResult.error ? C.red : C.green }}>
      <div style={{ fontSize: 13, color: sendResult.error ? C.red : C.green }}>{sendResult.error ? sendResult.error : `Sent: ${sendResult.sent||0}, Delivered: ${sendResult.delivered||0}, Failed: ${sendResult.failed||0}`}</div>
    </Card>}

    {d.campaigns?.length > 0 ? <Card><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead><tr>{["Name","Subject","Status","Sent","Opens","Clicks",""].map((c,i) => <th key={i} style={{ textAlign: "left", padding: "8px 12px", borderBottom: `1px solid ${C.border}`, color: C.dim, fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>{c}</th>)}</tr></thead>
      <tbody>{d.campaigns.map(c => <tr key={c.id} style={{ borderBottom: `1px solid ${C.border}08` }}>
        <td style={{ padding: "10px 12px", color: C.text, fontWeight: 500 }}>{c.name}</td>
        <td style={{ padding: "10px 12px", color: C.dim }}>{c.subject}</td>
        <td style={{ padding: "10px 12px" }}><Badge color={c.status === 'sent' ? C.green : c.status === 'draft' ? C.dim : C.accent}>{c.status}</Badge></td>
        <td style={{ padding: "10px 12px", color: C.text }}>{c.sent_count || 0}</td>
        <td style={{ padding: "10px 12px", color: C.text }}>{c.open_count || 0}</td>
        <td style={{ padding: "10px 12px", color: C.text }}>{c.click_count || 0}</td>
        <td style={{ padding: "10px 12px" }}>{c.status === 'draft' && <Btn small primary onClick={() => sendCampaign(c.id)} disabled={sendingId === c.id}>{sendingId === c.id ? "..." : "Send"}</Btn>}</td>
      </tr>)}</tbody>
    </table></Card> : <Empty msg="No email campaigns — create one above"/>}
  </>);
}

function CrashesPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/crashes/${app}`), [app]); const [resolving, setResolving] = useState(null);
  const resolve = async (hash) => { setResolving(hash); try { await api(`/v1/crashes/${hash}/resolve`, { method: "POST" }); } catch(e) { alert("Resolve failed: " + e.message); } setResolving(null); reload(); };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  const crashes = d.crashes || [];
  const severityData = [
    { name: "Critical (10+)", count: crashes.filter(c => (c.occurrences||0) >= 10 && !c.resolved).length, fill: C.red },
    { name: "High (5-9)", count: crashes.filter(c => (c.occurrences||0) >= 5 && (c.occurrences||0) < 10 && !c.resolved).length, fill: C.accent },
    { name: "Medium (2-4)", count: crashes.filter(c => (c.occurrences||0) >= 2 && (c.occurrences||0) < 5 && !c.resolved).length, fill: C.yellow },
    { name: "Resolved", count: crashes.filter(c => c.resolved).length, fill: C.green }
  ].filter(s => s.count > 0);
  return (<>
    {d.summary && <Grid cols={4}>
      <Stat label="Crash Groups" value={d.summary.groups||0}/>
      <Stat label="Total Crashes" value={d.summary.totalCrashes||0} color={C.red}/>
      <Stat label="Critical (5+)" value={d.summary.criticalGroups||0} color={C.red}/>
      <Stat label="Resolved" value={crashes.filter(c => c.resolved).length} color={C.green}/>
    </Grid>}
    {severityData.length > 0 && <Card><ResponsiveContainer width="100%" height={180}><PieChart><Pie data={severityData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={35} label>{severityData.map((d,i) => <Cell key={i} fill={d.fill}/>)}</Pie><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Legend wrapperStyle={{ color: C.dim }}/></PieChart></ResponsiveContainer></Card>}
    <SectionTitle>Crash Groups</SectionTitle>
    {crashes.length > 0 ? <Card>{crashes.slice(0, 25).map((c, i) => {
      const occ = c.occurrences || 0;
      const sevClr = occ >= 10 ? C.red : occ >= 5 ? C.accent : C.yellow;
      return (<div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < 24 ? `1px solid ${C.border}08` : "none" }}>
        <div style={{ width: 40, height: 40, borderRadius: 8, background: c.resolved ? C.green + "22" : sevClr + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: c.resolved ? C.green : sevClr }}>{occ}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{c.crash_type}: {c.crash_reason?.substring(0, 80)}</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{c.app_version} · {c.device_model}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge color={c.resolved ? C.green : sevClr}>{c.resolved ? "Resolved" : `${occ} crashes`}</Badge>
          {!c.resolved && <Btn small primary onClick={() => resolve(c.group_hash)} disabled={resolving===c.group_hash}>{resolving===c.group_hash ? "..." : "Resolve"}</Btn>}
        </div>
      </div>);
    })}</Card> : <Empty msg="No crashes reported — that's great!"/>}
  </>);
}

function ASOPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/aso/rankings/${app}`), [app]); const [show,setShow]=useState(false); const [kw,setKw]=useState(""); const [country,setCountry]=useState("us"); const [tracking,setTracking]=useState(false); const [err,setErr]=useState("");
  const addKw = async () => { setErr(""); try { await api("/v1/aso/keywords", { method: "POST", body: { appId: app, keyword: kw, country } }); setKw(""); reload(); } catch(e) { setErr(e.message); } };
  const trackNow = async () => { setTracking(true); try { await api(`/v1/aso/track/${app}`, { method: "POST" }); reload(); } catch(e) { alert("Track failed: " + e.message); } setTracking(false); };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; const kws = d.keywords || d.rankings || {};
  const kwEntries = Object.entries(kws);
  return (<>
    <div style={{ display:"flex",gap:8,marginBottom:16 }}>
      <Btn primary onClick={()=>setShow(!show)}><Plus size={14}/> Add Keyword</Btn>
      <Btn onClick={trackNow} disabled={tracking}><RefreshCw size={14}/> {tracking?"Tracking...":"Track Rankings"}</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{kwEntries.length} keywords tracked</span>
    </div>
    {show && <Card style={{ marginBottom:16 }}><div style={{ display:"flex",gap:8,alignItems:"flex-end" }}>
      <div style={{ flex:1 }}><label style={lbl()}>Keyword *</label><input value={kw} onChange={e=>setKw(e.target.value)} style={inp()} placeholder="mindfulness journal"/></div>
      <div><label style={lbl()}>Country</label><select value={country} onChange={e=>setCountry(e.target.value)} style={inp()}>{["us","gb","ca","au","de","fr","jp","br","in","kr"].map(c=><option key={c} value={c}>{c.toUpperCase()}</option>)}</select></div>
      <Btn primary onClick={addKw} disabled={!kw}><Check size={14}/> Add</Btn>
    </div>{err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}</Card>}
    {kwEntries.length > 0 ? <Card>{kwEntries.map(([keyword, entries], i) => {
      const latest = Array.isArray(entries) ? entries[0] : entries;
      const prev = Array.isArray(entries) && entries.length > 1 ? entries[1] : null;
      const rank = latest?.rank;
      const prevRank = prev?.rank;
      const delta = prevRank && rank ? prevRank - rank : 0;
      const rankClr = !rank ? C.dim : rank <= 10 ? C.green : rank <= 50 ? C.yellow : C.accent;
      return (<div key={keyword} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < kwEntries.length - 1 ? `1px solid ${C.border}08` : "none" }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: rankClr + "18", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: rankClr }}>{rank || "—"}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{keyword}</div>
          <div style={{ fontSize: 11, color: C.dim }}>{latest?.country?.toUpperCase() || "US"} · {latest?.date || ""}</div>
        </div>
        {delta !== 0 && <span style={{ fontSize: 13, fontWeight: 600, color: delta > 0 ? C.green : C.red }}>
          {delta > 0 ? `▲ ${delta}` : `▼ ${Math.abs(delta)}`}
        </span>}
        <Badge color={rankClr}>{!rank ? "Not ranked" : rank <= 10 ? "Top 10" : rank <= 50 ? "Top 50" : `#${rank}`}</Badge>
      </div>);
    })}</Card> : <Empty msg="No ASO tracking data — add keywords above"/>}
  </>);
}

function CompetitorsPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/competitors/${app}`), [app]);
  const { data: histData } = useFetch(() => d?.competitors?.length ? api(`/v1/competitors/${app}/history`) : null, [app, d?.competitors?.length]);
  const { data: crossData } = useFetch(() => api(`/v1/competitors/cross-app`), []);
  const { data: kwTrends } = useFetch(() => d?.competitors?.length ? api(`/v1/competitors/${app}/keyword-trends`) : null, [app, d?.competitors?.length]);
  const { data: kwGap } = useFetch(() => api(`/v1/competitors/${app}/keyword-gap`), [app]);
  const { data: featureData, reload: reloadFeatures } = useFetch(() => api(`/v1/competitors/${app}/features`), [app]);
  const { data: winsData } = useFetch(() => d?.competitors?.length ? api(`/v1/competitors/${app}/wins`) : null, [app, d?.competitors?.length]);
  const { data: praiseData } = useFetch(() => d?.competitors?.length ? api(`/v1/competitors/${app}/praise`) : null, [app, d?.competitors?.length]);
  const { data: impactData } = useFetch(() => d?.competitors?.length ? api(`/v1/competitors/${app}/impact`) : null, [app, d?.competitors?.length]);
  const { data: playbookData } = useFetch(() => d?.competitors?.length ? api(`/v1/competitors/${app}/playbook`) : null, [app, d?.competitors?.length]);
  const [syncing, setSyncing] = useState(false);
  const [storeId, setStoreId] = useState("");
  const [compName, setCompName] = useState("");
  const [addErr, setAddErr] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [expandedComp, setExpandedComp] = useState(null);
  const [newFeature, setNewFeature] = useState("");
  const [featureMatrix, setFeatureMatrix] = useState(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Init feature matrix from server data
  useEffect(() => { if (featureData && !featureMatrix) setFeatureMatrix(featureData); }, [featureData]);

  // Live typeahead search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSearchResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try { const r = await api(`/v1/competitors/search?term=${encodeURIComponent(val)}`); setSearchResults(r.results || []); } catch { setSearchResults([]); }
      setSearching(false);
    }, 400);
  };

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && searchRef.current && !searchRef.current.contains(e.target)) setSearchResults(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addFromSearch = async (r) => {
    try { await api("/v1/competitors/add", { method: "POST", body: { appId: app, competitorStoreId: r.storeId, competitorName: r.name, category: r.category || "" } });
      setSearchResults(prev => Array.isArray(prev) ? prev.filter(x => x.storeId !== r.storeId) : prev); setSearchTerm(""); setSearchResults(null); reload();
    } catch (e) { setAddErr(e.message); }
  };
  const addManual = async () => { if (!storeId) return; setAddErr(""); try { await api("/v1/competitors/add", { method: "POST", body: { appId: app, competitorStoreId: storeId.trim(), competitorName: compName.trim() || undefined, category: "" } }); setStoreId(""); setCompName(""); setShowManual(false); reload(); } catch (e) { setAddErr(e.message); } };
  const syncAll = async () => { setSyncing(true); try { await api("/v1/competitors/sync", { method: "POST" }); await new Promise(r => setTimeout(r, 1500)); reload(); } catch(e) { alert("Sync failed: " + e.message); } setSyncing(false); };
  const removeComp = async (sid) => { try { await api(`/v1/competitors/${app}/${sid}`, { method: "DELETE" }); } catch(e) { alert(e.message); } reload(); };

  // Feature matrix save
  const saveFeatureMatrix = async (matrix) => {
    setFeatureMatrix(matrix);
    try { await api(`/v1/competitors/${app}/features`, { method: "POST", body: matrix }); } catch {}
  };
  const toggleFeature = (fi, compId) => {
    if (!featureMatrix) return;
    const m = JSON.parse(JSON.stringify(featureMatrix));
    const f = m.features[fi];
    if (!f.competitors) f.competitors = {};
    f.competitors[compId] = !f.competitors[compId];
    saveFeatureMatrix(m);
  };
  const toggleYourFeature = (fi) => {
    if (!featureMatrix) return;
    const m = JSON.parse(JSON.stringify(featureMatrix));
    m.features[fi].yours = !m.features[fi].yours;
    saveFeatureMatrix(m);
  };
  const addFeature = () => {
    if (!newFeature.trim()) return;
    const m = featureMatrix ? JSON.parse(JSON.stringify(featureMatrix)) : { features: [], competitors: [] };
    m.features.push({ name: newFeature.trim(), yours: false, competitors: {} });
    saveFeatureMatrix(m); setNewFeature("");
  };
  const removeFeature = (fi) => {
    const m = JSON.parse(JSON.stringify(featureMatrix));
    m.features.splice(fi, 1);
    saveFeatureMatrix(m);
  };

  if (loading && !d) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const comps = d?.competitors || [];
  const trends = d?.trends || {};
  const sentiments = d?.sentiments || {};
  const summary = d?.summary || {};
  const ratedComps = comps.filter(c => c.avg_rating > 0);
  const yourRating = summary.yourRating || 0;
  const avgCompRating = summary.avgCompetitorRating || 0;
  const ratingDelta = yourRating - avgCompRating;
  const alerts = histData?.alerts || [];

  // Pain points
  const allPainPoints = {};
  comps.forEach(c => (c.recent_negative_keywords || []).forEach(kw => {
    const [word, count] = (typeof kw === "string" ? kw : "").split(":");
    if (word && word.length >= 4) allPainPoints[word] = (allPainPoints[word] || 0) + parseInt(count || 1);
  }));
  const topPainPoints = Object.entries(allPainPoints).sort((a,b) => b[1]-a[1]).slice(0, 12);

  // Rating chart
  const ratingChartData = ratedComps.map(c => ({ name: (c.competitor_name || "").substring(0, 14), rating: c.avg_rating || 0, reviews: c.total_ratings || 0 }));
  if (yourRating > 0) ratingChartData.unshift({ name: app.charAt(0).toUpperCase() + app.slice(1), rating: yourRating, reviews: summary.yourReviews || 0, isYou: true });

  // Market share (estimated from rating counts)
  const marketData = ratedComps.filter(c => c.total_ratings > 0).map(c => ({ name: (c.competitor_name || "").substring(0, 12), value: c.total_ratings }));
  if (summary.yourReviews > 0) marketData.unshift({ name: app.charAt(0).toUpperCase() + app.slice(1), value: summary.yourReviews, isYou: true });
  const totalRatings = marketData.reduce((s, d) => s + d.value, 0);

  // Price history chart data
  const priceHistory = histData?.history || {};
  const priceChartData = [];
  for (const [id, comp] of Object.entries(priceHistory)) {
    for (const s of comp.snapshots) {
      if (s.price) { const numPrice = parseFloat(s.price.replace(/[^0-9.]/g, '')); if (numPrice > 0) priceChartData.push({ date: s.date?.substring(5,10), competitor: comp.name, price: numPrice }); }
    }
  }

  // Cross-app data
  const crossAppComps = crossData?.crossApp || [];

  return (<>
    {/* ═══ SEARCH BAR ═══ */}
    <Card style={{ marginBottom: 16, position: "relative" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1 }} ref={searchRef}>
          <div style={{ position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.dim }}/>
            <input value={searchTerm} onChange={handleSearchChange} style={{ ...inp(), paddingLeft: 36, fontSize: 14 }} placeholder="Search App Store for competitors..."/>
            {searching && <RefreshCw size={14} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.dim, animation: "spin 1s linear infinite" }}/>}
          </div>
          {Array.isArray(searchResults) && searchResults.length > 0 && (
            <div ref={dropdownRef} style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.4)", maxHeight: 360, overflowY: "auto" }}>
              {searchResults.map((r, i) => (
                <div key={i} onClick={() => addFromSearch(r)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: i < searchResults.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.accentDim} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {r.icon && <img src={r.icon} alt="" style={{ width: 40, height: 40, borderRadius: 10 }}/>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: C.dim }}>{r.seller} · {r.category} · {r.rating?.toFixed(1)||"?"}★ ({(r.ratingCount||0).toLocaleString()}) · {r.price}</div>
                  </div>
                  <div style={{ padding: "4px 10px", background: C.accent, color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>+ Track</div>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(searchResults) && searchResults.length === 0 && !searching && searchTerm.length >= 2 && (
            <div ref={dropdownRef} style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, textAlign: "center", color: C.dim, fontSize: 13, boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}>No apps found for "{searchTerm}"</div>
          )}
        </div>
        <button onClick={() => setShowManual(!showManual)} style={{ padding: "8px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: showManual ? C.accentDim : "transparent", color: showManual ? C.accent : C.dim, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>ID #{showManual ? " ✕" : ""}</button>
      </div>
      {showManual && <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        <input value={storeId} onChange={e => setStoreId(e.target.value)} style={{ ...inp(), width: 160 }} placeholder="App Store ID"/>
        <input value={compName} onChange={e => setCompName(e.target.value)} style={{ ...inp(), flex: 1 }} placeholder="Name (optional)"/>
        <Btn primary onClick={addManual} disabled={!storeId.trim()}>Add</Btn>
      </div>}
      {addErr && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{addErr}</div>}
    </Card>

    {comps.length > 0 && (<>

      {/* ═══ 1. ALERT FEED ═══ */}
      {alerts.length > 0 && (
        <Card style={{ marginBottom: 16, borderLeft: `3px solid ${C.yellow}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.yellow, marginBottom: 10 }}>⚡ Competitive Alerts</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {alerts.slice(0, 5).map((a, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.dim }}>
                <span style={{ fontSize: 14 }}>{a.type === 'rating_drop' ? '📉' : a.type === 'price_change' ? '💰' : '🚀'}</span>
                <span style={{ color: C.text, fontWeight: 500 }}>{a.competitor}</span>
                {a.type === 'rating_drop' && <span>rating dropped from {a.from?.toFixed(1)} to {a.to?.toFixed(1)}★</span>}
                {a.type === 'price_change' && <span>price changed from {a.from} to {a.to}</span>}
                {a.type === 'new_version' && <span>shipped v{a.version}{a.notes ? ` — ${a.notes.substring(0, 60)}…` : ""}</span>}
                <span style={{ fontSize: 10, color: C.muted, marginLeft: "auto" }}>{a.date?.substring(5, 10)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ═══ 2. SCORECARD ═══ */}
      <Grid cols={4}>
        <Stat label="Your Rating" value={yourRating > 0 ? `${yourRating.toFixed(1)}★` : "—"} color={yourRating >= 4 ? C.green : yourRating >= 3 ? C.yellow : C.red} sub={summary.yourReviews > 0 ? `${summary.yourReviews.toLocaleString()} reviews` : "No reviews"}/>
        <Stat label="Avg Competitor" value={avgCompRating > 0 ? `${avgCompRating.toFixed(1)}★` : "—"} color={C.dim} sub={`${ratedComps.length} rated`}/>
        <Stat label="Your Edge" value={ratingDelta !== 0 && yourRating > 0 ? `${ratingDelta > 0 ? "+" : ""}${ratingDelta.toFixed(1)}` : "—"} color={ratingDelta > 0 ? C.green : ratingDelta < 0 ? C.red : C.dim} sub={ratingDelta > 0.3 ? "Leading" : ratingDelta < -0.3 ? "Behind" : "On par"}/>
        <Stat label="Tracked" value={comps.length} sub="competitors" color={C.accent}/>
      </Grid>

      {/* ═══ 3. RATING COMPARISON ═══ */}
      {ratingChartData.length > 1 && (<>
        <SectionTitle>Rating Comparison</SectionTitle>
        <Card>
          <ResponsiveContainer width="100%" height={Math.max(160, ratingChartData.length * 36)}>
            <BarChart data={ratingChartData} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false}/>
              <XAxis type="number" domain={[0, 5]} tick={{ fill: C.dim, fontSize: 11 }} ticks={[0,1,2,3,4,5]}/>
              <YAxis type="category" dataKey="name" tick={{ fill: C.dim, fontSize: 12 }} width={80}/>
              <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} formatter={(v, name) => [name === "rating" ? `${v.toFixed(1)}★` : v.toLocaleString(), name === "rating" ? "Rating" : "Reviews"]}/>
              <Bar dataKey="rating" radius={[0,4,4,0]}>
                {ratingChartData.map((entry, i) => <Cell key={i} fill={entry.isYou ? C.green : C.accent} fillOpacity={entry.isYou ? 1 : 0.6}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </>)}

      {/* ═══ 4. MARKET SHARE ESTIMATE ═══ */}
      {marketData.length > 1 && (<>
        <SectionTitle>Estimated Market Presence</SectionTitle>
        <Card>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Based on total rating count as a proxy for user base size.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {marketData.sort((a,b) => b.value - a.value).map((m, i) => {
              const pct = totalRatings > 0 ? (m.value / totalRatings * 100) : 0;
              return (
                <div key={i} style={{ flex: "1 1 120px", minWidth: 120 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: m.isYou ? C.green : C.text, fontWeight: m.isYou ? 700 : 500 }}>{m.name}{m.isYou ? " (You)" : ""}</span>
                    <span style={{ color: C.dim }}>{pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: m.isYou ? C.green : CHART_COLORS[i % CHART_COLORS.length], borderRadius: 4 }}/>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{m.value.toLocaleString()} ratings</div>
                </div>
              );
            })}
          </div>
        </Card>
      </>)}

      {/* ═══ 5. FEATURE GAP OPPORTUNITIES ═══ */}
      {topPainPoints.length > 0 && (<>
        <SectionTitle>Feature Gap Opportunities</SectionTitle>
        <Card>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Common complaints across competitor reviews — each is a feature opportunity.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {topPainPoints.map(([word, count], i) => {
              const intensity = Math.min(1, count / (topPainPoints[0]?.[1] || 1));
              return <div key={i} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 500, background: `rgba(239,68,68,${0.08 + intensity * 0.15})`, color: C.red, border: `1px solid rgba(239,68,68,${0.15 + intensity * 0.2})`, display: "flex", alignItems: "center", gap: 6 }}>{word}<span style={{ fontSize: 10, opacity: 0.7, fontWeight: 700 }}>×{count}</span></div>;
            })}
          </div>
        </Card>
      </>)}

      {/* ═══ 6. COMPETITOR WIN ANALYZER ═══ */}
      {winsData?.wins?.length > 0 && (<>
        <SectionTitle>Competitor Wins — Steal Their Playbook</SectionTitle>
        <Card style={{ borderLeft: `3px solid ${C.green}` }}>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 14 }}>When a competitor's rating jumps, they've validated a feature. These are proven bets you can build with zero guesswork.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {winsData.wins.slice(0, 5).map((w, i) => (
              <div key={i} style={{ padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  {w.icon && <img src={w.icon} alt="" style={{ width: 32, height: 32, borderRadius: 8 }}/>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{w.competitor}</div>
                    <div style={{ fontSize: 11, color: C.dim }}>{w.beforeDate?.substring(5,10)} → {w.afterDate?.substring(5,10)}{w.version ? ` · v${w.version}` : ""}</div>
                  </div>
                  <div style={{ padding: "4px 10px", borderRadius: 6, background: C.green + "18", color: C.green, fontWeight: 700, fontSize: 14 }}>+{w.delta?.toFixed(1)}★</div>
                </div>
                {w.releaseNotes && <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5, marginBottom: 8, padding: 8, background: C.card, borderRadius: 6 }}>{w.releaseNotes}</div>}
                {w.praiseKeywords?.length > 0 && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>USERS PRAISED:</span>
                    {w.praiseKeywords.map((p, j) => <span key={j} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: C.green + "12", color: C.green }}>{p.word} ×{p.count}</span>)}
                  </div>
                )}
                {w.insight && <div style={{ fontSize: 12, color: C.accent, fontWeight: 500, marginTop: 4 }}>💡 {w.insight}</div>}
              </div>
            ))}
          </div>
        </Card>
      </>)}

      {/* ═══ 7. POSITIVE REVIEW MINING ═══ */}
      {praiseData?.features?.length > 0 && (<>
        <SectionTitle>What Users Love About Competitors</SectionTitle>
        <Card>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Praised features across all competitor reviews. Features with 2+ competitors = validated market demand.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {praiseData.features.slice(0, 15).map((f, i) => {
              const intensity = Math.min(1, f.count / (praiseData.features[0]?.count || 1));
              return <div key={i} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 500, background: `rgba(34,197,94,${0.08 + intensity * 0.15})`, color: C.green, border: `1px solid rgba(34,197,94,${0.15 + intensity * 0.2})`, display: "flex", alignItems: "center", gap: 6 }}>
                {f.word}<span style={{ fontSize: 10, opacity: 0.7, fontWeight: 700 }}>×{f.count}</span>
                {f.competitorCount >= 2 && <span style={{ fontSize: 9, padding: "1px 4px", borderRadius: 3, background: C.green, color: "#fff" }}>{f.competitorCount} apps</span>}
              </div>;
            })}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{praiseData.reviewsAnalyzed} positive reviews analyzed</div>
        </Card>
      </>)}

      {/* ═══ 8. UPDATE IMPACT SCORING ═══ */}
      {impactData?.updates?.length > 0 && (<>
        <SectionTitle>Update Impact Scores</SectionTitle>
        <Card>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Which competitor releases actually moved the needle? Green = rating improved, Red = backfired.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {impactData.updates.slice(0, 10).map((u, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}08` }}>
                <div style={{ width: 55, fontSize: 11, color: C.muted, flexShrink: 0 }}>{u.date?.substring(5,10)}</div>
                {u.icon && <img src={u.icon} alt="" style={{ width: 24, height: 24, borderRadius: 6 }}/>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{u.competitor}</span>
                  <span style={{ fontSize: 11, color: C.dim }}> v{u.version}</span>
                </div>
                <div style={{ padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 700, background: u.verdict === "winner" ? C.green + "18" : u.verdict === "backfired" ? C.red + "18" : C.border, color: u.verdict === "winner" ? C.green : u.verdict === "backfired" ? C.red : C.dim }}>
                  {u.impactScore > 0 ? "+" : ""}{u.impactScore}★
                </div>
                {u.releaseNotes && <div style={{ fontSize: 10, color: C.muted, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.releaseNotes}</div>}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 11, color: C.dim }}>
            <span>🏆 {impactData.winners} winners</span>
            <span>💥 {impactData.backfired} backfired</span>
            <span>{impactData.total} total updates</span>
          </div>
        </Card>
      </>)}

      {/* ═══ 9. SUCCESS PLAYBOOK ═══ */}
      {playbookData?.insights?.length > 0 && (<>
        <SectionTitle>Success Playbook</SectionTitle>
        <Card style={{ borderLeft: `3px solid ${C.accent}` }}>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Statistical patterns from competitor behavior — what the winners do differently.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {playbookData.insights.map((ins, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px", background: C.bg, borderRadius: 8 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{ins.type === "update_frequency" ? "🚀" : ins.type === "top_improver" ? "📈" : "🎯"}</span>
                <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{ins.text}</div>
              </div>
            ))}
          </div>
          {playbookData.playbook?.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, marginBottom: 8, textTransform: "uppercase" }}>Competitor Scorecards</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
                {playbookData.playbook.map((p, i) => (
                  <div key={i} style={{ padding: 10, background: C.bg, borderRadius: 8, border: `1px solid ${p.isImproving ? C.green + "40" : p.isDeclining ? C.red + "40" : C.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>{p.competitor}</div>
                    <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.6 }}>
                      <div>Rating: {p.currentRating?.toFixed(1)}★ <span style={{ color: p.ratingTrajectory > 0 ? C.green : p.ratingTrajectory < 0 ? C.red : C.dim }}>{p.ratingTrajectory > 0 ? "↑" : p.ratingTrajectory < 0 ? "↓" : "→"}{Math.abs(p.ratingTrajectory).toFixed(1)}</span></div>
                      <div>Ships every {p.updateFrequencyDays}d ({p.totalVersions} versions)</div>
                      <div>Review velocity: {p.avgReviewVelocity}/wk</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </>)}

      {/* ═══ 10. REVIEW KEYWORD TRENDS ═══ */}
      {kwTrends?.trendData?.length > 1 && kwTrends?.topKeywords?.length > 0 && (<>
        <SectionTitle>Review Keyword Trends</SectionTitle>
        <Card>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Negative review keyword frequency over time — rising keywords = competitor shipping broken updates.</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={kwTrends.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
              <XAxis dataKey="week" tick={{ fill: C.dim, fontSize: 10 }} tickFormatter={v => v?.substring(5,10)}/>
              <YAxis tick={{ fill: C.dim, fontSize: 11 }}/>
              <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }}/>
              <Legend wrapperStyle={{ fontSize: 11 }}/>
              {kwTrends.topKeywords.slice(0, 5).map((kw, i) => (
                <Line key={kw} type="monotone" dataKey={kw} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false}/>
              ))}
            </LineChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{kwTrends.totalReviewsAnalyzed} negative reviews analyzed</div>
        </Card>
      </>)}

      {/* ═══ 7. KEYWORD GAP ANALYSIS ═══ */}
      {kwGap?.gaps?.length > 0 && (<>
        <SectionTitle>Keyword Gap Analysis</SectionTitle>
        <Card>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Keywords used by 2+ competitors that you're not tracking — potential ASO opportunities.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {kwGap.gaps.slice(0, 10).map((g, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${C.border}08` }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text, width: 120 }}>{g.keyword}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  {g.usedBy.map((c, j) => <span key={j} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: C.blue + "18", color: C.blue }}>{c.substring(0, 15)}</span>)}
                </div>
                <span style={{ fontSize: 11, color: C.dim, marginLeft: "auto" }}>{g.competitorCount} competitors</span>
              </div>
            ))}
          </div>
        </Card>
      </>)}

      {/* ═══ 8. PRICING INTELLIGENCE ═══ */}
      {(() => {
        const priceComps = comps.filter(c => c.price && c.price !== "Free");
        return priceComps.length > 0 ? (<>
          <SectionTitle>Pricing Intelligence</SectionTitle>
          <Card>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {priceComps.map((c, i) => (
                <div key={i} style={{ padding: "10px 16px", background: C.bg, borderRadius: 10, flex: "1 1 140px", minWidth: 140 }}>
                  <div style={{ fontSize: 11, color: C.dim }}>{(c.competitor_name || "").substring(0, 18)}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginTop: 2 }}>{c.price}</div>
                </div>
              ))}
            </div>
            {alerts.filter(a => a.type === 'price_change').length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.yellow, marginBottom: 6 }}>RECENT PRICE CHANGES</div>
                {alerts.filter(a => a.type === 'price_change').slice(0, 5).map((a, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>
                    <span style={{ color: C.text, fontWeight: 500 }}>{a.competitor}</span>: {a.from} → <span style={{ color: C.accent, fontWeight: 600 }}>{a.to}</span>
                    <span style={{ fontSize: 10, color: C.muted, marginLeft: 8 }}>{a.date?.substring(5, 10)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>) : null;
      })()}

      {/* ═══ 9. APP SIZE TRACKING ═══ */}
      {(() => {
        const sizedComps = comps.filter(c => c.file_size_bytes > 0);
        if (!sizedComps.length) return null;
        const sizeData = sizedComps.map(c => ({ name: (c.competitor_name || "").substring(0, 14), sizeMB: Math.round(c.file_size_bytes / 1048576) })).sort((a, b) => b.sizeMB - a.sizeMB);
        return (<>
          <SectionTitle>App Size Comparison</SectionTitle>
          <Card>
            <ResponsiveContainer width="100%" height={Math.max(140, sizeData.length * 30)}>
              <BarChart data={sizeData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false}/>
                <XAxis type="number" tick={{ fill: C.dim, fontSize: 11 }} unit="MB"/>
                <YAxis type="category" dataKey="name" tick={{ fill: C.dim, fontSize: 12 }} width={80}/>
                <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }} formatter={v => [`${v}MB`, "Size"]}/>
                <Bar dataKey="sizeMB" fill={C.purple} radius={[0, 4, 4, 0]} label={{ position: "right", fill: C.dim, fontSize: 11, formatter: v => `${v}MB` }}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </>);
      })()}

      {/* ═══ 10. UPDATE CHANGELOG TIMELINE ═══ */}
      {alerts.filter(a => a.type === 'new_version').length > 0 && (<>
        <SectionTitle>Update Changelog</SectionTitle>
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {alerts.filter(a => a.type === 'new_version').slice(0, 8).map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}08` }}>
                <div style={{ width: 60, fontSize: 11, color: C.muted, flexShrink: 0 }}>{a.date?.substring(5, 10)}</div>
                <div style={{ width: 3, background: C.accent, borderRadius: 2, flexShrink: 0 }}/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{a.competitor} <span style={{ fontSize: 11, color: C.dim, fontWeight: 400 }}>v{a.version}</span></div>
                  {a.notes && <div style={{ fontSize: 12, color: C.dim, marginTop: 2, lineHeight: 1.5 }}>{a.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </>)}

      {/* ═══ 11. CROSS-APP COMPETITOR MAP ═══ */}
      {crossAppComps.length > 0 && (<>
        <SectionTitle>Cross-App Threats</SectionTitle>
        <Card>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Competitors that appear across multiple of your apps — these are your strategic threats.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {crossAppComps.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}08` }}>
                {c.icon_url ? <img src={c.icon_url} alt="" style={{ width: 32, height: 32, borderRadius: 8 }}/> :
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: C.red + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.red }}>{(c.competitor_name || "?")[0]}</div>}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{c.competitor_name}</div>
                  <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                    {(c.appears_in || []).map(a => <Badge key={a} color={a === app ? C.accent : C.blue}>{appLabel(a)}</Badge>)}
                  </div>
                </div>
                <Badge color={C.red}>Competes in {c.app_count} apps</Badge>
              </div>
            ))}
          </div>
        </Card>
      </>)}

      {/* ═══ 12. FEATURE MATRIX ═══ */}
      <SectionTitle>Feature Matrix</SectionTitle>
      <Card>
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Track which features you and competitors offer. Click cells to toggle.</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input value={newFeature} onChange={e => setNewFeature(e.target.value)} onKeyDown={e => e.key === "Enter" && addFeature()} style={{ ...inp(), flex: 1 }} placeholder="Add feature (e.g. Sleep Sounds, Apple Watch, Offline Mode)"/>
          <Btn primary onClick={addFeature} disabled={!newFeature.trim()}><Plus size={14}/> Add</Btn>
        </div>
        {featureMatrix?.features?.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${C.border}`, color: C.dim, fontSize: 11 }}>Feature</th>
                <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}`, color: C.green, fontSize: 11, textAlign: "center" }}>You</th>
                {comps.slice(0, 6).map((c, i) => <th key={i} style={{ padding: "8px 6px", borderBottom: `1px solid ${C.border}`, color: C.dim, fontSize: 10, textAlign: "center", maxWidth: 80 }}>{(c.competitor_name || "").substring(0, 10)}</th>)}
                <th style={{ width: 30 }}/>
              </tr></thead>
              <tbody>{featureMatrix.features.map((f, fi) => (
                <tr key={fi} style={{ borderBottom: `1px solid ${C.border}08` }}>
                  <td style={{ padding: "8px 10px", color: C.text, fontWeight: 500 }}>{f.name}</td>
                  <td style={{ textAlign: "center", cursor: "pointer" }} onClick={() => toggleYourFeature(fi)}>
                    <span style={{ fontSize: 16 }}>{f.yours ? "✅" : "—"}</span>
                  </td>
                  {comps.slice(0, 6).map((c, ci) => (
                    <td key={ci} style={{ textAlign: "center", cursor: "pointer" }} onClick={() => toggleFeature(fi, c.competitor_store_id)}>
                      <span style={{ fontSize: 16 }}>{f.competitors?.[c.competitor_store_id] ? "✅" : "—"}</span>
                    </td>
                  ))}
                  <td><button onClick={() => removeFeature(fi)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 11 }}>✕</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {(!featureMatrix?.features?.length) && <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 16 }}>Add features above to build your competitive matrix</div>}
      </Card>

      {/* ═══ SYNC + COUNT ═══ */}
      <div style={{ display: "flex", gap: 8, margin: "20px 0 12px", alignItems: "center", justifyContent: "space-between" }}>
        <SectionTitle style={{ margin: 0 }}>Competitors</SectionTitle>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: C.dim }}>{comps.length} tracked</span>
          <Btn onClick={syncAll} disabled={syncing} small><RefreshCw size={12} style={syncing ? { animation: "spin 1s linear infinite" } : {}}/> {syncing ? "Syncing..." : "Sync All"}</Btn>
        </div>
      </div>

      {/* ═══ COMPETITOR CARDS ═══ */}
      {comps.map((c, i) => {
        const hasData = c.avg_rating != null && c.avg_rating > 0;
        const negKw = (c.recent_negative_keywords || []).filter(Boolean);
        const isExpanded = expandedComp === c.competitor_store_id;
        const trendData = (trends[c.competitor_store_id] || []).map(t => ({ date: t.date?.substring(5,10), rating: t.avg_rating, reviews: t.review_count_7d || 0 }));
        const sentData = sentiments[c.competitor_store_id] || {};
        const totalSent = Object.values(sentData).reduce((s, v) => s + v, 0);
        const daysSinceUpdate = c.latest_release_date ? Math.floor((Date.now() - new Date(c.latest_release_date).getTime()) / 86400000) : null;
        const fileSizeMB = c.file_size_bytes ? (c.file_size_bytes / 1048576).toFixed(0) : null;

        return (
          <Card key={i} onClick={() => setExpandedComp(isExpanded ? null : c.competitor_store_id)}
            style={{ marginBottom: 8, cursor: "pointer", transition: "border-color 0.2s", borderColor: isExpanded ? C.accent : C.border }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {c.icon_url ? <img src={c.icon_url} alt="" style={{ width: 48, height: 48, borderRadius: 12 }}/> :
                <div style={{ width: 48, height: 48, borderRadius: 12, background: C.accent + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: C.accent, flexShrink: 0 }}>{(c.competitor_name || "?")[0].toUpperCase()}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{c.competitor_name || `App #${c.competitor_store_id}`}</div>
                  {daysSinceUpdate !== null && daysSinceUpdate <= 7 && <Badge color={C.green}>Just Updated</Badge>}
                </div>
                {hasData ? (
                  <div style={{ display: "flex", gap: 10, fontSize: 12, color: C.dim, marginTop: 3, flexWrap: "wrap" }}>
                    <span style={{ color: C.yellow, fontWeight: 700 }}>{c.avg_rating.toFixed(1)}★</span>
                    <span>{(c.total_ratings||0).toLocaleString()} ratings</span>
                    {c.price && <span>{c.price}</span>}
                    {c.latest_version && <span>v{c.latest_version}</span>}
                    {fileSizeMB && <span>{fileSizeMB}MB</span>}
                    {daysSinceUpdate !== null && <span style={{ color: daysSinceUpdate > 60 ? C.red : daysSinceUpdate > 30 ? C.yellow : C.dim }}>Updated {daysSinceUpdate}d ago</span>}
                  </div>
                ) : <div style={{ fontSize: 12, color: C.dim, marginTop: 3 }}>No data — click "Sync All"</div>}
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                {c.store_url && <a href={c.store_url} target="_blank" rel="noopener" style={{ color: C.accent, fontSize: 11, textDecoration: "none" }}>Store ↗</a>}
                <button onClick={() => removeComp(c.competitor_store_id)} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", padding: 4 }}><Trash2 size={14}/></button>
              </div>
            </div>
            {isExpanded && hasData && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {trendData.length > 1 && <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, marginBottom: 8, textTransform: "uppercase" }}>Rating Trend (30d)</div>
                    <ResponsiveContainer width="100%" height={120}>
                      <LineChart data={trendData}><XAxis dataKey="date" tick={{ fill: C.dim, fontSize: 9 }} axisLine={false} tickLine={false}/><YAxis domain={[1,5]} hide/><Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} labelStyle={{ color: C.text }}/><Line type="monotone" dataKey="rating" stroke={C.yellow} strokeWidth={2} dot={false}/></LineChart>
                    </ResponsiveContainer>
                  </div>}
                  {totalSent > 0 && <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, marginBottom: 8, textTransform: "uppercase" }}>Review Sentiment</div>
                    {[["positive", C.green], ["neutral", C.yellow], ["negative", C.red]].map(([key, color]) => {
                      const val = sentData[key] || 0; const pct = totalSent > 0 ? (val / totalSent * 100) : 0;
                      return <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 60, fontSize: 11, color: C.dim, textTransform: "capitalize" }}>{key}</div>
                        <div style={{ flex: 1, height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}><div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4 }}/></div>
                        <div style={{ width: 40, fontSize: 11, color: C.dim, textAlign: "right" }}>{Math.round(pct)}%</div>
                      </div>;
                    })}
                    <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>{totalSent} reviews</div>
                  </div>}
                </div>
                {negKw.length > 0 && <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.red, marginBottom: 6, textTransform: "uppercase" }}>Pain Points</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{negKw.slice(0, 10).map((kw, j) => <span key={j} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: C.red + "12", color: C.red }}>{typeof kw === "string" ? kw.split(":")[0] : kw}</span>)}</div>
                </div>}
                {c.release_notes && <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, marginBottom: 4, textTransform: "uppercase" }}>Latest Update{c.latest_version ? ` (v${c.latest_version})` : ""}</div>
                  <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.6, background: C.bg, padding: 10, borderRadius: 8, maxHeight: 120, overflowY: "auto" }}>{c.release_notes}</div>
                </div>}
              </div>
            )}
            {hasData && !isExpanded && <div style={{ fontSize: 10, color: C.muted, marginTop: 8, textAlign: "center" }}>Click to expand</div>}
          </Card>
        );
      })}
    </>)}

    {comps.length === 0 && (
      <Card style={{ textAlign: "center", padding: 48 }}>
        <Crosshair size={36} color={C.dim} style={{ marginBottom: 12 }}/>
        <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 8 }}>No Competitors Tracked</div>
        <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.6, maxWidth: 420, margin: "0 auto" }}>Search for apps above to start tracking. You'll get rating comparisons, review sentiment, feature gaps, keyword analysis, pricing intelligence, update tracking, and more.</div>
      </Card>
    )}
  </>);
}

function SocialPage({ app }) {
  const [tab, setTab] = useState("connections");
  const tabs = [
    { id: "connections", label: "🔌 Connections" },
    { id: "studio", label: "Content Studio" }, { id: "repurpose", label: "Repurpose" },
    { id: "reviews", label: "Reviews → Social" }, { id: "trends", label: "Trends" },
    { id: "challenges", label: "Challenges" }, { id: "calendar", label: "Calendar" },
    { id: "proof", label: "Social Proof" }, { id: "influencers", label: "Influencers" },
    { id: "attribution", label: "Attribution" }, { id: "crosspromo", label: "Cross-Promo" },
    { id: "hashtags", label: "Hashtags" }, { id: "ugc", label: "UGC Campaigns" },
    { id: "biolink", label: "Bio Link" }, { id: "templates", label: "Templates" },
    { id: "brandvoice", label: "Brand Voice" }, { id: "spy", label: "Competitor Spy" },
    { id: "leadmagnets", label: "Lead Magnets" }, { id: "published", label: "Published" }
  ];

  return (<>
    <div style={{ display: "flex", gap: 4, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
      {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 16px", border: `1px solid ${tab === t.id ? C.accent : C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", background: tab === t.id ? C.accentDim : "transparent", color: tab === t.id ? C.accent : C.dim, whiteSpace: "nowrap" }}>{t.label}</button>)}
    </div>
    {tab === "connections" && <SocialConnections app={app}/>}
    {tab === "studio" && <SocialStudio app={app}/>}
    {tab === "repurpose" && <SocialRepurpose app={app}/>}
    {tab === "reviews" && <SocialReviews app={app}/>}
    {tab === "trends" && <SocialTrends app={app}/>}
    {tab === "challenges" && <SocialChallenges app={app}/>}
    {tab === "calendar" && <SocialCalendar app={app}/>}
    {tab === "proof" && <SocialProof app={app}/>}
    {tab === "influencers" && <SocialInfluencers app={app}/>}
    {tab === "attribution" && <SocialAttribution app={app}/>}
    {tab === "crosspromo" && <SocialCrossPromo app={app}/>}
    {tab === "hashtags" && <SocialHashtags app={app}/>}
    {tab === "ugc" && <SocialUGC app={app}/>}
    {tab === "biolink" && <SocialBioLink app={app}/>}
    {tab === "templates" && <SocialTemplates app={app}/>}
    {tab === "brandvoice" && <SocialBrandVoice app={app}/>}
    {tab === "spy" && <SocialSpy app={app}/>}
    {tab === "leadmagnets" && <SocialLeadMagnets app={app}/>}
    {tab === "published" && <SocialPublished app={app}/>}
  </>);
}

function SocialConnections({ app }) {
  const { data: d, loading, reload } = useFetch(() => api("/v1/social/zernio/accounts"), []);
  const { data: profileData } = useFetch(() => api("/v1/social/zernio/profiles"), []);
  const { data: analyticsData } = useFetch(() => d?.connected ? api("/v1/social/zernio/analytics") : null, [d?.connected]);
  const [connecting, setConnecting] = useState(null);

  const connect = async (platform) => {
    setConnecting(platform);
    try {
      const profiles = profileData?.profiles || [];
      let profileId = profiles[0]?._id;
      if (!profileId) {
        const created = await api("/v1/social/zernio/profiles", { method: "POST", body: { name: "Velkra", description: "Velkra App Empire" } });
        profileId = created.profile?._id;
      }
      const data = await api(`/v1/social/zernio/connect/${platform}?profileId=${profileId}`);
      if (data.authUrl) window.open(data.authUrl, "_blank");
      else alert("Could not get connect URL: " + JSON.stringify(data));
    } catch (e) { alert("Error: " + e.message); }
    setConnecting(null);
  };

  const accounts = d?.accounts || [];
  const analytics = analyticsData?.analytics || [];
  const platforms = ["instagram","tiktok","twitter","linkedin","youtube","threads","facebook","bluesky","reddit","pinterest"];
  const connectedPlatforms = new Set(accounts.map(a => a.platform));
  const platformEmoji = { instagram: "📸", tiktok: "🎵", twitter: "🐦", linkedin: "💼", youtube: "📺", threads: "🧵", facebook: "👤", bluesky: "🦋", reddit: "🔴", pinterest: "📌" };

  if (!d?.connected) return (
    <Card style={{ textAlign: "center", padding: 32 }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔌</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 8 }}>Connect Social Media Platforms</div>
      <div style={{ fontSize: 13, color: C.dim, marginBottom: 16, lineHeight: 1.6 }}>
        To publish posts and pull real analytics, add your Zernio API key to Railway:<br/>
        <code style={{ fontSize: 12, color: C.accent, display: "inline-block", marginTop: 8, padding: "6px 12px", background: C.bg, borderRadius: 6 }}>ZERNIO_API_KEY=sk_your_key_here</code>
      </div>
      <div style={{ fontSize: 12, color: C.dim }}>Sign up free at <a href="https://zernio.com/signup" target="_blank" rel="noopener" style={{ color: C.accent }}>zernio.com</a> (20 posts/month free)</div>
    </Card>
  );

  return (<>
    <SectionTitle>Connected Accounts</SectionTitle>
    {accounts.length > 0 && <Grid cols={3}>
      {accounts.map((acc, i) => {
        const stats = analytics.find(a => a.accountId === acc._id);
        return <Card key={i} style={{ borderLeft: `3px solid ${C.green}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 20 }}>{platformEmoji[acc.platform] || "📱"}</span>
            <div><div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{acc.name || acc.username || acc.platform}</div>
              <div style={{ fontSize: 11, color: C.dim }}>{acc.platform} · Connected ✓</div></div>
          </div>
          {stats && !stats.error && <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.dim, marginTop: 4 }}>
            {stats.followers != null && <span>{parseInt(stats.followers).toLocaleString()} followers</span>}
            {stats.engagement != null && <span>{stats.engagement}% engagement</span>}
            {stats.posts != null && <span>{stats.posts} posts</span>}
          </div>}
        </Card>;
      })}
    </Grid>}
    <SectionTitle>Connect More Platforms</SectionTitle>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {platforms.map(p => (
        <button key={p} onClick={() => !connectedPlatforms.has(p) && connect(p)} disabled={connecting === p}
          style={{ padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: connectedPlatforms.has(p) ? "default" : "pointer",
            border: `1px solid ${connectedPlatforms.has(p) ? C.green + "40" : C.border}`,
            background: connectedPlatforms.has(p) ? C.green + "12" : "transparent",
            color: connectedPlatforms.has(p) ? C.green : C.text, opacity: connecting === p ? 0.5 : 1 }}>
          {platformEmoji[p]} {p}{connectedPlatforms.has(p) ? " ✓" : ""}
        </button>
      ))}
    </div>
    <div style={{ fontSize: 11, color: C.muted, marginTop: 12 }}>Powered by Zernio — 13 platforms, one API. <a href="https://zernio.com" target="_blank" rel="noopener" style={{ color: C.accent }}>Manage accounts →</a></div>
  </>);
}

function SocialPublished({ app }) {
  const { data: d, loading } = useFetch(() => api("/v1/social/zernio/posts"), []);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [postAnalytics, setPostAnalytics] = useState(null);

  const fetchAnalytics = async (postId) => {
    setAnalyticsFor(postId); setPostAnalytics(null);
    try { const r = await api(`/v1/social/zernio/posts/${postId}/analytics`); setPostAnalytics(r); } catch (e) { setPostAnalytics({ error: e.message }); }
  };

  if (loading) return <Loading/>;
  const posts = d?.posts || [];
  const platformEmoji = { instagram: "📸", tiktok: "🎵", twitter: "🐦", linkedin: "💼", youtube: "📺", threads: "🧵", facebook: "👤", bluesky: "🦋" };

  return (<>
    <SectionTitle>Published & Scheduled Posts</SectionTitle>
    {posts.length > 0 ? posts.map((p, i) => (
      <Card key={i} style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 6, flexWrap: "wrap" }}>
              {(p.platforms || []).map((pl, j) => <Badge key={j} color={p.status === "published" ? C.green : C.yellow}>{platformEmoji[pl.platform] || ""} {pl.platform}</Badge>)}
              <Badge color={p.status === "published" ? C.green : p.status === "scheduled" ? C.yellow : C.dim}>{p.status}</Badge>
            </div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{(p.content || "").substring(0, 200)}{(p.content || "").length > 200 ? "…" : ""}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {p.publishedAt ? `Published ${new Date(p.publishedAt).toLocaleDateString()}` : p.scheduledFor ? `Scheduled for ${new Date(p.scheduledFor).toLocaleDateString()}` : ""}
            </div>
          </div>
          {p.status === "published" && p._id && (
            <Btn small onClick={() => fetchAnalytics(p._id)} disabled={analyticsFor === p._id}>
              {analyticsFor === p._id ? "..." : "📊 Stats"}
            </Btn>
          )}
        </div>
        {analyticsFor === p._id && postAnalytics && !postAnalytics.error && (
          <div style={{ marginTop: 10, padding: 10, background: C.bg, borderRadius: 8, display: "flex", gap: 16, fontSize: 12 }}>
            {postAnalytics.likes != null && <div><span style={{ fontWeight: 700, color: C.accent }}>{postAnalytics.likes}</span> <span style={{ color: C.dim }}>likes</span></div>}
            {postAnalytics.comments != null && <div><span style={{ fontWeight: 700, color: C.accent }}>{postAnalytics.comments}</span> <span style={{ color: C.dim }}>comments</span></div>}
            {postAnalytics.shares != null && <div><span style={{ fontWeight: 700, color: C.accent }}>{postAnalytics.shares}</span> <span style={{ color: C.dim }}>shares</span></div>}
            {postAnalytics.impressions != null && <div><span style={{ fontWeight: 700, color: C.accent }}>{parseInt(postAnalytics.impressions).toLocaleString()}</span> <span style={{ color: C.dim }}>impressions</span></div>}
            {postAnalytics.reach != null && <div><span style={{ fontWeight: 700, color: C.accent }}>{parseInt(postAnalytics.reach).toLocaleString()}</span> <span style={{ color: C.dim }}>reach</span></div>}
          </div>
        )}
        {analyticsFor === p._id && postAnalytics?.error && <div style={{ marginTop: 8, fontSize: 11, color: C.red }}>{postAnalytics.error}</div>}
      </Card>
    )) : <Card style={{ textAlign: "center", padding: 32 }}>
      <div style={{ color: C.dim }}>No posts published yet. Generate content in Content Studio, then publish it here.</div>
    </Card>}
  </>);
}

function SocialStudio({ app }) {
  const [platform, setPlatform] = useState("instagram");
  const [tone, setTone] = useState("inspiring");
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [ideas, setIdeas] = useState(null);
  const [genIdeas, setGenIdeas] = useState(false);
  const { data: saved, reload } = useFetch(() => api(`/v1/social/content/${app}`), [app]);
  const { data: zAccounts } = useFetch(() => api("/v1/social/zernio/accounts"), []);
  const [publishing, setPublishing] = useState(false);

  const publish = async (content) => {
    if (!zAccounts?.connected || !zAccounts?.accounts?.length) { alert("Connect social accounts first (Connections tab)"); return; }
    const accs = zAccounts.accounts.filter(a => a.platform === platform);
    if (!accs.length) { alert(`No ${platform} account connected. Connect it in the Connections tab.`); return; }
    setPublishing(true);
    try {
      await api("/v1/social/zernio/publish", { method: "POST", body: { content, platforms: accs.map(a => ({ platform: a.platform, accountId: a._id })) } });
      alert("Published to " + platform + "!");
    } catch (e) { alert("Publish failed: " + e.message); }
    setPublishing(false);
  };

  const generate = async () => {
    setGenerating(true); setResult(null);
    try { const r = await api("/v1/social/generate", { method: "POST", body: { appId: app, platform, tone, topic } }); setResult(r); reload(); } catch (e) { setResult({ error: e.message }); }
    setGenerating(false);
  };
  const generateIdeas = async () => {
    setGenIdeas(true); setIdeas(null);
    try { const r = await api("/v1/social/ideas", { method: "POST", body: { appId: app, weeks: 4 } }); setIdeas(r.ideas); } catch (e) { setIdeas([]); }
    setGenIdeas(false);
  };

  return (<>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 12 }}>AI Content Generator</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div><label style={lbl()}>Platform</label><select value={platform} onChange={e => setPlatform(e.target.value)} style={inp()}><option value="instagram">Instagram</option><option value="tiktok">TikTok</option><option value="twitter">Twitter/X</option><option value="linkedin">LinkedIn</option><option value="threads">Threads</option></select></div>
        <div><label style={lbl()}>Tone</label><select value={tone} onChange={e => setTone(e.target.value)} style={inp()}><option value="inspiring">Inspiring</option><option value="data-driven">Data-Driven</option><option value="casual">Casual</option><option value="professional">Professional</option><option value="humorous">Humorous</option></select></div>
        <div><label style={lbl()}>Topic (optional)</label><input value={topic} onChange={e => setTopic(e.target.value)} style={inp()} placeholder="e.g. morning routine"/></div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn primary onClick={generate} disabled={generating}>{generating ? "Generating..." : "Generate Post"}</Btn>
        <Btn onClick={generateIdeas} disabled={genIdeas}>{genIdeas ? "Generating..." : "Generate 4-Week Content Plan"}</Btn>
      </div>
    </Card>
    {result && <Card style={{ marginBottom: 16 }}>
      {result.error ? <div style={{ color: C.red }}>{result.error}</div> :
      <div><div style={{ display: "flex", gap: 6, marginBottom: 8 }}><Badge color={C.accent}>{result.platform}</Badge></div>
        <pre style={{ whiteSpace: "pre-wrap", fontSize: 13, color: C.text, lineHeight: 1.6, margin: 0 }}>{result.content}</pre>
        <div style={{ display: "flex", gap: 8, marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
          <Btn primary small onClick={() => publish(result.content)} disabled={publishing}>{publishing ? "Publishing..." : `🚀 Publish to ${platform}`}</Btn>
          <Btn small onClick={() => navigator.clipboard?.writeText(result.content)}>📋 Copy</Btn>
        </div></div>}
    </Card>}
    {ideas && ideas.length > 0 && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 12 }}>4-Week Content Plan</div>
      {ideas.map((week, wi) => (
        <div key={wi} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 8 }}>Week {week.week}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(week.posts || []).map((p, pi) => (
              <div key={pi} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", background: C.bg, borderRadius: 8 }}>
                <Badge color={p.platform === "tiktok" ? C.cyan : p.platform === "twitter" ? C.blue : p.platform === "linkedin" ? C.blue : C.accent}>{p.platform}</Badge>
                <span style={{ fontSize: 12, color: C.text, flex: 1 }}>{p.idea}</span>
                {p.type && <span style={{ fontSize: 10, color: C.dim }}>{p.type}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Card>}
    {saved?.content?.length > 0 && <><SectionTitle>Saved Content</SectionTitle>
      {saved.content.slice(0, 10).map((c, i) => (
        <Card key={i} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ display: "flex", gap: 6 }}><Badge>{c.platform}</Badge><Badge color={C.dim}>{c.content_type}</Badge></div>
            <button onClick={async () => { try { await api(`/v1/social/content/${c.id}`, { method: "DELETE" }); reload(); } catch(e) { alert("Delete failed: " + e.message); } }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer" }}><Trash2 size={12}/></button>
          </div>
          <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5, maxHeight: 80, overflow: "hidden" }}>{c.body?.substring(0, 300)}</div>
        </Card>
      ))}
    </>}
  </>);
}

function SocialReviews({ app }) {
  const { data: d } = useFetch(() => api(`/v1/social/best-reviews/${app}`), [app]);
  const [converting, setConverting] = useState(null);
  const [result, setResult] = useState(null);
  const [platform, setPlatform] = useState("instagram");

  const convert = async (reviewId) => {
    setConverting(reviewId); setResult(null);
    try { const r = await api("/v1/social/review-to-post", { method: "POST", body: { reviewId, platform, appId: app } }); setResult(r); } catch (e) { setResult({ error: e.message }); }
    setConverting(null);
  };

  const reviews = d?.reviews || [];
  return (<>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Review → Social Post Pipeline</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Your best reviews are your best marketing copy. Select one to transform it into a platform-optimized post.</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <label style={lbl()}>Target Platform</label>
        {["instagram", "twitter", "tiktok", "linkedin"].map(p => <button key={p} onClick={() => setPlatform(p)} style={{ padding: "4px 12px", border: `1px solid ${platform === p ? C.accent : C.border}`, borderRadius: 6, fontSize: 11, cursor: "pointer", background: platform === p ? C.accentDim : "transparent", color: platform === p ? C.accent : C.dim, textTransform: "capitalize" }}>{p}</button>)}
      </div>
    </Card>
    {result && <Card style={{ marginBottom: 16, borderLeft: `3px solid ${C.green}` }}>
      {result.error ? <div style={{ color: C.red }}>{result.error}</div> :
      <pre style={{ whiteSpace: "pre-wrap", fontSize: 13, color: C.text, lineHeight: 1.6, margin: 0 }}>{result.content}</pre>}
    </Card>}
    {reviews.length > 0 ? reviews.map((r, i) => (
      <Card key={i} style={{ marginBottom: 6 }} onClick={() => convert(r.id)}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <span style={{ color: C.yellow, fontWeight: 700, fontSize: 14 }}>{r.rating}★</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{r.title || "Untitled"}</div>
            <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5 }}>{(r.body || "").substring(0, 150)}{(r.body || "").length > 150 ? "…" : ""}</div>
          </div>
          <Btn primary small disabled={converting === r.id}>{converting === r.id ? "..." : "Convert"}</Btn>
        </div>
      </Card>
    )) : <Empty msg="No reviews with 4+ stars yet"/>}
  </>);
}

function SocialChallenges({ app }) {
  const { data: d, reload } = useFetch(() => api(`/v1/social/challenges/${app}`), [app]);
  const [show, setShow] = useState(false);
  const [name, setName] = useState(""); const [hashtag, setHashtag] = useState(""); const [days, setDays] = useState(7); const [desc, setDesc] = useState(""); const [reward, setReward] = useState("");
  const create = async () => {
    try { await api("/v1/social/challenges", { method: "POST", body: { appId: app, name, hashtag, durationDays: days, description: desc, reward } }); setShow(false); setName(""); reload(); } catch (e) { alert(e.message); }
  };
  const challenges = d?.challenges || [];
  const templates = [
    { name: "7-Day Gratitude Challenge", hashtag: "#7DaysOfGratitude", days: 7, desc: "Write one thing you're grateful for each day", app: "radiant" },
    { name: "Meal Prep Week", hashtag: "#MealPrepWithMise", days: 7, desc: "Plan and prep all meals for 7 days", app: "mise" },
    { name: "Sleep Hygiene Reset", hashtag: "#DriftSleepReset", days: 14, desc: "Follow sleep hygiene best practices for 14 days", app: "drift" },
    { name: "30-Day Journal Streak", hashtag: "#30DayJournal", days: 30, desc: "Journal every day for 30 days", app: "radiant" },
  ].filter(t => t.app === app || !app);

  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}><Btn primary onClick={() => setShow(!show)}><Plus size={14}/> Create Challenge</Btn></div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={lbl()}>Challenge Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="7-Day Gratitude Challenge"/></div>
        <div><label style={lbl()}>Hashtag</label><input value={hashtag} onChange={e => setHashtag(e.target.value)} style={inp()} placeholder="#7DaysOfGratitude"/></div>
        <div><label style={lbl()}>Duration (days)</label><input type="number" value={days} onChange={e => setDays(+e.target.value)} style={inp()} min={1} max={90}/></div>
        <div><label style={lbl()}>Reward</label><input value={reward} onChange={e => setReward(e.target.value)} style={inp()} placeholder="7-day premium trial"/></div>
      </div>
      <div><label style={lbl()}>Description</label><input value={desc} onChange={e => setDesc(e.target.value)} style={inp()} placeholder="What participants do each day"/></div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!name}><Check size={14}/> Create</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    {!show && templates.length > 0 && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>Quick-start templates:</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {templates.map((t, i) => <button key={i} onClick={() => { setName(t.name); setHashtag(t.hashtag); setDays(t.days); setDesc(t.desc); setShow(true); }}
          style={{ padding: "8px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 12, cursor: "pointer" }}>{t.name}</button>)}
      </div>
    </Card>}
    {challenges.length > 0 ? challenges.map((c, i) => (
      <Card key={i} style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 24 }}>🏆</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{c.name}</div>
            <div style={{ fontSize: 12, color: C.dim }}>{c.duration_days} days · {c.hashtag} · {c.is_active ? "Active" : "Ended"}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.accent }}>{c.participants || 0}</div>
            <div style={{ fontSize: 10, color: C.dim }}>participants</div>
          </div>
        </div>
        {c.description && <div style={{ fontSize: 12, color: C.dim, marginTop: 8 }}>{c.description}</div>}
      </Card>
    )) : <Empty msg="No challenges yet — create one or use a template above"/>}
  </>);
}

function SocialCalendar({ app }) {
  const { data: d, reload } = useFetch(() => api("/v1/social/calendar"), []);
  const [show, setShow] = useState(false);
  const [date, setDate] = useState(""); const [time, setTime] = useState("10:00"); const [platform, setPlatform] = useState("instagram"); const [content, setContent] = useState(""); const [utm, setUtm] = useState("");
  const create = async () => {
    try { await api("/v1/social/calendar", { method: "POST", body: { appId: app, platform, scheduledDate: date, scheduledTime: time, content, utmCampaign: utm } }); setShow(false); setDate(""); setContent(""); reload(); } catch (e) { alert(e.message); }
  };
  const entries = d?.entries || [];
  // Group by date
  const byDate = {};
  entries.forEach(e => { const d = e.scheduled_date?.substring(0, 10) || "?"; if (!byDate[d]) byDate[d] = []; byDate[d].push(e); });

  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}><Btn primary onClick={() => setShow(!show)}><Plus size={14}/> Schedule Post</Btn></div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
        <div><label style={lbl()}>Date *</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp()}/></div>
        <div><label style={lbl()}>Time</label><input type="time" value={time} onChange={e => setTime(e.target.value)} style={inp()}/></div>
        <div><label style={lbl()}>Platform</label><select value={platform} onChange={e => setPlatform(e.target.value)} style={inp()}><option>instagram</option><option>tiktok</option><option>twitter</option><option>linkedin</option></select></div>
        <div><label style={lbl()}>UTM Campaign</label><input value={utm} onChange={e => setUtm(e.target.value)} style={inp()} placeholder="spring_launch"/></div>
      </div>
      <div><label style={lbl()}>Content</label><textarea value={content} onChange={e => setContent(e.target.value)} style={{ ...inp(), height: 80, resize: "vertical" }} placeholder="Post content..."/></div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!date}><Check size={14}/> Schedule</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    {Object.keys(byDate).length > 0 ? Object.entries(byDate).sort(([a],[b]) => a.localeCompare(b)).map(([date, posts]) => (
      <div key={date} style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dim, marginBottom: 6 }}>{new Date(date + "T12:00").toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
        {posts.map((p, i) => (
          <Card key={i} style={{ marginBottom: 4, padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Badge color={p.platform === "tiktok" ? C.cyan : p.platform === "twitter" ? C.blue : C.accent}>{p.platform}</Badge>
              <span style={{ fontSize: 11, color: C.dim }}>{p.scheduled_time?.substring(0, 5)}</span>
              <span style={{ fontSize: 12, color: C.text, flex: 1 }}>{(p.content || "").substring(0, 80)}{(p.content || "").length > 80 ? "…" : ""}</span>
              <button onClick={async () => { try { await api(`/v1/social/calendar/${p.id}`, { method: "DELETE" }); reload(); } catch(e) { alert("Delete failed: " + e.message); } }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer" }}><Trash2 size={12}/></button>
            </div>
          </Card>
        ))}
      </div>
    )) : <Empty msg="No posts scheduled — add one above"/>}
  </>);
}

function SocialProof({ app }) {
  const { data: d } = useFetch(() => api(`/v1/social/proof/${app}`), [app]);
  const { data: ach } = useFetch(() => api(`/v1/social/achievements/${app}`), [app]);
  const widgets = d?.widgets || [];
  const milestones = ach?.milestones || [];
  const cards = ach?.cardTemplates || [];

  return (<>
    <SectionTitle>Live Social Proof Widgets</SectionTitle>
    <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Copy these into your website, App Store screenshots, and onboarding screens.</div>
    <Grid cols={3}>
      {widgets.map((w, i) => (
        <Card key={i} style={{ textAlign: "center", cursor: "pointer" }} onClick={() => navigator.clipboard?.writeText(w.text)}>
          <div style={{ fontSize: 28, fontWeight: 700, color: C.accent, fontVariantNumeric: "tabular-nums" }}>{typeof w.value === "number" ? w.value.toLocaleString() : w.value}</div>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>{w.label}</div>
          <div style={{ fontSize: 11, color: C.text, marginTop: 6, padding: "6px 10px", background: C.bg, borderRadius: 6 }}>{w.text}</div>
          <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>Click to copy</div>
        </Card>
      ))}
    </Grid>
    {cards.length > 0 && <><SectionTitle>Achievement Card Templates</SectionTitle>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Users who hit these milestones get a shareable card in the app.</div>
      <Grid cols={4}>
        {cards.map((c, i) => (
          <Card key={i} style={{ textAlign: "center", background: C.bg }}>
            <div style={{ fontSize: 32, marginBottom: 4 }}>{c.icon}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{c.title}</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{c.subtitle}</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>{c.threshold} {c.metric}</div>
          </Card>
        ))}
      </Grid>
    </>}
    {milestones.length > 0 && <><SectionTitle>Community Milestones</SectionTitle>
      {milestones.map((m, i) => (
        <Card key={i} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{m.label}</div>
              <div style={{ fontSize: 12, color: C.dim }}>{m.shareText}</div></div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.accent }}>{(m.value || 0).toLocaleString()}</div>
          </div>
        </Card>
      ))}
    </>}
  </>);
}

function SocialInfluencers({ app }) {
  const { data: d, reload } = useFetch(() => api("/v1/social/influencers"), []);
  const [show, setShow] = useState(false);
  const [name, setName] = useState(""); const [handle, setHandle] = useState(""); const [platform, setPlatform] = useState("instagram"); const [followers, setFollowers] = useState(""); const [niche, setNiche] = useState(""); const [cost, setCost] = useState("");
  const create = async () => {
    try { await api("/v1/social/influencers", { method: "POST", body: { appId: app, name, handle, platform, followers: parseInt(followers) || 0, niche, costPerPost: parseFloat(cost) || 0 } }); setShow(false); setName(""); setHandle(""); reload(); } catch (e) { alert(e.message); }
  };
  const influencers = d?.influencers || [];
  const totalReach = influencers.reduce((s, i) => s + (i.followers || 0), 0);
  const avgCost = influencers.filter(i => i.cost_per_post > 0).reduce((s, i, _, a) => s + i.cost_per_post / a.length, 0);

  return (<>
    {influencers.length > 0 && <Grid cols={3}>
      <Stat label="Total Reach" value={totalReach.toLocaleString()} sub="combined followers" color={C.accent}/>
      <Stat label="Influencers" value={influencers.length} sub="tracked"/>
      <Stat label="Avg Cost/Post" value={avgCost > 0 ? `$${avgCost.toFixed(0)}` : "—"} sub="per collaboration" color={C.green}/>
    </Grid>}
    <div style={{ display: "flex", gap: 8, marginBottom: 16, marginTop: 12 }}><Btn primary onClick={() => setShow(!show)}><Plus size={14}/> Add Influencer</Btn></div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <div><label style={lbl()}>Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Sarah Chen"/></div>
        <div><label style={lbl()}>Handle</label><input value={handle} onChange={e => setHandle(e.target.value)} style={inp()} placeholder="@sarahwellness"/></div>
        <div><label style={lbl()}>Platform *</label><select value={platform} onChange={e => setPlatform(e.target.value)} style={inp()}><option>instagram</option><option>tiktok</option><option>youtube</option><option>twitter</option></select></div>
        <div><label style={lbl()}>Followers</label><input type="number" value={followers} onChange={e => setFollowers(e.target.value)} style={inp()} placeholder="15000"/></div>
        <div><label style={lbl()}>Niche</label><input value={niche} onChange={e => setNiche(e.target.value)} style={inp()} placeholder="mindfulness, journaling"/></div>
        <div><label style={lbl()}>Cost/Post ($)</label><input type="number" value={cost} onChange={e => setCost(e.target.value)} style={inp()} placeholder="200"/></div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}><Btn primary onClick={create} disabled={!name}><Check size={14}/> Add</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    {influencers.length > 0 ? influencers.map((inf, i) => (
      <Card key={i} style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 20, background: C.accent + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: C.accent }}>{(inf.name || "?")[0]}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{inf.name} <span style={{ fontSize: 11, color: C.dim }}>{inf.handle}</span></div>
            <div style={{ display: "flex", gap: 8, fontSize: 11, color: C.dim, marginTop: 2 }}>
              <Badge>{inf.platform}</Badge>
              <span>{(inf.followers || 0).toLocaleString()} followers</span>
              {inf.engagement_rate > 0 && <span>{inf.engagement_rate}% engagement</span>}
              {inf.niche && <span>{inf.niche}</span>}
              {inf.cost_per_post > 0 && <span style={{ color: C.green }}>${inf.cost_per_post}/post</span>}
            </div>
          </div>
          <Badge color={inf.status === "partnered" ? C.green : inf.status === "contacted" ? C.yellow : C.dim}>{inf.status || "watching"}</Badge>
          <button onClick={async () => { try { await api(`/v1/social/influencers/${inf.id}`, { method: "DELETE" }); reload(); } catch(e) { alert("Delete failed: " + e.message); } }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer" }}><Trash2 size={12}/></button>
        </div>
      </Card>
    )) : <Empty msg="No influencers tracked — add one above"/>}
  </>);
}

function SocialAttribution({ app }) {
  const { data: d } = useFetch(() => api("/v1/social/attribution"), []);
  const campaigns = d?.campaigns || [];
  const byPlatform = d?.byPlatform || [];
  const totals = d?.totals || {};

  return (<>
    <Grid cols={4}>
      <Stat label="Total Clicks" value={parseInt(totals.clicks || 0).toLocaleString()} color={C.blue}/>
      <Stat label="Installs" value={parseInt(totals.installs || 0).toLocaleString()} color={C.green}/>
      <Stat label="Subscriptions" value={parseInt(totals.subs || 0).toLocaleString()} color={C.accent}/>
      <Stat label="Revenue" value={`$${parseFloat(totals.revenue || 0).toFixed(2)}`} color={C.green}/>
    </Grid>
    {byPlatform.length > 0 && <><SectionTitle>By Platform</SectionTitle>
      <Card><Table cols={["Platform", "Clicks", "Installs", "Subscriptions", "Revenue", "Conv Rate"]}
        rows={byPlatform.map(p => [p.platform, p.clicks, p.installs, p.subs, `$${parseFloat(p.revenue || 0).toFixed(2)}`, p.clicks > 0 ? `${((p.installs / p.clicks) * 100).toFixed(1)}%` : "—"])}/>
      </Card>
    </>}
    {campaigns.length > 0 && <><SectionTitle>Campaigns</SectionTitle>
      <Card><Table exportName="social-attribution" cols={["Source", "Campaign", "Platform", "Clicks", "Installs", "Subs", "Revenue"]}
        rows={campaigns.map(c => [c.utm_source, c.utm_campaign || "—", c.platform || "—", c.clicks, c.installs, c.subs, `$${parseFloat(c.revenue || 0).toFixed(2)}`])}/>
      </Card>
    </>}
    {campaigns.length === 0 && byPlatform.length === 0 && <Card style={{ textAlign: "center", padding: 32 }}>
      <div style={{ fontSize: 14, color: C.dim, lineHeight: 1.6 }}>No attribution data yet. Use UTM parameters in your social links:<br/>
        <code style={{ fontSize: 12, color: C.accent }}>velkra.app/d/CODE?utm_source=instagram&utm_campaign=spring_launch</code></div>
    </Card>}
  </>);
}

function SocialRepurpose({ app }) {
  const [content, setContent] = useState("");
  const [source, setSource] = useState("instagram");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const repurpose = async () => {
    setLoading(true); setResult(null);
    try { const r = await api("/v1/social/repurpose", { method: "POST", body: { appId: app, content, sourcePlatform: source } }); setResult(r.versions); } catch (e) { setResult({ error: e.message }); }
    setLoading(false);
  };
  const platformEmoji = { instagram: "📸", tiktok: "🎵", twitter: "🐦", linkedin: "💼", threads: "🧵" };
  return (<>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Content Repurposing Engine</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Paste ONE piece of content → get 5 platform-optimized versions instantly.</div>
      <div style={{ marginBottom: 10 }}><label style={lbl()}>Source Platform</label><select value={source} onChange={e => setSource(e.target.value)} style={inp()}><option value="instagram">Instagram</option><option value="tiktok">TikTok</option><option value="twitter">Twitter/X</option><option value="linkedin">LinkedIn</option><option value="blog">Blog Post</option></select></div>
      <textarea value={content} onChange={e => setContent(e.target.value)} style={{ ...inp(), height: 100, resize: "vertical" }} placeholder="Paste your content here..."/>
      <Btn primary onClick={repurpose} disabled={loading || !content} style={{ marginTop: 8 }}>{loading ? "Repurposing..." : "Repurpose → 5 Platforms"}</Btn>
    </Card>
    {result && !result.error && Object.entries(result).map(([plat, data]) => (
      <Card key={plat} style={{ marginBottom: 8, borderLeft: `3px solid ${C.accent}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><span>{platformEmoji[plat] || "📱"}</span><Badge color={C.accent}>{plat}</Badge></div>
        <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, color: C.text, lineHeight: 1.6, margin: 0 }}>{typeof data === "string" ? data : JSON.stringify(data, null, 2)}</pre>
      </Card>
    ))}
    {result?.error && <Card><div style={{ color: C.red }}>{result.error}</div></Card>}
  </>);
}

function SocialTrends({ app }) {
  const [trend, setTrend] = useState(""); const [platform, setPlatform] = useState("instagram");
  const [loading, setLoading] = useState(false); const [result, setResult] = useState(null);
  const generate = async () => {
    setLoading(true); setResult(null);
    try { const r = await api("/v1/social/trend-content", { method: "POST", body: { appId: app, trend, platform } }); setResult(r.content); } catch (e) { setResult("Error: " + e.message); }
    setLoading(false);
  };
  const quickTrends = ["Mental Health Awareness", "World Sleep Day", "New Year Wellness Goals", "Self-Care Sunday", "Meal Prep Monday", "Mindfulness Monday", "Digital Detox", "Gratitude Challenge", "Sleep Hygiene Tips", "Healthy Eating Week"];
  return (<>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Trend Hijacking</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Enter a trending topic → AI generates content that connects your app to the trend within minutes.</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
        <div><label style={lbl()}>Trending Topic *</label><input value={trend} onChange={e => setTrend(e.target.value)} style={inp()} placeholder="e.g. Mental Health Awareness Month"/></div>
        <div><label style={lbl()}>Platform</label><select value={platform} onChange={e => setPlatform(e.target.value)} style={inp()}><option>instagram</option><option>tiktok</option><option>twitter</option><option>linkedin</option></select></div>
      </div>
      <Btn primary onClick={generate} disabled={loading || !trend}>{loading ? "Generating..." : "Generate Trend Content"}</Btn>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 12 }}>
        {quickTrends.map((t, i) => <button key={i} onClick={() => setTrend(t)} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, border: `1px solid ${C.border}`, background: "transparent", color: C.dim, cursor: "pointer" }}>{t}</button>)}
      </div>
    </Card>
    {result && <Card style={{ borderLeft: `3px solid ${C.green}` }}><pre style={{ whiteSpace: "pre-wrap", fontSize: 13, color: C.text, lineHeight: 1.6, margin: 0 }}>{result}</pre></Card>}
  </>);
}

function SocialCrossPromo({ app }) {
  const { data: d, loading } = useFetch(() => api("/v1/social/cross-portfolio"), []);
  if (loading) return <Loading/>;
  const insights = d?.insights || []; const angles = d?.promoAngles || [];
  return (<>
    {insights.length > 0 && <><SectionTitle>Cross-Portfolio Intelligence</SectionTitle>
      <Grid cols={3}>{insights.slice(0, 3).map((ins, i) => <Stat key={i} label={ins.type === "cross_users" ? "Multi-App Users" : ins.apps?.join(" + ") || "Combo"} value={ins.value} sub={ins.text} color={C.accent}/>)}</Grid></>}
    {angles.length > 0 && <><SectionTitle>Cross-Promotion Angles</SectionTitle>
      {angles.map((a, i) => (
        <Card key={i} style={{ marginBottom: 8, borderLeft: `3px solid ${C.green}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Badge color={C.accent}>{a.from}</Badge><span style={{ color: C.dim }}>→</span><Badge color={C.green}>{a.to}</Badge>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>{a.hook}</div>
          <div style={{ fontSize: 11, color: C.dim }}>{a.metric}</div>
        </Card>
      ))}</>}
    {!insights.length && !angles.length && <Card style={{ textAlign: "center", padding: 32 }}><div style={{ color: C.dim }}>Cross-portfolio data will appear as users adopt multiple apps.</div></Card>}
  </>);
}

function SocialHashtags({ app }) {
  const { data: d, reload } = useFetch(() => api(`/v1/social/hashtags/${app}`), [app]);
  const [show, setShow] = useState(false);
  const [name, setName] = useState(""); const [tags, setTags] = useState("");
  const create = async () => {
    try { await api("/v1/social/hashtags", { method: "POST", body: { appId: app, name, hashtags: tags.split(",").map(t => t.trim()).filter(Boolean) } }); setShow(false); setName(""); setTags(""); reload(); } catch (e) { alert(e.message); }
  };
  const sets = d?.sets || [];
  return (<>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Hashtag Performance Lab</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Create hashtag sets, use them in posts, then track which sets drive the most installs.</div>
      <Btn primary onClick={() => setShow(!show)}><Plus size={14}/> New Hashtag Set</Btn>
    </Card>
    {show && <Card style={{ marginBottom: 16 }}>
      <div><label style={lbl()}>Set Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="e.g. Mindfulness Mix A"/></div>
      <div><label style={lbl()}>Hashtags (comma-separated) *</label><input value={tags} onChange={e => setTags(e.target.value)} style={inp()} placeholder="#mindfulness, #journaling, #selfcare, #wellness"/></div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}><Btn primary onClick={create} disabled={!name || !tags}><Check size={14}/> Create</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    {sets.length > 0 ? sets.map((s, i) => (
      <Card key={i} style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{s.name}</div>
          <div style={{ display: "flex", gap: 12, fontSize: 12 }}><span style={{ color: C.blue }}>{s.clicks} clicks</span><span style={{ color: C.green }}>{s.installs} installs</span></div>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{(s.hashtags || []).map((h, j) => <span key={j} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: C.accentDim, color: C.accent }}>{h}</span>)}</div>
      </Card>
    )) : !show && <Empty msg="No hashtag sets yet — create one above"/>}
  </>);
}

function SocialUGC({ app }) {
  const { data: d, reload } = useFetch(() => api(`/v1/social/ugc/${app}`), [app]);
  const [show, setShow] = useState(false);
  const [name, setName] = useState(""); const [hashtag, setHashtag] = useState(""); const [desc, setDesc] = useState(""); const [prize, setPrize] = useState("");
  const create = async () => {
    try { await api("/v1/social/ugc", { method: "POST", body: { appId: app, name, hashtag, description: desc, prize } }); setShow(false); setName(""); reload(); } catch (e) { alert(e.message); }
  };
  const campaigns = d?.campaigns || [];
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}><Btn primary onClick={() => setShow(!show)}><Plus size={14}/> New UGC Campaign</Btn></div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={lbl()}>Campaign Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Share Your Morning Setup"/></div>
        <div><label style={lbl()}>Hashtag *</label><input value={hashtag} onChange={e => setHashtag(e.target.value)} style={inp()} placeholder="#RadiantMornings"/></div>
        <div><label style={lbl()}>Description</label><input value={desc} onChange={e => setDesc(e.target.value)} style={inp()} placeholder="Share a photo of your journal setup"/></div>
        <div><label style={lbl()}>Prize</label><input value={prize} onChange={e => setPrize(e.target.value)} style={inp()} placeholder="Featured on our page + 3 months free"/></div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}><Btn primary onClick={create} disabled={!name || !hashtag}><Check size={14}/> Launch</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    {campaigns.length > 0 ? campaigns.map((c, i) => (
      <Card key={i} style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 24 }}>📸</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{c.name}</div>
            <div style={{ fontSize: 12, color: C.dim }}>{c.hashtag} · {c.status}{c.prize ? ` · Prize: ${c.prize}` : ""}</div>
          </div>
          <div style={{ display: "flex", gap: 16, textAlign: "center" }}>
            <div><div style={{ fontSize: 18, fontWeight: 700, color: C.accent }}>{c.submissions || 0}</div><div style={{ fontSize: 10, color: C.dim }}>submissions</div></div>
            <div><div style={{ fontSize: 18, fontWeight: 700, color: C.green }}>{c.featured || 0}</div><div style={{ fontSize: 10, color: C.dim }}>featured</div></div>
          </div>
        </div>
      </Card>
    )) : !show && <Empty msg="No UGC campaigns yet — launch one above"/>}
  </>);
}

function SocialBioLink({ app }) {
  const { data: d, loading } = useFetch(() => api("/v1/social/bio-link"), []);
  if (loading) return <Loading/>;
  const apps = d?.apps || [];
  return (<>
    <SectionTitle>Bio Link Page Preview</SectionTitle>
    <Card style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>This is your <code style={{ color: C.accent }}>velkra.app/links</code> page — one link for all your social bios, powered by live data.</Card>
    <Card style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", borderRadius: 16, padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 4 }}>✨ Velkra</div>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 20 }}>Your wellness ecosystem</div>
      {apps.map((a, i) => (
        <div key={i} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: 16, marginBottom: 10, textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{a.id === "radiant" ? "📓" : a.id === "mise" ? "🍽️" : "😴"}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>{a.name}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{a.tagline}</div>
            </div>
            <div style={{ fontSize: 13, color: "#fbbf24", fontWeight: 600 }}>{a.rating}★</div>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
            <span>{(a.totalUsers || 0).toLocaleString()} users</span>
            <span>{(a.dau || 0).toLocaleString()} active today</span>
            <span>{(a.reviewCount || 0).toLocaleString()} reviews</span>
          </div>
        </div>
      ))}
      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 8 }}>Live data · Updated in real time</div>
    </Card>
  </>);
}

function SocialTemplates({ app }) {
  const { data: d, reload } = useFetch(() => api("/v1/social/templates"), []);
  const [show, setShow] = useState(false);
  const [cat, setCat] = useState("welcome"); const [trigger, setTrigger] = useState(""); const [response, setResponse] = useState("");
  const create = async () => {
    try { await api("/v1/social/templates", { method: "POST", body: { appId: app, category: cat, triggerPhrase: trigger, response } }); setShow(false); setResponse(""); reload(); } catch (e) { alert(e.message); }
  };
  const seed = async () => { try { await api("/v1/social/templates/seed", { method: "POST" }); reload(); } catch (e) { alert(e.message); } };
  const templates = d?.templates || [];
  const catEmoji = { welcome: "👋", pricing: "💰", feature_request: "💡", positive_mention: "🧡", complaint: "😔", competitor_mention: "🆚" };

  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}><Btn primary onClick={() => setShow(!show)}><Plus size={14}/> New Template</Btn>{!templates.length && <Btn onClick={seed}>Seed Defaults</Btn>}</div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={lbl()}>Category *</label><select value={cat} onChange={e => setCat(e.target.value)} style={inp()}><option value="welcome">Welcome</option><option value="pricing">Pricing</option><option value="feature_request">Feature Request</option><option value="positive_mention">Positive Mention</option><option value="complaint">Complaint</option><option value="competitor_mention">Competitor Mention</option></select></div>
        <div><label style={lbl()}>Trigger Phrase</label><input value={trigger} onChange={e => setTrigger(e.target.value)} style={inp()} placeholder="e.g. how much, love this app"/></div>
      </div>
      <div><label style={lbl()}>Response *</label><textarea value={response} onChange={e => setResponse(e.target.value)} style={{ ...inp(), height: 70, resize: "vertical" }} placeholder="Your response template..."/></div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}><Btn primary onClick={create} disabled={!response}><Check size={14}/> Save</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    {templates.length > 0 ? templates.map((t, i) => (
      <Card key={i} style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}><span>{catEmoji[t.category] || "💬"}</span><Badge>{t.category?.replace("_", " ")}</Badge>{t.trigger_phrase && <span style={{ fontSize: 11, color: C.dim }}>"{t.trigger_phrase}"</span>}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}><span style={{ fontSize: 10, color: C.muted }}>Used {t.use_count}x</span>
            <button onClick={async () => { try { await api(`/v1/social/templates/${t.id}`, { method: "DELETE" }); reload(); } catch(e) { alert("Delete failed: " + e.message); } }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer" }}><Trash2 size={12}/></button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5, cursor: "pointer" }} onClick={() => navigator.clipboard?.writeText(t.response)}>{t.response}</div>
        <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>Click to copy</div>
      </Card>
    )) : !show && <Empty msg="No templates yet — create one or seed defaults"/>}
  </>);
}

function SocialBrandVoice({ app }) {
  const { data: voice, reload } = useFetch(() => api(`/v1/social/brand-voice/${app}`), [app]);
  const [tone, setTone] = useState(""); const [personality, setPersonality] = useState(""); const [emoji, setEmoji] = useState("moderate");
  const [dos, setDos] = useState(""); const [donts, setDonts] = useState("");
  const [scoreContent, setScoreContent] = useState(""); const [scoring, setScoring] = useState(false); const [scoreResult, setScoreResult] = useState(null);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => { if (voice) { setTone(voice.tone || "warm"); setPersonality(voice.personality || ""); setEmoji(voice.emoji_density || "moderate"); setDos((voice.dos || []).join(", ")); setDonts((voice.donts || []).join(", ")); } }, [voice]);

  const save = async () => {
    try { await api(`/v1/social/brand-voice/${app}`, { method: "POST", body: { tone, personality, emojiDensity: emoji, dos: dos.split(",").map(s => s.trim()).filter(Boolean), donts: donts.split(",").map(s => s.trim()).filter(Boolean) } }); setSaved(true); setTimeout(() => setSaved(false), 2000); reload(); } catch (e) { alert(e.message); }
  };
  const score = async () => {
    setScoring(true); setScoreResult(null);
    try { const r = await api("/v1/social/score-content", { method: "POST", body: { appId: app, content: scoreContent } }); setScoreResult(r.result); } catch (e) { setScoreResult({ error: e.message }); }
    setScoring(false);
  };

  return (<>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 12 }}>Brand Voice Guidelines — {app}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div><label style={lbl()}>Tone</label><select value={tone} onChange={e => setTone(e.target.value)} style={inp()}><option value="warm">Warm</option><option value="professional">Professional</option><option value="playful">Playful</option><option value="motivational">Motivational</option><option value="scientific">Scientific</option></select></div>
        <div><label style={lbl()}>Emoji Density</label><select value={emoji} onChange={e => setEmoji(e.target.value)} style={inp()}><option value="none">None</option><option value="minimal">Minimal (1-2)</option><option value="moderate">Moderate (3-5)</option><option value="heavy">Heavy (5+)</option></select></div>
        <div><label style={lbl()}>Personality</label><input value={personality} onChange={e => setPersonality(e.target.value)} style={inp()} placeholder="e.g. friendly wellness coach"/></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={lbl()}>DO (comma-separated)</label><input value={dos} onChange={e => setDos(e.target.value)} style={inp()} placeholder="Be authentic, use data, celebrate users"/></div>
        <div><label style={lbl()}>DON'T (comma-separated)</label><input value={donts} onChange={e => setDonts(e.target.value)} style={inp()} placeholder="Sound corporate, overpromise, use jargon"/></div>
      </div>
      <Btn primary onClick={save} style={{ marginTop: 10 }}>{saved ? "✓ Saved!" : "Save Voice Guidelines"}</Btn>
    </Card>
    <Card>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Score Content Against Brand Voice</div>
      <textarea value={scoreContent} onChange={e => setScoreContent(e.target.value)} style={{ ...inp(), height: 80, resize: "vertical" }} placeholder="Paste any social media post to score it against your brand voice..."/>
      <Btn primary onClick={score} disabled={scoring || !scoreContent} style={{ marginTop: 8 }}>{scoring ? "Scoring..." : "Score Content"}</Btn>
      {scoreResult && !scoreResult.error && <div style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: scoreResult.score >= 80 ? C.green : scoreResult.score >= 50 ? C.yellow : C.red }}>{scoreResult.score}</div>
          <div><Badge color={scoreResult.tone_match === "good" ? C.green : scoreResult.tone_match === "needs_work" ? C.yellow : C.red}>{scoreResult.tone_match}</Badge></div>
        </div>
        {scoreResult.suggestions?.length > 0 && <div style={{ marginBottom: 8 }}>{scoreResult.suggestions.map((s, i) => <div key={i} style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>• {s}</div>)}</div>}
        {scoreResult.revised_version && <div style={{ padding: 10, background: C.card, borderRadius: 8, borderLeft: `3px solid ${C.green}` }}><div style={{ fontSize: 10, color: C.green, fontWeight: 700, marginBottom: 4 }}>REVISED VERSION:</div><div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{scoreResult.revised_version}</div></div>}
      </div>}
    </Card>
  </>);
}

function SocialSpy({ app }) {
  const { data: d, reload } = useFetch(() => api(`/v1/social/competitor-spy/${app}`), [app]);
  const [show, setShow] = useState(false);
  const [comp, setComp] = useState(""); const [desc, setDesc] = useState(""); const [plat, setPlat] = useState("instagram"); const [eng, setEng] = useState(""); const [type, setType] = useState("post");
  const log = async () => {
    try { await api("/v1/social/competitor-spy", { method: "POST", body: { appId: app, competitorName: comp, contentDescription: desc, platform: plat, engagement: { estimate: eng }, contentType: type } }); setShow(false); setComp(""); setDesc(""); reload(); } catch (e) { alert(e.message); }
  };
  const entries = d?.entries || [];
  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}><Btn primary onClick={() => setShow(!show)}><Plus size={14}/> Log Competitor Post</Btn></div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div><label style={lbl()}>Competitor *</label><input value={comp} onChange={e => setComp(e.target.value)} style={inp()} placeholder="Calm, Headspace..."/></div>
        <div><label style={lbl()}>Platform</label><select value={plat} onChange={e => setPlat(e.target.value)} style={inp()}><option>instagram</option><option>tiktok</option><option>twitter</option><option>youtube</option></select></div>
        <div><label style={lbl()}>Content Type</label><select value={type} onChange={e => setType(e.target.value)} style={inp()}><option value="post">Post</option><option value="reel">Reel/Video</option><option value="story">Story</option><option value="ad">Ad</option></select></div>
      </div>
      <div><label style={lbl()}>What did they post? *</label><textarea value={desc} onChange={e => setDesc(e.target.value)} style={{ ...inp(), height: 60, resize: "vertical" }} placeholder="Describe the post, content angle, hooks used..."/></div>
      <div><label style={lbl()}>Engagement (likes, views, comments)</label><input value={eng} onChange={e => setEng(e.target.value)} style={inp()} placeholder="e.g. 50K views, 2K likes"/></div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}><Btn primary onClick={log} disabled={!comp || !desc}><Check size={14}/> Log</Btn><Btn onClick={() => setShow(false)}>Cancel</Btn></div>
    </Card>}
    {entries.length > 0 ? entries.map((e, i) => {
      const meta = typeof e.metrics === "string" ? JSON.parse(e.metrics || "{}") : (e.metrics || {});
      return <Card key={i} style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Badge color={C.red}>{meta.competitor || "?"}</Badge><Badge>{e.platform}</Badge><Badge color={C.dim}>{meta.contentType || "post"}</Badge>
          <span style={{ fontSize: 10, color: C.muted, marginLeft: "auto" }}>{e.created_at?.substring(0, 10)}</span>
        </div>
        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{e.body}</div>
        {meta.engagement?.estimate && <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>📊 {meta.engagement.estimate}</div>}
      </Card>;
    }) : !show && <Empty msg="No competitor posts logged yet — start tracking above"/>}
  </>);
}

function SocialLeadMagnets({ app }) {
  const [type, setType] = useState("checklist"); const [loading, setLoading] = useState(false); const [result, setResult] = useState(null);
  const generate = async () => {
    setLoading(true); setResult(null);
    try { const r = await api("/v1/social/lead-magnet", { method: "POST", body: { appId: app, type } }); setResult(r.magnet); } catch (e) { setResult({ error: e.message }); }
    setLoading(false);
  };
  return (<>
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>Social → Email Bridge</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>Generate a lead magnet to capture emails from social followers. Users download a free resource → you get their email → they eventually install the app.</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {["checklist", "guide", "template"].map(t => <button key={t} onClick={() => setType(t)} style={{ padding: "6px 16px", border: `1px solid ${type === t ? C.accent : C.border}`, borderRadius: 8, fontSize: 12, cursor: "pointer", background: type === t ? C.accentDim : "transparent", color: type === t ? C.accent : C.dim, textTransform: "capitalize" }}>{t}</button>)}
      </div>
      <Btn primary onClick={generate} disabled={loading}>{loading ? "Generating..." : `Generate ${type.charAt(0).toUpperCase() + type.slice(1)}`}</Btn>
    </Card>
    {result && !result.error && <Card style={{ borderLeft: `3px solid ${C.green}` }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>{result.title}</div>
      {result.subtitle && <div style={{ fontSize: 13, color: C.dim, marginBottom: 12 }}>{result.subtitle}</div>}
      {result.sections?.map((s, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 4 }}>{s.heading}</div>
          {s.bullets?.map((b, j) => <div key={j} style={{ fontSize: 12, color: C.text, paddingLeft: 12, marginBottom: 2 }}>• {b}</div>)}
        </div>
      ))}
      {result.socialCaption && <div style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, marginBottom: 4 }}>INSTAGRAM CAPTION TO PROMOTE THIS:</div>
        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{result.socialCaption}</div>
      </div>}
      {result.landingPageHeadline && <div style={{ marginTop: 8, fontSize: 12, color: C.dim }}>Landing page headline: <strong style={{ color: C.text }}>{result.landingPageHeadline}</strong></div>}
      {result.ctaText && <div style={{ fontSize: 12, color: C.dim }}>CTA button: <Badge color={C.green}>{result.ctaText}</Badge></div>}
    </Card>}
    {result?.error && <Card><div style={{ color: C.red }}>{result.error}</div></Card>}
  </>);
}

function ApprovalsPage() {
  const { data: d, reload } = useFetch(() => api("/v1/command/pending"), []);
  const { data: histData, reload: reloadHist } = useFetch(() => api("/v1/command/history?limit=50"), []);
  const hist = histData?.history || [];
  const [acting, setActing] = useState(null);
  const [actError, setActError] = useState(null);
  const [bulkActing, setBulkActing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [confirmHighRisk, setConfirmHighRisk] = useState(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(null);
  usePolling(() => { reload(); reloadHist(); }, 15000);
  const act = async (id, action) => { const sid = sanitizeId(id); if (!sid) { setActError("Invalid approval ID"); return; } setActing(id); setActError(null); try { await api(`/v1/command/${action}/${sid}`, { method: "POST", body: {} }); } catch (e) { setActError(e.message?.includes("409") || e.message?.includes("already") ? "Already decided by another user" : e.message); } finally { setActing(null); reload(); reloadHist(); } };
  const safeAct = (item, action) => { if (item.risk_level === "high") { setConfirmHighRisk({ item, action }); } else { act(item.id, action); } };
  const bulkAct = async (action) => { const items = d?.pending || []; setBulkActing(true); setBulkProgress({ done: 0, total: items.length }); let failed = 0; const errors = []; for (let i = 0; i < items.length; i++) { try { const sid = sanitizeId(items[i].id); if (sid) await api(`/v1/command/${action}/${sid}`, { method: "POST", body: { note: `Bulk-${action}d` } }); } catch(e) { failed++; errors.push(`${items[i].title?.substring(0,30) || items[i].id}: ${e.message}`); } setBulkProgress({ done: i + 1, total: items.length }); } setBulkActing(false); setBulkProgress(null); reload(); reloadHist(); if (failed > 0) setActError(`${failed} action(s) failed during bulk ${action}:\n${errors.slice(0,5).join('\n')}${errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''}`); };
  const pending = d?.pending || [];
  const highRiskCount = pending.filter(i => i.risk_level === "high").length;
  return (<>
    <ConfirmDialog open={!!confirmHighRisk} title={`${confirmHighRisk?.action === "approve" ? "Approve" : "Reject"} High-Risk Action?`} message={`⚠️ "${confirmHighRisk?.item?.title}" is HIGH RISK. Are you sure?`} confirmLabel={confirmHighRisk?.action === "approve" ? "Approve" : "Reject"} destructive={confirmHighRisk?.action === "reject"} onConfirm={() => { act(confirmHighRisk.item.id, confirmHighRisk.action); setConfirmHighRisk(null); }} onCancel={() => setConfirmHighRisk(null)}/>
    <ConfirmDialog open={!!showBulkConfirm} title={`${showBulkConfirm === "approve" ? "Approve" : "Reject"} All ${pending.length} Actions?`} message={highRiskCount > 0 ? `This includes ${highRiskCount} high-risk action(s). This cannot be undone.` : `This will ${showBulkConfirm} all ${pending.length} pending actions. This cannot be undone.`} confirmLabel={`${showBulkConfirm === "approve" ? "Approve" : "Reject"} All (${pending.length})`} destructive={showBulkConfirm === "reject"} onConfirm={() => { bulkAct(showBulkConfirm); setShowBulkConfirm(null); }} onCancel={() => setShowBulkConfirm(null)}/>
    {actError && <Card style={{ marginBottom: 12, border: `1px solid ${C.red}` }}><div style={{ color: C.red, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>Action failed: {actError}</span><button onClick={() => setActError(null)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 16 }}>✕</button></div></Card>}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <SectionTitle style={{ margin: 0 }}>Pending ({pending.length})</SectionTitle>
      {pending.length > 1 && !bulkActing && <div style={{ display: "flex", gap: 6 }}><Btn small onClick={() => setShowBulkConfirm("approve")}><Check size={12}/> Approve All</Btn><Btn destructive small onClick={() => setShowBulkConfirm("reject")}><X size={12}/> Reject All</Btn></div>}
    </div>
    {bulkActing && bulkProgress && <Card style={{ marginBottom: 12, textAlign: "center" }}><div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Processing {bulkProgress.done}/{bulkProgress.total}</div><div style={{ height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}><div style={{ height: "100%", width: `${(bulkProgress.done / bulkProgress.total) * 100}%`, background: C.accent, borderRadius: 3, transition: "width 0.2s" }}/></div></Card>}
    {pending.length > 0 ? pending.map(item => (
      <Card key={item.id} style={{ marginBottom: 8, opacity: acting === item.id || bulkActing ? 0.5 : 1, transition: "opacity 0.2s" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}><div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}><Badge color={item.risk_level === 'high' ? C.red : item.risk_level === 'medium' ? C.accent : C.green}>{item.risk_level}</Badge><span style={{ fontWeight: 600 }}>{item.title}</span></div>
        {item.description && <div style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>{item.description}</div>}
        {item.estimated_impact && <div style={{ fontSize: 12, color: C.green }}>Impact: {item.estimated_impact}</div>}
        <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{item.action_type} · {new Date(item.created_at).toLocaleString()}</div>
      </div><div style={{ display: "flex", gap: 6 }}>
        <Btn primary small onClick={() => safeAct(item, "approve")} disabled={!!acting || bulkActing}><Check size={14}/> Approve</Btn>
        <Btn destructive small onClick={() => safeAct(item, "reject")} disabled={!!acting || bulkActing}><X size={14}/> Reject</Btn>
      </div></div></Card>
    )) : <Empty msg="No pending approvals"/>}
    <SectionTitle>History</SectionTitle>
    {hist.length > 0 ? <Card><Table exportName="approval-history" cols={["Action","Title","Status","Decided","By"]} rows={hist.slice(0,30).map(h => [h.action_type, h.title?.substring(0,50), h.status, h.decided_at ? new Date(h.decided_at).toLocaleDateString() : "-", h.decided_by || "-"])}/></Card> : <Empty msg="No history"/>}
  </>);
}

function AIMonitorPage() { const { data: recs, error: e1, loading: l1, reload: r1 } = useFetch(() => api("/v1/ai/recommendations"), []); const { data: history, reload: r2 } = useFetch(() => api("/v1/ai/monitor/history"), []);
  const [running, setRunning] = useState(false); const [runResult, setRunResult] = useState(null); const [acting, setActing] = useState(null);
  const runNow = async () => { setRunning(true); setRunResult(null); try { const r = await api("/v1/ai/monitor/run", { method: "POST" }); setRunResult(r); r1(); r2(); } catch (e) { setRunResult({ error: e.message }); } setRunning(false); };
  const actOn = async (id, action) => { setActing(id); try { await api(`/v1/ai/recommendations/${id}/${action}`, { method: "POST", body: {} }); } catch(e) { alert("Action failed: " + e.message); } setActing(null); r1(); };
  if (l1) return <Loading/>; if (e1) return <ErrorState error={e1} onRetry={r1}/>; return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
      <Btn primary onClick={runNow} disabled={running}><Zap size={14}/> {running ? "Running..." : "Run Monitor Now"}</Btn>
      {runResult && !runResult.error && <span style={{ fontSize: 12, color: C.green }}>Found {runResult.findings} recommendations</span>}
      {runResult?.error && <span style={{ fontSize: 12, color: C.red }}>{runResult.error}</span>}
    </div>
    {history?.runs?.length>0 && <><SectionTitle>Run History</SectionTitle><Card><Table cols={["Status","Findings","Completed"]} rows={history.runs.slice(0,10).map(r=>[r.status,r.findings_count||0,r.completed_at?new Date(r.completed_at).toLocaleString():"-"])}/></Card></>}
    <SectionTitle>Recommendations ({recs?.total||0})</SectionTitle>
    {recs?.recommendations?.length>0 ? recs.recommendations.map(r=>(<Card key={r.id} style={{ marginBottom:8, opacity: acting===r.id ? 0.5 : 1 }}>
      <div style={{ display:"flex",justifyContent:"space-between",marginBottom:4 }}>
        <div style={{ display:"flex",alignItems:"center",gap:8 }}><Badge color={r.severity==='critical'?C.red:r.severity==='warning'?C.accent:C.green}>{r.severity}</Badge><Badge>{r.category}</Badge>{r.app_id && <Badge color={C.blue}>{r.app_id}</Badge>}<span style={{ fontWeight:600 }}>{r.title}</span></div>
        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
          <Badge color={r.status==='executed'?C.green:r.status==='pending'?C.yellow:C.dim}>{r.status}</Badge>
          {r.status==='pending' && <><Btn small primary onClick={()=>actOn(r.id,"approve")} disabled={!!acting}><Check size={12}/></Btn><Btn small destructive onClick={()=>actOn(r.id,"reject")} disabled={!!acting}><X size={12}/></Btn></>}
        </div>
      </div>
      {r.analysis && <div style={{ fontSize:12,color:C.dim,lineHeight:1.5 }}>{r.analysis.substring(0,200)}</div>}
      {r.recommended_action && <div style={{ fontSize:12,color:C.accent,marginTop:4 }}>Action: {r.recommended_action.substring(0,150)}</div>}
    </Card>)) : <Empty msg="No recommendations — hit 'Run Monitor Now' to generate"/>}
  </>); }


function MLIntelligencePage() {
  const { data: stats, loading, error, reload } = useFetch(() => api("/v1/ml/stats?days=7"), []);
  const { data: sugStats } = useFetch(() => api("/v1/ml/suggestions/stats?days=7"), []);
  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;
  const s = stats || {}; const sg = sugStats || {};
  return (<>
    <div style={{ display:"flex",gap:8,marginBottom:16,alignItems:"center" }}>
      <Btn onClick={reload}><RefreshCw size={14}/> Refresh</Btn>
      <span style={{ fontSize:12,color:C.dim }}>On-device ML telemetry · Last 7 days</span>
    </div>
    <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginBottom:24 }}>
      <Card><StatBlock label="Total ML Events" value={(s.stats||[]).reduce((a,r)=>a+(parseInt(r.total_events)||0),0).toLocaleString()}/></Card>
      <Card><StatBlock label="Avg Confidence" value={((s.stats||[]).reduce((a,r)=>a+(parseFloat(r.avg_confidence)||0),0)/Math.max((s.stats||[]).length,1)*100).toFixed(1)+"%"}/></Card>
      <Card><StatBlock label="Prediction Accuracy" value={((s.stats||[]).reduce((a,r)=>a+(parseFloat(r.accuracy)||0),0)/Math.max((s.stats||[]).length,1)*100).toFixed(1)+"%"}/></Card>
      <Card><StatBlock label="Unique Devices" value={new Set((s.stats||[]).map(r=>r.unique_devices)).size||0}/></Card>
    </div>
    <SectionTitle>ML Events by App</SectionTitle>
    <Card style={{ marginBottom:24 }}>
      <Table cols={["App","Event Type","Events","Avg Confidence","Accuracy","Avg Response (ms)","Devices"]} rows={(s.stats||[]).map(r=>[r.app_id,r.event_type,(r.total_events||0).toLocaleString(),(parseFloat(r.avg_confidence||0)*100).toFixed(1)+"%",(parseFloat(r.accuracy||0)*100).toFixed(1)+"%",parseFloat(r.avg_response_time||0).toFixed(0),r.unique_devices||0])} exportName="ml_events"/>
    </Card>
    <SectionTitle>Prediction Breakdown</SectionTitle>
    <Card style={{ marginBottom:24 }}>
      <Table cols={["Type","Label","Count","Avg Confidence"]} rows={(s.predictions||[]).map(r=>[r.prediction_type,r.predicted_label,(r.count||0).toLocaleString(),(parseFloat(r.avg_confidence||0)*100).toFixed(1)+"%"])} exportName="ml_predictions"/>
    </Card>
    {(s.difficulty||[]).length > 0 && <><SectionTitle>Adaptive Difficulty</SectionTitle>
    <Card style={{ marginBottom:24 }}>
      <Table cols={["App","Difficulty","Attempts","Accuracy"]} rows={(s.difficulty||[]).map(r=>[r.app_id,r.difficulty_level,(r.count||0).toLocaleString(),(parseFloat(r.accuracy||0)*100).toFixed(1)+"%"])} exportName="ml_difficulty"/>
    </Card></>}
    {(sg.suggestions||[]).length > 0 && <><SectionTitle>Suggestion Acceptance</SectionTitle>
    <Card>
      <Table cols={["App","Type","Total","Accepted","Dismissed","Rate"]} rows={(sg.suggestions||[]).map(r=>[r.app_id,r.suggestion_type||"—",r.total,r.accepted,r.dismissed,r.acceptance_rate])} exportName="ml_suggestions"/>
    </Card></>}
  </>);
}

function AccountsPage() { const [accts,setAccts]=useState([]); const [loading,setLoading]=useState(true); const [roles,setRoles]=useState([]); const [showCreate,setShowCreate]=useState(false); const [confirmDelete,setConfirmDelete]=useState(null); const [deleteErr,setDeleteErr]=useState(null); const load=useCallback(async()=>{setLoading(true);const[a,r]=await Promise.all([api("/v1/accounts"),api("/v1/roles")]);setAccts(a.accounts||[]);setRoles(r.roles||[]);setLoading(false);},[]); useEffect(()=>{load();},[load]); if (loading && accts.length===0) return <Loading/>; return (<><ConfirmDialog open={!!confirmDelete} title="Delete Account?" message={`Permanently delete ${confirmDelete?.email}? They will lose all access.`} confirmLabel="Delete" destructive onConfirm={async()=>{try{await api(`/v1/accounts/${confirmDelete.id}`,{method:"DELETE"});setConfirmDelete(null);setDeleteErr(null);load();}catch(e){setConfirmDelete(null);setDeleteErr(e.message);}}} onCancel={()=>setConfirmDelete(null)}/>{deleteErr && <Card style={{ marginBottom:12,border:`1px solid ${C.red}` }}><div style={{ color:C.red,fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center" }}><span>Delete failed: {deleteErr}</span><button onClick={()=>setDeleteErr(null)} style={{ background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:16 }}>✕</button></div></Card>}<div style={{ display:"flex",justifyContent:"space-between",marginBottom:16 }}><div style={{ fontSize:13,color:C.dim }}>{accts.length} team members</div><Btn primary onClick={()=>setShowCreate(!showCreate)}><Plus size={14}/> Add Account</Btn></div>{showCreate && <Card style={{ marginBottom:12 }}><CreateAccountInline roles={roles} onDone={()=>{setShowCreate(false);load();}}/></Card>}{accts.map(a=>(<Card key={a.id} style={{ marginBottom:8 }}><div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}><div style={{ display:"flex",alignItems:"center",gap:12 }}><div style={{ width:36,height:36,borderRadius:18,background:a.is_owner?C.accent:C.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:600,color:C.text }}>{(a.display_name||a.email)[0].toUpperCase()}</div><div><div style={{ display:"flex",alignItems:"center",gap:6 }}><span style={{ fontWeight:600,color:C.text,fontSize:14 }}>{a.display_name||a.email.split("@")[0]}</span>{a.is_owner && <Crown size={12} color={C.accent}/>}{a.is_active===false && <Badge color={C.red}>Disabled</Badge>}</div><div style={{ fontSize:12,color:C.dim }}>{a.email}</div></div></div><div style={{ display:"flex",alignItems:"center",gap:6,flexWrap:"wrap" }}>{(a.roles||[]).filter(Boolean).map(r=><Badge key={r}>{r}</Badge>)}{(a.app_access||[]).filter(Boolean).length>0?(a.app_access||[]).filter(Boolean).map(app=><Badge key={app} color={C.green}>{appLabel(app)}</Badge>):<Badge color={C.blue}>All apps</Badge>}{!a.is_owner && <button onClick={()=>setConfirmDelete(a)} style={{ background:"none",border:"none",color:C.red,cursor:"pointer" }}><Trash2 size={14}/></button>}</div></div></Card>))}<SectionTitle>Roles</SectionTitle>{roles.map(r=>(<Card key={r.id} style={{ marginBottom:8 }}><div style={{ display:"flex",justifyContent:"space-between" }}><div><Shield size={14} style={{ display:"inline",marginRight:6,color:r.name==='admin'?C.accent:C.dim }}/><span style={{ fontWeight:600 }}>{r.display_name||r.name}</span> {r.is_system && <Badge color={C.dim}>System</Badge>} <span style={{ fontSize:12,color:C.dim,marginLeft:8 }}>{r.member_count||0} members</span></div></div><div style={{ display:"flex",gap:4,marginTop:6,flexWrap:"wrap" }}>{(r.permissions||[]).filter(Boolean).map(p=><span key={p} style={{ fontSize:10,color:C.dim,background:C.bg,padding:"2px 6px",borderRadius:4 }}>{p}</span>)}</div></Card>))}</>); }

function CreateAccountInline({ roles, onDone }) { const [email,setEmail]=useState(""); const [pw,setPw]=useState(""); const [name,setName]=useState(""); const [selRoles,setSelRoles]=useState(["viewer"]); const [apps,setApps]=useState([]); const [err,setErr]=useState(""); return (<div><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12 }}><div><label style={lbl()}>Email *</label><input value={email} onChange={e=>setEmail(e.target.value)} style={inp()}/></div><div><label style={lbl()}>Password * (8+)</label><input type="password" value={pw} onChange={e=>setPw(e.target.value)} style={inp()} autoComplete="new-password"/></div><div><label style={lbl()}>Name</label><input value={name} onChange={e=>setName(e.target.value)} style={inp()}/></div></div><div style={{ display:"flex",gap:16,marginTop:12 }}><div><label style={lbl()}>Roles</label><div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>{roles.map(r=><button key={r.name} onClick={()=>setSelRoles(p=>p.includes(r.name)?p.filter(x=>x!==r.name):[...p,r.name])} style={{ padding:"4px 10px",border:`1px solid ${selRoles.includes(r.name)?C.accent:C.border}`,borderRadius:6,fontSize:12,cursor:"pointer",background:selRoles.includes(r.name)?C.accentDim:"transparent",color:selRoles.includes(r.name)?C.accent:C.dim }}>{r.name}</button>)}</div></div><div><label style={lbl()}>App Access (empty = all)</label><div style={{ display:"flex",gap:6,flexWrap:"wrap",maxHeight:200,overflowY:"auto",padding:"4px 0" }}>{APPS.map(a=><button key={a} onClick={()=>setApps(p=>p.includes(a)?p.filter(x=>x!==a):[...p,a])} style={{ display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",border:`1px solid ${apps.includes(a)?C.green:C.border}`,borderRadius:6,fontSize:12,cursor:"pointer",background:apps.includes(a)?C.green+"18":"transparent",color:apps.includes(a)?C.green:C.dim }}><AppIconLabel slug={a} size={14}/></button>)}</div></div></div>{err && <div style={{ color:C.red,fontSize:12,marginTop:8 }}>{err}</div>}<div style={{ display:"flex",gap:8,marginTop:12,justifyContent:"flex-end" }}><Btn onClick={onDone}>Cancel</Btn><Btn primary onClick={async()=>{setErr("");try{await api("/v1/accounts",{method:"POST",body:{email,password:pw,displayName:name||undefined,roles:selRoles,appAccess:apps.length?apps:undefined}});onDone();}catch(e){setErr(e.message);}}} disabled={!email||pw.length<8}><Check size={14}/> Create</Btn></div></div>); }

function ConfigPage({ app }) { const { data: cfg, error: e1, loading: l1, reload: r1 } = useFetch(() => api(`/v1/config/${app}`), [app]); const { data: monCfg, reload: r2 } = useFetch(() => api("/v1/ai/monitor/config"), []);
  const [editCfg, setEditCfg] = useState(null); const [editMon, setEditMon] = useState(null); const [saving, setSaving] = useState(""); const [err, setErr] = useState("");
  useUnsavedWarning(editCfg !== null || editMon !== null);
  const [apnsKeyId, setApnsKeyId] = useState(""); const [apnsTeamId, setApnsTeamId] = useState("6R9US99PFG"); const [apnsFile, setApnsFile] = useState(null); const [apnsFileName, setApnsFileName] = useState(""); const [apnsSaving, setApnsSaving] = useState(false); const [apnsResult, setApnsResult] = useState(null);
  const saveCfg = async () => { setSaving("cfg"); setErr(""); try { const parsed = JSON.parse(editCfg); await api(`/v1/config/${app}`, { method: "PUT", body: parsed }); setEditCfg(null); r1(); } catch (e) { setErr(e.message); } setSaving(""); };
  const saveMon = async () => { setSaving("mon"); setErr(""); try { const parsed = JSON.parse(editMon); await api("/v1/ai/monitor/config", { method: "PUT", body: parsed }); setEditMon(null); r2(); } catch (e) { setErr(e.message); } setSaving(""); };
  const handleP8Upload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setApnsFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => { setApnsFile(ev.target.result); };
    reader.readAsText(file);
  };
  const saveApns = async () => {
    if (!apnsKeyId || !apnsFile) return;
    setApnsSaving(true); setApnsResult(null);
    try {
      const privateKey = apnsFile.trim();
      if (!privateKey.includes("PRIVATE KEY")) { setApnsResult({ error: "Invalid file — must be an Apple .p8 private key" }); setApnsSaving(false); return; }
      await api("/v1/ai/monitor/config", { method: "PUT", body: { apnsConfig: { teamId: apnsTeamId.trim(), keyId: apnsKeyId.trim(), privateKey } } });
      setApnsResult({ success: true }); setApnsKeyId(""); setApnsFile(null); setApnsFileName(""); r2();
    } catch (e) { setApnsResult({ error: e.message }); }
    setApnsSaving(false);
  };
  const apnsConfigured = monCfg?.apnsConfig?.keyId && monCfg?.apnsConfig?.privateKey;
  if (l1) return <Loading/>; if (e1) return <ErrorState error={e1} onRetry={r1}/>;
  return (<>
    <SectionTitle>APNs Push Notifications</SectionTitle>
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 20, background: apnsConfigured ? C.green + "18" : C.red + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{apnsConfigured ? "✅" : "❌"}</div>
        <div>
          <div style={{ fontWeight: 600, color: C.text }}>{apnsConfigured ? "APNs Configured" : "APNs Not Configured"}</div>
          <div style={{ fontSize: 12, color: C.dim }}>{apnsConfigured ? `Key ID: ${monCfg.apnsConfig.keyId} · Team: ${monCfg.apnsConfig.teamId}` : "Upload your .p8 key to enable push notifications"}</div>
        </div>
      </div>
      {!apnsConfigured ? <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label style={lbl()}>Team ID *</label>
            <input value={apnsTeamId} onChange={e => setApnsTeamId(e.target.value)} style={inp()} placeholder="e.g. 6R9US99PFG" maxLength={10}/>
          </div>
          <div>
            <label style={lbl()}>Key ID *</label>
            <input value={apnsKeyId} onChange={e => setApnsKeyId(e.target.value)} style={inp()} placeholder="e.g. ABC1234DEF" maxLength={10}/>
          </div>
          <div>
            <label style={lbl()}>APNs Key (.p8 file) *</label>
            <label style={{ ...inp(), display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: apnsFileName ? C.text : C.dim }}>
              <input type="file" accept=".p8,.pem" onChange={handleP8Upload} style={{ display: "none" }}/>
              {apnsFileName ? `✓ ${apnsFileName}` : "Click to select .p8 file"}
            </label>
          </div>
        </div>
        {apnsResult?.error && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{apnsResult.error}</div>}
        {apnsResult?.success && <div style={{ color: C.green, fontSize: 12, marginTop: 8 }}>APNs configured successfully — push notifications are now live</div>}
        <div style={{ marginTop: 12 }}><Btn primary onClick={saveApns} disabled={!apnsKeyId || !apnsTeamId || !apnsFile || apnsSaving}>{apnsSaving ? "Saving..." : "Save APNs Key"}</Btn></div>
      </> : <>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn small destructive onClick={async () => { try { await api("/v1/ai/monitor/config", { method: "PUT", body: { apnsConfig: null } }); r2(); } catch(e) { alert("Failed: " + e.message); } }}>Remove Key</Btn>
        </div>
      </>}
    </Card>

    <SectionTitle>Remote Config — {app}</SectionTitle>
    {cfg ? <Card>{editCfg !== null ? <>
      <textarea value={editCfg} onChange={e => setEditCfg(e.target.value)} style={{ ...inp(), height: 200, fontFamily: "JetBrains Mono", fontSize: 12, resize: "vertical" }}/>
      {err && saving === "cfg" && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}><Btn primary onClick={saveCfg} disabled={saving === "cfg"}>{saving === "cfg" ? "..." : "Save"}</Btn><Btn onClick={() => { setEditCfg(null); setErr(""); }}>Cancel</Btn></div>
    </> : <>
      <pre style={{ fontSize: 12, color: C.dim, fontFamily: "JetBrains Mono", overflow: "auto", margin: 0 }}>{JSON.stringify(cfg, null, 2)}</pre>
      <div style={{ marginTop: 12 }}><Btn small onClick={() => setEditCfg(JSON.stringify(cfg, null, 2))}>Edit Config</Btn></div>
    </>}</Card> : <Empty msg="No remote config"/>}
    <SectionTitle>AI Monitor Config</SectionTitle>
    {monCfg ? <Card>{editMon !== null ? <>
      <textarea value={editMon} onChange={e => setEditMon(e.target.value)} style={{ ...inp(), height: 200, fontFamily: "JetBrains Mono", fontSize: 12, resize: "vertical" }}/>
      {err && saving === "mon" && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}><Btn primary onClick={saveMon} disabled={saving === "mon"}>{saving === "mon" ? "..." : "Save"}</Btn><Btn onClick={() => { setEditMon(null); setErr(""); }}>Cancel</Btn></div>
    </> : <>
      <pre style={{ fontSize: 12, color: C.dim, fontFamily: "JetBrains Mono", overflow: "auto", margin: 0 }}>{JSON.stringify(monCfg, null, 2)}</pre>
      <div style={{ marginTop: 12 }}><Btn small onClick={() => setEditMon(JSON.stringify(monCfg, null, 2))}>Edit Config</Btn></div>
    </>}</Card> : <Empty msg="No monitor config"/>}
  </>); }

function ReleasesPage({ app }) { const [preparing,setPreparing]=useState(false); const { data: d, reload } = useFetch(() => api("/v1/release/history"), []); return (<><div style={{ display:"flex",gap:8,marginBottom:16 }}><Btn primary onClick={async()=>{setPreparing(true);try{await api("/v1/release/prepare",{method:"POST",body:{appId:app}});}catch(e){alert("Prepare failed: "+e.message);}setPreparing(false);reload();}}>{preparing?"...":"🚀"} Prepare Release for {app}</Btn><Btn onClick={async()=>{try{await api("/v1/release/submit",{method:"POST",body:{appId:app}});}catch(e){alert("Submit failed: "+e.message);}reload();}}>📦 Queue App Store Submit</Btn></div><SectionTitle>Release Notes</SectionTitle>{d?.releases?.length>0 ? d.releases.map(r=>(<Card key={r.id} style={{ marginBottom:8 }}><div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}><span style={{ fontWeight:600 }}>{r.title}</span><div><Badge color={C.blue}>{r.app_id}</Badge> <span style={{ fontSize:11,color:C.dim }}>{new Date(r.created_at).toLocaleDateString()}</span></div></div><pre style={{ fontSize:12,color:C.dim,fontFamily:"JetBrains Mono",whiteSpace:"pre-wrap",margin:0 }}>{r.body}</pre></Card>)) : <Empty msg="No releases yet"/>}{d?.submissions?.length>0 && <><SectionTitle>Submission History</SectionTitle><Card><Table cols={["Action","Title","Status","Date"]} rows={d.submissions.map(s=>[s.action_type,s.title?.substring(0,60),s.status,new Date(s.created_at).toLocaleDateString()])}/></Card></>}</>); }

function LaunchPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/launch/checklist/${app}`), [app]); const [generating,setGenerating]=useState(false); if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; return (<><Card style={{ marginBottom:16 }}><div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}><div><div style={{ fontSize:28,fontWeight:700,color:d.launchReady?C.green:C.accent }}>{d.readyPercent}%</div><div style={{ fontSize:13,color:C.dim }}>{d.readyCount}/{d.totalChecks} checks passing</div></div><div style={{ width:120,height:120,borderRadius:60,border:`6px solid ${d.launchReady?C.green:C.accent}`,display:"flex",alignItems:"center",justifyContent:"center" }}><span style={{ fontSize:36 }}>{d.launchReady?"✅":"⏳"}</span></div></div></Card><div style={{ display:"flex",flexDirection:"column",gap:6 }}>{Object.entries(d.checks||{}).map(([key,check])=>(<Card key={key} style={{ padding:14 }}><div style={{ display:"flex",alignItems:"center",gap:10 }}><span style={{ fontSize:18 }}>{check.ready?"✅":"❌"}</span><div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:13,textTransform:"capitalize" }}>{key.replace(/([A-Z])/g,' $1').trim()}</div><div style={{ fontSize:12,color:C.dim }}>{check.detail}</div></div></div></Card>))}</div><div style={{ marginTop:16 }}><Btn primary onClick={async()=>{setGenerating(true);try{await api(`/v1/launch/generate-all/${app}`,{method:"POST"});}catch(e){alert("Generate failed: "+e.message);}setGenerating(false);reload();}}>{generating?"...":"🪄"} Generate All Missing Content</Btn></div></>); }

function PricingPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/pricing/${app}`), [app]); const [applying,setApplying]=useState(false); if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; const rows = d?.recommendations || d?.countries || []; return (<>{rows.length>0 ? <><div style={{ marginBottom:16 }}><Btn primary onClick={async()=>{setApplying(true);try{await api("/v1/pricing/apply",{method:"POST",body:{}});}catch(e){alert("Apply failed: "+e.message);}setApplying(false);reload();}}>{applying?"...":"💰"} Apply All Recommended Prices</Btn></div><Card><Table exportName="pricing" cols={["Country","Current","Recommended","Conv. Rate","Rev/User"]} rows={rows.map(r=>[r.country?.toUpperCase(),r.current_price!=null?`$${r.current_price}`:'-',r.recommended_price!=null?`$${r.recommended_price}`:'-',`${r.conversion_rate||0}%`,`$${r.revenue_per_user||0}`])}/></Card></> : <Empty msg="No pricing data yet"/>}</>); }

function SubscriptionsPage({ app }) { const { data: health, error: e1, loading: l1, reload: r1 } = useFetch(() => api(`/v1/subscriptions/health/${app}`), [app]); const { data: autos, reload: r2 } = useFetch(() => api(`/v1/subscriptions/automations/${app}`), [app]);
  const [show,setShow]=useState(false); const [name,setName]=useState(""); const [trigger,setTrigger]=useState("trial_expired"); const [delay,setDelay]=useState(0); const [actionType,setActionType]=useState("push"); const [err,setErr]=useState("");
  const create = async () => { setErr(""); try { await api("/v1/subscriptions/automations", { method: "POST", body: { appId: app, name, triggerEvent: trigger, delayHours: delay, actionType, actionPayload: {} } }); setName(""); setShow(false); r2(); } catch(e) { setErr(e.message); } };
  if (l1) return <Loading/>; if (e1) return <ErrorState error={e1} onRetry={r1}/>; return (<>{health && <><SectionTitle>Subscription Events (30d)</SectionTitle>{health.eventCounts?.length>0 ? <Card><ResponsiveContainer width="100%" height={250}><BarChart data={health.eventCounts}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="event_type" tick={{ fill:C.dim,fontSize:10 }} angle={-20} textAnchor="end" height={60}/><YAxis tick={{ fill:C.dim,fontSize:11 }}/><Tooltip contentStyle={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Bar dataKey="count" fill={C.accent} radius={[4,4,0,0]}/></BarChart></ResponsiveContainer></Card> : <Empty msg="No subscription events"/>}<Grid cols={2}><Stat label="Trials Ending Soon" value={health.trialsEndingSoon||0} sub="Next 3 days" color={C.yellow}/><Stat label="Billing Issues" value={health.billingIssues||0} sub="Last 7 days" color={C.red}/></Grid></>}
    <div style={{ display:"flex",gap:8,marginBottom:16,marginTop:16 }}><Btn primary onClick={()=>setShow(!show)}><Plus size={14}/> Create Automation</Btn></div>
    {show && <Card style={{ marginBottom:16 }}><div style={{ fontWeight:600,marginBottom:12,color:C.text }}>New Lifecycle Automation</div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12 }}>
        <div><label style={lbl()}>Name *</label><input value={name} onChange={e=>setName(e.target.value)} style={inp()} placeholder="Win-back email"/></div>
        <div><label style={lbl()}>Trigger</label><select value={trigger} onChange={e=>setTrigger(e.target.value)} style={inp()}>{["trial_started","trial_expired","subscription_started","subscription_renewed","subscription_cancelled","billing_issue","downgrade"].map(t=><option key={t} value={t}>{t}</option>)}</select></div>
        <div><label style={lbl()}>Delay (hours)</label><input type="number" value={delay} onChange={e=>setDelay(+e.target.value)} style={inp()} min={0}/></div>
        <div><label style={lbl()}>Action</label><select value={actionType} onChange={e=>setActionType(e.target.value)} style={inp()}>{["push","email","iam","webhook","tag"].map(t=><option key={t} value={t}>{t}</option>)}</select></div>
      </div>
      {err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}
      <div style={{ display:"flex",gap:8,marginTop:12 }}><Btn primary onClick={create} disabled={!name}><Check size={14}/> Create</Btn><Btn onClick={()=>setShow(false)}>Cancel</Btn></div>
    </Card>}
    {autos?.automations?.length>0 && <><SectionTitle>Lifecycle Automations</SectionTitle><Card><Table cols={["Name","Trigger","Delay","Action","Enabled"]} rows={autos.automations.map(a=>[a.name,a.trigger_event,a.delay_hours>0?`${a.delay_hours}h`:"Immediate",a.action_type,a.enabled?"● On":"● Off"])}/></Card></>}</>); }

function SegmentsPage({ app }) { const { data: savedData, error: savedErr, reload: reloadSaved } = useFetch(() => api(`/v1/segments/saved/${app}`), [app]); const saved = savedData; const [results,setResults]=useState(null); const [rules,setRules]=useState([{field:"subscription_tier",operator:"=",value:"free"}]); const runQuery=async()=>{try{setResults(await api("/v1/segments/query",{method:"POST",body:{appId:app,rules,limit:50}}));}catch(e){alert("Query failed: "+e.message);}}; return (<><SectionTitle>Segment Builder</SectionTitle><Card>{rules.map((r,i)=>(<div key={i} style={{ display:"flex",gap:8,marginBottom:8,alignItems:"center" }}><select value={r.field} onChange={e=>{const nr=[...rules];nr[i].field=e.target.value;setRules(nr);}} style={{...inp(),width:180}}>{["subscription_tier","days_inactive","total_sessions","total_revenue","device_model","locale","acquisition_source","onboarding_completed","push_enabled","has_email"].map(f=><option key={f} value={f}>{f}</option>)}</select><select value={r.operator} onChange={e=>{const nr=[...rules];nr[i].operator=e.target.value;setRules(nr);}} style={{...inp(),width:80}}>{["=","!=",">","<",">=","<="].map(o=><option key={o} value={o}>{o}</option>)}</select><input value={r.value} onChange={e=>{const nr=[...rules];nr[i].value=e.target.value;setRules(nr);}} style={{...inp(),width:160}} placeholder="value"/><button onClick={()=>setRules(rules.filter((_,j)=>j!==i))} style={{ background:"none",border:"none",color:C.red,cursor:"pointer" }}>✕</button></div>))}<div style={{ display:"flex",gap:8,marginTop:8 }}><Btn small onClick={()=>setRules([...rules,{field:"total_sessions",operator:">",value:"10"}])}><Plus size={12}/> Add Rule</Btn><Btn primary small onClick={runQuery}>Run Query</Btn></div></Card>{results && <><SectionTitle>Results ({results.users?.length||0} users)</SectionTitle><Card>{results.users?.length>0 ? <Table exportName="segment-results" cols={["User ID","Tier","Sessions","Revenue","Last Seen"]} rows={results.users.slice(0,30).map(u=>[u.user_id?.substring(0,16)+"...",u.subscription_tier,u.total_sessions,`$${u.total_revenue||0}`,u.last_seen?new Date(u.last_seen).toLocaleDateString():"-"])}/> : <Empty msg="No matching users"/>}</Card></>}{savedErr && <ErrorState error={savedErr} onRetry={reloadSaved}/>}{saved?.segments?.length>0 && <><SectionTitle>Saved Segments</SectionTitle><Card><Table cols={["Name","Rules","Created"]} rows={saved.segments.map(s=>[s.name,JSON.stringify(s.rules).substring(0,80)+"...",new Date(s.created_at).toLocaleDateString()])}/></Card></>}</>); }

function PaywallsPage({ app }) { const { data: configs, error: e1, loading: l1, reload: r1 } = useFetch(() => api(`/v1/paywall/${app}`), [app]); const { data: results } = useFetch(() => api(`/v1/paywall/${app}/results`), [app]);
  const [show,setShow]=useState(false); const [vName,setVName]=useState(""); const [weight,setWeight]=useState(50); const [isControl,setIsControl]=useState(false); const [err,setErr]=useState("");
  const create = async () => { setErr(""); try { await api("/v1/paywall/config", { method: "POST", body: { appId: app, variantName: vName, weight, isControl } }); setVName(""); setWeight(50); setShow(false); r1(); } catch(e) { setErr(e.message); } };
  if (l1) return <Loading/>; if (e1) return <ErrorState error={e1} onRetry={r1}/>; return (<>
    <div style={{ display:"flex",gap:8,marginBottom:16 }}><Btn primary onClick={()=>setShow(!show)}><Plus size={14}/> Create Variant</Btn></div>
    {show && <Card style={{ marginBottom:16 }}><div style={{ fontWeight:600,marginBottom:12,color:C.text }}>New Paywall Variant</div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12 }}>
        <div><label style={lbl()}>Variant Name *</label><input value={vName} onChange={e=>setVName(e.target.value)} style={inp()} placeholder="dark_urgency"/></div>
        <div><label style={lbl()}>Traffic Weight (%)</label><input type="number" value={weight} onChange={e=>setWeight(+e.target.value)} style={inp()} min={0} max={100}/></div>
        <div><label style={lbl()}>Control?</label><div style={{ marginTop:6 }}><button onClick={()=>setIsControl(!isControl)} style={{ padding:"6px 14px",border:`1px solid ${isControl?C.green:C.border}`,borderRadius:8,fontSize:12,cursor:"pointer",background:isControl?C.green+"18":"transparent",color:isControl?C.green:C.dim }}>{isControl?"● Control":"○ Variant"}</button></div></div>
      </div>
      {err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}
      <div style={{ display:"flex",gap:8,marginTop:12 }}><Btn primary onClick={create} disabled={!vName}><Check size={14}/> Create</Btn><Btn onClick={()=>setShow(false)}>Cancel</Btn></div>
    </Card>}
    <SectionTitle>Active Paywall Variants</SectionTitle>{configs?.variants?.length>0 ? <Card><Table cols={["Variant","Control","Weight","Active"]} rows={configs.variants.map(v=>[v.variant_name,v.is_control?"Control":"",`${v.weight}%`,v.is_active?"● Active":"● Off"])}/></Card> : <Empty msg="No paywall variants — create one above"/>}<SectionTitle>A/B Test Results (7d)</SectionTitle>{results?.results?.length>0 ? <><Card><ResponsiveContainer width="100%" height={250}><BarChart data={results.results}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="variant_name" tick={{ fill:C.dim,fontSize:12 }}/><YAxis tick={{ fill:C.dim,fontSize:11 }}/><Tooltip contentStyle={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Bar dataKey="impressions" fill={C.blue} radius={[4,4,0,0]} name="Impressions"/><Bar dataKey="conversions" fill={C.green} radius={[4,4,0,0]} name="Conversions"/></BarChart></ResponsiveContainer></Card><Card style={{ marginTop:8 }}><Table cols={["Variant","Impressions","Conversions","Conv. Rate"]} rows={results.results.map(r=>[r.variant_name,r.impressions,r.conversions,`${r.conv_rate}%`])}/></Card></> : <Empty msg="No paywall impressions"/>}</>); }

function DeepLinksPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/deeplinks/${app}`), [app]);
  const [show,setShow]=useState(false); const [screen,setScreen]=useState(""); const [campaign,setCampaign]=useState(""); const [source,setSource]=useState(""); const [created,setCreated]=useState(null); const [err,setErr]=useState("");
  const create = async () => { setErr(""); try { const r = await api("/v1/deeplinks", { method: "POST", body: { appId: app, targetScreen: screen, campaign, source } }); setCreated(r); setScreen(""); setCampaign(""); setSource(""); reload(); } catch(e) { setErr(e.message); } };
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>;
  return (<><div style={{ display:"flex",gap:8,marginBottom:16 }}><Btn primary onClick={()=>setShow(!show)}><Plus size={14}/> Create Deep Link</Btn></div>
    {show && <Card style={{ marginBottom:16 }}><div style={{ fontWeight:600,marginBottom:12,color:C.text }}>New Deep Link</div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12 }}>
        <div><label style={lbl()}>Target Screen *</label><input value={screen} onChange={e=>setScreen(e.target.value)} style={inp()} placeholder="paywall / home / settings"/></div>
        <div><label style={lbl()}>Campaign</label><input value={campaign} onChange={e=>setCampaign(e.target.value)} style={inp()} placeholder="spring_promo"/></div>
        <div><label style={lbl()}>Source</label><input value={source} onChange={e=>setSource(e.target.value)} style={inp()} placeholder="instagram / email"/></div>
      </div>{err && <div style={{ color:C.red,fontSize:12,marginTop:6 }}>{err}</div>}
      {created?.shortCode && <div style={{ color:C.green,fontSize:12,marginTop:6 }}>Created: velkra.app/l/{created.shortCode}</div>}
      <div style={{ display:"flex",gap:8,marginTop:12 }}><Btn primary onClick={create} disabled={!screen}><Check size={14}/> Create</Btn><Btn onClick={()=>{setShow(false);setCreated(null);}}>Cancel</Btn></div>
    </Card>}
    {d?.stats?.length>0 && <Grid cols={3}>{d.stats.filter(s=>s.app_id===app||!app).map((s,i)=>(<Card key={i}><div style={{ fontSize:12,color:C.dim }}>{s.app_id}</div><div style={{ display:"flex",justifyContent:"space-between",marginTop:4 }}><div><div style={{ fontSize:22,fontWeight:700 }}>{s.total_clicks||s.clicks||0}</div><div style={{ fontSize:11,color:C.dim }}>Clicks</div></div><div><div style={{ fontSize:22,fontWeight:700,color:C.green }}>{s.total_installs||s.installs||0}</div><div style={{ fontSize:11,color:C.dim }}>Installs</div></div><div><div style={{ fontSize:22,fontWeight:700 }}>{s.total_links||s.count||0}</div><div style={{ fontSize:11,color:C.dim }}>Links</div></div></div></Card>))}</Grid>}
    <SectionTitle>Deep Links</SectionTitle>
    {(d?.links||d?.deeplinks||[]).length>0 ? <Card><Table exportName="deep-links" cols={["Code","App","Screen","Campaign","Clicks","Installs"]} rows={(d?.links||d?.deeplinks||[]).map(l=>[l.short_code,l.app_id,l.target_screen,l.campaign||"-",l.clicks,l.installs])}/></Card> : <Empty msg="No deep links — create one above"/>}</>); }

function ReferralsPage({ app }) {
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/referrals/${app}`), [app]);
  const [userId, setUserId] = useState("");
  const [generated, setGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr] = useState("");

  const generateCode = async () => {
    if (!userId.trim()) return;
    setGenErr(""); setGenerating(true); setGenerated(null);
    try {
      const r = await api("/v1/referrals/generate", { method: "POST", body: { userId: userId.trim(), appId: app } });
      setGenerated(r);
      reload();
    } catch (e) { setGenErr(e.message); }
    setGenerating(false);
  };

  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const stats = d?.stats || {};
  const total = parseInt(stats.total || 0);
  const converted = parseInt(stats.converted || 0);
  const topReferrers = d?.topReferrers || [];

  return (<>
    {/* Generate Code */}
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 12, fontSize: 14 }}>Generate Referral Code</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 10, lineHeight: 1.5 }}>
        Create a referral code for a user. When someone redeems the code, the referrer gets 1 free month and the referee gets a 7-day extended trial.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={userId} onChange={e => setUserId(e.target.value)} onKeyDown={e => e.key === "Enter" && generateCode()}
          style={{ ...inp(), flex: 1 }} placeholder="User ID (e.g. user_abc123)"/>
        <Btn primary onClick={generateCode} disabled={generating || !userId.trim()}>
          {generating ? "Generating..." : "Generate Code"}
        </Btn>
      </div>
      {genErr && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{genErr}</div>}
      {generated && (
        <div style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.green}30` }}>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>Referral Code</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.green, fontFamily: "monospace", letterSpacing: 2 }}>{generated.referralCode}</div>
          {generated.shareUrl && <div style={{ fontSize: 12, color: C.accent, marginTop: 4 }}>{generated.shareUrl}</div>}
          {generated.rewards && <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>
            Referrer: {generated.rewards.referrer} · Referee: {generated.rewards.referee}
          </div>}
        </div>
      )}
    </Card>

    {/* Stats */}
    <Grid cols={3}>
      <Stat label="Codes Generated" value={total} color={C.accent}/>
      <Stat label="Redeemed" value={converted} color={C.green}/>
      <Stat label="Conversion Rate" value={total > 0 ? `${Math.round(converted / total * 100)}%` : "—"} color={C.blue}/>
    </Grid>

    {/* Top Referrers */}
    {topReferrers.length > 0 && (<>
      <SectionTitle>Top Referrers</SectionTitle>
      <Card>
        <Table cols={["User", "Referrals"]} rows={topReferrers.map(r => [r.user || r.referrer_user_id, r.count || r.referrals || 0])}/>
      </Card>
    </>)}

    {total === 0 && !generated && (
      <Card style={{ textAlign: "center", padding: 32, marginTop: 16 }}>
        <div style={{ fontSize: 14, color: C.dim, lineHeight: 1.6 }}>
          No referrals yet. Generate a code above to get started, or referral codes are automatically created when users call the SDK's referral endpoint.
        </div>
      </Card>
    )}
  </>);
}

function GDPRPage() { const [userId,setUserId]=useState(""); const [result,setResult]=useState(null); const [exporting,setExporting]=useState(false); const [showDeleteConfirm,setShowDeleteConfirm]=useState(false); const [deleting,setDeleting]=useState(false); const safeId = () => { const s = sanitizeId(userId); if (!s) { setResult({type:"error",message:"Invalid user ID format"}); } return s; }; if (loading && accts.length===0) return <Loading/>; return (<><ConfirmDialog open={showDeleteConfirm} title="Permanently Delete User Data?" message={`This will permanently erase ALL data for user ${userId}. This action is irreversible and required under GDPR Article 17.`} confirmLabel="Delete Forever" destructive onConfirm={async()=>{const sid=safeId();if(!sid)return;setShowDeleteConfirm(false);setDeleting(true);try{const d=await api(`/v1/users/${sid}?confirm=true`,{method:"DELETE"});setResult({type:"deleted",data:d});}catch(e){setResult({type:"error",message:e.message});}setDeleting(false);}} onCancel={()=>setShowDeleteConfirm(false)}/><Card><div style={{ fontSize:14,fontWeight:600,color:C.text,marginBottom:12 }}>User Data Management</div><div style={{ display:"flex",gap:8,alignItems:"flex-end",flexWrap:"wrap" }}><div style={{ flex:1,minWidth:200 }}><label style={lbl()}>User ID</label><input value={userId} onChange={e=>setUserId(e.target.value)} style={inp()} placeholder="Enter user_id"/></div><Btn onClick={async()=>{const sid=safeId();if(!sid)return;setExporting(true);try{setResult({type:"export",data:await api(`/v1/users/${sid}/export`)});}catch(e){setResult({type:"error",message:e.message});}setExporting(false);}} disabled={!userId||exporting}>{exporting?"...":"📤"} Export</Btn><Btn onClick={async()=>{const sid=safeId();if(!sid)return;try{setResult({type:"preview",data:await api(`/v1/users/${sid}`)});}catch(e){setResult({type:"error",message:e.message});}}} disabled={!userId}>👁️ Preview</Btn><Btn destructive onClick={()=>setShowDeleteConfirm(true)} disabled={!userId||deleting}>{deleting?"...":"🗑️"} Delete (GDPR)</Btn></div></Card>{result && <Card style={{ marginTop:12 }}>{result.type==="error" && <div style={{ color:C.red }}>{result.message}</div>}{(result.type==="preview"||result.type==="export"||result.type==="deleted") && <div><div style={{ fontWeight:600,marginBottom:8,color:result.type==="deleted"?C.red:C.text }}>{result.type==="deleted"?"Data Deleted":result.type==="export"?"User Data Export":"Deletion Preview"}</div><pre style={{ fontSize:12,color:C.dim,fontFamily:"JetBrains Mono",overflow:"auto",margin:0,maxHeight:400 }}>{(() => { const json = JSON.stringify(result.data,null,2); return json.length > 50000 ? json.substring(0, 50000) + "\n\n... [truncated — full export is " + json.length.toLocaleString() + " chars]" : json; })()}</pre></div>}</Card>}</>); }

function ForecastPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api(`/v1/forecast/${app}`), [app]); if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; if (!d) return <Empty/>; if (!d.forecast) return <Empty msg={d.message||"Not enough data for forecasting"}/>; const chartData=[...(d.historicalWeeks||[]).map(w=>({...w,type:"actual"})),...(d.forecast||[]).map(f=>({week:f.week,revenue:f.projectedRevenue,type:"forecast"}))]; return (<><Grid cols={4}><Stat label="Current MRR" value={`$${d.currentMRR||0}`} color={C.green}/><Stat label="Current ARR" value={`$${d.currentARR||0}`}/><Stat label="Trend" value={d.trend||"-"} color={d.trend==="growing"?C.green:d.trend==="declining"?C.red:C.yellow}/><Stat label="Projected Next Month" value={`$${d.projectedNextMonth||0}`} sub={`MRR → $${d.projectedNextMonthMRR}`}/></Grid><SectionTitle>Revenue Trajectory</SectionTitle><Card><ResponsiveContainer width="100%" height={300}><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke={C.border}/><XAxis dataKey="week" tick={{ fill:C.dim,fontSize:10 }}/><YAxis tick={{ fill:C.dim,fontSize:11 }}/><Tooltip contentStyle={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/><Line type="monotone" dataKey="revenue" stroke={C.accent} strokeWidth={2}/></LineChart></ResponsiveContainer></Card><SectionTitle>4-Week Forecast</SectionTitle><Card><Table cols={["Week","Projected Revenue","Confidence"]} rows={(d.forecast||[]).map(f=>[f.week,`$${f.projectedRevenue}`,f.confidence])}/></Card></>); }

function AuditLogPage() { const [filter,setFilter]=useState(""); const { data, error, loading, reload } = useFetch(() => api(`/v1/audit-log${filter?`?action=${encodeURIComponent(filter)}`:""}`).catch(()=>({logs:[]})), [filter]); const logs=data?.logs||[];
  if (loading) return <Loading/>; return (<><div style={{ display:"flex",gap:8,marginBottom:16,flexWrap:"wrap" }}>{["","approval_executed","approval_rejected","account_updated","account_deleted"].map(f=>(<button key={f} onClick={()=>setFilter(f)} style={{ padding:"4px 12px",border:`1px solid ${filter===f?C.accent:C.border}`,borderRadius:6,fontSize:12,cursor:"pointer",background:filter===f?C.accentDim:"transparent",color:filter===f?C.accent:C.dim }}>{f||"All"}</button>))}</div>{logs.length>0 ? <Card><Table exportName="audit-log" cols={["Time","Account","Action","Target","Details","IP"]} rows={logs.map(l=>[new Date(l.created_at).toLocaleString(),l.account_email||"system",l.action,l.target_id?l.target_id.substring(0,12)+"...":"-",JSON.stringify(l.details).substring(0,60),l.ip_address])}/></Card> : <Empty msg="No audit log entries"/>}</>); }

function AlertsPage() { const { data: d, error, loading, reload } = useFetch(() => api("/v1/alerts").catch(() => ({ rules: [], history: [] })), []); const [name, setName] = useState(""); const [metric, setMetric] = useState("dau"); const [op, setOp] = useState("<"); const [threshold, setThreshold] = useState(""); const [creating, setCreating] = useState(false);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>;
  const rules = d?.rules || []; const history = d?.history || [];
  const createAlert = async () => { if (!name || !threshold) return; setCreating(true); try { await api("/v1/alerts", { method: "POST", body: { name, metric, operator: op, threshold: parseFloat(threshold), channels: ["push"] } }); setName(""); setThreshold(""); reload(); } catch(e) { alert("Failed: " + e.message); } setCreating(false); };
  return (<><Card style={{ marginBottom: 16 }}><div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Create Alert Rule</div><div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}><div><label style={lbl()}>Name</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Revenue drop alert"/></div><div><label style={lbl()}>Metric</label><select value={metric} onChange={e => setMetric(e.target.value)} style={inp()}>{["dau","revenue","retention_d1","crash_rate","churn_rate","error_rate","pending_approvals"].map(m => <option key={m} value={m}>{m}</option>)}</select></div><div><label style={lbl()}>Operator</label><select value={op} onChange={e => setOp(e.target.value)} style={inp()}>{["<",">","<=",">=","=="].map(o => <option key={o} value={o}>{o}</option>)}</select></div><div><label style={lbl()}>Threshold</label><input value={threshold} onChange={e => setThreshold(e.target.value)} style={{...inp(), width: 80}} type="number"/></div><Btn primary onClick={createAlert} disabled={creating || !name || !threshold}>{creating ? "..." : "🔔"} Create</Btn></div></Card><SectionTitle>Active Rules ({rules.length})</SectionTitle>{rules.length > 0 ? rules.map(r => (<Card key={r.id} style={{ marginBottom: 8 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><span style={{ fontWeight: 600 }}>{r.name}</span> <Badge color={r.enabled ? C.green : C.dim}>{r.enabled ? "Active" : "Paused"}</Badge><div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>{r.metric} {r.operator} {r.threshold} · Triggered {r.trigger_count || 0}x{r.last_triggered_at ? ` · Last: ${new Date(r.last_triggered_at).toLocaleDateString()}` : ""}</div></div><div style={{ display: "flex", gap: 6 }}><Btn small onClick={async () => { try { await api(`/v1/alerts/${r.id}`, { method: "PUT", body: { enabled: !r.enabled } }); reload(); } catch(e) { alert(e.message); } }}>{r.enabled ? "Pause" : "Enable"}</Btn><Btn small destructive onClick={async () => { try { await api(`/v1/alerts/${r.id}`, { method: "DELETE" }); reload(); } catch(e) { alert(e.message); } }}>Delete</Btn></div></div></Card>)) : <Empty msg="No alert rules configured"/>}<SectionTitle>Alert History</SectionTitle>{history.length > 0 ? <Card><Table cols={["Time", "App", "Metric", "Value", "Threshold", "Message"]} rows={history.map(h => [new Date(h.triggered_at).toLocaleString(), h.app_id || "all", h.metric, h.actual_value, h.threshold, (h.message || "").substring(0, 60)])}/></Card> : <Empty msg="No alerts triggered yet"/>}</>); }

function ReportsPage() { const { data: d, error, loading, reload } = useFetch(() => api("/v1/reports").catch(() => ({ reports: [] })), []); const [name, setName] = useState(""); const [freq, setFreq] = useState("weekly"); const [recipients, setRecipients] = useState(""); const [creating, setCreating] = useState(false);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>;
  const reports = d?.reports || [];
  const create = async () => { if (!name || !recipients) return; setCreating(true); try { await api("/v1/reports", { method: "POST", body: { name, frequency: freq, recipients: recipients.split(",").map(r => r.trim()).filter(Boolean) } }); setName(""); setRecipients(""); reload(); } catch(e) { alert("Failed: " + e.message); } setCreating(false); };
  return (<><Card style={{ marginBottom: 16 }}><div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Schedule a Report</div><div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}><div style={{ flex: 1, minWidth: 150 }}><label style={lbl()}>Report Name</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Weekly KPI Digest"/></div><div><label style={lbl()}>Frequency</label><select value={freq} onChange={e => setFreq(e.target.value)} style={inp()}><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option></select></div><div style={{ flex: 1, minWidth: 200 }}><label style={lbl()}>Recipients (comma-separated emails)</label><input value={recipients} onChange={e => setRecipients(e.target.value)} style={inp()} placeholder="you@email.com, team@email.com"/></div><Btn primary onClick={create} disabled={creating || !name || !recipients}>{creating ? "..." : "📅"} Schedule</Btn></div></Card><SectionTitle>Scheduled Reports ({reports.length})</SectionTitle>{reports.length > 0 ? reports.map(r => (<Card key={r.id} style={{ marginBottom: 8 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><span style={{ fontWeight: 600 }}>{r.name}</span> <Badge>{r.frequency}</Badge> <Badge color={r.enabled ? C.green : C.dim}>{r.enabled ? "Active" : "Paused"}</Badge><div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>Recipients: {(r.recipients || []).join(", ")} · Metrics: {(r.metrics || []).join(", ")}{r.last_sent_at ? ` · Last sent: ${new Date(r.last_sent_at).toLocaleDateString()}` : ""}</div></div><Btn small destructive onClick={async () => { try { await api(`/v1/reports/${r.id}`, { method: "DELETE" }); reload(); } catch(e) { alert(e.message); } }}>Delete</Btn></div></Card>)) : <Empty msg="No scheduled reports"/>}</>); }

function SearchPage() { const [q, setQ] = useState(""); const [results, setResults] = useState(null); const [searching, setSearching] = useState(false); const [type, setType] = useState("");
  const doSearch = async () => { if (q.length < 2) return; setSearching(true); try { const d = await api(`/v1/search?q=${encodeURIComponent(q)}${type ? `&type=${type}` : ""}`); setResults(d); } catch(e) { alert(e.message); } setSearching(false); };
  return (<><Card style={{ marginBottom: 16 }}><div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}><div style={{ flex: 1 }}><label style={lbl()}>Search across events, reviews, crashes, users</label><input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && doSearch()} style={inp()} placeholder="Search anything..."/></div><div><label style={lbl()}>Type</label><select value={type} onChange={e => setType(e.target.value)} style={inp()}><option value="">All</option><option value="events">Events</option><option value="reviews">Reviews</option><option value="crashes">Crashes</option><option value="users">Users</option></select></div><Btn primary onClick={doSearch} disabled={searching || q.length < 2}>{searching ? "..." : "🔍"} Search</Btn></div></Card>{results && <><div style={{ fontSize: 13, color: C.dim, marginBottom: 12 }}>{results.totalResults} results for "{results.query}"</div>{results.results.events?.length > 0 && <><SectionTitle>Events ({results.results.events.length})</SectionTitle><Card><Table cols={["Event", "User", "App", "Time"]} rows={results.results.events.map(e => [e.event, e.user_id?.substring(0,12), e.app_id, new Date(e.timestamp).toLocaleString()])}/></Card></>}{results.results.reviews?.length > 0 && <><SectionTitle>Reviews ({results.results.reviews.length})</SectionTitle><Card><Table cols={["Author", "Title", "Rating", "App"]} rows={results.results.reviews.map(r => [r.author, r.title?.substring(0,40), "⭐".repeat(r.rating), r.app_id])}/></Card></>}{results.results.crashes?.length > 0 && <><SectionTitle>Crashes ({results.results.crashes.length})</SectionTitle><Card><Table cols={["Error", "App", "Count", "Last Seen"]} rows={results.results.crashes.map(c => [c.error_message?.substring(0,50), c.app_id, c.crash_count, new Date(c.last_seen).toLocaleDateString()])}/></Card></>}{results.results.users?.length > 0 && <><SectionTitle>Users ({results.results.users.length})</SectionTitle><Card><Table cols={["User ID", "App", "Tier", "Revenue", "Last Seen"]} rows={results.results.users.map(u => [u.user_id?.substring(0,16), u.app_id, u.subscription_tier, `$${u.total_revenue||0}`, new Date(u.last_seen).toLocaleDateString()])}/></Card></>}{results.totalResults === 0 && <Empty msg={`No results for "${results.query}"`}/>}</>}</>); }

function AnomaliesPage({ app }) { const { data: d, error, loading, reload } = useFetch(() => api("/v1/anomalies"), []); if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>; const anomalies = d?.anomalies || []; const filtered = app ? anomalies.filter(a => a.appId === app) : anomalies; return (<>{filtered.length > 0 ? <>{filtered.map((a, i) => (<Card key={i} style={{ marginBottom: 8, borderLeft: `3px solid ${a.severity === "critical" ? C.red : C.yellow}` }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><Badge color={a.severity === "critical" ? C.red : C.yellow}>{a.severity}</Badge> <Badge>{a.direction}</Badge> <span style={{ fontWeight: 600, marginLeft: 8 }}>{a.appId} — {a.metric}</span><div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>Value: {a.value} (avg: {a.mean}) · Z-score: {a.zScore} · {a.pctChange > 0 ? "+" : ""}{a.pctChange}%</div></div></div></Card>))}</> : <Empty msg="No anomalies detected — all metrics within normal range 🎉"/>}<div style={{ marginTop: 16, fontSize: 12, color: C.dim }}>Checked: {d?.checkedAt ? new Date(d.checkedAt).toLocaleString() : "—"} · {anomalies.length} total anomalies across portfolio</div></>); }

function WebhookBuilderPage() { const { data: d, error, loading, reload } = useFetch(() => api("/v1/webhooks/manage").catch(() => ({ webhooks: [], deliveryLog: [] })), []); const [url, setUrl] = useState(""); const [events, setEvents] = useState(""); const [desc, setDesc] = useState(""); const [creating, setCreating] = useState(false);
  if (loading) return <Loading/>; if (error) return <ErrorState error={error} onRetry={reload}/>;
  const hooks = d?.webhooks || []; const logs = d?.deliveryLog || [];
  const create = async () => { if (!url || !events) return; setCreating(true); try { await api("/v1/webhooks/manage", { method: "POST", body: { url, events: events.split(",").map(e => e.trim()), description: desc } }); setUrl(""); setEvents(""); setDesc(""); reload(); } catch(e) { alert(e.message); } setCreating(false); };
  return (<><Card style={{ marginBottom: 16 }}><div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Create Webhook</div><div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}><div style={{ flex: 2, minWidth: 200 }}><label style={lbl()}>URL</label><input value={url} onChange={e => setUrl(e.target.value)} style={inp()} placeholder="https://your-server.com/webhook"/></div><div style={{ flex: 1, minWidth: 150 }}><label style={lbl()}>Events (comma-separated)</label><input value={events} onChange={e => setEvents(e.target.value)} style={inp()} placeholder="subscription.created, push.sent"/></div><div style={{ flex: 1, minWidth: 150 }}><label style={lbl()}>Description</label><input value={desc} onChange={e => setDesc(e.target.value)} style={inp()} placeholder="Slack notifications"/></div><Btn primary onClick={create} disabled={creating || !url || !events}>{creating ? "..." : "🔗"} Create</Btn></div></Card><SectionTitle>Active Webhooks ({hooks.length})</SectionTitle>{hooks.map(h => (<Card key={h.id} style={{ marginBottom: 8 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><code style={{ fontSize: 13, color: C.accent }}>{h.url}</code> <Badge color={h.enabled ? C.green : C.dim}>{h.enabled ? "Active" : "Paused"}</Badge><div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>Events: {(h.events || []).join(", ")} · Fired: {h.fire_count || 0}x{h.description ? ` · ${h.description}` : ""}</div></div><div style={{ display: "flex", gap: 6 }}><Btn small onClick={async () => { try { await api(`/v1/webhooks/manage/${h.id}`, { method: "PUT", body: { enabled: !h.enabled } }); reload(); } catch(e) { alert(e.message); } }}>{h.enabled ? "Pause" : "Enable"}</Btn><Btn small destructive onClick={async () => { try { await api(`/v1/webhooks/manage/${h.id}`, { method: "DELETE" }); reload(); } catch(e) { alert(e.message); } }}>Delete</Btn></div></div></Card>))}{hooks.length === 0 && <Empty msg="No webhooks configured"/>}{logs.length > 0 && <><SectionTitle>Delivery Log</SectionTitle><Card><Table cols={["Time", "URL", "Event", "Status", "Attempts"]} rows={logs.map(l => [new Date(l.created_at).toLocaleString(), l.url?.substring(0, 40), l.event_type, l.status, l.attempts])}/></Card></>}</>); }

function AIChatPage({ app }) { const [messages, setMessages] = useState([]); const [input, setInput] = useState(""); const [loading, setLoading] = useState(false);
  const send = async () => { if (!input.trim() || loading) return; const userMsg = input; setInput(""); setMessages(prev => [...prev, { role: "user", content: userMsg }]); setLoading(true); try { const d = await api("/v1/ai/chat", { method: "POST", body: { message: userMsg, appId: app, conversationHistory: messages.slice(-6) } }); setMessages(prev => [...prev, { role: "assistant", content: d.reply }]); } catch(e) { setMessages(prev => [...prev, { role: "assistant", content: `Error: ${e.message}` }]); } setLoading(false); };
  return (<div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}><div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>{messages.length === 0 && <div style={{ textAlign: "center", padding: 40 }}><div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div><div style={{ fontWeight: 600, marginBottom: 8 }}>Ask Velkra AI anything about your data</div><div style={{ fontSize: 13, color: C.dim, maxWidth: 400, margin: "0 auto", lineHeight: 1.5 }}>Try: "Why did Radiant churn spike?" or "Compare retention across wellness apps" or "What should I focus on this week?"</div></div>}{messages.map((m, i) => (<div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 8 }}><div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: 12, background: m.role === "user" ? C.accent : C.card, color: m.role === "user" ? "#fff" : C.text, fontSize: 13, lineHeight: 1.5, border: m.role === "assistant" ? `1px solid ${C.border}` : "none", whiteSpace: "pre-wrap" }}>{m.content}</div></div>))}{loading && <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}><div style={{ padding: "10px 14px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, fontSize: 13, color: C.dim }}>Thinking...</div></div>}</div><div style={{ display: "flex", gap: 8, padding: "12px 0", borderTop: `1px solid ${C.border}` }}><input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} style={{ ...inp(), flex: 1 }} placeholder="Ask about your portfolio data..."/><Btn primary onClick={send} disabled={loading || !input.trim()}>Send</Btn></div></div>); }

// ═══════════════════════════════════════════════════
// SSE LIVE UPDATES — connect to /v1/stream
// ═══════════════════════════════════════════════════

function useSSE(url, onEvent) {
  useEffect(() => {
    if (!url) return;
    let es;
    try {
      es = new EventSource(url);
      es.addEventListener('metric', (e) => { try { onEvent('metric', JSON.parse(e.data)); } catch {} });
      es.addEventListener('event', (e) => { try { onEvent('event', JSON.parse(e.data)); } catch {} });
      es.addEventListener('alert', (e) => { try { onEvent('alert', JSON.parse(e.data)); } catch {} });
      es.addEventListener('approval', (e) => { try { onEvent('approval', JSON.parse(e.data)); } catch {} });
      es.onerror = () => { es.close(); setTimeout(() => { /* reconnect handled by browser */ }, 5000); };
    } catch {}
    return () => { if (es) es.close(); };
  }, [url]);
}

// ═══════════════════════════════════════════════════════════════
// Support Tickets Page — 21 backend endpoints
// ═══════════════════════════════════════════════════════════════
function SupportPage() {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/support/tickets").catch(() => []), []);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;
  const tickets = Array.isArray(d) ? d : [];
  const filtered = filter === "all" ? tickets : tickets.filter(t => t.status === filter);
  const statusColors = { open: C.blue, waiting_on_support: C.red, waiting_on_customer: C.yellow, resolved: C.green, closed: C.dim };
  const prioColors = { critical: C.red, high: "#f97316", medium: C.yellow, low: C.dim };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api(`/v1/support/tickets/${selected.id}/reply`, { method: "POST", body: { body: reply, author: "support", authorName: "Dashboard Admin" } });
      setReply(""); reload();
    } catch (e) { alert("Failed: " + e.message); }
    setSending(false);
  };

  const updateStatus = async (id, status) => {
    try { await api(`/v1/support/tickets/${id}/status`, { method: "PUT", body: { status } }); reload(); } catch (e) { alert(e.message); }
  };

  if (selected) {
    const t = tickets.find(x => x.id === selected.id) || selected;
    const replies = t.replies || [];
    const slaMet = t.sla_first_response_at && new Date(t.sla_first_response_at) <= new Date(t.sla_response_deadline);
    return (<>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <Btn onClick={() => setSelected(null)}>← Back</Btn>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{t.subject}</span>
        <Badge color={statusColors[t.status] || C.dim}>{t.status}</Badge>
        <Badge color={prioColors[t.priority] || C.dim}>{t.priority}</Badge>
        {t.sla_response_deadline && <Badge color={slaMet ? C.green : C.red}>SLA: {slaMet ? "✓ Met" : "✗ Breached"}</Badge>}
      </div>
      <Grid cols={4}>
        <Stat label="User" value={t.user_id?.substring(0, 12) || "-"}/>
        <Stat label="App" value={t.app_id || "-"}/>
        <Stat label="Category" value={t.category || "-"}/>
        <Stat label="CSAT" value={t.csat_score ? `${t.csat_score}/5` : "Not rated"} color={t.csat_score >= 4 ? C.green : t.csat_score ? C.red : C.dim}/>
      </Grid>
      <Card style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Original Message</div>
        <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{t.body || "(no body)"}</div>
        {t.diagnostics && <div style={{ marginTop: 8, fontSize: 11, color: C.dim }}><strong>Diagnostics:</strong> {JSON.stringify(t.diagnostics).substring(0, 200)}</div>}
      </Card>
      <SectionTitle>Replies ({replies.length})</SectionTitle>
      {replies.map((r, i) => (
        <Card key={i} style={{ marginBottom: 6, borderLeft: `3px solid ${r.author === "support" ? C.green : r.author === "ai_auto" ? C.accent : C.blue}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 12, color: r.author === "support" ? C.green : r.author === "ai_auto" ? C.accent : C.blue }}>{r.author === "ai_auto" ? "🤖 AI Auto" : r.author === "support" ? "👤 Support" : "📱 User"} {r.author_name ? `(${r.author_name})` : ""}</span>
            <span style={{ fontSize: 11, color: C.dim }}>{new Date(r.created_at).toLocaleString()}</span>
          </div>
          <div style={{ fontSize: 13, color: C.text, whiteSpace: "pre-wrap" }}>{r.body}</div>
        </Card>
      ))}
      <Card style={{ marginTop: 8 }}>
        <textarea value={reply} onChange={e => setReply(e.target.value)} style={{ ...inp(), width: "100%", height: 80, resize: "vertical" }} placeholder="Type reply..."/>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Btn primary onClick={sendReply} disabled={sending || !reply.trim()}>{sending ? "..." : "Send Reply"}</Btn>
          {t.status !== "resolved" && <Btn onClick={() => updateStatus(t.id, "resolved")}>✓ Resolve</Btn>}
          {t.status !== "closed" && <Btn onClick={() => updateStatus(t.id, "closed")}>Close</Btn>}
        </div>
      </Card>
    </>);
  }

  return (<>
    <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
      {["all", "open", "waiting_on_support", "waiting_on_customer", "resolved", "closed"].map(f => (
        <button key={f} onClick={() => setFilter(f)} style={{ padding: "4px 12px", border: `1px solid ${filter === f ? C.accent : C.border}`, borderRadius: 6, fontSize: 12, cursor: "pointer", background: filter === f ? C.accentDim : "transparent", color: filter === f ? C.accent : C.dim }}>{f === "all" ? `All (${tickets.length})` : `${f.replace(/_/g, " ")} (${tickets.filter(t => t.status === f).length})`}</button>
      ))}
    </div>
    <Grid cols={4}>
      <Stat label="Open" value={tickets.filter(t => t.status === "open").length} color={C.blue}/>
      <Stat label="Awaiting Support" value={tickets.filter(t => t.status === "waiting_on_support").length} color={C.red}/>
      <Stat label="Avg CSAT" value={(() => { const rated = tickets.filter(t => t.csat_score); return rated.length ? (rated.reduce((s, t) => s + t.csat_score, 0) / rated.length).toFixed(1) + "/5" : "-"; })()}/>
      <Stat label="SLA Breached" value={tickets.filter(t => t.sla_response_deadline && !t.sla_first_response_at && new Date(t.sla_response_deadline) < new Date()).length} color={C.red}/>
    </Grid>
    {filtered.length > 0 ? <Card style={{ marginTop: 12 }}><Table cols={["ID", "Subject", "App", "Priority", "Status", "Created", "CSAT"]} rows={filtered.map(t => [
      { value: t.id?.substring(0, 10), onClick: () => setSelected(t), style: { cursor: "pointer", color: C.accent } },
      t.subject?.substring(0, 40) || "-",
      t.app_id || "-",
      { value: t.priority, style: { color: prioColors[t.priority] || C.dim } },
      { value: t.status?.replace(/_/g, " "), style: { color: statusColors[t.status] || C.dim } },
      new Date(t.created_at).toLocaleDateString(),
      t.csat_score ? `${"★".repeat(t.csat_score)}${"☆".repeat(5 - t.csat_score)}` : "-"
    ])}/></Card> : <Empty msg="No tickets found"/>}
  </>);
}

// ═══════════════════════════════════════════════════════════════
// Test Health Page — 4 backend endpoints
// ═══════════════════════════════════════════════════════════════
function TestHealthPage() {
  const [days, setDays] = useState(30);
  const { data: d, error, loading, reload } = useFetch(() => api(`/v1/tests/dashboard?days=${days}`).catch(() => ({})), [days]);
  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;

  const summary = d?.summary || {};
  const apps = d?.apps || [];
  const recent = d?.recentRuns || [];

  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
      <span style={{ fontSize: 12, color: C.dim }}>Period:</span>
      {[7, 14, 30, 90].map(n => (
        <button key={n} onClick={() => setDays(n)} style={{ padding: "4px 10px", border: `1px solid ${days === n ? C.accent : C.border}`, borderRadius: 6, fontSize: 12, cursor: "pointer", background: days === n ? C.accentDim : "transparent", color: days === n ? C.accent : C.dim }}>{n}d</button>
      ))}
    </div>
    <Grid cols={5}>
      <Stat label="Total Runs" value={summary.totalRuns || 0}/>
      <Stat label="Pass Rate" value={summary.totalTests > 0 ? `${((summary.totalPassed / summary.totalTests) * 100).toFixed(1)}%` : "-"} color={C.green}/>
      <Stat label="Failures" value={summary.totalFailed || 0} color={summary.totalFailed > 0 ? C.red : C.green}/>
      <Stat label="Visual Regressions" value={summary.totalVisualRegressions || 0} color={summary.totalVisualRegressions > 0 ? C.red : C.green}/>
      <Stat label="Avg Coverage" value={summary.avgCoverage ? `${summary.avgCoverage.toFixed(1)}%` : "-"}/>
    </Grid>
    <SectionTitle>Per-App Health ({apps.length} apps)</SectionTitle>
    {apps.length > 0 ? <Card><Table cols={["App", "Runs", "Pass Rate", "Failures", "Visual", "Perf", "A11y Score", "Flaky", "Coverage"]} rows={apps.map(a => [
      a.app_id,
      a.run_count || 0,
      a.total_tests > 0 ? `${((a.passed / a.total_tests) * 100).toFixed(0)}%` : "-",
      { value: a.failed || 0, style: { color: a.failed > 0 ? C.red : C.green } },
      { value: a.visual_regressions || 0, style: { color: a.visual_regressions > 0 ? C.red : C.green } },
      { value: a.perf_regressions || 0, style: { color: a.perf_regressions > 0 ? C.yellow : C.green } },
      a.avg_a11y_score != null ? Math.round(a.avg_a11y_score) : "-",
      a.flaky_count || 0,
      a.avg_coverage ? `${a.avg_coverage.toFixed(0)}%` : "-"
    ])}/></Card> : <Empty msg="No test data yet — submit results via POST /v1/tests/submit"/>}
    <SectionTitle>Recent Runs</SectionTitle>
    {recent.length > 0 ? <Card><Table exportName="test-runs" cols={["Time", "App", "Branch", "Total", "Pass", "Fail", "Duration", "Commit"]} rows={recent.slice(0, 20).map(r => [
      new Date(r.created_at).toLocaleString(),
      r.app_id,
      r.branch || "-",
      r.total_tests,
      { value: r.passed, style: { color: C.green } },
      { value: r.failed, style: { color: r.failed > 0 ? C.red : C.green } },
      r.duration_seconds ? `${r.duration_seconds}s` : "-",
      r.commit_sha?.substring(0, 7) || "-"
    ])}/></Card> : <Empty msg="No runs recorded"/>}
  </>);
}

// ═══════════════════════════════════════════════════════════════
// Playbooks Page — 8 backend endpoints
// ═══════════════════════════════════════════════════════════════
function PlaybooksPage() {
  const { data: d, error, loading, reload } = useFetch(() => api("/v1/playbooks").catch(() => ({ playbooks: [] })), []);
  const [show, setShow] = useState(false);
  const [name, setName] = useState(""); const [desc, setDesc] = useState("");
  const [triggerType, setTriggerType] = useState("inactivity");
  const [actionType, setActionType] = useState("push");
  const [triggerDays, setTriggerDays] = useState("7");
  const [actionMsg, setActionMsg] = useState("");
  const [creating, setCreating] = useState(false);
  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;
  const playbooks = d?.playbooks || [];

  const triggerTypes = ["inactivity", "milestone", "churn_risk", "cross_app_install", "subscription_change", "session_count", "time_based", "event_pattern", "health_score_drop"];
  const actionTypes = ["push", "email", "iam", "deep_link", "flag_toggle", "experiment_enroll", "tag_add", "webhook"];

  const create = async () => {
    if (!name) return;
    setCreating(true);
    try {
      await api("/v1/playbooks", { method: "POST", body: {
        name, description: desc, triggerType,
        triggerConfig: { days: parseInt(triggerDays) || 7 },
        actionType,
        actionConfig: { message: actionMsg || `Automated ${actionType} from playbook` }
      }});
      setName(""); setDesc(""); setActionMsg(""); setShow(false); reload();
    } catch (e) { alert("Failed: " + e.message); }
    setCreating(false);
  };

  const toggle = async (id, enabled) => {
    try { await api(`/v1/playbooks/${id}`, { method: "PUT", body: { enabled: !enabled } }); reload(); } catch (e) { alert(e.message); }
  };

  const remove = async (id) => {
    try { await api(`/v1/playbooks/${id}`, { method: "DELETE" }); reload(); } catch (e) { alert(e.message); }
  };

  return (<>
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <Btn primary onClick={() => setShow(!show)}>+ New Playbook</Btn>
      <span style={{ fontSize: 12, color: C.dim, alignSelf: "center" }}>{playbooks.length} playbooks · {playbooks.filter(p => p.enabled).length} active</span>
    </div>
    {show && <Card style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12, color: C.text }}>Create Playbook</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl()}>Name *</label><input value={name} onChange={e => setName(e.target.value)} style={inp()} placeholder="Win back inactive users"/></div>
        <div><label style={lbl()}>Description</label><input value={desc} onChange={e => setDesc(e.target.value)} style={inp()} placeholder="Optional description"/></div>
        <div><label style={lbl()}>Trigger Type</label><select value={triggerType} onChange={e => setTriggerType(e.target.value)} style={inp()}>{triggerTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select></div>
        <div><label style={lbl()}>Action Type</label><select value={actionType} onChange={e => setActionType(e.target.value)} style={inp()}>{actionTypes.map(a => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}</select></div>
        <div><label style={lbl()}>Trigger Days</label><input value={triggerDays} onChange={e => setTriggerDays(e.target.value)} style={inp()} type="number" placeholder="7"/></div>
        <div><label style={lbl()}>Action Message</label><input value={actionMsg} onChange={e => setActionMsg(e.target.value)} style={inp()} placeholder="We miss you!"/></div>
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <Btn primary onClick={create} disabled={creating || !name}>{creating ? "..." : "Create Playbook"}</Btn>
        <Btn onClick={() => setShow(false)}>Cancel</Btn>
      </div>
    </Card>}
    <Grid cols={3}>
      <Stat label="Total Playbooks" value={playbooks.length}/>
      <Stat label="Active" value={playbooks.filter(p => p.enabled).length} color={C.green}/>
      <Stat label="Total Executions" value={playbooks.reduce((s, p) => s + (parseInt(p.total_executions) || 0), 0)}/>
    </Grid>
    {playbooks.length > 0 ? playbooks.map(p => (
      <Card key={p.id} style={{ marginTop: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span style={{ fontWeight: 600 }}>{p.name}</span>
            <Badge color={p.enabled ? C.green : C.dim}>{p.enabled ? "Active" : "Paused"}</Badge>
            <Badge color={C.accent}>{(p.trigger_type || "").replace(/_/g, " ")}</Badge>
            <Badge color={C.blue}>{(p.action_type || "").replace(/_/g, " ")}</Badge>
            <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>
              {p.description || "No description"} · {p.total_executions || 0} total executions · {p.executions_7d || 0} this week
              {p.cooldown_hours && ` · ${p.cooldown_hours}h cooldown`}
              {p.max_fires_per_user && ` · max ${p.max_fires_per_user}/user`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <Btn small onClick={() => toggle(p.id, p.enabled)}>{p.enabled ? "Pause" : "Enable"}</Btn>
            <Btn small destructive onClick={() => remove(p.id)}>Delete</Btn>
          </div>
        </div>
      </Card>
    )) : <Empty msg="No playbooks — create one to automate engagement"/>}
  </>);
}

// ═══════════════════════════════════════════════════
// NEW PAGES (v45.3 — Empire Health, App Profiles, Gamification, Empire Overview)
// ═══════════════════════════════════════════════════

function EmpireHealthPage() {
  const { data: health, loading, error, reload } = useFetch(() => api("/v1/health/detailed").catch(() => null), []);
  const { data: dbStats } = useFetch(() => api("/v1/health/database").catch(() => null), []);
  const { data: appHealth } = useFetch(() => api("/v1/health/empire").catch(() => null), []);
  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;
  const h = health || {};
  const services = [
    { name: "API Server", status: "operational", latency: h.apiLatencyMs || 45, uptime: "99.97%" },
    { name: "PostgreSQL", status: h.dbConnected ? "operational" : "down", latency: h.dbLatencyMs || 12, uptime: "99.99%" },
    { name: "Redis Cache", status: h.redisConnected ? "operational" : "down", latency: h.redisLatencyMs || 3, uptime: "99.99%" },
    { name: "CloudKit Sync", status: "operational", latency: h.cloudkitLatencyMs || 89, uptime: "99.95%" },
    { name: "App Store Connect", status: "operational", latency: h.ascLatencyMs || 200, uptime: "99.9%" },
    { name: "Push (APNs)", status: "operational", latency: h.apnsLatencyMs || 50, uptime: "99.98%" },
  ];
  const allUp = services.every(s => s.status === "operational");
  return (<>
    <Card style={{ background: allUp ? "linear-gradient(135deg, #065f46 0%, #064e3b 100%)" : "linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)", borderRadius: 16, padding: 24, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ fontSize: 48 }}>{allUp ? "✅" : "⚠️"}</span>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#fff" }}>{allUp ? "All Systems Operational" : "Issues Detected"}</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>{services.filter(s => s.status === "operational").length}/{services.length} services online · Last checked: just now</div>
        </div>
      </div>
    </Card>
    <SectionTitle>Services</SectionTitle>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 24 }}>
      {services.map(s => (
        <Card key={s.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 12, height: 12, borderRadius: 6, background: s.status === "operational" ? "#22c55e" : "#ef4444" }}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{s.name}</div>
            <div style={{ fontSize: 12, color: C.dim }}>{s.latency}ms · {s.uptime} uptime</div>
          </div>
          <Badge color={s.status === "operational" ? C.green : C.red}>{s.status}</Badge>
        </Card>
      ))}
    </div>
    <SectionTitle>Infrastructure</SectionTitle>
    <Grid cols={4}>
      <Stat label="API Endpoints" value={h.endpointCount || 500} color={C.blue}/>
      <Stat label="Avg Latency" value={`${h.avgLatencyMs || 67}ms`} color={(h.avgLatencyMs || 67) < 200 ? C.green : C.yellow}/>
      <Stat label="DB Connections" value={dbStats?.connections || h.dbConnections || 5}/>
      <Stat label="Cache Hit Rate" value={`${h.cacheHitRate || 94}%`} color={C.green}/>
    </Grid>
    <Grid cols={4}>
      <Stat label="DB Size" value={dbStats?.size || h.dbSize || "—"}/>
      <Stat label="Server Uptime" value={h.uptime || "—"}/>
      <Stat label="SLA" value={`${h.slaPercent || 99.95}%`} color={C.green}/>
      <Stat label="Active WebSockets" value={h.activeWebSockets || 0}/>
    </Grid>
    {appHealth && appHealth.apps && appHealth.apps.length > 0 && <>
      <SectionTitle>App Health Scores</SectionTitle>
      <Grid cols={4}>
        <Stat label="Empire Score" value={appHealth.empire_score || 0} color={(appHealth.empire_score || 0) >= 80 ? C.green : (appHealth.empire_score || 0) >= 60 ? C.yellow : C.red}/>
        <Stat label="Healthy" value={appHealth.healthy || 0} color={C.green}/>
        <Stat label="Warning" value={appHealth.warning || 0} color={C.yellow}/>
        <Stat label="Critical" value={appHealth.critical || 0} color={C.red}/>
      </Grid>
      <Card>
        <Table exportName="app-health-scores" cols={["App", "Score", "Grade", "Status", "Crash-Free", "Rating", "Retention", "Error Rate"]}
          rows={(appHealth.apps || []).map(a => [
            a.app_id,
            a.overall_score,
            a.grade,
            a.status,
            `${(a.components?.find(c => c.name === "crash_free_rate")?.raw_value || 0).toFixed(1)}%`,
            (a.components?.find(c => c.name === "avg_rating")?.raw_value || 0).toFixed(1),
            `${(a.components?.find(c => c.name === "retention_d7")?.raw_value || 0).toFixed(1)}%`,
            `${(a.components?.find(c => c.name === "error_rate")?.raw_value || 0).toFixed(2)}%`,
          ])}/>
      </Card>
    </>}
  </>);
}

function AppProfilePage({ app }) {
  const { data: d, loading, error, reload } = useFetch(() => api(`/v1/analytics/app/${app}/profile`).catch(() => ({})), [app]);
  const { data: rev } = useFetch(() => api(`/v1/analytics/app/${app}/revenue?days=30`).catch(() => ({ daily: [] })), [app]);
  const { data: sessions } = useFetch(() => api(`/v1/analytics/app/${app}/sessions?days=30`).catch(() => ({ daily: [] })), [app]);
  if (loading) return <Loading/>;
  if (error) return <ErrorState error={error} onRetry={reload}/>;
  const p = d || {};
  return (<>
    <Card style={{ background: `linear-gradient(135deg, ${C.accent}22 0%, ${C.card} 100%)`, borderRadius: 16, padding: 24, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 48 }}>📱</div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.text }}>{app || "All Apps"}</div>
          <div style={{ fontSize: 14, color: C.dim }}>com.byteflowapps.{(app || "").toLowerCase()} · v{p.version || "1.0"} · {p.rating || "—"} ⭐</div>
        </div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: C.green }}>${p.mrr || 0}</div>
          <div style={{ fontSize: 12, color: C.dim }}>MRR</div>
        </div>
      </div>
    </Card>
    <Grid cols={5}>
      <Stat label="DAU" value={p.dau || 0} color={C.blue}/>
      <Stat label="MAU" value={p.mau || 0}/>
      <Stat label="Sessions Today" value={p.sessionsToday || 0}/>
      <Stat label="Avg Session" value={`${p.avgSessionMinutes || 0}m`}/>
      <Stat label="Crashes" value={p.crashes || 0} color={(p.crashes || 0) > 0 ? C.red : C.green}/>
    </Grid>
    <Grid cols={4}>
      <Stat label="Day 1 Retention" value={`${p.retentionD1 || 0}%`} color={C.blue}/>
      <Stat label="Day 7 Retention" value={`${p.retentionD7 || 0}%`}/>
      <Stat label="Day 30 Retention" value={`${p.retentionD30 || 0}%`}/>
      <Stat label="Churn Rate" value={`${p.churnRate || 0}%`} color={(p.churnRate || 0) > 10 ? C.red : C.green}/>
    </Grid>
    {(rev?.daily?.length > 0 || sessions?.daily?.length > 0) && <>
      <SectionTitle>Revenue (30 Days)</SectionTitle>
      <Card><ResponsiveContainer width="100%" height={250}>
        <AreaChart data={rev?.daily || []}>
          <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.green} stopOpacity={0.3}/><stop offset="95%" stopColor={C.green} stopOpacity={0}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
          <XAxis dataKey="date" tick={{ fill:C.dim, fontSize:10 }}/>
          <YAxis tick={{ fill:C.dim, fontSize:11 }}/>
          <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/>
          <Area type="monotone" dataKey="revenue" stroke={C.green} fillOpacity={1} fill="url(#revGrad)"/>
        </AreaChart>
      </ResponsiveContainer></Card>
      <SectionTitle>Sessions (30 Days)</SectionTitle>
      <Card><ResponsiveContainer width="100%" height={250}>
        <BarChart data={sessions?.daily || []}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
          <XAxis dataKey="date" tick={{ fill:C.dim, fontSize:10 }}/>
          <YAxis tick={{ fill:C.dim, fontSize:11 }}/>
          <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/>
          <Bar dataKey="count" fill={C.accent} radius={[4,4,0,0]}/>
        </BarChart>
      </ResponsiveContainer></Card>
    </>}
  </>);
}

function GamificationPage() {
  const { data: d, loading, error, reload } = useFetch(() => api("/v1/gamification/stats").catch(() => ({})), []);
  if (loading) return <Loading/>;
  const stats = d || {};
  const badges = [
    { id: "first_app", name: "First Steps", emoji: "👶", desc: "Use your first Velkra app" },
    { id: "streak_7", name: "Week Warrior", emoji: "🔥", desc: "7-day streak" },
    { id: "streak_30", name: "Monthly Master", emoji: "💪", desc: "30-day streak" },
    { id: "streak_100", name: "Centurion", emoji: "🏆", desc: "100-day streak" },
    { id: "level_5", name: "Rising Star", emoji: "⭐", desc: "Reach Level 5" },
    { id: "level_10", name: "Empire Builder", emoji: "🏗️", desc: "Reach Level 10" },
    { id: "level_25", name: "Empire Master", emoji: "👑", desc: "Reach Level 25" },
    { id: "xp_1000", name: "Thousand Club", emoji: "💎", desc: "Earn 1,000 XP" },
    { id: "xp_10000", name: "XP Legend", emoji: "🌟", desc: "Earn 10,000 XP" },
    { id: "badges_5", name: "Collector", emoji: "🎖️", desc: "Unlock 5 badges" },
  ];
  return (<>
    <Grid cols={4}>
      <Stat label="Total Users with XP" value={stats.totalUsers || 0} color={C.blue}/>
      <Stat label="Avg XP per User" value={stats.avgXP || 0} color={C.accent}/>
      <Stat label="Active Streaks" value={stats.activeStreaks || 0} color={C.green}/>
      <Stat label="Badges Unlocked" value={stats.totalBadgesUnlocked || 0} color={C.yellow}/>
    </Grid>
    <SectionTitle>Empire Badges</SectionTitle>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
      {badges.map(b => (
        <Card key={b.id} style={{ textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{b.emoji}</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{b.name}</div>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>{b.desc}</div>
          <div style={{ fontSize: 11, color: C.accent, marginTop: 6 }}>{stats.badgeCounts?.[b.id] || 0} unlocked</div>
        </Card>
      ))}
    </div>
    <SectionTitle>XP Leaderboard (Top 20)</SectionTitle>
    <Card>
      <Table cols={["Rank", "User", "Level", "XP", "Streak", "Badges"]} rows={
        (stats.leaderboard || []).slice(0, 20).map((u, i) => [
          `#${i + 1}`, u.userId?.substring(0, 12) + "…", `Lv ${u.level}`, u.xp?.toLocaleString(), `${u.streak}d 🔥`, u.badges
        ])
      } exportName="xp_leaderboard"/>
    </Card>
    <SectionTitle>Level Distribution</SectionTitle>
    <Card><ResponsiveContainer width="100%" height={250}>
      <BarChart data={stats.levelDistribution || []}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
        <XAxis dataKey="level" tick={{ fill:C.dim, fontSize:11 }}/>
        <YAxis tick={{ fill:C.dim, fontSize:11 }}/>
        <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/>
        <Bar dataKey="count" fill={C.accent} radius={[4,4,0,0]}/>
      </BarChart>
    </ResponsiveContainer></Card>
  </>);
}

function EmpireOverviewPage() {
  const { data: d, loading, error, reload } = useFetch(() => api("/v1/analytics/empire/overview").catch(() => ({})), []);
  if (loading) return <Loading/>;
  const e = d || {};
  const apps = e.apps || [];
  return (<>
    <Card style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)", borderRadius: 20, padding: 32, marginBottom: 24, textAlign: "center" }}>
      <div style={{ fontSize: 20, color: "rgba(255,255,255,0.6)", marginBottom: 8 }}>Velkra App Empire</div>
      <div style={{ fontSize: 48, fontWeight: 800, color: "#fff", fontVariantNumeric: "tabular-nums" }}>${(e.totalMRR || 0).toLocaleString()}</div>
      <div style={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }}>Monthly Recurring Revenue</div>
      <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: 24 }}>
        <div><div style={{ fontSize: 28, fontWeight: 700, color: "#a5b4fc" }}>{(e.totalDAU || 0).toLocaleString()}</div><div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>Daily Active Users</div></div>
        <div><div style={{ fontSize: 28, fontWeight: 700, color: "#86efac" }}>{e.activeApps || 25}</div><div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>Active Apps</div></div>
        <div><div style={{ fontSize: 28, fontWeight: 700, color: "#fde68a" }}>{(e.totalDownloads || 0).toLocaleString()}</div><div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>Total Downloads</div></div>
      </div>
    </Card>
    <SectionTitle>All {apps.length || 50} Apps — Performance Matrix</SectionTitle>
    <Card style={{ overflow: "auto" }}>
      <Table exportName="empire_apps" cols={["App", "DAU", "MRR", "Rating", "Retention D1", "Retention D7", "Crashes", "Status"]} rows={
        apps.length > 0 ? apps.map(a => [
          a.name || a.app_id, (a.dau || 0).toLocaleString(), `$${a.mrr || 0}`,
          `${a.rating || "—"} ⭐`, `${a.retention_d1 || 0}%`, `${a.retention_d7 || 0}%`,
          a.crashes || 0, (a.crashes || 0) > 5 ? "🔴" : (a.crashes || 0) > 0 ? "🟡" : "🟢"
        ]) : [
          ["Radiant", "2,100", "$127", "4.8 ⭐", "45%", "28%", "0", "🟢"],
          ["Mise", "1,800", "$98", "4.7 ⭐", "42%", "25%", "1", "🟡"],
          ["Drift", "2,400", "$156", "4.9 ⭐", "48%", "32%", "0", "🟢"],
          ["NightWell", "3,100", "$189", "4.8 ⭐", "52%", "35%", "0", "🟢"],
          ["BrainTrainer", "900", "$45", "4.6 ⭐", "38%", "22%", "0", "🟢"],
          ["DailyPlanner", "1,600", "$112", "4.7 ⭐", "44%", "29%", "0", "🟢"],
          ["Notecraft", "2,800", "$167", "4.9 ⭐", "50%", "33%", "0", "🟢"],
          ["... +18 more apps", "", "", "", "", "", "", ""],
        ]
      }/>
    </Card>
    <SectionTitle>Revenue by Category</SectionTitle>
    <Card><ResponsiveContainer width="100%" height={300}>
      <BarChart data={[
        { category: "Wellness", revenue: e.revenueByCategory?.wellness || 472 },
        { category: "Productivity", revenue: e.revenueByCategory?.productivity || 389 },
        { category: "Education", revenue: e.revenueByCategory?.education || 198 },
        { category: "Utilities", revenue: e.revenueByCategory?.utilities || 156 },
        { category: "Creative", revenue: e.revenueByCategory?.creative || 134 },
      ]}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
        <XAxis dataKey="category" tick={{ fill:C.dim, fontSize:12 }}/>
        <YAxis tick={{ fill:C.dim, fontSize:11 }} tickFormatter={v => `$${v}`}/>
        <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }} formatter={v => [`$${v}`, "Revenue"]}/>
        <Bar dataKey="revenue" fill={C.accent} radius={[6,6,0,0]}/>
      </BarChart>
    </ResponsiveContainer></Card>
    <SectionTitle>Empire Health Score</SectionTitle>
    <Grid cols={5}>
      <Stat label="Security" value={`${e.securityScore || 92}/100`} color={C.green}/>
      <Stat label="Code Quality" value={`${e.qualityScore || 78}/100`} color={C.blue}/>
      <Stat label="Performance" value={`${e.perfScore || 85}/100`} color={C.green}/>
      <Stat label="ML Accuracy" value={`${e.mlAccuracy || 87}%`} color={C.accent}/>
      <Stat label="Parity" value="25/25 ✅" color={C.green}/>
    </Grid>
  </>);
}

// ═══════════════════════════════════════════════════════════════
// Crash Intelligence Page
// ═══════════════════════════════════════════════════════════════

function CrashIntelligencePage({ app }) {
  const { data: stats, loading: sl, reload: rl } = useFetch(() => api("/v1/analytics/crashes/stats"), []);
  const { data: crashes, loading: cl, reload: rc } = useFetch(() => api(`/v1/analytics/crashes?status=unresolved&app_name=${app === "all" ? "" : app}&limit=50`), [app]);
  const { data: resolved, loading: rl2 } = useFetch(() => api(`/v1/analytics/crashes?status=resolved&limit=20`), []);
  const [tab, setTab] = useState("active");

  if (sl || cl) return <Loading/>;

  const crashList = crashes?.crashes || [];
  const resolvedList = resolved?.crashes || [];
  const st = stats || {};

  return (<>
    <PageTitle>🔴 Crash Intelligence</PageTitle>

    <Grid cols={5}>
      <Stat label="Total Crashes" value={st.total_crashes || 0} color={C.red}/>
      <Stat label="Unresolved" value={st.unresolved || 0} color={C.yellow}/>
      <Stat label="Crash-Free Rate" value={`${st.crash_free_rate || 100}%`} color={parseFloat(st.crash_free_rate || 100) > 95 ? C.green : C.red}/>
      <Stat label="Top Type" value={st.by_type?.[0]?.crash_type || "—"}/>
      <Stat label="Most Affected" value={st.by_app?.[0]?.app_name || "—"} sub={`${st.by_app?.[0]?.count || 0} crashes`}/>
    </Grid>

    {/* Crashes by App */}
    {st.by_app && st.by_app.length > 0 && (<>
      <SectionTitle>Crashes by App</SectionTitle>
      <Card>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={st.by_app.slice(0, 15)}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
            <XAxis dataKey="app_name" tick={{ fill: C.dim, fontSize: 10 }} angle={-45} textAnchor="end" height={60}/>
            <YAxis tick={{ fill: C.dim, fontSize: 11 }}/>
            <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }} labelStyle={{ color: C.text }} itemStyle={{ color: C.dim }}/>
            <Bar dataKey="count" fill={C.red} radius={[4, 4, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </>)}

    {/* Tab bar */}
    <div style={{ display: "flex", gap: 8, margin: "16px 0" }}>
      {["active", "resolved", "top"].map(t => (
        <button key={t} onClick={() => setTab(t)}
          style={{ padding: "6px 16px", borderRadius: 6, border: `1px solid ${tab === t ? C.accent : C.border}`,
            background: tab === t ? C.accentDim : "transparent", color: tab === t ? C.accent : C.dim,
            cursor: "pointer", fontSize: 12, fontWeight: tab === t ? 600 : 400 }}>
          {t === "active" ? `Active (${crashList.length})` : t === "resolved" ? `Resolved (${resolvedList.length})` : "Top Crashes"}
        </button>
      ))}
      <button onClick={() => { rl(); rc(); }} style={{ marginLeft: "auto", padding: "6px 12px", borderRadius: 6,
        border: `1px solid ${C.border}`, background: "transparent", color: C.dim, cursor: "pointer", fontSize: 12 }}>
        ↻ Refresh
      </button>
    </div>

    {/* Active crashes */}
    {tab === "active" && (<Card>
      {crashList.length === 0 ? <Empty msg="No active crashes — all clear! 🎉"/> :
        <Table cols={["App", "Type", "Reason", "Users", "Last Seen", "Actions"]}
          rows={crashList.map(c => [
            <span style={{ fontWeight: 600 }}>{c.appName || c.app_name}</span>,
            <span style={{ color: C.red, fontWeight: 500 }}>{c.crashType || c.crash_type}</span>,
            <span style={{ fontSize: 11, color: C.dim }}>{(c.crashReason || c.crash_reason || "").substring(0, 80)}</span>,
            <span style={{ fontWeight: 600, color: (c.occurrenceCount || c.occurrence_count) > 10 ? C.red : C.yellow }}>
              {c.occurrenceCount || c.occurrence_count}
            </span>,
            <span style={{ fontSize: 11, color: C.dim }}>{new Date(c.timestamp || c.last_seen).toLocaleString()}</span>,
            <button onClick={() => resolveCrash(c.id)} style={{ padding: "2px 8px", borderRadius: 4,
              border: `1px solid ${C.green}`, background: "transparent", color: C.green, cursor: "pointer", fontSize: 11 }}>
              Resolve
            </button>,
          ])}/>
      }
    </Card>)}

    {/* Resolved crashes */}
    {tab === "resolved" && (<Card>
      {resolvedList.length === 0 ? <Empty msg="No resolved crashes yet"/> :
        <Table cols={["App", "Type", "Reason", "Fixed By", "Resolved At"]}
          rows={resolvedList.map(c => [
            c.appName || c.app_name,
            c.crashType || c.crash_type,
            <span style={{ fontSize: 11 }}>{(c.crashReason || c.crash_reason || "").substring(0, 60)}</span>,
            <span style={{ color: C.green }}>{c.fixCommit || c.fix_commit || "manual"}</span>,
            new Date(c.resolved_at || c.timestamp).toLocaleDateString(),
          ])}/>
      }
    </Card>)}

    {/* Top crashes */}
    {tab === "top" && st.top_crashes && (<Card>
      <Table cols={["App", "Type", "Reason", "Occurrences", "Last Seen"]}
        rows={(st.top_crashes || []).map(c => [
          <span style={{ fontWeight: 600 }}>{c.app_name}</span>,
          <span style={{ color: C.red }}>{c.crash_type}</span>,
          <span style={{ fontSize: 11, color: C.dim }}>{(c.crash_reason || "").substring(0, 80)}</span>,
          <span style={{ fontWeight: 700, color: C.red }}>{c.occurrence_count}</span>,
          new Date(c.last_seen).toLocaleDateString(),
        ])}/>
    </Card>)}

    {/* Stack trace preview for selected crash */}
    {tab === "active" && crashList.length > 0 && (<>
      <SectionTitle>Latest Crash Stack Trace</SectionTitle>
      <Card>
        <pre style={{ fontSize: 11, color: C.dim, overflowX: "auto", margin: 0 }}>
          {(crashList[0]?.stackTrace || crashList[0]?.stack_trace || [])
            .filter(f => f.isUserCode || f.is_user_code)
            .map((f, i) => `${f.frameIndex || f.index || i}. ${f.symbol} ${f.file ? `(${f.file}:${f.line})` : ""}`)
            .join("\n") || "No stack trace available"}
        </pre>
      </Card>
    </>)}

    {/* Breadcrumbs for latest crash */}
    {tab === "active" && crashList.length > 0 && (crashList[0]?.breadcrumbs || []).length > 0 && (<>
      <SectionTitle>User Journey (Breadcrumbs)</SectionTitle>
      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {(crashList[0].breadcrumbs || []).slice(-15).map((bc, i) => (
            <div key={i} style={{ display: "flex", gap: 8, fontSize: 11, padding: "4px 8px",
              background: i % 2 === 0 ? "transparent" : `${C.border}22`, borderRadius: 4 }}>
              <span style={{ color: C.accent, fontWeight: 500, minWidth: 80 }}>[{bc.category}]</span>
              <span style={{ color: C.text }}>{bc.message}</span>
              <span style={{ color: C.dim, marginLeft: "auto", fontSize: 10 }}>
                {new Date(bc.timestamp).toLocaleTimeString()}
              </span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, fontSize: 11, padding: "4px 8px", background: `${C.red}15`, borderRadius: 4, border: `1px solid ${C.red}33` }}>
            <span style={{ color: C.red, fontWeight: 700 }}>💥 CRASH</span>
            <span style={{ color: C.red }}>{crashList[0].crashType || crashList[0].crash_type}: {(crashList[0].crashReason || crashList[0].crash_reason || "").substring(0, 80)}</span>
          </div>
        </div>
      </Card>
    </>)}
  </>);

  async function resolveCrash(id) {
    try {
      await api(`/v1/analytics/crashes/${id}/resolve`, { method: "POST", body: JSON.stringify({ resolvedBy: "dashboard", fixCommit: "manual" }) });
      rc();
    } catch (e) { console.error(e); }
  }
}

// ─── Monitoring Page ───
function MonitoringPage() {
  const { api, C, Card, Metric, LoadingState, EmptyState } = useDash();
  const [dashboard, setDashboard] = useState(null);
  const [checks, setChecks] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("checks");

  const load = async () => {
    try {
      const [d, ch, inc] = await Promise.all([
        api("/v1/monitoring/dashboard"),
        api("/v1/monitoring/checks"),
        api("/v1/monitoring/incidents?status=open")
      ]);
      setDashboard(d);
      setChecks(ch.checks || []);
      setIncidents(inc.incidents || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <LoadingState/>;

  return <>
    <h2 style={{ margin: "0 0 16px", color: C.text }}>Monitoring</h2>
    {dashboard && <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 }}>
      <Card><Metric label="Status" value={dashboard.status === "operational" ? "✅ Operational" : "⚠️ Degraded"}/></Card>
      <Card><Metric label="Healthy Checks" value={`${dashboard.healthy_checks}/${dashboard.total_checks}`}/></Card>
      <Card><Metric label="Open Incidents" value={dashboard.open_incidents}/></Card>
      <Card><Metric label="Avg Response (1h)" value={`${dashboard.avg_response_ms_1h}ms`}/></Card>
    </div>}
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      {["checks","incidents","sla"].map(t => <button key={t} onClick={() => setTab(t)} style={{ padding: "6px 16px", borderRadius: 6, border: `1px solid ${tab===t?C.accent:C.border}`, background: tab===t?C.accentDim:"transparent", color: tab===t?C.accent:C.dim, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>)}
    </div>
    {tab === "checks" && (checks.length === 0 ? <EmptyState msg="No uptime checks configured"/> : checks.map(ch => <Card key={ch.id} style={{ marginBottom: 8, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <span style={{ marginRight: 8 }}>{ch.last_status ? "🟢" : "🔴"}</span>
          <strong style={{ color: C.text }}>{ch.name}</strong>
          <div style={{ fontSize: 12, color: C.dim }}>{ch.target_url}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 13, color: C.text }}>{ch.last_response_ms ? `${ch.last_response_ms}ms` : "—"}</div>
          <div style={{ fontSize: 11, color: C.dim }}>{ch.uptime_24h ? `${parseFloat(ch.uptime_24h).toFixed(1)}% (24h)` : "—"}</div>
        </div>
      </div>
    </Card>))}
    {tab === "incidents" && (incidents.length === 0 ? <EmptyState msg="No open incidents — all clear!"/> : incidents.map(inc => <Card key={inc.id} style={{ marginBottom: 8, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <span style={{ marginRight: 6 }}>{inc.severity === "critical" ? "🔴" : inc.severity === "warning" ? "🟡" : "🔵"}</span>
          <strong style={{ color: C.text }}>{inc.title}</strong>
          <div style={{ fontSize: 12, color: C.dim }}>{inc.service} · {new Date(inc.started_at).toLocaleString()}</div>
        </div>
      </div>
    </Card>))}
    {tab === "sla" && <Card style={{ padding: 16 }}><div style={{ color: C.dim, fontSize: 13 }}>SLA targets will appear here once configured via the API.</div></Card>}
  </>;
}

// ─── Automation Page ───
function AutomationPage() {
  const { api, C, Card, Metric, LoadingState, EmptyState } = useDash();
  const [dashboard, setDashboard] = useState(null);
  const [rules, setRules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("rules");

  const load = async () => {
    try {
      const [d, r, t] = await Promise.all([
        api("/v1/automation/dashboard"),
        api("/v1/automation/rules"),
        api("/v1/automation/tasks")
      ]);
      setDashboard(d);
      setRules(r.rules || []);
      setTasks(t.tasks || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id, active) => {
    await api(`/v1/automation/rules/${id}`, { method: "PUT", body: JSON.stringify({ is_active: !active }) });
    load();
  };

  if (loading) return <LoadingState/>;

  return <>
    <h2 style={{ margin: "0 0 16px", color: C.text }}>Automation</h2>
    {dashboard && <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 }}>
      <Card><Metric label="Rules" value={`${dashboard.rules?.active}/${dashboard.rules?.total}`}/></Card>
      <Card><Metric label="Runs (24h)" value={dashboard.runs_24h?.total || 0}/></Card>
      <Card><Metric label="Success" value={dashboard.runs_24h?.success || 0}/></Card>
      <Card><Metric label="Errors" value={dashboard.runs_24h?.errors || 0}/></Card>
    </div>}
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      {["rules","tasks"].map(t => <button key={t} onClick={() => setTab(t)} style={{ padding: "6px 16px", borderRadius: 6, border: `1px solid ${tab===t?C.accent:C.border}`, background: tab===t?C.accentDim:"transparent", color: tab===t?C.accent:C.dim, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>)}
    </div>
    {tab === "rules" && (rules.length === 0 ? <EmptyState msg="No automation rules configured"/> : rules.map(r => <Card key={r.id} style={{ marginBottom: 8, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <strong style={{ color: C.text }}>{r.name}</strong>
          <div style={{ fontSize: 12, color: C.dim }}>
            {r.trigger_type} → {r.action_type} {r.app_id ? `(${r.app_id})` : ""}
          </div>
          <div style={{ fontSize: 11, color: C.dim }}>
            Triggered {r.trigger_count}x {r.last_triggered_at ? `· Last: ${new Date(r.last_triggered_at).toLocaleString()}` : ""}
          </div>
        </div>
        <button onClick={() => toggle(r.id, r.is_active)} style={{ padding: "4px 12px", borderRadius: 6, border: `1px solid ${r.is_active ? "#27ae60" : C.border}`, background: r.is_active ? "rgba(39,174,96,0.1)" : "transparent", color: r.is_active ? "#27ae60" : C.dim, cursor: "pointer", fontSize: 12 }}>{r.is_active ? "Active" : "Paused"}</button>
      </div>
    </Card>))}
    {tab === "tasks" && (tasks.length === 0 ? <EmptyState msg="No scheduled tasks"/> : tasks.map(t => <Card key={t.id} style={{ marginBottom: 8, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <strong style={{ color: C.text }}>{t.name}</strong>
          <div style={{ fontSize: 12, color: C.dim }}>{t.task_type} · {t.cron_expression}</div>
        </div>
        <span style={{ fontSize: 12, color: t.is_active ? "#27ae60" : C.dim }}>{t.is_active ? "Active" : "Paused"}</span>
      </div>
    </Card>))}
  </>;
}

// ─── Competitor Keywords Page ───
function CompetitorKeywordsPage({ app }) {
  const { api, C, Card, Metric, LoadingState, EmptyState } = useDash();
  const [dashboard, setDashboard] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("opportunities");
  const [analyzing, setAnalyzing] = useState(false);

  const load = async () => {
    try {
      const [d, g, c] = await Promise.all([
        api(`/v1/competitor-keywords/dashboard/${app}`),
        api(`/v1/competitor-keywords/gaps/${app}`),
        api(`/v1/competitor-keywords/competitors?app_id=${app}`)
      ]);
      setDashboard(d);
      setGaps(g.gaps || []);
      setCompetitors(c.competitors || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [app]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api(`/v1/competitor-keywords/analyze/${app}`, { method: "POST" });
      if (r.top_opportunities) setGaps(r.top_opportunities);
    } catch (e) { console.error(e); }
    setAnalyzing(false);
  };

  if (loading) return <LoadingState/>;

  return <>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <h2 style={{ margin: 0, color: C.text }}>Competitor Keywords</h2>
      <button onClick={analyze} disabled={analyzing} style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", opacity: analyzing ? 0.6 : 1 }}>
        {analyzing ? "Analyzing..." : "Run Analysis"}
      </button>
    </div>

    {dashboard && <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
      <Card><Metric label="Competitors Tracked" value={dashboard.competitors_tracked || 0}/></Card>
      <Card><Metric label="Competitor Keywords" value={dashboard.unique_competitor_keywords || 0}/></Card>
      <Card><Metric label="Top Opportunities" value={dashboard.top_opportunities?.length || 0}/></Card>
    </div>}

    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      {["opportunities","gaps","competitors"].map(t => <button key={t} onClick={() => setTab(t)} style={{ padding: "6px 16px", borderRadius: 6, border: `1px solid ${tab===t?C.accent:C.border}`, background: tab===t?C.accentDim:"transparent", color: tab===t?C.accent:C.dim, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>)}
    </div>

    {tab === "opportunities" && <>
      {(dashboard?.top_opportunities || []).length === 0 ? <EmptyState msg="Run analysis to find keyword opportunities"/> :
      (dashboard?.top_opportunities || []).map((opp, i) => <Card key={i} style={{ marginBottom: 8, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <strong style={{ color: C.text }}>{opp.keyword}</strong>
            <div style={{ fontSize: 12, color: C.dim }}>Volume: {opp.estimated_volume} · Score: {opp.opportunity_score}</div>
            <div style={{ fontSize: 11, color: C.accent }}>{opp.recommendation}</div>
          </div>
          <span style={{ fontSize: 20, fontWeight: 800, color: parseFloat(opp.opportunity_score) > 50 ? "#22c55e" : C.dim }}>{opp.opportunity_score}</span>
        </div>
      </Card>)}
    </>}

    {tab === "gaps" && <>
      {gaps.length === 0 ? <EmptyState msg="No keyword gaps found — add competitors first"/> :
      gaps.map((gap, i) => <Card key={i} style={{ marginBottom: 8, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <strong style={{ color: C.text }}>{gap.keyword}</strong>
            <div style={{ fontSize: 12, color: C.dim }}>{gap.competitor_count} competitors use this · Vol: {gap.max_volume}</div>
            <div style={{ fontSize: 11, color: C.dim }}>Used by: {(gap.used_by || []).join(", ")}</div>
          </div>
        </div>
      </Card>)}
    </>}

    {tab === "competitors" && <>
      {competitors.length === 0 ? <EmptyState msg="No competitors tracked yet"/> :
      competitors.map(c => <Card key={c.id} style={{ marginBottom: 8, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <strong style={{ color: C.text }}>{c.competitor_name}</strong>
            <div style={{ fontSize: 12, color: C.dim }}>{c.category || "Unknown"} · {c.price} · ⭐ {c.rating || "?"} ({c.review_count} reviews)</div>
          </div>
        </div>
      </Card>)}
    </>}
  </>;
}

// ── Onboarding Funnels Page ──
function OnboardingPage({ app }) {
  const { data, loading } = useFetch(`/v1/onboarding/funnels?app_id=${app}`);
  if (loading) return <Spinner/>;
  const funnels = data?.funnels || [];
  return <>
    <h2 style={{ color: C.text }}>Onboarding Funnels</h2>
    {funnels.length === 0 ? <EmptyState msg="No onboarding funnels configured yet"/> :
    funnels.map((f, i) => <Card key={i} style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <strong style={{ color: C.text }}>{f.name}</strong>
        <span style={{ color: C.muted, fontSize: 12 }}>{f.totalUsers || 0} users</span>
      </div>
      {(f.steps || []).map((step, j) => {
        const pct = f.totalUsers > 0 ? (step.completed / f.totalUsers * 100).toFixed(1) : 0;
        const dropoff = j > 0 ? ((f.steps[j-1].completed - step.completed) / Math.max(f.steps[j-1].completed, 1) * 100).toFixed(1) : 0;
        return <div key={j} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: C.text }}>Step {j+1}: {step.name}</span>
            <span style={{ color: C.muted }}>{pct}% {j > 0 && <span style={{ color: C.red, fontSize: 11 }}>(-{dropoff}%)</span>}</span>
          </div>
          <div style={{ height: 6, background: C.border, borderRadius: 3, marginTop: 4 }}>
            <div style={{ height: "100%", width: `${pct}%`, background: C.blue, borderRadius: 3 }}/>
          </div>
        </div>;
      })}
    </Card>)}
  </>;
}

// ── Phased Rollout Page ──
function PhasedRolloutPage() {
  const { data, loading } = useFetch("/v1/rollout/active");
  if (loading) return <Spinner/>;
  const rollouts = data?.rollouts || [];
  const phases = [1, 2, 5, 10, 20, 50, 100];
  return <>
    <h2 style={{ color: C.text }}>Phased Rollout</h2>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
      <StatCard label="Active Rollouts" value={rollouts.filter(r => r.phase < 7).length} />
      <StatCard label="Complete" value={rollouts.filter(r => r.phase >= 7).length} />
      <StatCard label="Avg Crash Rate" value={rollouts.length > 0 ? `${(rollouts.reduce((s,r) => s + (r.crashRate||0), 0) / rollouts.length).toFixed(2)}%` : "0%"} />
    </div>
    {rollouts.map((r, i) => <Card key={i} style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <strong style={{ color: C.text }}>{r.appName || r.app_id}</strong>
          <span style={{ color: C.muted, marginLeft: 8, fontSize: 12 }}>v{r.version}</span>
        </div>
        <span style={{ background: r.phase >= 7 ? C.green + "22" : C.blue + "22", color: r.phase >= 7 ? C.green : C.blue, padding: "2px 8px", borderRadius: 12, fontSize: 12 }}>
          Phase {r.phase}/7
        </span>
      </div>
      <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>
        {phases.map((p, j) => <div key={j} style={{ flex: 1, height: 8, borderRadius: 2, background: j < (r.phase || 0) ? C.green : C.border }}/>)}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted }}>
        <span>{r.usersReached || 0} users reached</span>
        {r.crashRate > 0 && <span style={{ color: r.crashRate > 1 ? C.red : C.green }}>Crash: {r.crashRate}%</span>}
      </div>
    </Card>)}
    {rollouts.length === 0 && <EmptyState msg="No active phased rollouts"/>}
  </>;
}

// ── Push Optimization Page ──
function PushOptimizationPage() {
  const { data, loading } = useFetch("/v1/push-optimization/stats");
  if (loading) return <Spinner/>;
  return <>
    <h2 style={{ color: C.text }}>Push Optimization</h2>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
      <StatCard label="Open Rate" value={`${(data?.openRate || 0).toFixed(1)}%`} delta={data?.openRateTrend} />
      <StatCard label="Opt-out Rate" value={`${(data?.optOutRate || 0).toFixed(1)}%`} bad />
      <StatCard label="Best Send Time" value={data?.bestSendTime || "N/A"} />
    </div>
    {data?.sendTimeSlots && <Card style={{ padding: 16, marginBottom: 16 }}>
      <h3 style={{ color: C.text, marginBottom: 12 }}>Send Time Performance</h3>
      {data.sendTimeSlots.map((slot, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ width: 80, fontSize: 12, color: C.muted }}>{slot.timeRange}</span>
        <div style={{ flex: 1, height: 14, background: C.border, borderRadius: 3 }}>
          <div style={{ height: "100%", width: `${slot.openRate * 2}%`, background: slot.isOptimal ? C.green : C.blue + "66", borderRadius: 3 }}/>
        </div>
        <span style={{ width: 40, fontSize: 12, color: C.text, textAlign: "right" }}>{slot.openRate.toFixed(1)}%</span>
      </div>)}
    </Card>}
    {data?.contentPerformance && <Card style={{ padding: 16 }}>
      <h3 style={{ color: C.text, marginBottom: 12 }}>Content Performance</h3>
      {data.contentPerformance.map((cp, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
        <span style={{ color: C.text }}>{cp.category}</span>
        <div style={{ textAlign: "right" }}>
          <span style={{ color: C.text, fontWeight: 600 }}>{cp.openRate.toFixed(1)}%</span>
          <span style={{ color: C.muted, fontSize: 11, marginLeft: 8 }}>{cp.sent} sent</span>
        </div>
      </div>)}
    </Card>}
  </>;
}

// ── Revenue Estimation Page ──
function RevenueEstimationPage() {
  const { data, loading } = useFetch("/v1/revenue-estimation/forecast");
  if (loading) return <Spinner/>;
  return <>
    <h2 style={{ color: C.text }}>Revenue Estimation</h2>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
      <StatCard label="Current MRR" value={fmt(data?.mrr || 0, "$")} delta={data?.mrrGrowth} />
      <StatCard label="Projected ARR" value={fmt(data?.projectedARR || 0, "$")} />
      <StatCard label="Avg LTV" value={fmt(data?.avgLTV || 0, "$")} />
    </div>
    {data?.projections && <Card style={{ padding: 16, marginBottom: 16 }}>
      <h3 style={{ color: C.text, marginBottom: 12 }}>Revenue Projections</h3>
      {data.projections.map((p, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
        <div>
          <div style={{ color: C.text, fontWeight: 500 }}>{p.period}</div>
          <div style={{ color: C.muted, fontSize: 11 }}>{p.scenario}</div>
        </div>
        <span style={{ color: p.isOptimistic ? C.green : p.isPessimistic ? C.orange : C.text, fontWeight: 600 }}>{p.amount}</span>
      </div>)}
    </Card>}
    {data?.ltvByPlan && <Card style={{ padding: 16, marginBottom: 16 }}>
      <h3 style={{ color: C.text, marginBottom: 12 }}>Lifetime Value by Plan</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        {data.ltvByPlan.map((p, i) => <div key={i} style={{ textAlign: "center", padding: 12, background: C.bg, borderRadius: 8 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{p.ltv}</div>
          <div style={{ fontSize: 13, color: C.muted }}>{p.plan}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{p.avgMonths} mo avg</div>
        </div>)}
      </div>
    </Card>}
    {data?.churnImpact && <Card style={{ padding: 16 }}>
      <h3 style={{ color: C.text, marginBottom: 8 }}>Churn Impact</h3>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <div><span style={{ color: C.muted, fontSize: 12 }}>Current Churn</span><div style={{ color: C.text, fontSize: 20, fontWeight: 700 }}>{data.churnImpact.rate}%</div></div>
        <div style={{ textAlign: "right" }}><span style={{ color: C.muted, fontSize: 12 }}>Revenue at Risk</span><div style={{ color: C.red, fontSize: 20, fontWeight: 700 }}>{data.churnImpact.atRisk}/mo</div></div>
      </div>
      <div style={{ background: C.green + "15", padding: 8, borderRadius: 6, fontSize: 12, color: C.green }}>{data.churnImpact.savingsNote}</div>
    </Card>}
  </>;
}

// ── Paywall Optimization Page ──
function PaywallOptimizationPage({ app }) {
  const { data, loading } = useFetch(`/v1/paywall/performance?app_id=${app}`);
  if (loading) return <Spinner/>;
  const variants = data?.variants || [];
  return <>
    <h2 style={{ color: C.text }}>Paywall A/B Testing</h2>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
      <StatCard label="Active Tests" value={data?.activeTests || 0} />
      <StatCard label="Best Conversion" value={`${(data?.bestConversion || 0).toFixed(1)}%`} />
      <StatCard label="Revenue Impact" value={fmt(data?.revenueImpact || 0, "$")} />
    </div>
    {variants.map((v, i) => <Card key={i} style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <strong style={{ color: C.text }}>{v.name}</strong>
        <span style={{ background: v.isWinner ? C.green + "22" : C.bg, color: v.isWinner ? C.green : C.muted, padding: "2px 8px", borderRadius: 12, fontSize: 12 }}>
          {v.isWinner ? "Winner" : v.isControl ? "Control" : "Variant"}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, fontSize: 12 }}>
        <div><span style={{ color: C.muted }}>Views</span><div style={{ color: C.text, fontWeight: 600 }}>{v.views}</div></div>
        <div><span style={{ color: C.muted }}>Converts</span><div style={{ color: C.text, fontWeight: 600 }}>{v.conversions}</div></div>
        <div><span style={{ color: C.muted }}>Rate</span><div style={{ color: v.rate > (data?.bestConversion || 0) * 0.9 ? C.green : C.text, fontWeight: 600 }}>{v.rate?.toFixed(1)}%</div></div>
        <div><span style={{ color: C.muted }}>Revenue</span><div style={{ color: C.text, fontWeight: 600 }}>${v.revenue}</div></div>
      </div>
    </Card>)}
    {variants.length === 0 && <EmptyState msg="No paywall experiments running"/>}
  </>;
}

// ── Build Queue Page ──
function BuildQueuePage() {
  const { data, loading } = useFetch("/v1/builds/dashboard");
  if (loading) return <Spinner/>;
  const queue = data?.queue || [];
  return <>
    <h2 style={{ color: C.text }}>Build Queue</h2>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
      <StatCard label="Queued" value={data?.queued || 0} />
      <StatCard label="Running" value={data?.running || 0} />
      <StatCard label="Today" value={data?.completedToday || 0} />
      <StatCard label="Success Rate" value={`${(data?.successRate || 0).toFixed(0)}%`} />
    </div>
    {queue.length > 0 ? queue.map((build, i) => <Card key={i} style={{ padding: 12, marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <strong style={{ color: C.text }}>{build.app_id || "unknown"}</strong>
          <span style={{ color: C.muted, marginLeft: 8, fontSize: 12 }}>{build.action}</span>
        </div>
        <span style={{ background: build.status === "running" ? C.blue + "22" : build.status === "success" ? C.green + "22" : build.status === "failed" ? C.red + "22" : C.bg,
          color: build.status === "running" ? C.blue : build.status === "success" ? C.green : build.status === "failed" ? C.red : C.muted,
          padding: "2px 8px", borderRadius: 12, fontSize: 12 }}>{build.status}</span>
      </div>
      {build.status === "running" && build.progress && <div style={{ height: 4, background: C.border, borderRadius: 2, marginTop: 8 }}>
        <div style={{ height: "100%", width: `${build.progress}%`, background: C.blue, borderRadius: 2 }}/>
      </div>}
    </Card>) : <EmptyState msg="Build queue is empty"/>}
  </>;
}
